/**
 * Examination Report — Status tab, step 3 (s3)
 * Figma: Status → Examination Report (node-id=28:4101)
 *
 * Sub-status tabs: Accepted | Objected | Accepted after Objection | Hearing after Objection
 * Accepted (implemented):
 *   TM selector row → per-TM two-column panel:
 *     Left: Status of Acceptance | Notice to Client of Acceptance | Inform client about Acceptance
 *     Right: detail panel
 *       - Status of Acceptance → PDF upload (drag/drop) + Save
 *       - Notice to Client of Acceptance → Send merged PDF to client WhatsApp
 *       - Inform client about Acceptance → Monthly-update message preview + Send
 * Other sub-statuses are placeholders (mirrors MarkedForExamView).
 */
import { Fragment, useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { HiOutlineInformationCircle, HiOutlineX } from "react-icons/hi";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { getWorkflowStatus, skipStep, NewApplicationStep } from "api/applications";
import {
  getLeadDetails,
  getExaminationReportAcceptedStatus,
  saveExaminationReportAccepted,
  sendExaminationReportAcceptancePdfToWhatsApp,
  getExaminationReportAcceptedInformClientPreview,
  sendExaminationReportAcceptedInformClientWhatsApp,
  generateUploadPresignedUrls,
  getStatusOfObjection,
  saveStatusOfObjection,
  sendStatusOfObjectionPdfToWhatsApp,
  getStatusOfObjectionInformClientPreview,
  sendStatusOfObjectionInformClientToWhatsApp,
  getStatusOfObjectionConfirmationFromClient,
  saveStatusOfObjectionConfirmationFromClient,
  getReplyToObjectionInvoicePreview,
  getReplyToObjectionInvoiceStatus,
  saveReplyToObjectionInvoice,
  saveReplyToObjectionCollectedAmount,
  generateReplyToObjectionInvoicePdf,
  sendReplyToObjectionInvoiceToClient,
  sendReplyToObjectionInvoicePaymentMessage,
  getReplyToObjectionInvoicePaymentMessagePreview,
  recordStatusOfObjectionCallClientReply,
  recordStatusOfObjectionDocumentsFromClient,
  getAcceptedAfterObjectionStatus,
  saveAcceptedAfterObjection,
  sendAcceptedAfterObjectionPdfToWhatsApp,
  getAcceptedAfterObjectionInformClientPreview,
  sendAcceptedAfterObjectionInformClientWhatsApp,
  type AcceptedAfterObjectionApplication,
  type AcceptedAfterObjectionStatus,
  getReplyToObjectionFiledStatus,
  saveReplyToObjectionFiled,
  getReplyToObjectionFiledInformClientPreview,
  sendReplyToObjectionFiledInformClientWhatsApp,
  getShowCauseHearingStatusDetails,
  setShowCauseHearingStatusDetails,
  getShowCauseHearingInformClientPreview,
  sendShowCauseHearingInformClient,
  getShowCauseHearingInformClientAboutHearingPreview,
  sendShowCauseHearingInformClientAboutHearing,
  sendShowCauseHearingNoticeToClient,
  recordShowCauseHearingCallClient,
  getShowCauseHearingConfirmation,
  saveShowCauseHearingConfirmation,
  getHearingInvoiceStatus,
  getHearingInvoicePreview,
  saveHearingInvoice,
  generateHearingInvoicePdf,
  getRevisedHearingInvoiceStatus,
  saveRevisedHearingInvoice,
  generateRevisedHearingInvoicePdf,
  saveHearingCollectedAmount,
  getHearingInvoicePaymentMessagePreview,
  sendHearingInvoiceToClient,
  sendHearingInvoicePaymentMessage,
  type HearingInvoiceEmailPreview,
  getTmMBeforeHearingInvoiceStatus,
  getTmMBeforeHearingInvoicePreview,
  saveTmMBeforeHearingInvoice,
  generateTmMBeforeHearingInvoicePdf,
  getTmMBeforeHearingInvoicePaymentMessagePreview,
  sendTmMBeforeHearingInvoiceToClient,
  sendTmMBeforeHearingInvoicePaymentMessage,
  type TmMBeforeHearingInvoiceEmailPreview,
  saveTmMBeforeHearingCollectedAmount,
  getTmMBeforeHearingFiledStatus,
  saveTmMBeforeHearingFiled,
  getTmMBeforeHearingInformClientPreview,
  sendTmMBeforeHearingInformClient,
  getStampPaperForHearingStatus,
  saveStampPaperForHearing,
  getStampPaperForHearingMessagePreview,
  sendStampPaperForHearingToVendor,
  saveStampPaperPaymentToVendorHearing,
  recordDocumentsFromClientHearing,
  getUserAffidavitForHearingStatus,
  saveUserAffidavitForHearing,
  generateUserAffidavitForHearingPdf,
  recordSignatureFromClientHearing,
  getNotarisationForHearingStatus,
  saveNotaryPaymentForHearing,
  recordScanningOfDocumentsForHearing,
  recordFilingHearingDocument,
  getNoteOnHearingStatus,
  saveNoteOnHearing,
  generateNoteOnHearingPdf,
  uploadNoteOnHearingSimilarMarkImage,
  getAttendHearingStatus,
  saveAttendHearing,
  getHearingDidntHappenStatus,
  saveHearingDidntHappenPostponementNotice,
  sendHearingDidntHappenToClient,
  getInformAfterHearingStatus,
  getInformAfterHearingPreview,
  sendInformAfterHearingScenario,
  sendAllInformAfterHearing,
  recordUploadDocumentsAfterHearing,
  getTmMAfterHearingInvoiceStatus,
  saveTmMAfterHearingInvoice,
  generateTmMAfterHearingInvoicePdf,
  getTmMAfterHearingInvoicePaymentMessagePreview,
  sendTmMAfterHearingInvoiceToClient,
  sendTmMAfterHearingInvoicePaymentMessage,
  saveTmMAfterHearingCollectedAmount,
  getTmMAfterHearingFiledStatus,
  saveTmMAfterHearingFiled,
  getInformClientTmMAfterHearingPreview,
  sendInformClientTmMAfterHearing,
  getSubsequentHearingStatus,
  saveSubsequentHearingDetails,
  getSubsequentHearingNoticePreview,
  sendSubsequentHearingNotice,
  saveSubsequentHearingOutcome,
  saveSubsequentHearingPostponement,
  getSubsequentHearingInvoiceStatus,
  getSubsequentHearingInvoicePreview,
  getSubsequentHearingInvoiceWhatsAppPreview,
  saveSubsequentHearingInvoice,
  generateSubsequentHearingInvoicePdf,
  sendSubsequentHearingInvoiceToClient,
  saveSubsequentHearingCollectedAmount,
  type SubsequentHearingInvoiceStatus,
  type SubsequentHearingInvoicePreview,
  getOrderOfHearingStatus,
  saveOrderOfHearingDetails,
  forwardOrderOfHearingPdfsToClient,
  getOrderOfHearingMessagePreview,
  sendOrderOfHearingMessage,
  type StampPaperForHearingStatus,
  type UserAffidavitForHearingStatus,
  type UserAffidavitForHearingItem,
  type UserAffidavitForHearingTurnover,
  type NotarisationForHearingStatus,
  type NotarisationForHearingApplication,
  type NoteOnHearingStatus,
  type NoteOnHearingApplicationData,
  type NoteOnHearingSimilarMark,
  type NoteOnHearingSimilarMarkNote,
  type AttendHearingStatus,
  type AttendHearingApplicationData,
  type HearingStatusEnum,
  type HearingOfficerRemarkEnum,
  type HearingDidntHappenStatus,
  type HearingDidntHappenApplication,
  type InformAfterHearingStatus,
  type InformAfterHearingScenarioGroup,
  type InformAfterHearingScenario,
  type TmMAfterHearingInvoiceStatus,
  type TmMAfterHearingInvoiceEmailPreview,
  type TmMAfterHearingFiledStatus,
  type SubsequentHearingStatus,
  type SubsequentHearingApplication,
  type OrderOfHearingStatus,
  type OrderOfHearingApplication,
  type OrderOfHearingOutcome,
  type HearingClientConfirmation,
  type HearingSuggestedNextStep,
  type HearingConfirmationApplication,
  type HearingInvoiceStatus,
  type HearingInvoiceTmRow,
  type RevisedHearingInvoiceStatus,
  type HearingInvoicePreview,
  type TmMBeforeHearingInvoiceStatus,
  type TmMBeforeHearingInvoicePreview,
  type TmMBeforeHearingFiledStatus,
  type TmMBeforeHearingFiledApplication,
  type ShowCauseHearingApplication,
  type ShowCauseHearingStatus,
  type ReplyToObjectionFiledApplication,
  type ReplyToObjectionInvoicePreview,
  type ReplyToObjectionInvoiceStatus,
  type ExaminationReportAcceptedApplication,
  type ExaminationReportAcceptedStatus,
  type StatusOfObjectionApplication,
  type StatusOfObjectionStatus,
  type ReasonForObjection,
  type SimilarMark,
  getStampVendors,
  type StampVendorOption,
} from "api/discussion";
import { formatTmNoForDisplay } from "utils/tmNumberDisplay";
import { formatOrdinalLongDate } from "utils/ordinalLongDate";
import { downloadFileFromUrlWithFallback } from "utils/downloadFileFromUrl";
import { renderWhatsAppFormatting } from "lib/whatsappFormatting";
import uploadDropzoneGraphic from "assets/svg/upload-modal-dropzone-graphic.svg";
import pdfIcon from "assets/svg/pdf-icon.svg";
import invoiceAmountCheck from "assets/svg/invoice-amount-check.svg";
import invoiceGenerate from "assets/svg/invoice-generate.svg";
import collectDocumentsGraphic from "assets/svg/collectDocuments.svg";
import processToNextStepGraphic from "assets/svg/Process-to-next-step.svg";
import ConfirmMessageModal from "components/ConfirmMessageModal";
import FilePreviewModal, { detectPreviewKind } from "components/FilePreviewModal";
import IncludeInvoiceNoCheckbox from "components/IncludeInvoiceNoCheckbox";
import type { FilePreviewKind } from "components/FilePreviewModal";
import DateTimePicker from "components/DateTimePicker";
import legalAdviceHeadset from "assets/svg/legal-advice-headset.svg";
import chevronDownIcon from "assets/svg/chevron-down.svg";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export const EXAMINATION_REPORT_NOTICE_FORM_ID =
  "examination-report-notice-to-client-form";
export const EXAMINATION_REPORT_SOA_FORM_ID =
  "examination-report-status-of-acceptance-form";
export const EXAMINATION_REPORT_INFORM_FORM_ID =
  "examination-report-inform-client-form";
export const EXAMINATION_REPORT_SOO_FORM_ID =
  "examination-report-status-of-objection-form";
export const EXAMINATION_REPORT_NTCO_FORM_ID =
  "examination-report-notice-to-client-objection-form";
export const EXAMINATION_REPORT_INCO_FORM_ID =
  "examination-report-inform-client-objection-form";
export const EXAMINATION_REPORT_CCRO_FORM_ID =
  "examination-report-call-client-reply-objection-form";
export const EXAMINATION_REPORT_CCO_FORM_ID =
  "examination-report-client-confirmation-objection-form";
export const EXAMINATION_REPORT_INV_FORM_ID =
  "examination-report-invoice-to-client-objection-form";
export const EXAMINATION_REPORT_PAY_FORM_ID =
  "examination-report-payment-objection-form";
export const EXAMINATION_REPORT_DOC_FORM_ID =
  "examination-report-documents-objection-form";
export const EXAMINATION_REPORT_FRO_FORM_ID =
  "examination-report-filing-reply-objection-form";
export const EXAMINATION_REPORT_ICR_FORM_ID =
  "examination-report-inform-client-reply-objection-form";
export const EXAMINATION_REPORT_AAO_STATUS_FORM_ID =
  "examination-report-status-of-accepted-after-objection-form";
export const EXAMINATION_REPORT_AAO_NOTICE_FORM_ID =
  "examination-report-notice-to-client-aao-form";
export const EXAMINATION_REPORT_AAO_INFORM_FORM_ID =
  "examination-report-inform-client-aao-form";
export const EXAMINATION_REPORT_HAO_INFORM_FORM_ID =
  "examination-report-inform-client-hao-form";
export const EXAMINATION_REPORT_HAO_STATUS_FORM_ID =
  "examination-report-status-details-hearing-form";
export const EXAMINATION_REPORT_HAO_NOTICE_FORM_ID =
  "examination-report-notice-to-client-hearing-form";
export const EXAMINATION_REPORT_HAO_INFORM_HEARING_FORM_ID =
  "examination-report-inform-client-about-hearing-form";
export const EXAMINATION_REPORT_HAO_CALL_CLIENT_FORM_ID =
  "examination-report-call-client-hearing-form";
export const EXAMINATION_REPORT_HAO_CONFIRMATION_FORM_ID =
  "examination-report-confirmation-hearing-form";
export const EXAMINATION_REPORT_HAO_REVISED_INVOICE_FORM_ID =
  "examination-report-revised-invoice-hearing-form";
export const EXAMINATION_REPORT_HAO_INVOICE_FORM_ID =
  "examination-report-invoice-for-hearing-form";
export const EXAMINATION_REPORT_HAO_TMM_INVOICE_FORM_ID =
  "examination-report-tmm-before-invoice-form";
export const EXAMINATION_REPORT_HAO_TMM_PAYMENT_FORM_ID =
  "examination-report-tmm-before-payment-form";
export const EXAMINATION_REPORT_HAO_TMM_FILE_FORM_ID =
  "examination-report-tmm-before-file-form";
export const EXAMINATION_REPORT_HAO_TMM_INFORM_FORM_ID =
  "examination-report-tmm-before-inform-form";
export const EXAMINATION_REPORT_HAO_PAYMENT_FORM_ID =
  "examination-report-payment-from-client-hearing-form";
export const EXAMINATION_REPORT_HAO_STAMP_VENDOR_FORM_ID =
  "examination-report-stamp-paper-vendor-hearing-form";
export const EXAMINATION_REPORT_HAO_STAMP_FROM_VENDOR_FORM_ID =
  "examination-report-stamp-paper-from-vendor-hearing-form";
export const EXAMINATION_REPORT_HAO_DOCS_FROM_CLIENT_FORM_ID =
  "examination-report-documents-from-client-hearing-form";
export const EXAMINATION_REPORT_HAO_USER_AFFIDAVIT_FORM_ID =
  "examination-report-user-affidavit-hearing-form";
export const EXAMINATION_REPORT_HAO_SIGNATURE_FORM_ID =
  "examination-report-signature-from-client-hearing-form";
export const EXAMINATION_REPORT_HAO_NOTARISATION_FORM_ID =
  "examination-report-notarisation-hearing-form";
export const EXAMINATION_REPORT_HAO_SCANNING_FORM_ID =
  "examination-report-scanning-hearing-form";
export const EXAMINATION_REPORT_HAO_FILE_HEARING_DOC_FORM_ID =
  "examination-report-file-hearing-document-form";
export const EXAMINATION_REPORT_HAO_NOTE_ON_HEARING_FORM_ID =
  "examination-report-note-on-hearing-form";
export const EXAMINATION_REPORT_HAO_ATTEND_HEARING_FORM_ID =
  "examination-report-attend-hearing-form";
export const EXAMINATION_REPORT_HAO_HEARING_NOT_HAPPENED_FORM_ID =
  "examination-report-hearing-not-happened-form";
export const EXAMINATION_REPORT_HAO_INFORM_AFTER_HEARING_FORM_ID =
  "examination-report-inform-after-hearing-form";
export const EXAMINATION_REPORT_HAO_UPLOAD_DOCS_AFTER_HEARING_FORM_ID =
  "examination-report-upload-documents-after-hearing-form";
export const EXAMINATION_REPORT_HAO_TMM_AFTER_INVOICE_FORM_ID =
  "examination-report-tmm-after-invoice-form";
export const EXAMINATION_REPORT_HAO_TMM_AFTER_PAYMENT_FORM_ID =
  "examination-report-tmm-after-payment-form";
export const EXAMINATION_REPORT_HAO_FILE_TMM_AFTER_HEARING_FORM_ID =
  "examination-report-file-tmm-after-hearing-form";
export const EXAMINATION_REPORT_HAO_INFORM_TMM_AFTER_HEARING_FORM_ID =
  "examination-report-inform-tmm-after-hearing-form";
export const EXAMINATION_REPORT_HAO_SUBSEQUENT_HEARINGS_FORM_ID =
  "examination-report-subsequent-hearings-form";
export const EXAMINATION_REPORT_HAO_SUBSEQUENT_INVOICE_FORM_ID =
  "examination-report-subsequent-hearing-invoice-form";
export const EXAMINATION_REPORT_HAO_SUBSEQUENT_PAYMENT_FORM_ID =
  "examination-report-subsequent-hearing-payment-from-client-form";
export const EXAMINATION_REPORT_HAO_SUBSEQUENT_ATTEND_FORM_ID =
  "examination-report-subsequent-hearing-attend-form";
export const EXAMINATION_REPORT_HAO_SUBSEQUENT_POSTPONEMENT_FORM_ID =
  "examination-report-subsequent-hearing-postponement-form";
export const EXAMINATION_REPORT_HAO_ORDER_OF_HEARING_FORM_ID =
  "examination-report-order-of-hearing-form";

const APPLICANT_TYPE_LABELS: Record<string, string> = {
  SOLE_PROPRIETORSHIP: "Sole Proprietorship",
  TRUST: "Trust",
  PARTNERSHIP_FIRM: "Partnership Firm",
  SOCIETY: "Society",
  PRIVATE_LIMITED_COMPANY: "Private Limited Company",
  LIMITED_LIABILITY_PARTNERSHIP: "Limited Liability Partnership",
  OTHERS: "Others",
};

const formatApplicantType = (value: string | null | undefined): string =>
  value ? APPLICANT_TYPE_LABELS[value] ?? value : "";

const NOTICE_STEP_SLUG =
  "S3_03A_NOTICE_TO_CLIENT_EXAMINATION_REPORT_ACCEPTED";
const INFORM_STEP_SLUG =
  "S3_03B_INFORM_CLIENT_EXAMINATION_REPORT_ACCEPTED";
const NOTICE_TO_CLIENT_OBJECTION_STEP_SLUG =
  "S3_03B_II_NOTICE_TO_CLIENT_OBJECTION";
const INFORM_CLIENT_OBJECTION_STEP_SLUG =
  "S3_03B_III_INFORM_CLIENT_OBJECTION";
const CALL_CLIENT_REPLY_TO_OBJECTION_STEP_SLUG =
  "S3_03B_IV_CALL_CLIENT_REPLY_TO_OBJECTION";
const CONFIRMATION_FROM_CLIENT_OBJECTION_STEP_SLUG =
  "S3_03B_V_CONFIRMATION_FROM_CLIENT_REPLY_TO_OBJECTION";
const DOCUMENTS_FROM_CLIENT_OBJECTION_STEP_SLUG =
  "S3_03B_VIII_DOCUMENTS_FROM_CLIENT_REPLY_TO_OBJECTION";
const FILING_OF_REPLY_TO_OBJECTION_STEP_SLUG =
  "S3_03B_IX_FILING_OF_REPLY_TO_OBJECTION";
const INFORM_CLIENT_REPLY_TO_OBJECTION_STEP_SLUG =
  "S3_03B_X_INFORM_CLIENT_REPLY_TO_OBJECTION";
const NOTICE_TO_CLIENT_AAO_STEP_SLUG =
  "S3_03C_II_NOTICE_TO_CLIENT_ACCEPTED_AFTER_OBJECTION";
const INFORM_CLIENT_AAO_STEP_SLUG =
  "S3_03C_III_INFORM_CLIENT_ACCEPTED_AFTER_OBJECTION";
const INFORM_CLIENT_HEARING_STEP_SLUG =
  "S3_03D_I_INFORM_CLIENT_READY_FOR_SHOW_CAUSE_HEARING";
const STATUS_DETAILS_HEARING_STEP_SLUG = "S3_03D_II_STATUS_DETAILS_OF_HEARING";
const NOTICE_TO_CLIENT_HEARING_STEP_SLUG = "S3_03D_III_NOTICE_TO_CLIENT_HEARING";
const INFORM_CLIENT_ABOUT_HEARING_STEP_SLUG =
  "S3_03D_IV_INFORM_CLIENT_ABOUT_HEARING_1";
const CALL_CLIENT_HEARING_STEP_SLUG = "S3_03D_VI_CALL_CLIENT_HEARING_1";
const CONFIRMATION_FROM_CLIENT_HEARING_STEP_SLUG =
  "S3_03D_VII_CONFIRMATION_FROM_CLIENT_HEARING_1";
const INVOICE_FOR_HEARING_STEP_SLUG = "S3_03D_VA_SAVE_INVOICE_HEARING_1";
const TMM_BEFORE_HEARING_INVOICE_STEP_SLUG =
  "S3_03D_VIII_A_SAVE_INVOICE_TM_M_BEFORE_HEARING";
const TMM_BEFORE_HEARING_SEND_INVOICE_STEP_SLUG =
  "S3_03D_VIII_B_SEND_INVOICE_TM_M_BEFORE_HEARING";
const TMM_AFTER_HEARING_SEND_INVOICE_STEP_SLUG =
  "S3_03D_XXVI_B_SEND_INVOICE_TM_M_AFTER_HEARING";
const TMM_BEFORE_HEARING_PAYMENT_STEP_SLUG =
  "S3_03D_IX_PAYMENT_FROM_CLIENT_TM_M_BEFORE_HEARING";
const TMM_BEFORE_HEARING_FILE_STEP_SLUG =
  "S3_03D_X_FILE_TM_M_BEFORE_HEARING";
const TMM_BEFORE_HEARING_INFORM_STEP_SLUG =
  "S3_03D_XI_INFORM_CLIENT_TM_M_BEFORE_HEARING_FILED";
const PAYMENT_FROM_CLIENT_HEARING_STEP_SLUG =
  "S3_03D_XII_PAYMENT_FROM_CLIENT_HEARING";
const STAMP_PAPER_VENDOR_HEARING_STEP_SLUG =
  "S3_03D_XIII_STAMP_PAPER_DETAILS_TO_VENDOR_HEARING";

const TM_M_BEFORE_HEARING_INVOICE_FOLDER = "tm-m-before-hearing-invoice";
const TM_M_BEFORE_HEARING_FILED_FOLDER = "tm-m-before-hearing-filed";

const HEARING_NOTICE_FOLDER = "hearing-notice";

/** Convert "DD-MM-YYYY" or "YYYY-MM-DD" to a Date object. */
function parseDateForHearingInput(s: string | null | undefined): Date | null {
  if (!s) return null;
  const trimmed = s.trim();
  if (!trimmed) return null;
  const ddmmyyyy = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (ddmmyyyy) {
    const [, dd, mm, yyyy] = ddmmyyyy;
    const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
    return isNaN(d.getTime()) ? null : d;
  }
  const yyyymmdd = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (yyyymmdd) {
    const d = new Date(`${trimmed}T00:00:00`);
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(trimmed);
  return isNaN(d.getTime()) ? null : d;
}

function formatDateDDMMYYYYForApi(d: Date): string {
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = String(d.getFullYear());
  return `${dd}-${mm}-${yyyy}`;
}

/** Inverse of `formatDateDDMMYYYYForApi` — the status endpoints return DD-MM-YYYY. */
function parseDdMmYyyy(value: string | null | undefined): Date | null {
  if (!value) return null;
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value.trim());
  if (!m) return null;
  const [, dd, mm, yyyy] = m;
  const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  return Number.isNaN(d.getTime()) ? null : d;
}

const REPLY_TO_OBJECTION_FILED_FOLDER = "reply-to-objection-filed";
const EXAMINATION_REPORT_ACCEPTANCE_AFTER_OBJECTION_FOLDER =
  "examination-report-acceptance-after-objection";

type AaoSubStep = "status-of-aao" | "notice-to-client" | "inform-client";

const AAO_SUB_STEPS: { id: AaoSubStep; label: string }[] = [
  { id: "status-of-aao", label: "Status of Accepted after Objection" },
  { id: "notice-to-client", label: "Notice to Client" },
  { id: "inform-client", label: "Inform Client" },
];

const HAO_SUB_STEPS = [
  { id: "inform-client-showcause", label: "Inform Client (Showcause)" },
  { id: "status-details-of-hearing", label: "Status details of Hearing" },
  { id: "notice-to-client-for-hearing", label: "Notice to Client for Hearing" },
  { id: "inform-client-about-hearing", label: "Inform Client about Hearing" },
  { id: "invoice-for-hearing", label: "Invoice to Client for Hearing 1" },
  { id: "call-client-for-hearing", label: "Call Client for Hearing" },
  {
    id: "confirmation-from-client-for-hearing",
    label: "Confirmation from Client for Hearing",
  },
  {
    id: "revised-invoice-for-hearing",
    label: "Revised Invoice for Hearing 1",
  },
  { id: "invoice-for-tmm-before-hearing", label: "Invoice for TM-M before hearing" },
  {
    id: "payment-from-client-for-tmm-before-hearing",
    label: "Payment from Client for TM-M before Hearing",
  },
  { id: "file-tmm-before-hearing", label: "File TM-M before Hearing" },
  {
    id: "inform-client-about-filing-tmm-before-hearing",
    label: "Inform Client about Filing of TM-M before Hearing",
  },
  {
    id: "payment-from-client-for-hearing",
    label: "Payment from Client for Hearing",
  },
  {
    id: "stamp-paper-details-to-vendor-for-hearing",
    label: "Stamp Paper details to Vendor for hearing",
  },
  {
    id: "stamp-paper-from-vendor-for-hearing",
    label: "Stamp Paper from Vendor for hearing",
  },
  {
    id: "documents-from-client-for-hearing",
    label: "Documents from Client for hearing",
  },
  {
    id: "preparation-of-user-affidavit-for-hearing",
    label: "Preparation of User Affidavit for hearing",
  },
  { id: "signature-from-client-for-hearing", label: "Signature from Client for hearing" },
  {
    id: "notarisation-of-documents-for-hearing",
    label: "Notarisation of Documents for hearing",
  },
  {
    id: "scanning-of-documents-for-hearing",
    label: "Scanning of Documents for hearing",
  },
  { id: "file-hearing-document", label: "File Hearing Document" },
  { id: "note-on-hearing", label: "Note on Hearing" },
  { id: "attend-hearing", label: "Attend Hearing" },
  {
    id: "notice-to-client-for-hearing-not-happened",
    label: "Notice to Client for hearing not happened",
  },
  { id: "inform-client-after-hearing", label: "Inform Client after hearing" },
  { id: "upload-documents-after-hearing", label: "Upload Documents after Hearing" },
  { id: "invoice-for-tmm-after-hearing", label: "Invoice for TM-M after hearing" },
  {
    id: "payment-from-client-for-tmm-after-hearing",
    label: "Payment from Client for TM-M after Hearing",
  },
  { id: "file-tmm-after-hearing-1", label: "File TM-M after Hearing 1" },
  {
    id: "inform-client-about-filing-tmm-after-hearing",
    label: "Inform Client about filing TM-M after hearing",
  },
  { id: "subsequent-hearings", label: "Subsequent Hearings" },
  {
    id: "subsequent-hearings-invoice",
    label: "Invoice for Subsequent Hearing",
  },
  {
    id: "subsequent-hearings-payment-from-client",
    label: "Payment from Client for Subsequent Hearing",
  },
  {
    id: "subsequent-hearings-attend",
    label: "Attend Subsequent Hearing",
  },
  {
    id: "subsequent-hearings-postponement",
    label: "Notice for Subsequent Hearing Postponed",
  },
  { id: "order-of-hearing-to-client", label: "Order of Hearing to Client" },
] as const;

type HaoSubStep = (typeof HAO_SUB_STEPS)[number]["id"];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ExamSubStatus =
  | "accepted"
  | "objected"
  | "accepted-after-objection"
  | "hearing-after-objection";

type AcceptedSubStep =
  | "status-of-acceptance"
  | "notice-to-client"
  | "inform-client";

type ObjectedSubStep =
  | "status-of-objection"
  | "notice-to-client"
  | "inform-client"
  | "call-client"
  | "client-confirmation"
  | "invoice-to-client"
  | "payment"
  | "documents"
  | "filing-reply"
  | "inform-client-final";

const OBJECTED_SUB_STEPS: { id: ObjectedSubStep; label: string }[] = [
  { id: "status-of-objection", label: "Status of Objection" },
  { id: "notice-to-client", label: "Notice to Client" },
  { id: "inform-client", label: "Inform Client" },
  { id: "call-client", label: "Call Client" },
  { id: "client-confirmation", label: "Client Confirmation" },
  { id: "invoice-to-client", label: "Invoice to Client" },
  { id: "payment", label: "Payment" },
  { id: "documents", label: "Documents" },
  { id: "filing-reply", label: "Filing Reply" },
  { id: "inform-client-final", label: "Inform Client" },
];

const REASON_OPTIONS: {
  key: keyof Pick<
    ReasonForObjection,
    "similarity" | "nonDistinctive" | "supportingDocuments" | "tmMYes" | "others"
  >;
  label: string;
}[] = [
  { key: "similarity", label: "Similarity" },
  { key: "nonDistinctive", label: "Non Distinctive" },
  { key: "supportingDocuments", label: "Supporting Documents" },
  { key: "tmMYes", label: "TM-M : Yes" },
  { key: "others", label: "Others (Specify)" },
];

const SUB_STATUS_TABS: { id: ExamSubStatus; label: string }[] = [
  { id: "accepted", label: "Accepted" },
  { id: "objected", label: "Objected" },
  { id: "accepted-after-objection", label: "Accepted after Objection" },
  { id: "hearing-after-objection", label: "Hearing after Objection" },
];

const ACCEPTED_SUB_STEPS: { id: AcceptedSubStep; label: string }[] = [
  { id: "status-of-acceptance", label: "Status of Acceptance" },
  { id: "notice-to-client", label: "Notice to Client of Acceptance" },
  { id: "inform-client", label: "Inform client about Acceptance" },
];

const EXAMINATION_REPORT_ACCEPTANCE_FOLDER = "examination-report-acceptance";
const EXAMINATION_REPORT_OBJECTION_FOLDER = "examination-report-objection";
const UPLOAD_ACCEPT = ".pdf,.jpg,.jpeg,.png";
const UPLOAD_HELP = "PDF, JPEG, PNG formats, up to 50MB";

// ---------------------------------------------------------------------------
// Shared primitives (mirrors MarkedForExamView & FormalitiesCheckView)
// ---------------------------------------------------------------------------

function RadioCircle({ active }: { active: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="flex-shrink-0"
    >
      <circle cx="8" cy="8" r="8" fill={active ? "white" : "#6D6D6D"} />
      <path
        d="M4.5 8.2 L6.8 10.5 L11.6 5.3"
        stroke={active ? "#222222" : "white"}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

function ChevronRight({ active }: { active: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6 4L10 8L6 12"
        stroke={active ? "white" : "#6D6D6D"}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ComingSoonPanel({ label }: { label: string }) {
  return (
    <div
      className="flex flex-col gap-3"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <p className="text-sm text-[#6D6D6D]">{label} — Coming soon</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Drag/drop file upload card — Figma: Main Container + uploader + Browse btn
// ---------------------------------------------------------------------------

function UploaderIllustration() {
  return (
    <img
      src={uploadDropzoneGraphic}
      alt=""
      width={60}
      height={60}
      className="flex-shrink-0"
    />
  );
}

function FileUploadDropzone({
  inputId,
  hasFile,
  fileLabel,
  isDragging,
  uploading,
  onDropFile,
  onPickFile,
  onRemove,
  onDragEnter,
  onDragLeave,
}: {
  inputId: string;
  hasFile: boolean;
  fileLabel: string | null;
  isDragging: boolean;
  uploading: boolean;
  onDropFile: (file: File) => void;
  onPickFile: (file: File) => void;
  onRemove: () => void;
  onDragEnter: () => void;
  onDragLeave: () => void;
}) {
  return (
    <div
      className="flex flex-col gap-5 self-stretch bg-white border border-[#EEEEEE] rounded-[10px] p-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Header */}
      <div className="flex flex-col gap-2 self-stretch">
        <p
          className="font-semibold text-[#101111]"
          style={{
            fontSize: "14px",
            lineHeight: "100%",
            letterSpacing: "0.01em",
          }}
        >
          File Upload
        </p>
        <p
          className="font-normal text-[#7D7D7D]"
          style={{
            fontSize: "12px",
            lineHeight: "24px",
            letterSpacing: "0.04em",
          }}
        >
          Choose a file and upload securely to proceed.
        </p>
      </div>

      {/* Uploader */}
      <div
        className={`self-stretch flex flex-row justify-between items-center gap-5 px-5 py-5 rounded-[16px] border border-dashed bg-white transition-colors cursor-pointer ${
          isDragging
            ? "border-[#222222] bg-[#F5F5F5]"
            : hasFile
            ? "border-[#22C55E] bg-[#F0FDF4]"
            : "border-[#D8DAEB]"
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          onDragEnter();
        }}
        onDragLeave={onDragLeave}
        onDrop={(e) => {
          e.preventDefault();
          onDragLeave();
          const file = e.dataTransfer.files[0];
          if (file) onDropFile(file);
        }}
        onClick={() => {
          if (uploading) return;
          document.getElementById(inputId)?.click();
        }}
      >
        <div className="flex flex-row items-center gap-5 min-w-0">
          <UploaderIllustration />
          <div className="flex flex-col gap-3 min-w-0">
            {hasFile ? (
              <>
                <p
                  className="font-medium text-[#16A34A] truncate"
                  style={{
                    fontSize: "16px",
                    lineHeight: "100%",
                    letterSpacing: "0.01em",
                  }}
                >
                  {fileLabel ?? "File selected"}
                </p>
                <p
                  className="font-normal text-[#7D7D7D]"
                  style={{ fontSize: "14px", lineHeight: "21px" }}
                >
                  {uploading ? "Uploading…" : UPLOAD_HELP}
                </p>
              </>
            ) : (
              <>
                <p
                  className="font-medium text-[#101111]"
                  style={{
                    fontSize: "16px",
                    lineHeight: "100%",
                    letterSpacing: "0.01em",
                  }}
                >
                  Drag and drop your files
                </p>
                <p
                  className="font-normal text-[#7D7D7D]"
                  style={{ fontSize: "14px", lineHeight: "21px" }}
                >
                  {UPLOAD_HELP}
                </p>
              </>
            )}
          </div>
        </div>

        {hasFile ? (
          <button
            type="button"
            disabled={uploading}
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="flex-shrink-0 flex items-center justify-center w-[107px] h-9 px-4 rounded-[10px] bg-white border border-[#D5D3E0] capitalize text-[#2E3037] hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              boxShadow: "0px 5px 20px rgba(0, 0, 0, 0.02)",
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: "16px",
              letterSpacing: "0.04em",
            }}
          >
            Remove
          </button>
        ) : (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              document.getElementById(inputId)?.click();
            }}
            className="flex-shrink-0 flex items-center justify-center w-[107px] h-9 px-4 rounded-[10px] bg-white border border-[#D5D3E0] capitalize text-[#2E3037] hover:bg-gray-50"
            style={{
              boxShadow: "0px 5px 20px rgba(0, 0, 0, 0.02)",
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: "16px",
              letterSpacing: "0.04em",
            }}
          >
            Browse
          </button>
        )}
      </div>

      <input
        id={inputId}
        type="file"
        accept={UPLOAD_ACCEPT}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onPickFile(file);
          e.currentTarget.value = "";
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared message preview / WhatsApp send card
// ---------------------------------------------------------------------------

function MessagePreviewCard({
  loading,
  message,
  phone,
  sending,
  sent,
  onSend,
  sendLabel = "Send to WhatsApp",
}: {
  loading: boolean;
  message: string | null;
  phone: string;
  sending: boolean;
  sent: boolean;
  onSend: () => void;
  sendLabel?: string;
}) {
  return (
    <div
      className="flex flex-col gap-2.5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <span
        className="text-sm font-normal text-[#222222]"
        style={{ lineHeight: "20px" }}
      >
        Message Preview
      </span>

      <div className="border border-[#EEEEEE] rounded-xl overflow-hidden">
        <div className="px-4 py-4 min-h-[80px]">
          {loading ? (
            <span
              className="text-xs text-[#6D6D6D]"
              style={{ lineHeight: "20px" }}
            >
              Loading preview…
            </span>
          ) : message ? (
            <p
              className="text-xs text-[#222222] whitespace-pre-line"
              style={{ lineHeight: "20px" }}
            >
              {renderWhatsAppFormatting(message)}
            </p>
          ) : (
            <span
              className="text-xs text-[#6D6D6D]"
              style={{ lineHeight: "20px" }}
            >
              No preview available
            </span>
          )}
        </div>

        <div className="border-t border-[#EEEEEE] flex items-center gap-3 px-3 py-2.5">
          <div className="flex-1 min-w-0 h-9 flex items-center px-2.5 rounded-[6px] border border-[#EEEEEE] bg-[#F7F7F7]">
            <span
              className="text-xs text-[#222222] truncate"
              style={{ lineHeight: "20px" }}
            >
              {phone || "(No phone number)"}
            </span>
          </div>
          <button
            type="button"
            onClick={onSend}
            disabled={!phone || sending || loading}
            className={`h-9 px-4 text-xs font-semibold text-white rounded-md flex-shrink-0 transition-colors ${
              sent
                ? "bg-[#333333] hover:bg-[#444444]"
                : "bg-[#222222] hover:bg-[#333333]"
            } disabled:opacity-60 disabled:cursor-not-allowed`}
            style={{ lineHeight: "20px" }}
          >
            {sending
              ? "Sending…"
              : sent
              ? sendLabel.startsWith("Resend")
                ? sendLabel
                : `Re${sendLabel.toLowerCase()}`
              : sendLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status of Acceptance panel — PDF upload per selected TM
// ---------------------------------------------------------------------------

function StatusOfAcceptancePanel({
  leadId,
  application,
  onSaved,
}: {
  leadId: string;
  application: ExaminationReportAcceptedApplication;
  onSaved: (next: ExaminationReportAcceptedApplication) => void;
}) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingName, setPendingName] = useState<string | null>(null);
  const [preview, setPreview] = useState<
    { url: string; name: string; kind: FilePreviewKind } | null
  >(null);

  const openExistingPreview = () => {
    const url = application.examinationReportAcceptanceFileUrl;
    if (!url) return;
    const name = application.examinationReportAcceptanceFilename ?? "Examination report";
    setPreview({ url, name, kind: detectPreviewKind(name) });
  };

  const hasExistingFile = !!application.examinationReportAcceptanceFileKey;
  const displayName =
    pendingName ??
    application.examinationReportAcceptanceFilename ??
    null;

  const uploadFile = async (file: File) => {
    if (!leadId || uploading) return;
    setUploading(true);
    setPendingName(file.name);
    try {
      const [presigned] = await generateUploadPresignedUrls(
        [
          {
            filename: file.name,
            mimeType: file.type || "application/pdf",
          },
        ],
        "private",
        EXAMINATION_REPORT_ACCEPTANCE_FOLDER
      );

      const putRes = await fetch(presigned.url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "application/pdf" },
      });
      if (!putRes.ok) throw new Error("Upload failed");

      const updated = await saveExaminationReportAccepted(leadId, {
        applications: [
          {
            tmNo: application.tmNo,
            examinationReportAcceptanceFileKey: presigned.key,
            examinationReportAcceptanceFilename: file.name,
          },
        ],
      });
      const next =
        updated.applications.find((a) => a.tmNo === application.tmNo) ??
        application;
      onSaved(next);
      setPendingName(null);
      toast.success("Examination Report of Acceptance uploaded");
    } catch (err) {
      setPendingName(null);
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to upload Examination Report"
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="flex flex-col gap-4"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <FileUploadDropzone
        inputId={`examination-report-accepted-file-${application.tmNo}`}
        hasFile={!!displayName}
        fileLabel={displayName}
        isDragging={dragging}
        uploading={uploading}
        onDropFile={(file) => void uploadFile(file)}
        onPickFile={(file) => void uploadFile(file)}
        onRemove={() => setPendingName(null)}
        onDragEnter={() => setDragging(true)}
        onDragLeave={() => setDragging(false)}
      />

      {hasExistingFile && !uploading && (
        <div className="flex items-center gap-3">
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="9" cy="9" r="9" fill="#22C55E" />
            <path
              d="M5 9L8 12L13 6"
              stroke="white"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="text-sm font-medium text-[#16A34A]">Uploaded</span>
          {application.examinationReportAcceptanceFileUrl ? (
            <button
              type="button"
              onClick={openExistingPreview}
              className="ml-1 inline-flex items-center justify-center h-8 px-3 text-xs font-medium text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[8px] hover:bg-gray-50"
            >
              Preview
            </button>
          ) : null}
        </div>
      )}

      <FilePreviewModal
        open={!!preview}
        onClose={() => setPreview(null)}
        name={preview?.name ?? ""}
        url={preview?.url}
        kind={preview?.kind}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Notice to Client of Acceptance panel — PDF link + phone chip
// Footer drives the send; resend button appears inline when already sent.
// ---------------------------------------------------------------------------

/**
 * Blocks a 3A client send while any Accepted application still lacks its
 * Examination Report of Acceptance PDF. Returns true when the send may proceed.
 */
function assertAllAcceptanceUploadsPresent(tmNosMissingUpload: string[]): boolean {
  if (!tmNosMissingUpload.length) return true;
  toast.error(
    `Upload the Examination Report of Acceptance for TM No.(s): ${tmNosMissingUpload
      .map(formatTmNoForDisplay)
      .join(", ")} before sending to the client.`
  );
  return false;
}

/** Inline notice listing the Accepted applications still missing an upload. */
function PendingAcceptanceUploadsWarning({
  tmNosMissingUpload,
}: {
  tmNosMissingUpload: string[];
}) {
  if (!tmNosMissingUpload.length) return null;
  return (
    <div
      className="flex flex-col gap-1 self-stretch px-3.5 py-3 bg-[#FFFBEB] border border-[#FDE68A] rounded-[6px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <span className="text-[13px] font-medium leading-5 text-[#92400E]">
        Upload pending for{" "}
        {tmNosMissingUpload.map(formatTmNoForDisplay).join(", ")}
      </span>
      <span className="text-[12px] leading-5 text-[#B45309]">
        This message covers every application accepted under this acknowledgement.
        Upload the Examination Report of Acceptance for the marks above in "Status of
        Acceptance" before sending it to the client.
      </span>
    </div>
  );
}

function NoticeToClientPanel({
  leadId,
  phone,
  pdfFilename,
  hasSent,
  tmNosMissingUpload,
  onSent,
  onAdvance,
  onSavingChange,
  resending,
  onResend,
}: {
  leadId: string;
  phone: string;
  pdfFilename: string | null;
  hasSent: boolean;
  /** Accepted TM No.(s) with no acceptance PDF yet — blocks the send while non-empty */
  tmNosMissingUpload: string[];
  /** Called after a successful send — marks hasSent and advances to next sub-step */
  onSent: () => void;
  /** Called when Save & Continue is clicked on an already-sent notice — just advance */
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
  resending: boolean;
  onResend: () => void;
}) {
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSent) {
      onAdvance();
      return;
    }
    if (!assertAllAcceptanceUploadsPresent(tmNosMissingUpload)) return;
    onSavingChange?.(true);
    try {
      // Unscoped on purpose: the acknowledgement's Accepted PDFs are merged into one
      // combined file, and it is what lets the backend's own missing-PDF guard run.
      await sendExaminationReportAcceptancePdfToWhatsApp(leadId);
      toast.success("Examination Report of Acceptance sent to client");
      onSent();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to send PDF to client"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  if (!pdfFilename) {
    return (
      <div
        className="flex flex-col gap-3"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Upload the Examination Report of Acceptance PDF in "Status of
          Acceptance" first.
        </p>
      </div>
    );
  }

  return (
    <form
      id={EXAMINATION_REPORT_NOTICE_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-5 self-stretch"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Message Preview label */}
      <span
        className="text-[#222222]"
        style={{
          fontSize: "16px",
          lineHeight: "20px",
          fontWeight: 400,
        }}
      >
        Message Preview
      </span>

      <PendingAcceptanceUploadsWarning tmNosMissingUpload={tmNosMissingUpload} />

      {/* PDF link row */}
      <div
        className="flex flex-row items-center gap-2.5 self-stretch h-12 px-3.5 bg-white border border-[#EEEEEE] rounded-[6px]"
        style={{ padding: "14px" }}
      >
        <span
          className="flex-1 min-w-0 truncate text-[#222222]"
          style={{
            fontSize: "12px",
            lineHeight: "20px",
            fontWeight: 400,
          }}
        >
          {pdfFilename}
        </span>
      </div>

      {/* Phone chip */}
      <div
        className="flex flex-row items-center gap-2.5 bg-[#FCFCFC] border border-[#EEEEEE] rounded-[6px]"
        style={{
          width: "345px",
          maxWidth: "100%",
          height: "44px",
          padding: "4px 8px 4px 16px",
        }}
      >
        <span
          className="flex-1 min-w-0 truncate text-[#222222]"
          style={{
            fontSize: "12px",
            lineHeight: "20px",
            fontWeight: 500,
          }}
        >
          {phone || "(No phone number)"}
        </span>
      </div>

      {/* Inline Resend button — only after already sent */}
      {hasSent && (
        <div className="flex flex-row items-center gap-3">
          <button
            type="button"
            disabled={resending}
            onClick={onResend}
            className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {resending ? "Resending…" : "Resend to WhatsApp"}
          </button>
        </div>
      )}
    </form>
  );
}

// ---------------------------------------------------------------------------
// Inform client about Acceptance panel — monthly update message
// Footer drives send; resend button shown inline when already sent.
// ---------------------------------------------------------------------------

function InformClientPanel({
  leadId,
  phone,
  hasSent,
  tmNosMissingUpload,
  onSent,
  onAdvance,
  onSavingChange,
  resending,
  onResend,
}: {
  leadId: string;
  phone: string;
  hasSent: boolean;
  /** Accepted TM No.(s) with no acceptance PDF yet — blocks the send while non-empty */
  tmNosMissingUpload: string[];
  /** Called after a successful send — marks hasSent and advances to next main step */
  onSent: () => void;
  /** Called when Save & Continue is clicked on an already-sent inform — just advance */
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
  resending: boolean;
  onResend: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setMessage(null);
    // Unscoped so the preview is the acknowledgement-wide message that gets sent.
    getExaminationReportAcceptedInformClientPreview(leadId)
      .then(({ message: m }) => {
        if (!cancelled) setMessage(m);
      })
      .catch((err) => {
        if (!cancelled)
          toast.error(
            err instanceof Error ? err.message : "Failed to load preview"
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSent) {
      return;
    }
    if (!assertAllAcceptanceUploadsPresent(tmNosMissingUpload)) return;
    onSavingChange?.(true);
    try {
      await sendExaminationReportAcceptedInformClientWhatsApp(leadId);
      toast.success("Monthly update sent to client");
      onSent();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to send message"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  return (
    <form
      id={EXAMINATION_REPORT_INFORM_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-5 self-stretch"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Message Preview label */}
      <span
        className="text-[#222222]"
        style={{
          fontSize: "16px",
          lineHeight: "20px",
          fontWeight: 400,
        }}
      >
        Message Preview
      </span>

      <PendingAcceptanceUploadsWarning tmNosMissingUpload={tmNosMissingUpload} />

      {/* Message body */}
      <div className="self-stretch min-h-[80px] px-3.5 py-3 bg-white border border-[#EEEEEE] rounded-[6px]">
        {loading ? (
          <span
            className="text-[#6D6D6D]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            Loading preview…
          </span>
        ) : message ? (
          <p
            className="text-[#222222] whitespace-pre-line"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            {renderWhatsAppFormatting(message)}
          </p>
        ) : (
          <span
            className="text-[#6D6D6D]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            No preview available
          </span>
        )}
      </div>

      {/* Phone chip */}
      <div
        className="flex flex-row items-center gap-2.5 bg-[#FCFCFC] border border-[#EEEEEE] rounded-[6px]"
        style={{
          width: "345px",
          maxWidth: "100%",
          height: "44px",
          padding: "4px 8px 4px 16px",
        }}
      >
        <span
          className="flex-1 min-w-0 truncate text-[#222222]"
          style={{
            fontSize: "12px",
            lineHeight: "20px",
            fontWeight: 500,
          }}
        >
          {phone || "(No phone number)"}
        </span>
      </div>

      {/* Inline Resend button — only after already sent */}
      {hasSent && (
        <div className="flex flex-row items-center gap-3">
          <button
            type="button"
            disabled={resending}
            onClick={onResend}
            className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {resending ? "Resending…" : "Resend to WhatsApp"}
          </button>
        </div>
      )}
    </form>
  );
}

// ---------------------------------------------------------------------------
// Accepted branch — TM selector + two-column (sub-steps | detail)
// ---------------------------------------------------------------------------

function AcceptedBranch({
  leadId,
  phone,
  active,
  onSuccess,
  onSavingChange,
  onNoticeFooterSendModeChange,
  onNoticeFooterSaveModeChange,
  onInformFooterSendModeChange,
  onInformFooterSaveModeChange,
  onSoaFooterFormModeChange,
}: {
  leadId: string;
  phone: string;
  /** True when the Accepted sub-status tab is visible — governs footer signaling */
  active: boolean;
  onSuccess?: (leadId: string) => void;
  onSavingChange?: (saving: boolean) => void;
  onNoticeFooterSendModeChange?: (needsSend: boolean) => void;
  onNoticeFooterSaveModeChange?: (needsSave: boolean) => void;
  onInformFooterSendModeChange?: (needsSend: boolean) => void;
  onInformFooterSaveModeChange?: (needsSave: boolean) => void;
  onSoaFooterFormModeChange?: (needsSubmit: boolean) => void;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTmNo = searchParams.get("tm");
  const urlSubStep = searchParams.get("substep") as AcceptedSubStep | null;
  const validAcceptedSubSteps: AcceptedSubStep[] = useMemo(
    () => ACCEPTED_SUB_STEPS.map((s) => s.id),
    []
  );

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<ExaminationReportAcceptedStatus | null>(
    null
  );
  const [selectedTmIdx, setSelectedTmIdxState] = useState(0);
  const [subStep, setSubStepState] = useState<AcceptedSubStep>(
    urlSubStep && validAcceptedSubSteps.includes(urlSubStep)
      ? urlSubStep
      : "status-of-acceptance"
  );
  const [hasSentNotice, setHasSentNotice] = useState(false);
  const [resendingNotice, setResendingNotice] = useState(false);
  const [hasSentInform, setHasSentInform] = useState(false);
  const [resendingInform, setResendingInform] = useState(false);

  const writeUrl = (patch: { tm?: string | null; substep?: string | null }) => {
    const params = new URLSearchParams(searchParams);
    if (patch.tm !== undefined) {
      if (patch.tm) params.set("tm", patch.tm);
      else params.delete("tm");
    }
    if (patch.substep !== undefined) {
      if (patch.substep) params.set("substep", patch.substep);
      else params.delete("substep");
    }
    setSearchParams(params, { replace: true });
  };

  const setSelectedTmIdx = (idx: number) => {
    setSelectedTmIdxState(idx);
    const apps = status?.applications ?? [];
    const tm = apps[idx]?.tmNo ?? null;
    writeUrl({ tm });
  };
  const setSubStep = (next: AcceptedSubStep) => {
    setSubStepState(next);
    writeUrl({ substep: next });
  };

  // Sliding left-edge indicator: tracks the active sub-step button's vertical
  // position and animates between rows. Also scrolls active row into view when
  // it changes (URL restore or programmatic advance after Send/Save).
  const activeSubStepBtnRef = useRef<HTMLButtonElement | null>(null);
  const [subStepIndicator, setSubStepIndicator] = useState({ top: 0, height: 0 });
  useLayoutEffect(() => {
    const btn = activeSubStepBtnRef.current;
    if (!btn) return;
    setSubStepIndicator({ top: btn.offsetTop, height: btn.offsetHeight });
    btn.scrollIntoView({ block: "nearest" });
  }, [subStep]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getExaminationReportAcceptedStatus(leadId)
      .then((data) => {
        if (cancelled) return;
        setStatus(data);
        // Restore TM from URL on first load (if present and valid)
        const idx = urlTmNo
          ? data.applications.findIndex((a) => a.tmNo === urlTmNo)
          : -1;
        setSelectedTmIdxState(idx >= 0 ? idx : 0);
      })
      .catch((err) => {
        if (!cancelled)
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Examination Report"
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  // Read notice + inform step completion from workflow status
  useEffect(() => {
    if (!leadId) {
      setHasSentNotice(false);
      setHasSentInform(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const wf = await getWorkflowStatus(leadId);
        if (cancelled) return;
        const noticeStep = wf.steps.find((s) => s.slug === NOTICE_STEP_SLUG);
        const informStep = wf.steps.find((s) => s.slug === INFORM_STEP_SLUG);
        setHasSentNotice(Boolean(noticeStep?.isCompleted));
        setHasSentInform(Boolean(informStep?.isCompleted));
      } catch {
        if (!cancelled) {
          setHasSentNotice(false);
          setHasSentInform(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const applications = status?.applications ?? [];
  const selected =
    applications[Math.min(selectedTmIdx, applications.length - 1)] ?? null;

  // 3A(ii) and 3A(iii) send one combined message for every Accepted application in
  // the acknowledgement, so no client confirmation may go out while any of them is
  // still missing its Examination Report of Acceptance PDF. The backend enforces the
  // same rule, but only across the TMs it is handed — scoping the send to the selected
  // mark bypassed it entirely.
  const tmNosMissingUpload = applications
    .filter((app) => !app.examinationReportAcceptanceFileKey)
    .map((app) => app.tmNo);

  // Tell dashboard footer when Notice panel needs Send & Continue (not yet sent)
  useLayoutEffect(() => {
    const needsSend =
      active &&
      subStep === "notice-to-client" &&
      !hasSentNotice &&
      !!selected?.examinationReportAcceptanceFileKey &&
      !!leadId;
    onNoticeFooterSendModeChange?.(needsSend);
  }, [
    active,
    subStep,
    hasSentNotice,
    selected,
    leadId,
    onNoticeFooterSendModeChange,
  ]);

  // Tell dashboard footer when Notice panel is in Save & Continue mode (already sent)
  useLayoutEffect(() => {
    const needsSave =
      active && subStep === "notice-to-client" && hasSentNotice;
    onNoticeFooterSaveModeChange?.(needsSave);
  }, [active, subStep, hasSentNotice, onNoticeFooterSaveModeChange]);

  // Tell dashboard footer when Inform panel needs Send & Continue (not yet sent)
  useLayoutEffect(() => {
    const needsSend =
      active && subStep === "inform-client" && !hasSentInform && !!leadId;
    onInformFooterSendModeChange?.(needsSend);
  }, [active, subStep, hasSentInform, leadId, onInformFooterSendModeChange]);

  // Tell dashboard footer when Inform panel is in Save & Continue mode (already sent)
  useLayoutEffect(() => {
    const needsSave =
      active && subStep === "inform-client" && hasSentInform;
    onInformFooterSaveModeChange?.(needsSave);
  }, [active, subStep, hasSentInform, onInformFooterSaveModeChange]);

  // Tell dashboard footer when SOA sub-step is visible — footer submits SOA form
  useLayoutEffect(() => {
    const needsSubmit = active && subStep === "status-of-acceptance";
    onSoaFooterFormModeChange?.(needsSubmit);
  }, [active, subStep, onSoaFooterFormModeChange]);

  if (loading) {
    return <p className="text-sm text-[#6D6D6D]">Loading…</p>;
  }

  if (applications.length === 0 || !selected) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        No applications with status "Examination Report – Accepted" in this
        acknowledgement yet.
      </p>
    );
  }

  const updateSelected = (next: ExaminationReportAcceptedApplication) => {
    setStatus((prev) =>
      prev
        ? {
            ...prev,
            applications: prev.applications.map((a) =>
              a.tmNo === next.tmNo ? next : a
            ),
          }
        : prev
    );
  };

  const handleNoticeResend = async () => {
    if (!leadId || resendingNotice) return;
    setResendingNotice(true);
    onSavingChange?.(true);
    try {
      // Unscoped to match the original send — the same combined PDF goes out again.
      await sendExaminationReportAcceptancePdfToWhatsApp(leadId);
      toast.success("Examination Report of Acceptance resent to client");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to resend");
    } finally {
      setResendingNotice(false);
      onSavingChange?.(false);
    }
  };

  const handleInformResend = async () => {
    if (!leadId || resendingInform) return;
    setResendingInform(true);
    onSavingChange?.(true);
    try {
      await sendExaminationReportAcceptedInformClientWhatsApp(leadId);
      toast.success("Monthly update resent to client");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to resend");
    } finally {
      setResendingInform(false);
      onSavingChange?.(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {/* TM selector */}
      <div className="flex flex-row flex-wrap gap-3">
        {applications.map((app, idx) => {
          const isActive = selected.tmNo === app.tmNo;
          return (
            <button
              key={app.tmNo}
              type="button"
              onClick={() => setSelectedTmIdx(idx)}
              className={`flex items-center gap-2.5 px-2.5 py-2 rounded-[6px] h-10 min-w-[140px] border transition-colors ${
                isActive
                  ? "bg-[#222222] text-white border-transparent"
                  : "bg-[#FCFCFC] text-[#6D6D6D] border-[#EEEEEE]"
              }`}
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: "12px",
                fontWeight: isActive ? 500 : 400,
                lineHeight: "20px",
              }}
            >
              <RadioCircle active={isActive} />
              {formatTmNoForDisplay(app.tmNo)}
            </button>
          );
        })}
      </div>

      {/* Two-column sub-step panel */}
      <div className="border border-[#EEEEEE] rounded-xl overflow-hidden flex flex-col md:flex-row min-h-0">
        {/* Left: sub-steps */}
        <div className="relative w-full md:w-[235px] md:flex-shrink-0 border-b md:border-b-0 md:border-r border-[#EEEEEE] p-2 flex flex-col gap-1 overflow-y-auto">
          <div
            aria-hidden
            className="pointer-events-none absolute left-0 w-[3px] bg-[#222222] rounded-r-md transition-all duration-200 ease-out"
            style={{
              top: subStepIndicator.top,
              height: subStepIndicator.height,
              opacity: subStepIndicator.height > 0 ? 1 : 0,
            }}
          />
          {ACCEPTED_SUB_STEPS.map((step) => {
            const isActive = subStep === step.id;
            return (
              <button
                key={step.id}
                ref={isActive ? activeSubStepBtnRef : null}
                type="button"
                onClick={() => setSubStep(step.id)}
                className={`w-full flex items-center justify-between px-2.5 py-2.5 gap-2.5 rounded-[6px] text-xs font-${
                  isActive ? "500" : "400"
                } transition-colors ${
                  isActive
                    ? "bg-[#222222] text-white"
                    : "bg-[#F7F7F7] text-[#222222] hover:bg-[#EEEEEE]"
                }`}
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                <span className="text-left line-clamp-1" title={step.label}>{step.label}</span>
                <ChevronRight active={isActive} />
              </button>
            );
          })}
        </div>

        {/* Right: detail panel */}
        <div className="flex-1 min-w-0 px-4 py-4 overflow-y-auto">
          {subStep === "status-of-acceptance" ? (
            <form
              id={EXAMINATION_REPORT_SOA_FORM_ID}
              onSubmit={(e) => {
                e.preventDefault();
                setSubStep("notice-to-client");
              }}
            >
              <StatusOfAcceptancePanel
                leadId={leadId}
                application={selected}
                onSaved={updateSelected}
              />
            </form>
          ) : subStep === "notice-to-client" ? (
            <NoticeToClientPanel
              leadId={leadId}
              phone={phone}
              pdfFilename={
                selected.examinationReportAcceptanceFilename ??
                (selected.examinationReportAcceptanceFileKey ? "Uploaded PDF" : null)
              }
              hasSent={hasSentNotice}
              tmNosMissingUpload={tmNosMissingUpload}
              onSent={() => {
                setHasSentNotice(true);
                setSubStep("inform-client");
              }}
              onAdvance={() => setSubStep("inform-client")}
              onSavingChange={onSavingChange}
              resending={resendingNotice}
              onResend={() => void handleNoticeResend()}
            />
          ) : (
            <InformClientPanel
              leadId={leadId}
              phone={phone}
              hasSent={hasSentInform}
              tmNosMissingUpload={tmNosMissingUpload}
              onSent={() => {
                setHasSentInform(true);
              }}
              onAdvance={() => {}}
              onSavingChange={onSavingChange}
              resending={resendingInform}
              onResend={() => void handleInformResend()}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Objected branch — Status of Objection (3p, 3q, 3r, 3s, 3t)
// ---------------------------------------------------------------------------

function toIsoDate(d: Date | null): string | undefined {
  if (!d) return undefined;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Statement text derived from the selected reason flags (mirrors backend 3t derivation). */
function deriveStatement(reason: ReasonForObjection): string {
  const parts: string[] = [];
  if (reason.similarity) parts.push("similar marks exist in their record");
  if (reason.nonDistinctive) parts.push("the mark is devoid of any distinctive character");
  if (reason.supportingDocuments) parts.push("more supporting documents need to be uploaded");
  if (reason.tmMYes) parts.push("TM-M form needs to be filed");
  if (reason.others) parts.push("as specified in Others column");
  return parts.join("; ");
}

function ReasonCheckboxBox({ checked }: { checked: boolean }) {
  return (
    <span
      className="flex items-center justify-center w-6 h-6 flex-shrink-0"
      aria-hidden
    >
      <span
        className={`flex items-center justify-center w-5 h-5 rounded-md border ${
          checked
            ? "bg-[#222222] border-[#222222]"
            : "bg-white border-[#D6D8DB]"
        }`}
      >
        {checked && (
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.5 6.2 L4.8 8.5 L9.5 3.3"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        )}
      </span>
    </span>
  );
}

const SIMILAR_MARK_HEADERS: {
  key: keyof SimilarMark;
  label: string;
}[] = [
  { key: "mark", label: "Marks" },
  { key: "tmNo", label: "TM Number" },
  { key: "class", label: "Class" },
  { key: "goodsServices", label: "Goods/Service" },
  { key: "userDate", label: "User Date" },
  { key: "place", label: "Place" },
];

function SimilarMarksTable({
  rows,
  onChange,
  onAdd,
  onRemove,
}: {
  rows: SimilarMark[];
  onChange: (idx: number, patch: Partial<SimilarMark>) => void;
  onAdd: () => void;
  onRemove: (idx: number) => void;
}) {
  const cellInputCls =
    "w-full h-9 px-2.5 py-2 text-xs border border-[#EEEEEE] rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-[#222] placeholder:text-[#6D6D6D]";
  const dateInputCls =
    "w-full h-9 px-2.5 py-2 text-xs border border-[#EEEEEE] rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-[#222] text-left";
  return (
    <div className="flex flex-col gap-2 self-stretch">
      {/* Header row */}
      <div
        className="grid items-center gap-2 px-2.5"
        style={{
          gridTemplateColumns:
            "minmax(80px,1fr) minmax(90px,1fr) minmax(60px,0.7fr) minmax(110px,1.3fr) minmax(90px,1fr) minmax(80px,1fr) 32px",
        }}
      >
        {SIMILAR_MARK_HEADERS.map((h) => (
          <span
            key={h.key as string}
            className="text-xs font-medium leading-5 text-[#222222]"
          >
            {h.label}
          </span>
        ))}
        <span aria-hidden />
      </div>

      {/* Data rows */}
      {rows.map((row, idx) => {
        return (
          <div
            key={idx}
            className="grid items-center gap-2 px-2.5"
            style={{
              gridTemplateColumns:
                "minmax(80px,1fr) minmax(90px,1fr) minmax(60px,0.7fr) minmax(110px,1.3fr) minmax(90px,1fr) minmax(80px,1fr) 32px",
            }}
          >
            <input
              type="text"
              value={row.mark ?? ""}
              onChange={(e) => onChange(idx, { mark: e.target.value })}
              placeholder={`Mark ${idx + 1}`}
              className={cellInputCls}
            />
            <input
              type="text"
              value={row.tmNo ?? ""}
              onChange={(e) => onChange(idx, { tmNo: e.target.value })}
              placeholder="TM Number"
              className={cellInputCls}
            />
            <Select
              value={row.class ?? ""}
              onValueChange={(v) => onChange(idx, { class: v })}
            >
              <SelectTrigger className={cellInputCls}>
                <SelectValue placeholder="Class" />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 45 }, (_, i) => String(i + 1)).map((n) => (
                  <SelectItem key={n} value={n}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input
              type="text"
              value={row.goodsServices ?? ""}
              onChange={(e) => onChange(idx, { goodsServices: e.target.value })}
              placeholder="Goods/Service"
              className={cellInputCls}
            />
            <DateTimePicker
              value={parseDateForHearingInput(row.userDate)}
              onChange={(d) =>
                onChange(idx, {
                  userDate: d ? formatDateDDMMYYYYForApi(d) : null,
                })
              }
              showTime={false}
              placeholder="User Date"
              buttonClassName={dateInputCls}
            />
            <input
              type="text"
              value={row.place ?? ""}
              onChange={(e) => onChange(idx, { place: e.target.value })}
              placeholder="Place"
              className={cellInputCls}
            />
            <button
              type="button"
              onClick={() => onRemove(idx)}
              disabled={rows.length <= 1}
              aria-label={`Remove similar mark ${idx + 1}`}
              className="flex items-center justify-center h-9 w-8 rounded-md text-[#6D6D6D] hover:bg-[#F7F7F7] disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 3 L11 11 M11 3 L3 11"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        );
      })}

      {/* Add row */}
      <button
        type="button"
        onClick={onAdd}
        className="self-start ml-2.5 mt-1 text-xs leading-5 text-[#0070F0] hover:underline"
      >
        + Add another mark
      </button>
    </div>
  );
}

function StatusOfObjectionPanel({
  leadId,
  application,
  onSaved,
  onAdvance,
  onSavingChange,
}: {
  leadId: string;
  application: StatusOfObjectionApplication;
  onSaved: (next: StatusOfObjectionApplication) => void;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
}) {
  const [dateOfExam, setDateOfExam] = useState<Date | null>(() => {
    const raw = application.dateOfExaminationReport;
    let parsed: Date | null = null;
    if (typeof raw === "string" && raw.length > 0) {
      const nums = raw.split("-").map((p) => Number(p.replace(/\D/g, "")));
      if (nums.length === 3 && nums.every((n) => Number.isFinite(n))) {
        const [a, b, c] = nums;
        const isYMD = String(raw.split("-")[0]).length === 4;
        const year = isYMD ? a : c;
        const month = b;
        const day = isYMD ? c : a;
        if (year >= 1900 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
          parsed = new Date(year, month - 1, day);
        }
      }
    }
    return parsed;
  });
  const [reason, setReason] = useState<ReasonForObjection>(
    () => application.reasonForObjection ?? {}
  );
  const [similarMarks, setSimilarMarks] = useState<SimilarMark[]>(() =>
    application.similarMarks?.length ? application.similarMarks : [{}]
  );
  const [statement, setStatement] = useState<string>(() =>
    application.statementOfReasonForObjection?.length
      ? application.statementOfReasonForObjection.join("; ")
      : deriveStatement(application.reasonForObjection ?? {})
  );
  const [statementDirty, setStatementDirty] = useState(false);
  const [preview, setPreview] = useState<
    { url: string; name: string; kind: FilePreviewKind } | null
  >(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [pendingName, setPendingName] = useState<string | null>(null);

  // Reset local state when selected TM changes
  useEffect(() => {
    const raw = application.dateOfExaminationReport;
    let parsed: Date | null = null;
    if (typeof raw === "string" && raw.length > 0) {
      const nums = raw.split("-").map((p) => Number(p.replace(/\D/g, "")));
      if (nums.length === 3 && nums.every((n) => Number.isFinite(n))) {
        const [a, b, c] = nums;
        const isYMD = String(raw.split("-")[0]).length === 4;
        const year = isYMD ? a : c;
        const month = b;
        const day = isYMD ? c : a;
        if (year >= 1900 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
          parsed = new Date(year, month - 1, day);
        }
      }
    }
    setDateOfExam(parsed);
    setReason(application.reasonForObjection ?? {});
    setSimilarMarks(
      application.similarMarks?.length ? application.similarMarks : [{}]
    );
    setStatement(
      application.statementOfReasonForObjection?.length
        ? application.statementOfReasonForObjection.join("; ")
        : deriveStatement(application.reasonForObjection ?? {})
    );
    setStatementDirty(false);
  }, [application]);

  // Auto-update statement when reasons change (until user manually edits)
  useEffect(() => {
    if (statementDirty) return;
    setStatement(deriveStatement(reason));
  }, [reason, statementDirty]);

  // 3s) Last date for Reply (Auto): 30 days after Date of Examination Report
  const lastDateForReply = useMemo<Date | null>(() => {
    if (!dateOfExam) return null;
    const d = new Date(dateOfExam);
    d.setDate(d.getDate() + 30);
    return d;
  }, [dateOfExam]);

  const allChecked =
    REASON_OPTIONS.every((opt) => Boolean(reason[opt.key])) &&
    REASON_OPTIONS.length > 0;

  const toggleReason = (key: typeof REASON_OPTIONS[number]["key"]) => {
    setReason((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleSelectAll = () => {
    if (allChecked) {
      setReason({});
    } else {
      const next: ReasonForObjection = {};
      REASON_OPTIONS.forEach((opt) => {
        next[opt.key] = true;
      });
      setReason(next);
    }
  };

  const hasExistingFile = !!application.examinationReportFileKey;
  const displayName =
    pendingName ?? application.examinationReportFilename ?? null;
  const fileUrl = application.examinationReportFileUrl ?? null;

  const openExistingPreview = () => {
    if (!fileUrl) {
      toast.error("Examination Report PDF not available yet");
      return;
    }
    const name = application.examinationReportFilename ?? "Examination Report";
    setPreview({ url: fileUrl, name, kind: detectPreviewKind(name) });
  };

  const handlePickPdf = async (file: File) => {
    if (!leadId || uploading) return;
    setUploading(true);
    setPendingName(file.name);
    onSavingChange?.(true);
    try {
      const [presigned] = await generateUploadPresignedUrls(
        [{ filename: file.name, mimeType: file.type || "application/pdf" }],
        "private",
        EXAMINATION_REPORT_OBJECTION_FOLDER
      );
      const putRes = await fetch(presigned.url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "application/pdf" },
      });
      if (!putRes.ok) throw new Error("Upload failed");

      // Send the full current form state alongside the file — the backend
      // upsert overwrites every field, so omitting these would clobber any
      // unsaved date/reasons/similar-marks the user typed before uploading.
      const trimmedSimilarMarks: SimilarMark[] = reason.similarity
        ? similarMarks
            .map((m) => ({
              mark: m.mark?.trim() || null,
              tmNo: m.tmNo?.trim() || null,
              class: m.class?.trim() || null,
              goodsServices: m.goodsServices?.trim() || null,
              userDate: m.userDate?.trim() || null,
              place: m.place?.trim() || null,
            }))
            .filter(
              (m) =>
                m.mark || m.tmNo || m.class || m.goodsServices || m.userDate || m.place
            )
        : [];

      const updated = await saveStatusOfObjection(leadId, {
        applications: [
          {
            tmNo: application.tmNo,
            dateOfExaminationReport: toIsoDate(dateOfExam) ?? null,
            reasonForObjection: {
              similarity: !!reason.similarity,
              nonDistinctive: !!reason.nonDistinctive,
              supportingDocuments: !!reason.supportingDocuments,
              others: !!reason.others,
              othersSpecify: reason.othersSpecify,
              tmMYes: !!reason.tmMYes,
            },
            similarMarks: trimmedSimilarMarks,
            examinationReportFileKey: presigned.key,
            examinationReportFilename: file.name,
          },
        ],
      });
      const next =
        updated.applications.find((a) => a.tmNo === application.tmNo) ??
        application;
      onSaved(next);
      setPendingName(null);
      toast.success("Examination Report uploaded");
    } catch (err) {
      setPendingName(null);
      toast.error(
        err instanceof Error ? err.message : "Failed to upload Examination Report"
      );
    } finally {
      setUploading(false);
      onSavingChange?.(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (!dateOfExam) {
      toast.error("Date of Examination Report is required");
      return;
    }
    const anyReasonChecked = REASON_OPTIONS.some((opt) =>
      Boolean(reason[opt.key])
    );
    if (!anyReasonChecked) {
      toast.error("Select at least one Reason for Objection");
      return;
    }
    if (!statement.trim()) {
      toast.error("Statement is required");
      return;
    }
    if (reason.similarity) {
      const hasAtLeastOneFilled = similarMarks.some(
        (m) =>
          (m.mark ?? "").trim() ||
          (m.tmNo ?? "").trim() ||
          (m.class ?? "").trim() ||
          (m.goodsServices ?? "").trim() ||
          (m.userDate ?? "").trim() ||
          (m.place ?? "").trim()
      );
      if (!hasAtLeastOneFilled) {
        toast.error("Add at least one Similar Mark");
        return;
      }
    }
    if (uploading) {
      toast.error("Please wait for the upload to finish");
      return;
    }
    if (!hasExistingFile && !pendingName) {
      toast.error("Upload the Examination Report PDF first");
      return;
    }
    onSavingChange?.(true);
    try {
      const trimmedSimilarMarks: SimilarMark[] = reason.similarity
        ? similarMarks
            .map((m) => ({
              mark: m.mark?.trim() || null,
              tmNo: m.tmNo?.trim() || null,
              class: m.class?.trim() || null,
              goodsServices: m.goodsServices?.trim() || null,
              userDate: m.userDate?.trim() || null,
              place: m.place?.trim() || null,
            }))
            .filter(
              (m) =>
                m.mark || m.tmNo || m.class || m.goodsServices || m.userDate || m.place
            )
        : [];
      const payload = {
        applications: [
          {
            tmNo: application.tmNo,
            dateOfExaminationReport: toIsoDate(dateOfExam) ?? null,
            reasonForObjection: {
              similarity: !!reason.similarity,
              nonDistinctive: !!reason.nonDistinctive,
              supportingDocuments: !!reason.supportingDocuments,
              others: !!reason.others,
              othersSpecify: reason.othersSpecify,
              tmMYes: !!reason.tmMYes,
            },
            similarMarks: trimmedSimilarMarks,
          },
        ],
      };
      const updated = await saveStatusOfObjection(leadId, payload);
      const next =
        updated.applications.find((a) => a.tmNo === application.tmNo) ??
        application;
      onSaved(next);
      toast.success("Status of Objection saved");
      onAdvance();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save Status of Objection"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const dateInputCls =
    "w-full h-9 px-2.5 py-2 text-xs border border-[#EEEEEE] rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-[#222] text-left";

  return (
    <form
      id={EXAMINATION_REPORT_SOO_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-5 self-stretch"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Date row */}
      <div className="flex flex-row items-center gap-5 self-stretch">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs leading-5 text-[#222222]">
            Date of Exam Report
          </label>
          <DateTimePicker
            value={dateOfExam}
            onChange={setDateOfExam}
            placeholder="Select Date"
            buttonClassName={dateInputCls}
          />
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs leading-5 text-[#222222]">
            Last Date for Reply (Auto)
          </label>
          <DateTimePicker
            value={lastDateForReply}
            placeholder="Select Date"
            buttonClassName={dateInputCls}
            readOnly
          />
        </div>
      </div>

      {/* Reason for Objection */}
      <div className="flex flex-col gap-5 self-stretch">
        <div className="flex flex-row items-start justify-between gap-5 self-stretch">
          <span className="text-base leading-5 text-[#222222]">
            Reason for Objection
          </span>
          <button
            type="button"
            onClick={toggleSelectAll}
            className="text-sm leading-5 text-[#0070F0] hover:underline"
          >
            {allChecked ? "Unselect All" : "Select All"}
          </button>
        </div>

        <div className="flex flex-col gap-2 self-stretch">
          {REASON_OPTIONS.map((opt) => {
            const checked = Boolean(reason[opt.key]);
            const isSimilarityChecked = opt.key === "similarity" && checked;
            return (
              <div key={opt.key} className="flex flex-col gap-2 self-stretch">
                <label
                  className={`flex flex-row items-center gap-2 self-stretch px-2.5 py-2.5 cursor-pointer h-11 ${
                    isSimilarityChecked
                      ? "bg-transparent"
                      : "bg-white border border-[#EEEEEE] rounded-[6px]"
                  }`}
                >
                  <ReasonCheckboxBox checked={checked} />
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={checked}
                    onChange={() => toggleReason(opt.key)}
                  />
                  <span
                    className={`text-xs leading-5 text-[#222222] ${
                      isSimilarityChecked ? "font-medium" : ""
                    }`}
                  >
                    {opt.label}
                  </span>
                </label>

                {/* Similar Marks table — visible when Similarity is checked */}
                {opt.key === "similarity" && checked && (
                  <SimilarMarksTable
                    rows={similarMarks}
                    onChange={(idx, patch) =>
                      setSimilarMarks((prev) =>
                        prev.map((m, i) => (i === idx ? { ...m, ...patch } : m))
                      )
                    }
                    onAdd={() => setSimilarMarks((prev) => [...prev, {}])}
                    onRemove={(idx) =>
                      setSimilarMarks((prev) =>
                        prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev
                      )
                    }
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Others (Specify) — visible when Others is checked */}
      {reason.others && (
        <div className="flex flex-col gap-1 self-stretch">
          <label className="text-xs leading-5 text-[#222222]">
            Others (Specify)
          </label>
          <input
            type="text"
            value={reason.othersSpecify ?? ""}
            onChange={(e) =>
              setReason((prev) => ({ ...prev, othersSpecify: e.target.value }))
            }
            placeholder="Specify reason"
            className="w-full h-9 px-2.5 py-2 text-xs border border-[#EEEEEE] rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-[#222] placeholder:text-[#6D6D6D]"
          />
        </div>
      )}

      {/* Statement */}
      <div className="flex flex-col gap-1 self-stretch">
        <label className="text-xs leading-5 text-[#222222]">Statement of Reason for Objection</label>
        <textarea
          value={statement}
          onChange={(e) => {
            setStatement(e.target.value);
            setStatementDirty(true);
          }}
          placeholder="Reason"
          rows={3}
          className="w-full min-h-[80px] px-2.5 py-2 text-xs border border-[#EEEEEE] rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-[#222] placeholder:text-[#6D6D6D] resize-y"
        />
      </div>

      {/* Examination Report PDF — standard dropzone + uploaded indicator */}
      <FileUploadDropzone
        inputId={`status-of-objection-pdf-${application.tmNo}`}
        hasFile={!!displayName}
        fileLabel={displayName}
        isDragging={dragging}
        uploading={uploading}
        onDropFile={(file) => void handlePickPdf(file)}
        onPickFile={(file) => void handlePickPdf(file)}
        onRemove={() => setPendingName(null)}
        onDragEnter={() => setDragging(true)}
        onDragLeave={() => setDragging(false)}
      />

      {hasExistingFile && !uploading && (
        <div className="flex items-center gap-3">
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="9" cy="9" r="9" fill="#22C55E" />
            <path
              d="M5 9L8 12L13 6"
              stroke="white"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="text-sm font-medium text-[#16A34A]">Uploaded</span>
          {fileUrl ? (
            <button
              type="button"
              onClick={openExistingPreview}
              className="ml-1 inline-flex items-center justify-center h-8 px-3 text-xs font-medium text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[8px] hover:bg-gray-50"
            >
              Preview
            </button>
          ) : null}
        </div>
      )}

      <FilePreviewModal
        open={!!preview}
        onClose={() => setPreview(null)}
        name={preview?.name ?? ""}
        url={preview?.url || undefined}
        kind={preview?.kind}
      />
    </form>
  );
}

function NoticeToClientObjectionPanel({
  leadId,
  applications,
  hasSent,
  onSent,
  onAdvance,
  onSavingChange,
  resending,
  onResend,
}: {
  leadId: string;
  applications: StatusOfObjectionApplication[];
  hasSent: boolean;
  onSent: () => void;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
  resending: boolean;
  onResend: () => void;
}) {
  const [preview, setPreview] = useState<
    { url: string; name: string; kind: FilePreviewKind } | null
  >(null);

  // Display rows: each objected application's uploaded Examination Report PDF.
  const rows = applications
    .filter((a) => a.examinationReportFileKey || a.examinationReportFilename)
    .map((a, idx) => ({
      tmNo: a.tmNo,
      name:
        a.examinationReportFilename ??
        (a.mark ? a.mark : `Mark ${String(idx + 1).padStart(2, "0")}`),
      url: a.examinationReportFileUrl ?? null,
    }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSent) {
      onAdvance();
      return;
    }
    if (!rows.length) {
      toast.error(
        "Upload Examination Report PDF in 'Status of Objection' for at least one application first"
      );
      return;
    }
    onSavingChange?.(true);
    try {
      await sendStatusOfObjectionPdfToWhatsApp(leadId);
      toast.success("Examination Report (Objection) sent to client");
      onSent();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to send PDF to client"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const openPreview = (row: { url: string | null; name: string }) => {
    if (!row.url) {
      toast.error("Examination Report PDF not available yet");
      return;
    }
    setPreview({
      url: row.url,
      name: row.name,
      kind: detectPreviewKind(row.name),
    });
  };

  return (
    <form
      id={EXAMINATION_REPORT_NTCO_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-2.5 self-stretch py-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {rows.length === 0 ? (
        <p className="text-xs leading-5 text-[#6D6D6D]">
          No Examination Report PDFs uploaded yet. Upload them in "Status of
          Objection" first.
        </p>
      ) : (
        rows.map((row) => (
          <div
            key={row.tmNo}
            className="flex flex-row items-center gap-2.5 self-stretch bg-[#FCFCFC] border border-[#EEEEEE] rounded-[6px]"
            style={{ height: "44px", padding: "4px 8px 4px 16px" }}
          >
            <img
              src={pdfIcon}
              alt=""
              className="w-[22px] h-[22px] flex-shrink-0"
            />
            <span
              className="flex-1 min-w-0 truncate text-[#222222]"
              style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 500 }}
            >
              {row.name}
            </span>
            <button
              type="button"
              onClick={() => openPreview(row)}
              className="flex items-center justify-center h-9 px-4 rounded-[6px] bg-[#222222] hover:bg-[#333] text-white text-xs font-medium"
              style={{ width: "78px" }}
            >
              Preview
            </button>
          </div>
        ))
      )}

      {hasSent && (
        <div className="flex flex-row items-center gap-3 pt-2">
          <button
            type="button"
            disabled={resending}
            onClick={onResend}
            className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {resending ? "Resending…" : "Resend to WhatsApp"}
          </button>
        </div>
      )}

      <FilePreviewModal
        open={!!preview}
        onClose={() => setPreview(null)}
        name={preview?.name ?? ""}
        url={preview?.url}
        kind={preview?.kind}
      />
    </form>
  );
}

function InformClientObjectionPanel({
  leadId,
  hasSent,
  onSent,
  onAdvance,
  onSavingChange,
  resending,
  onResend,
}: {
  leadId: string;
  hasSent: boolean;
  onSent: () => void;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
  resending: boolean;
  onResend: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState<string[]>([]);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      setMessages([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getStatusOfObjectionInformClientPreview(leadId)
      .then(({ messages: m }) => {
        if (!cancelled) setMessages(m);
      })
      .catch((err) => {
        if (!cancelled) {
          setMessages([]);
          toast.error(
            err instanceof Error ? err.message : "Failed to load preview"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSent) {
      onAdvance();
      return;
    }
    if (!messages.length) {
      toast.error("No message available to send");
      return;
    }
    onSavingChange?.(true);
    try {
      await sendStatusOfObjectionInformClientToWhatsApp(leadId);
      toast.success("Inform Client about Objection sent to client");
      onSent();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to send message"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const combined = messages.join("\n\n---\n\n");

  return (
    <form
      id={EXAMINATION_REPORT_INCO_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-5 self-stretch"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <span
        className="text-[#222222]"
        style={{ fontSize: "16px", lineHeight: "20px", fontWeight: 400 }}
      >
        Message Preview
      </span>

      <div
        className="self-stretch bg-white border border-[#EEEEEE] rounded-[6px]"
        style={{ padding: "14px" }}
      >
        {loading ? (
          <span
            className="text-[#6D6D6D]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            Loading preview…
          </span>
        ) : combined ? (
          <p
            className="text-[#222222] whitespace-pre-line"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            {renderWhatsAppFormatting(combined)}
          </p>
        ) : (
          <span
            className="text-[#6D6D6D]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            No preview available
          </span>
        )}
      </div>

      {hasSent && (
        <div className="flex flex-row items-center gap-3">
          <button
            type="button"
            disabled={resending}
            onClick={onResend}
            className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {resending ? "Resending…" : "Resend to WhatsApp"}
          </button>
        </div>
      )}
    </form>
  );
}

function CheckCircleIcon({ active }: { active: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="flex-shrink-0"
    >
      <circle cx="8" cy="8" r="8" fill={active ? "white" : "#6D6D6D"} />
      <path
        d="M4.5 8.2 L6.8 10.5 L11.6 5.3"
        stroke={active ? "#222222" : "white"}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

function ConfirmField({
  label,
  value,
  placeholder,
}: {
  label: string;
  value: string | null | undefined;
  placeholder: string;
}) {
  const display =
    value !== null && value !== undefined && String(value).trim() !== ""
      ? String(value)
      : null;
  return (
    <div className="flex flex-col gap-1 flex-1 min-w-0">
      <label
        className="text-[#222222]"
        style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
      >
        {label}
      </label>
      <div
        className="flex flex-row justify-between items-center self-stretch h-9 px-2.5 py-2 bg-white border border-[#EEEEEE] rounded-[6px]"
        style={{ gap: "10px" }}
      >
        <span
          className="flex-1 min-w-0 truncate"
          style={{
            fontSize: "12px",
            lineHeight: "20px",
            fontWeight: 400,
            color: display ? "#222222" : "#6D6D6D",
          }}
        >
          {display ?? placeholder}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Substep 3B(iv) — Call Client about Reply to Objection.
// A phone conversation only; nothing is entered. The step is recorded so the
// workflow advances, mirroring 3D(vi) / 1B(v) / 4A(iv).
// ---------------------------------------------------------------------------

function CallClientReplyToObjectionPanel({
  leadId,
  tmNo,
  hasRecorded,
  onRecorded,
  onAdvance,
  onSavingChange,
}: {
  leadId: string;
  tmNo: string;
  hasRecorded: boolean;
  onRecorded: () => void;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
}) {
  const [recording, setRecording] = useState(false);

  const handleRecord = async () => {
    if (!leadId || recording || hasRecorded) return;
    setRecording(true);
    onSavingChange?.(true);
    try {
      await recordStatusOfObjectionCallClientReply(leadId, [tmNo]);
      onRecorded();
      toast.success("Call Client step recorded");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to record call");
    } finally {
      setRecording(false);
      onSavingChange?.(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasRecorded) {
      toast.error("Mark the call as completed first.");
      return;
    }
    onAdvance();
  };

  return (
    <form
      id={EXAMINATION_REPORT_CCRO_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-5 self-stretch pt-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div
        className="flex flex-row items-center gap-4 p-4"
        style={{
          background: "#FFECD3",
          border: "1px dashed #E18308",
          borderRadius: "16px",
        }}
      >
        <img
          src={legalAdviceHeadset}
          alt=""
          className="w-[60px] h-[60px] flex-shrink-0"
        />
        <div className="flex flex-col gap-3 flex-1 min-w-0">
          <p
            className="text-sm font-normal text-[#222222]"
            style={{ lineHeight: "20px" }}
          >
            Call the client to explain the objection raised on{" "}
            {formatTmNoForDisplay(tmNo)} and the need to file a reply, then
            confirm whether they wish to go ahead.
          </p>
          {hasRecorded ? (
            <div className="flex items-center gap-2">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="flex-shrink-0"
              >
                <circle cx="8" cy="8" r="7.5" fill="#222222" />
                <path
                  d="M5.33325 8.00008L7.33325 10.0001L10.6666 6.00008"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span
                className="text-xs font-semibold text-[#222222]"
                style={{ lineHeight: "20px" }}
              >
                Call Completed
              </span>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => void handleRecord()}
              disabled={recording || !leadId}
              className="self-start h-8 px-4 text-xs font-medium text-[#222222] bg-white border border-[#D1D5DB] rounded-md hover:bg-[#F9FAFB] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              style={{ lineHeight: "20px" }}
            >
              {recording ? "Recording…" : "Call Completed"}
            </button>
          )}
        </div>
      </div>
    </form>
  );
}

function ClientConfirmationPanel({
  leadId,
  application,
  status,
  hasSaved,
  initialChoice,
  onSaved,
  onAdvance,
  onSavingChange,
}: {
  leadId: string;
  application: StatusOfObjectionApplication;
  status: StatusOfObjectionStatus | null;
  hasSaved: boolean;
  initialChoice: boolean | null;
  onSaved: (clientGoingAhead: boolean) => void;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
}) {
  const [choice, setChoice] = useState<boolean | null>(
    initialChoice ?? true
  );
  const [invoicePreview, setInvoicePreview] =
    useState<ReplyToObjectionInvoicePreview | null>(null);

  useEffect(() => {
    setChoice(initialChoice ?? true);
  }, [initialChoice, application.tmNo]);

  // Load invoice preview for context fields
  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    getReplyToObjectionInvoicePreview(leadId, [application.tmNo])
      .then((data) => {
        if (!cancelled) setInvoicePreview(data);
      })
      .catch(() => {
        // Silent — preview is just for display fields
        if (!cancelled) setInvoicePreview(null);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, application.tmNo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (choice === null) {
      toast.error("Select YES Proceed With Reply or NO Abandon Application");
      return;
    }
    // Already saved with same choice → just advance
    if (hasSaved && initialChoice === choice) {
      onAdvance();
      return;
    }
    onSavingChange?.(true);
    try {
      await saveStatusOfObjectionConfirmationFromClient(leadId, {
        applications: [{ tmNo: application.tmNo, clientGoingAhead: choice }],
      });
      toast.success("Confirmation from Client saved");
      onSaved(choice);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save confirmation"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const statementText =
    application.statementOfReasonForObjection?.length
      ? application.statementOfReasonForObjection.join("; ")
      : "";

  return (
    <form
      id={EXAMINATION_REPORT_CCO_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-5 self-stretch pt-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* YES / NO segmented control */}
      <div className="flex flex-row flex-wrap items-center gap-5 self-stretch">
        {[
          { value: true, label: "YES Proceed With Reply" },
          { value: false, label: "NO Abandon Application" },
        ].map((opt) => {
          const isActive = choice === opt.value;
          return (
            <button
              key={String(opt.value)}
              type="button"
              onClick={() => setChoice(opt.value)}
              className={`flex flex-row items-center gap-2.5 h-9 px-2.5 py-2 rounded-[6px] border transition-colors ${
                isActive
                  ? "bg-[#222222] border-[#222222]"
                  : "bg-[#FCFCFC] border-[#EEEEEE]"
              }`}
              style={{ width: "230px" }}
            >
              <CheckCircleIcon active={isActive} />
              <span
                className="text-left"
                style={{
                  fontSize: "12px",
                  lineHeight: "20px",
                  fontWeight: 400,
                  color: isActive ? "#FFFFFF" : "#6D6D6D",
                }}
              >
                {opt.label}
              </span>
            </button>
          );
        })}
      </div>

      {/*
        Review item 78 — these fields are the data for *proceeding with* a reply, so they only
        belong to the YES branch. Choosing "NO Abandon Application" used to render the identical
        screen; now it explains where the application goes instead (5B(i), same as 3D(vii) NO).
      */}
      {choice === false ? (
        <p className="text-sm text-[#6D6D6D]">
          The application will be abandoned — no reply to the objection will be filed. Continue to
          record the outcome in{" "}
          <span className="font-medium text-[#222222]">
            Final Order → Refused or Abandoned
          </span>
          .
        </p>
      ) : (
        <div className="flex flex-col gap-4 self-stretch">
        <div className="flex flex-row items-center gap-5 self-stretch flex-wrap">
          <ConfirmField
            label="Mark"
            value={application.mark}
            placeholder="Mark"
          />
          <ConfirmField
            label="Selected Class"
            value={
              application.classNumber ? String(application.classNumber) : null
            }
            placeholder="Class"
          />
        </div>
        <div className="flex flex-row items-center gap-5 self-stretch flex-wrap">
          <ConfirmField
            label="Selected Type of Mark"
            value={application.typeOfMark}
            placeholder="Text"
          />
          <ConfirmField
            label="Acknowledgement No."
            value={status?.acknowledgementNo}
            placeholder="Text"
          />
        </div>
        <div className="flex flex-row items-center gap-5 self-stretch flex-wrap">
          <ConfirmField
            label="Date of Filing"
            value={status?.dateOfFiling}
            placeholder="Select Date"
          />
          <ConfirmField
            label="Addressee"
            value={invoicePreview?.addressee}
            placeholder="Text"
          />
        </div>
        <div className="flex flex-row items-center gap-5 self-stretch flex-wrap">
          <ConfirmField
            label="Form of Address"
            value={invoicePreview?.formOfAddress}
            placeholder="Text"
          />
          <ConfirmField
            label="Statement of Reason for Objection"
            value={statementText}
            placeholder="Text"
          />
        </div>
        <div className="flex flex-row items-center gap-5 self-stretch flex-wrap">
          <ConfirmField
            label="No. of applications for Reply to Objection"
            value={
              status?.numberOfApplicationsForReplyToObjection != null
                ? String(status.numberOfApplicationsForReplyToObjection)
                : null
            }
            placeholder="Text"
          />
          <div className="flex-1 min-w-0" aria-hidden />
        </div>
        </div>
      )}
    </form>
  );
}

function formatRupees(n: number | null | undefined): string {
  if (n == null) return "—";
  return `Rs. ${Number(n).toLocaleString("en-IN")}/-`;
}

type InvoiceReplyPreviewTab = "summary" | "pdf" | "message";

function parseInvoiceDate(s: string | null | undefined): Date | null {
  if (!s) return null;
  const iso =
    s.includes("-") && s.length === 10 && s.indexOf("-") === 4
      ? s
      : s.split("-").reverse().join("-");
  const d = new Date(iso + "T12:00:00");
  return isNaN(d.getTime()) ? null : d;
}

function formatInvoiceDateDDMMYYYY(d: Date | null): string {
  if (!d) return "—";
  return `${String(d.getDate()).padStart(2, "0")}-${String(
    d.getMonth() + 1
  ).padStart(2, "0")}-${d.getFullYear()}`;
}

function formatInvoiceRupees(val: string): string {
  const n = parseFloat(val);
  if (isNaN(n) || val === "") return "—";
  return `Rs.${Math.round(n).toLocaleString("en-IN")}/-`;
}

function parseInvoicePaymentMessage(message: string): {
  header: string;
  rows: Array<{ label: string; value: string } | { text: string }>;
} {
  const lines = message.split("\n");
  let header = "";
  const rows: Array<{ label: string; value: string } | { text: string }> = [];

  let i = 0;
  for (; i < lines.length; i++) {
    if (lines[i].trim()) {
      header = lines[i].trim();
      i++;
      break;
    }
  }

  while (i < lines.length) {
    const line = lines[i].trim();
    if (!line) {
      i++;
      continue;
    }
    const idx = line.indexOf(":");
    if (idx > 0) {
      const label = line.slice(0, idx).trim();
      const inlineValue = line.slice(idx + 1).trim();
      if (inlineValue) {
        rows.push({ label, value: inlineValue });
        i++;
        continue;
      }
      // Two-line shape: label on this line, value on the next non-empty line.
      let j = i + 1;
      while (j < lines.length && !lines[j].trim()) j++;
      if (j < lines.length) {
        rows.push({ label, value: lines[j].trim() });
        i = j + 1;
        continue;
      }
      rows.push({ label, value: "" });
      i++;
      continue;
    }
    rows.push({ text: line });
    i++;
  }

  return { header, rows };
}

function renderInvoicePaymentMessageLines(message: string) {
  const { header, rows } = parseInvoicePaymentMessage(message);
  return (
    <div
      className="grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-2 gap-y-1.5 font-inter text-[13px] leading-relaxed text-[#222]"
      style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
    >
      {header && (
        <p className="col-span-3 m-0 mb-2 break-words font-medium">
          {renderWhatsAppFormatting(header)}
        </p>
      )}
      {rows.map((row, i) =>
        "label" in row ? (
          <Fragment key={i}>
            <div className="break-words text-[#222]">
              {renderWhatsAppFormatting(row.label)}
            </div>
            <div className="text-[#222]">:</div>
            <div className="min-w-0 break-words text-[#222]">
              {renderWhatsAppFormatting(row.value)}
            </div>
          </Fragment>
        ) : (
          <p key={i} className="col-span-3 m-0 break-words">
            {renderWhatsAppFormatting(row.text)}
          </p>
        )
      )}
    </div>
  );
}

function ReplyInvoiceAmountTooltip({
  replyPerApp,
  tmMPerApp,
  numberOfApplications,
  numberOfApplicationsWithTmM,
  professionalChargesReply,
  professionalChargesTmM,
  computedInvoiceTotal,
  invoiceAmount,
}: {
  replyPerApp: number;
  tmMPerApp: number;
  numberOfApplications: number;
  numberOfApplicationsWithTmM: number;
  professionalChargesReply: number | null;
  professionalChargesTmM: number | null;
  computedInvoiceTotal: number;
  invoiceAmount: number;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div
      ref={containerRef}
      className="relative inline-flex items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label="How is the invoice amount calculated?"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex items-center justify-center w-4 h-4 text-[#6D6D6D] hover:text-[#222] focus:outline-none focus:ring-1 focus:ring-[#222] rounded-full"
      >
        <HiOutlineInformationCircle className="w-4 h-4" strokeWidth={1.75} />
      </button>
      {open && (
        <div
          role="tooltip"
          className="absolute left-0 top-5 z-30 w-[320px] rounded-[8px] border border-[#EEEEEE] bg-white p-3 shadow-lg"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          <p className="text-[11px] font-semibold leading-4 text-[#222]">
            How the invoice amount is calculated
          </p>
          <p className="mt-1 text-[11px] leading-4 text-[#6D6D6D]">
            (Reply charges × applications) + (TM-M charges × applications with TM-M)
          </p>
          <div className="mt-2 flex flex-col gap-1 text-[11px] leading-4 text-[#222]">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[#6D6D6D]">Reply charge / app</span>
              <span className="font-medium">
                @ Rs. {Math.round(replyPerApp).toLocaleString("en-IN")}/-
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[#6D6D6D]">TM-M charge / app</span>
              <span className="font-medium">
                @ Rs. {Math.round(tmMPerApp).toLocaleString("en-IN")}/-
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[#6D6D6D]">No. of applications</span>
              <span className="font-medium">{numberOfApplications}</span>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[#6D6D6D]">No. of applications with TM-M</span>
              <span className="font-medium">{numberOfApplicationsWithTmM}</span>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[#6D6D6D]">Total reply charges</span>
              <span className="font-medium">
                Rs. {Math.round(Number(professionalChargesReply ?? 0)).toLocaleString("en-IN")}/-
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[#6D6D6D]">Total TM-M charges</span>
              <span className="font-medium">
                Rs. {Math.round(Number(professionalChargesTmM ?? 0)).toLocaleString("en-IN")}/-
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[#6D6D6D]">Computed total</span>
              <span className="font-medium">
                Rs. {Math.round(computedInvoiceTotal).toLocaleString("en-IN")}/-
              </span>
            </div>
            <div className="mt-1 flex items-baseline justify-between gap-2 border-t border-[#EEEEEE] pt-1.5">
              <span className="font-semibold text-[#222]">Invoice amount</span>
              <span className="font-semibold text-[#222]">
                Rs. {Math.round(invoiceAmount).toLocaleString("en-IN")}/-
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ReplyInvoicePreviewRow({
  label,
  children,
  alignTop = false,
}: {
  label: string;
  children: React.ReactNode;
  alignTop?: boolean;
}) {
  const align = alignTop ? "items-start" : "items-baseline";
  return (
    <>
      <div className={`flex ${align} font-medium text-[#222]`}>{label}</div>
      <div className={`flex ${align} text-[#222]`}>:</div>
      <div className="text-[#222] break-words min-w-0">{children}</div>
    </>
  );
}

function InvoiceReplyTabsPanel({
  previewApps,
  designation,
  addresseeLine1,
  addresseeLine2,
  addresseeLine3,
  lastDateReply,
  professionalChargesReply,
  professionalChargesTmM,
  invoiceAmount,
  invoiceDate,
  invoiceNo,
  pdfUrl,
  previewTab,
  setPreviewTab,
  numPages,
  setNumPages,
  paymentMessage,
  paymentMessageLoading,
  embedInDialog = true,
  onGeneratePdf,
  generating = false,
}: {
  previewApps: ReplyToObjectionInvoicePreview["applications"];
  designation: string;
  addresseeLine1: string;
  addresseeLine2: string;
  addresseeLine3: string;
  lastDateReply: Date | null;
  professionalChargesReply: number | null;
  professionalChargesTmM: number | null;
  invoiceAmount: string;
  invoiceDate: Date | null;
  invoiceNo: string;
  pdfUrl: string | null;
  previewTab: InvoiceReplyPreviewTab;
  setPreviewTab: (tab: InvoiceReplyPreviewTab) => void;
  numPages: number | null;
  setNumPages: (n: number | null) => void;
  paymentMessage: string | null;
  paymentMessageLoading: boolean;
  embedInDialog?: boolean;
  onGeneratePdf?: () => void;
  generating?: boolean;
}) {
  const pageWidth = Math.min(
    800,
    typeof window !== "undefined" ? window.innerWidth - 48 : 800
  );

  const formatRupeesNum = (n: number | null) =>
    n != null ? `Rs.${Math.round(n).toLocaleString("en-IN")}/-` : "—";

  return (
    <div
      className={`flex min-h-0 flex-col ${
        embedInDialog ? "" : "h-full min-h-0"
      }`}
    >
      <div
        className="flex shrink-0 gap-1 border-b border-[#e3e3e3] bg-white px-2 sm:px-3"
        role="tablist"
      >
        <button
          type="button"
          role="tab"
          aria-selected={previewTab === "summary"}
          onClick={() => setPreviewTab("summary")}
          className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation ${
            previewTab === "summary"
              ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
              : "text-[#6d6d6d] hover:text-[#222]"
          }`}
        >
          Invoice details
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={previewTab === "pdf"}
          onClick={() => setPreviewTab("pdf")}
          className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation ${
            previewTab === "pdf"
              ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
              : "text-[#6d6d6d] hover:text-[#222]"
          }`}
        >
          Generated PDF
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={previewTab === "message"}
          onClick={() => setPreviewTab("message")}
          className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation ${
            previewTab === "message"
              ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
              : "text-[#6d6d6d] hover:text-[#222]"
          }`}
        >
          Message Preview
        </button>
      </div>
      <div
        className={
          embedInDialog
            ? "max-h-[55vh] min-h-0 flex-1 overflow-auto bg-[#fafafa] p-2 sm:p-3 md:max-h-[min(480px,55vh)]"
            : "min-h-0 flex-1 overflow-auto bg-[#fafafa] p-2 sm:p-4"
        }
      >
        {previewTab === "summary" ? (
          <div
            className="flex w-full max-w-3xl mx-auto flex-col gap-3 font-inter"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Invoice details
              </p>
              <div
                className="grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-3 gap-y-1.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2.5 text-[12px] leading-5 text-[#222]"
              >
                <ReplyInvoicePreviewRow label="Addressee" alignTop>
                  {[addresseeLine1, addresseeLine2, addresseeLine3].some(Boolean) ? (
                    <div className="flex flex-col">
                      {[addresseeLine1, addresseeLine2, addresseeLine3]
                        .filter(Boolean)
                        .map((line, i) => (
                          <span key={i}>{line}</span>
                        ))}
                    </div>
                  ) : (
                    <span className="text-[#6D6D6D]">—</span>
                  )}
                </ReplyInvoicePreviewRow>
                <ReplyInvoicePreviewRow label="Form of Address">
                  {designation?.trim() || (
                    <span className="text-[#6D6D6D]">—</span>
                  )}
                </ReplyInvoicePreviewRow>
                <ReplyInvoicePreviewRow label="Last date for filing replies">
                  {lastDateReply ? (
                    formatInvoiceDateDDMMYYYY(lastDateReply)
                  ) : (
                    <span className="text-[#6D6D6D]">—</span>
                  )}
                </ReplyInvoicePreviewRow>
                <ReplyInvoicePreviewRow label="Professional Charges for filing replies">
                  {formatRupeesNum(professionalChargesReply)}
                </ReplyInvoicePreviewRow>
                <ReplyInvoicePreviewRow label="Professional Charges for filing TM-M">
                  {formatRupeesNum(professionalChargesTmM)}
                </ReplyInvoicePreviewRow>
                <ReplyInvoicePreviewRow label="Invoice Amount for Reply to Objection">
                  {formatInvoiceRupees(invoiceAmount)}
                </ReplyInvoicePreviewRow>
                <ReplyInvoicePreviewRow label="Date of Invoice for Reply to Objection">
                  {invoiceDate ? (
                    formatInvoiceDateDDMMYYYY(invoiceDate)
                  ) : (
                    <span className="text-[#6D6D6D]">—</span>
                  )}
                </ReplyInvoicePreviewRow>
                <ReplyInvoicePreviewRow label="Invoice No. for Reply to Objection">
                  {invoiceNo?.trim() || (
                    <span className="text-[#6D6D6D]">Auto-generated on save</span>
                  )}
                </ReplyInvoicePreviewRow>
              </div>
            </section>

            <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Applications
              </p>
              <div className="overflow-x-auto rounded-[6px] border border-[#EEEEEE]">
                <table className="w-full border-collapse text-[12px] leading-5">
                  <thead className="bg-[#F7F7F7]">
                    <tr>
                      <th className="px-2.5 py-2 text-left font-semibold text-[#222222] border-b border-[#EEEEEE] whitespace-nowrap">
                        Application Number
                      </th>
                      <th className="px-2.5 py-2 text-left font-semibold text-[#222222] border-b border-[#EEEEEE]">
                        Class
                      </th>
                      <th className="px-2.5 py-2 text-left font-semibold text-[#222222] border-b border-[#EEEEEE]">
                        Mark
                      </th>
                      <th className="px-2.5 py-2 text-left font-semibold text-[#222222] border-b border-[#EEEEEE]">
                        Type of Mark
                      </th>
                      <th className="px-2.5 py-2 text-left font-semibold text-[#222222] border-b border-[#EEEEEE] whitespace-nowrap">
                        Date of Filing
                      </th>
                      <th className="px-2.5 py-2 text-left font-semibold text-[#222222] border-b border-[#EEEEEE] min-w-[180px]">
                        Reason for Objection
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewApps.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-2.5 py-3 text-center text-[#6D6D6D]"
                        >
                          No applications.
                        </td>
                      </tr>
                    ) : (
                      previewApps.map((app, idx) => (
                        <tr
                          key={`${app.tmNo}-${idx}`}
                          className={
                            idx < previewApps.length - 1
                              ? "border-b border-[#EEEEEE]"
                              : ""
                          }
                        >
                          <td className="px-2.5 py-2 text-[#222222] align-top whitespace-nowrap">
                            {app.tmNo || "—"}
                          </td>
                          <td className="px-2.5 py-2 text-[#222222] align-top">
                            {app.classNumber != null
                              ? String(app.classNumber)
                              : "—"}
                          </td>
                          <td className="px-2.5 py-2 text-[#222222] align-top">
                            {app.mark?.trim() || "—"}
                          </td>
                          <td className="px-2.5 py-2 text-[#222222] align-top">
                            {app.typeOfMark?.trim() || "—"}
                          </td>
                          <td className="px-2.5 py-2 text-[#222222] align-top whitespace-nowrap">
                            {formatInvoiceDateDDMMYYYY(
                              parseInvoiceDate(app.applicationDate)
                            )}
                          </td>
                          <td className="px-2.5 py-2 text-[#222222] align-top whitespace-pre-line">
                            {app.statementOfReasonForObjection?.trim() || "—"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        ) : previewTab === "pdf" ? (
          pdfUrl ? (
            <Document
              file={pdfUrl}
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
              loading={
                <p className="text-sm text-[#6d6d6d] font-inter">
                  Loading PDF…
                </p>
              }
              error={
                <div className="flex flex-col items-center justify-center gap-3 py-8 font-inter">
                  <p className="text-sm text-red-600">Failed to load PDF.</p>
                  {onGeneratePdf && (
                    <button
                      type="button"
                      onClick={onGeneratePdf}
                      disabled={generating}
                      className="inline-flex items-center justify-center h-9 px-4 rounded-[6px] bg-[#222222] text-xs font-medium text-white hover:bg-[#333] disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {generating ? "Generating…" : "Regenerate PDF"}
                    </button>
                  )}
                </div>
              }
              className="flex flex-col items-center gap-4"
            >
              {numPages != null &&
                Array.from({ length: numPages }, (_, i) => (
                  <Page
                    key={i + 1}
                    pageNumber={i + 1}
                    width={pageWidth}
                    renderTextLayer
                    renderAnnotationLayer
                  />
                ))}
            </Document>
          ) : (
            <div className="flex flex-col items-center justify-center gap-3 py-12 font-inter">
              <p className="text-sm text-[#6d6d6d]">
                No invoice PDF has been generated yet.
              </p>
              {onGeneratePdf && (
                <button
                  type="button"
                  onClick={onGeneratePdf}
                  disabled={generating}
                  className="inline-flex items-center justify-center h-9 px-4 rounded-[6px] bg-[#222222] text-xs font-medium text-white hover:bg-[#333] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {generating ? "Generating…" : "Generate PDF"}
                </button>
              )}
            </div>
          )
        ) : (
          <div className="rounded-lg border border-[#EEEEEE] bg-white p-4 shadow-sm sm:p-5">
            {paymentMessageLoading ? (
              <p className="text-sm text-[#6d6d6d] font-inter">
                Loading preview…
              </p>
            ) : paymentMessage ? (
              renderInvoicePaymentMessageLines(paymentMessage)
            ) : (
              <p className="text-sm text-[#6d6d6d] font-inter">
                Payment message preview not available.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function InvoiceToClientObjectionPanel({
  leadId,
  application,
  phone,
  onAdvance,
  onSavingChange,
}: {
  leadId: string;
  application: StatusOfObjectionApplication;
  phone?: string;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
}) {
  // Read-only fields auto-populated from preview/status
  const [mark, setMark] = useState<string>("");
  const [classNo, setClassNo] = useState<string>("");
  const [typeOfMark, setTypeOfMark] = useState<string>("");
  const [ackNo, setAckNo] = useState<string>("");
  const [filingDate, setFilingDate] = useState<Date | null>(null);
  const [lastDateReply, setLastDateReply] = useState<Date | null>(null);

  // Editable invoice fields
  const [invoiceAmount, setInvoiceAmount] = useState<string>("");
  const [invoiceNo, setInvoiceNo] = useState<string>("");
  const [invoiceDate, setInvoiceDate] = useState<Date | null>(null);
  const [includeInvoiceNo, setIncludeInvoiceNo] = useState(false);

  // Addressee fields (pre-populated from preview)
  const [designation, setDesignation] = useState<string>("");
  const [addresseeLine1, setAddresseeLine1] = useState<string>("");
  const [addresseeLine2, setAddresseeLine2] = useState<string>("");
  const [addresseeLine3, setAddresseeLine3] = useState<string>("");

  // Per-app preview rows + selection
  const [previewApps, setPreviewApps] = useState<
    ReplyToObjectionInvoicePreview["applications"]
  >([]);
  const [selectedTmNo, setSelectedTmNo] = useState<string>(application.tmNo);
  const [professionalChargesReply, setProfessionalChargesReply] = useState<
    number | null
  >(null);
  const [professionalChargesTmM, setProfessionalChargesTmM] = useState<
    number | null
  >(null);
  const [professionalChargesReplyPerApp, setProfessionalChargesReplyPerApp] =
    useState<number>(0);
  const [professionalChargesTmMPerApp, setProfessionalChargesTmMPerApp] =
    useState<number>(0);
  const [numberOfApplications, setNumberOfApplications] = useState<number>(0);
  const [numberOfApplicationsWithTmM, setNumberOfApplicationsWithTmM] =
    useState<number>(0);
  const [computedInvoiceTotal, setComputedInvoiceTotal] = useState<number>(0);

  // Action state
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [hasSent, setHasSent] = useState(false);

  // Preview modal state
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewTab, setPreviewTab] =
    useState<InvoiceReplyPreviewTab>("summary");
  const [numPages, setNumPages] = useState<number | null>(null);
  const [paymentMessageText, setPaymentMessageText] = useState<string | null>(
    null
  );
  const [paymentMessageLoading, setPaymentMessageLoading] = useState(false);

  const loadInvoice = async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      let status = await getReplyToObjectionInvoiceStatus(
        leadId,
        application.tmNo
      );
      if (status.invoiceAmount == null) {
        await saveReplyToObjectionInvoice(leadId, {});
        status = await getReplyToObjectionInvoiceStatus(
          leadId,
          application.tmNo
        );
      }
      const preview = await getReplyToObjectionInvoicePreview(leadId).catch(
        () => null
      );

      if (preview) {
        setPreviewApps(preview.applications);
        setLastDateReply(parseInvoiceDate(preview.lastDateForReplyToObjection));
        setDesignation(preview.formOfAddress ?? "");
        setAddresseeLine1(
          preview.addresseeLine1 ?? preview.addressee ?? ""
        );
        setAddresseeLine2(preview.addresseeLine2 ?? "");
        setAddresseeLine3(preview.addresseeLine3 ?? "");
        setProfessionalChargesReply(preview.professionalChargesReply);
        setProfessionalChargesTmM(preview.professionalChargesTmM);
        setProfessionalChargesReplyPerApp(
          Number(preview.professionalChargesReplyPerApp ?? 0)
        );
        setProfessionalChargesTmMPerApp(
          Number(preview.professionalChargesTmMPerApp ?? 0)
        );
        setNumberOfApplications(Number(preview.numberOfApplications ?? 0));
        setNumberOfApplicationsWithTmM(
          Number(preview.numberOfApplicationsWithTmM ?? 0)
        );
        setComputedInvoiceTotal(Number(preview.computedInvoiceTotal ?? 0));

        const selectedApp =
          preview.applications.find(
            (a: ReplyToObjectionInvoicePreview["applications"][number]) =>
              a.tmNo === application.tmNo
          ) ??
          preview.applications[0] ??
          null;
        if (selectedApp) {
          setSelectedTmNo(selectedApp.tmNo);
          setMark(selectedApp.mark ?? "");
          setClassNo(
            selectedApp.classNumber ? `Class ${selectedApp.classNumber}` : ""
          );
          setTypeOfMark(selectedApp.typeOfMark ?? "");
          setFilingDate(parseInvoiceDate(selectedApp.applicationDate));
          if (selectedApp.invoiceAmount != null)
            setInvoiceAmount(String(selectedApp.invoiceAmount));
          if (selectedApp.individualInvoiceNo)
            setInvoiceNo(selectedApp.individualInvoiceNo);
        }
      }

      setAckNo(application.status ? "" : "");
      // Ack number comes from the StatusOfObjection status passed via context — not preview.
      // We don't have it here directly; keep blank or pull from invoice header below.

      const row =
        status.tmInvoiceRows.find((r) => r.tmNo === application.tmNo) ?? null;
      if (row?.invoiceAmount != null) setInvoiceAmount(String(row.invoiceAmount));
      if (row?.invoiceDate) setInvoiceDate(parseInvoiceDate(row.invoiceDate));
      if (row?.individualInvoiceNo) setInvoiceNo(row.individualInvoiceNo);
      if (!row) {
        if (status.invoiceAmount != null)
          setInvoiceAmount(String(status.invoiceAmount));
        if (status.invoiceDate) setInvoiceDate(parseInvoiceDate(status.invoiceDate));
        if (status.invoiceNo) setInvoiceNo(status.invoiceNo);
      }
      setIncludeInvoiceNo(
        Boolean(row?.includeInvoiceNo ?? status.includeInvoiceNo)
      );

      const resolvedPdfUrl =
        row?.replyToObjectionInvoicePdfUrl ??
        status.replyToObjectionInvoicePdfUrl ??
        null;
      setPdfUrl(resolvedPdfUrl);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to load invoice"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadInvoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, application.tmNo]);

  const buildSavePayload = () => {
    const tmNo = selectedTmNo || application.tmNo;
    const amountNum = parseFloat(invoiceAmount);
    return {
      applications: [
        {
          tmNo,
          ...(Number.isFinite(amountNum) && amountNum >= 0
            ? { invoiceAmount: amountNum }
            : {}),
          ...(invoiceDate
            ? { invoiceDate: formatDateDDMMYYYYForApi(invoiceDate) }
            : {}),
          includeInvoiceNo,
        },
      ],
    };
  };

  const handleGeneratePdf = async () => {
    if (!leadId || generating) return;
    setGenerating(true);
    onSavingChange?.(true);
    try {
      const saved = await saveReplyToObjectionInvoice(leadId, buildSavePayload());
      const savedRow =
        saved.tmInvoiceRows.find((r) => r.tmNo === selectedTmNo) ?? null;
      const savedInvoiceNo =
        savedRow?.individualInvoiceNo ?? saved.invoiceNo ?? "";
      if (savedInvoiceNo) setInvoiceNo(savedInvoiceNo);

      await generateReplyToObjectionInvoicePdf(leadId, [selectedTmNo]);

      const refreshed = await getReplyToObjectionInvoiceStatus(
        leadId,
        selectedTmNo
      );
      const refreshedRow =
        refreshed.tmInvoiceRows.find((r) => r.tmNo === selectedTmNo) ?? null;
      const refreshedPdfUrl =
        refreshedRow?.replyToObjectionInvoicePdfUrl ??
        refreshed.replyToObjectionInvoicePdfUrl ??
        null;
      setPdfUrl(refreshedPdfUrl);
      const refreshedInvoiceNo =
        refreshedRow?.individualInvoiceNo ?? refreshed.invoiceNo ?? "";
      if (refreshedInvoiceNo) setInvoiceNo(refreshedInvoiceNo);

      toast.success("Invoice PDF generated successfully.");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to generate PDF"
      );
    } finally {
      setGenerating(false);
      onSavingChange?.(false);
    }
  };

  const handlePreview = () => {
    if (!leadId) return;
    // Preview is read-only: show current details using local state.
    // PDF is created only when the user explicitly clicks Generate PDF.
    setPaymentMessageText(null);
    setPreviewTab("summary");
    setNumPages(null);
    setPreviewModalOpen(true);
    // Fetch the WhatsApp payment-message preview in the background so the
    // "Message Preview" tab is ready when the user switches to it.
    setPaymentMessageLoading(true);
    void getReplyToObjectionInvoicePaymentMessagePreview(leadId, {
      tmNos: [selectedTmNo],
    })
      .then((res) => setPaymentMessageText(res.message ?? ""))
      .catch(() => {
        // Non-blocking — tab will show empty-state.
        setPaymentMessageText(null);
      })
      .finally(() => setPaymentMessageLoading(false));
  };

  const handleSendToClient = async (): Promise<boolean> => {
    if (!leadId || sending) return false;
    setSending(true);
    onSavingChange?.(true);
    try {
      // Save invoice fields first so backend has the latest amount/date.
      const saved = await saveReplyToObjectionInvoice(leadId, buildSavePayload());
      const savedRow =
        saved.tmInvoiceRows.find((r) => r.tmNo === selectedTmNo) ?? null;
      const savedInvoiceNo =
        savedRow?.individualInvoiceNo ?? saved.invoiceNo ?? "";
      if (savedInvoiceNo) setInvoiceNo(savedInvoiceNo);

      // Ensure the PDF exists before sending.
      let activePdfUrl =
        savedRow?.replyToObjectionInvoicePdfUrl ??
        saved.replyToObjectionInvoicePdfUrl ??
        pdfUrl;
      if (!activePdfUrl) {
        await generateReplyToObjectionInvoicePdf(leadId, [selectedTmNo]);
        const refreshed = await getReplyToObjectionInvoiceStatus(
          leadId,
          selectedTmNo
        );
        const refreshedRow =
          refreshed.tmInvoiceRows.find((r) => r.tmNo === selectedTmNo) ?? null;
        activePdfUrl =
          refreshedRow?.replyToObjectionInvoicePdfUrl ??
          refreshed.replyToObjectionInvoicePdfUrl ??
          null;
      }
      setPdfUrl(activePdfUrl);

      await Promise.all([
        sendReplyToObjectionInvoiceToClient(leadId, { tmNos: [selectedTmNo] }),
        sendReplyToObjectionInvoicePaymentMessage(leadId, {
          tmNos: [selectedTmNo],
        }),
      ]);
      setHasSent(true);
      toast.success("Invoice sent to client");
      return true;
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to send invoice to client"
      );
      return false;
    } finally {
      setSending(false);
      onSavingChange?.(false);
    }
  };

  const handleResend = async () => {
    if (!leadId || sending) return;
    await handleSendToClient();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || sending) return;
    if (hasSent) {
      // Already sent — Save & Continue just advances.
      onAdvance();
      return;
    }
    const ok = await handleSendToClient();
    if (ok) onAdvance();
  };

  if (loading) {
    return (
      <p
        className="text-xs text-[#6D6D6D]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        Loading invoice…
      </p>
    );
  }

  const FORM_STYLES = { fontFamily: "Inter, sans-serif" } as const;
  const LABEL_CLASS = "text-[12px] font-normal leading-5 text-[#222222]";
  const READONLY_CLASS =
    "h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-[#F7F7F7] px-2.5 py-2 text-[12px] leading-5 text-[#6D6D6D] box-border flex items-center truncate";
  const dateBtnCls =
    "w-full !h-9 !min-h-0 flex items-center justify-between px-2.5 !text-[12px] border border-[#EEEEEE] rounded-[6px] focus:outline-none";

  const parsedAmount = parseFloat(invoiceAmount);
  const amountForDisplay =
    Number.isFinite(parsedAmount) && parsedAmount >= 0 ? parsedAmount : 0;

  return (
    <form
      id={EXAMINATION_REPORT_INV_FORM_ID}
      onSubmit={handleSubmit}
      className="flex w-full max-w-[1085px] flex-col gap-5"
      style={FORM_STYLES}
    >
      {/* Row 1 (Editable + computed): Invoice Date | Invoice Amount */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Date of Invoice for Reply to Objection</label>
          <DateTimePicker
            value={invoiceDate}
            onChange={setInvoiceDate}
            showTime={false}
            placeholder="Select Date"
            buttonClassName={dateBtnCls + " bg-white text-[#6D6D6D] hover:bg-[#F7F7F7]"}
          />
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5">
            <label className={LABEL_CLASS}>Invoice Amount for Reply to Objection</label>
            <ReplyInvoiceAmountTooltip
              replyPerApp={professionalChargesReplyPerApp}
              tmMPerApp={professionalChargesTmMPerApp}
              numberOfApplications={numberOfApplications}
              numberOfApplicationsWithTmM={numberOfApplicationsWithTmM}
              professionalChargesReply={professionalChargesReply}
              professionalChargesTmM={professionalChargesTmM}
              computedInvoiceTotal={computedInvoiceTotal}
              invoiceAmount={amountForDisplay}
            />
          </div>
          <div className="flex h-9 min-h-[44px] md:min-h-0 border border-[#EEEEEE] rounded-[6px] bg-white overflow-hidden box-border">
            <input
              type="text"
              inputMode="decimal"
              value={invoiceAmount}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^\d.]/g, "");
                const parts = raw.split(".");
                const v =
                  parts.length > 1
                    ? `${parts[0]}.${parts.slice(1).join("")}`
                    : raw;
                setInvoiceAmount(v);
              }}
              placeholder="00.00"
              className="flex-1 min-w-0 h-full px-2.5 text-[12px] font-normal text-[#222222] placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222] bg-transparent"
            />
            <div className="flex items-center gap-0.5 px-2.5 border-l border-[#EEEEEE] text-[12px] text-[#222222] shrink-0">
              Rs.(₹)
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
                className="ml-0.5 opacity-40"
              >
                <path
                  d="M2.5 3.75L5 6.25L7.5 3.75"
                  stroke="#222222"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Row 2 (Readonly): Invoice No. for Reply to Objection */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Invoice No. for Reply to Objection</label>
          <input
            type="text"
            value={invoiceNo}
            placeholder="Auto-generated on save"
            readOnly
            className="h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-[#F7F7F7] px-2.5 py-2 text-[12px] leading-5 text-[#6D6D6D] box-border"
          />
        </div>
      </div>

      <IncludeInvoiceNoCheckbox
        id="reply-to-objection-include-invoice-no"
        checked={includeInvoiceNo}
        onChange={setIncludeInvoiceNo}
      />

      {/* Invoice Amount summary bar */}
      <div
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-3 rounded-[8px] px-4 sm:px-5 py-4 sm:py-0 min-h-[72px] sm:h-[72px]"
        style={{ background: "#222222" }}
      >
        <div className="flex flex-row items-center gap-2 min-w-0">
          <img
            src={invoiceAmountCheck}
            alt=""
            className="w-5 h-5 flex-shrink-0"
          />
          <span
            className="text-[12px] md:text-sm font-normal leading-5 text-white/90 truncate"
            style={FORM_STYLES}
          >
            INVOICE AMOUNT FOR REPLY TO OBJECTION
          </span>
        </div>
        <span
          className="text-lg md:text-[22px] font-bold leading-8 md:leading-10 text-white shrink-0"
          style={FORM_STYLES}
        >
          {formatRupees(amountForDisplay)}
        </span>
      </div>

      {/* Generate PDF + Download + Preview */}
      <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2.5">
        <div className="flex flex-row items-center gap-2.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] pl-4 pr-2 py-2 min-h-[44px] sm:h-11">
          <img
            src={invoiceGenerate}
            alt=""
            className="w-[22px] h-[22px] flex-shrink-0"
          />
          <button
            type="button"
            onClick={() => void handleGeneratePdf()}
            disabled={generating || !leadId}
            className="flex flex-row items-center justify-center min-h-[40px] h-9 px-4 rounded-[6px] bg-[#222222] text-[12px] md:text-xs font-medium text-white hover:bg-[#333333] transition-colors shrink-0 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
            style={FORM_STYLES}
          >
            {generating
              ? "Generating…"
              : pdfUrl
                ? "Regenerate PDF"
                : "Generate PDF"}
          </button>
        </div>
        {pdfUrl ? (
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-row items-center justify-center min-h-[44px] sm:h-11 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] md:text-xs font-medium text-[#222222] hover:bg-[#F7F7F7] transition-colors shrink-0 touch-manipulation"
            style={FORM_STYLES}
          >
            Download
          </a>
        ) : (
          <button
            type="button"
            disabled
            className="flex flex-row items-center justify-center min-h-[44px] sm:h-11 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] md:text-xs font-medium text-[#222222] opacity-40 shrink-0 touch-manipulation"
            style={FORM_STYLES}
          >
            Download
          </button>
        )}
        <button
          type="button"
          onClick={handlePreview}
          disabled={generating || !leadId}
          className="flex flex-row items-center justify-center min-h-[44px] sm:h-11 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] md:text-xs font-medium text-[#222222] hover:bg-[#F7F7F7] transition-colors shrink-0 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
          style={FORM_STYLES}
        >
          Preview
        </button>
      </div>

      {/* Phone number + optional Resend */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="tel"
          inputMode="numeric"
          value={phone ?? ""}
          readOnly
          placeholder="e.g. +919876543210"
          className="w-full min-w-0 sm:w-[200px] h-9 px-[10px] py-2 text-[12px] text-[#222] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] placeholder:text-[#6D6D6D]"
        />
        {hasSent && (
          <button
            type="button"
            disabled={sending}
            onClick={() => void handleResend()}
            className="inline-flex items-center justify-center h-9 px-4 text-[12px] font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
            style={FORM_STYLES}
          >
            {sending ? "Sending…" : "Resend Message"}
          </button>
        )}
      </div>

      {/* Invoice preview modal */}
      {previewModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4">
          <div className="relative flex h-[95vh] sm:h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
            <div className="flex shrink-0 items-center justify-between border-b border-[#e3e3e3] px-3 py-3 sm:px-4">
              <span className="truncate pr-2 text-xs font-medium text-[#222] sm:text-sm font-inter">
                Invoice for Reply to Objection — Preview
              </span>
              <button
                type="button"
                onClick={() => setPreviewModalOpen(false)}
                className="flex size-10 shrink-0 items-center justify-center rounded text-[#6d6d6d] hover:bg-[#eee] hover:text-[#222] sm:size-8 touch-manipulation"
                aria-label="Close"
              >
                <HiOutlineX className="size-5" strokeWidth={2} />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-hidden p-2 sm:p-4">
              <InvoiceReplyTabsPanel
                previewApps={previewApps}
                designation={designation}
                addresseeLine1={addresseeLine1}
                addresseeLine2={addresseeLine2}
                addresseeLine3={addresseeLine3}
                lastDateReply={lastDateReply}
                professionalChargesReply={professionalChargesReply}
                professionalChargesTmM={professionalChargesTmM}
                invoiceAmount={invoiceAmount}
                invoiceDate={invoiceDate}
                invoiceNo={invoiceNo}
                pdfUrl={pdfUrl}
                previewTab={previewTab}
                setPreviewTab={setPreviewTab}
                numPages={numPages}
                setNumPages={setNumPages}
                paymentMessage={paymentMessageText}
                paymentMessageLoading={paymentMessageLoading}
                embedInDialog={false}
                onGeneratePdf={() => void handleGeneratePdf()}
                generating={generating}
              />
            </div>
          </div>
        </div>
      )}
    </form>
  );
}

function PaymentPanel({
  leadId,
  application,
  onAdvance,
  onSavingChange,
  onProgressActionChange,
}: {
  leadId: string;
  application: StatusOfObjectionApplication;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
  onProgressActionChange?: (run: (() => Promise<void>) | null) => void;
}) {
  const [invoice, setInvoice] = useState<ReplyToObjectionInvoiceStatus | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [collectedInput, setCollectedInput] = useState("");
  const [recording, setRecording] = useState(false);

  // Resolve current TM's invoice line (when scoped) — fall back to header values
  const tmRow = invoice?.tmInvoiceRows.find(
    (r) => r.tmNo === application.tmNo
  );
  const invoiceAmount =
    tmRow?.invoiceAmount ??
    tmRow?.computedInvoiceTotal ??
    invoice?.invoiceAmount ??
    invoice?.computedInvoiceTotal ??
    null;
  const collectedAmount = tmRow?.collectedAmount ?? invoice?.collectedAmount ?? null;
  const outstandingAmount =
    tmRow?.outstandingAmount ?? invoice?.outstandingAmount ?? null;

  const loadInvoice = async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      let data = await getReplyToObjectionInvoiceStatus(
        leadId,
        application.tmNo
      );
      // First-time entry to Payment: invoice/per-TM lines may not exist yet.
      // PATCH /reply-to-objection/invoice with empty body creates the invoice
      // header + per-TM lines from app settings (required before collected-amount
      // and generate-PDF). Skip when invoice header is already saved
      // (`invoiceAmount` non-null) to avoid the upsert wiping per-TM
      // `collectedAmount` values via `replaceByStageInvoiceId`.
      if (data.invoiceAmount == null) {
        await saveReplyToObjectionInvoice(leadId, {});
        data = await getReplyToObjectionInvoiceStatus(leadId, application.tmNo);
      }
      setInvoice(data);
      // Input represents a new installment (matches follow-up payment screen).
      // The previously saved total is surfaced via the "Already collected"
      // helper line below the input.
      setCollectedInput("");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to load invoice"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadInvoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, application.tmNo]);

  const saveCollectedAmount = async (): Promise<boolean> => {
    if (!leadId || recording) return false;
    const trimmed = collectedInput.trim();
    if (trimmed === "") {
      toast.error("Enter the collected amount");
      return false;
    }
    const thisPayment = Number(trimmed);
    if (!Number.isFinite(thisPayment) || thisPayment <= 0) {
      toast.error("Collected amount must be greater than 0");
      return false;
    }
    const savedCollected = Number(collectedAmount ?? 0);
    if (invoiceAmount != null && savedCollected >= invoiceAmount) {
      toast.error("This invoice is already fully paid.");
      return false;
    }
    const newTotalCollected = savedCollected + thisPayment;
    if (invoiceAmount != null && newTotalCollected > invoiceAmount) {
      toast.error(
        "Collected amount cannot exceed the outstanding invoice balance."
      );
      return false;
    }
    setRecording(true);
    onSavingChange?.(true);
    try {
      const data = await saveReplyToObjectionCollectedAmount(leadId, {
        applications: [
          { tmNo: application.tmNo, collectedAmount: newTotalCollected },
        ],
      });
      setInvoice(data);
      // Clear the installment input — the new total is now reflected via the
      // "Already collected" helper sourced from invoice state.
      setCollectedInput("");
      return true;
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save collected amount"
      );
      return false;
    } finally {
      setRecording(false);
      onSavingChange?.(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const ok = await saveCollectedAmount();
    if (ok) onAdvance();
  };

  // Register the Progress (skip) handler with the dashboard footer so the
  // Progress button shows up next to "Save and Continue" — matches the
  // FC-Fail Invoice pattern in FormalitiesCheckView.
  useEffect(() => {
    if (!onProgressActionChange) return;
    if (!leadId) {
      onProgressActionChange(null);
      return;
    }
    const run = async () => {
      try {
        await skipStep(
          leadId,
          NewApplicationStep.S3_03b_vii_PaymentFromClientReplyToObjection
        );
        toast.success("Step skipped successfully.");
        onAdvance();
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to skip step"
        );
      }
    };
    onProgressActionChange(run);
    return () => onProgressActionChange(null);
  }, [leadId, onAdvance, onProgressActionChange]);

  // Outstanding reflects live input: invoice − (savedCollected + thisPayment).
  const parsedInstallment = Number(collectedInput);
  const liveInstallment =
    collectedInput.trim() === "" || !Number.isFinite(parsedInstallment)
      ? 0
      : Math.max(0, parsedInstallment);
  const savedCollectedAmount = Number(collectedAmount ?? 0);
  const fallbackOutstanding = Number(outstandingAmount ?? 0);
  const liveOutstanding =
    invoiceAmount != null
      ? Math.max(
          0,
          Number(invoiceAmount) - savedCollectedAmount - liveInstallment
        )
      : Math.max(0, fallbackOutstanding - liveInstallment);
  const outstandingFormatted = Math.round(liveOutstanding).toLocaleString(
    "en-IN"
  );

  return (
    <form
      id={EXAMINATION_REPORT_PAY_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-5 self-stretch w-full max-w-[1085px] pt-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Collected + Invoice Amount side-by-side (follow-up style) */}
      <div className="flex flex-col sm:flex-row items-start gap-5 w-full">
        <div
          className="flex flex-col gap-1"
          style={{ flex: "1 1 0", minWidth: 0 }}
        >
          <label
            className="text-[12px] font-normal leading-5 text-[#222222]"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            Collected Amount for Reply to Objection
          </label>
          <div
            className="flex flex-row justify-between items-center w-full h-9"
            style={{
              background: "#FFFFFF",
              border: "1px solid #EEEEEE",
              borderRadius: "6px",
              boxSizing: "border-box",
            }}
          >
            <input
              type="text"
              inputMode="decimal"
              value={collectedInput}
              onChange={(e) => {
                const cleaned = e.target.value.replace(/[^\d.]/g, "");
                const parts = cleaned.split(".");
                const next =
                  parts.length > 1
                    ? `${parts[0]}.${parts.slice(1).join("")}`
                    : cleaned;
                setCollectedInput(next);
              }}
              placeholder="00.00"
              className="flex-1 min-w-0 h-full pl-2.5 pr-0 py-0 bg-transparent text-[12px] font-normal leading-5 text-[#222222] placeholder:text-[#6D6D6D] border-0 outline-none focus:ring-0"
              style={{ fontFamily: "Inter, sans-serif" }}
            />
            <div
              className="flex flex-row justify-center items-center shrink-0 px-2.5 py-2 gap-1 h-9 min-w-[76px]"
              style={{ background: "#F7F7F7", borderRadius: "0 6px 6px 0" }}
            >
              <span
                className="text-[12px] font-normal leading-5 text-[#6D6D6D]"
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                Rs.(₹)
              </span>
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
                aria-hidden
                className="opacity-40"
              >
                <path
                  d="M2.5 3.75L5 6.25L7.5 3.75"
                  stroke="#222222"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
          {collectedAmount != null && Number(collectedAmount) > 0 && (
            <span
              className="text-[11px] leading-4 text-[#6D6D6D]"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              Already collected: Rs.{" "}
              {Number(collectedAmount).toLocaleString("en-IN", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })}
              /-
            </span>
          )}
        </div>

        <div
          className="flex flex-col gap-1"
          style={{ flex: "1 1 0", minWidth: 0 }}
        >
          <label
            className="text-[12px] font-normal leading-5 text-[#222222]"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            Invoice Amount for Reply to Objection
          </label>
          <div
            className="flex flex-row justify-between items-center w-full h-9"
            style={{
              background: "#F7F7F7",
              border: "1px solid #EEEEEE",
              borderRadius: "6px",
              boxSizing: "border-box",
            }}
          >
            <input
              type="text"
              value={loading ? "…" : formatRupees(invoiceAmount)}
              readOnly
              className="flex-1 min-w-0 h-full pl-2.5 pr-0 py-0 bg-transparent text-[12px] font-normal leading-5 text-[#222222] border-0 outline-none"
              style={{ fontFamily: "Inter, sans-serif" }}
            />
          </div>
        </div>
      </div>

      {/* Outstanding amount bar (follow-up style) */}
      <div
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-3 w-full rounded-[8px] px-4 sm:px-5 py-4 sm:py-0 min-h-[60px] sm:h-[60px]"
        style={{ background: "#222222" }}
      >
        <span
          className="text-[12px] sm:text-[14px] font-normal leading-5"
          style={{
            fontFamily: "Inter, sans-serif",
            color: "rgba(255, 255, 255, 0.9)",
          }}
        >
          OUTSTANDING AMOUNT FOR REPLY TO OBJECTION
        </span>
        <span
          className="text-[18px] sm:text-[22px] font-bold leading-8 sm:leading-10 text-white"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          {loading ? "…" : `Rs. ${outstandingFormatted}/-`}
        </span>
      </div>

    </form>
  );
}

function DocumentsObjectionPanel({
  leadId,
  hasRecorded,
  onRecorded,
  onAdvance,
  onSavingChange,
  onProgressActionChange,
}: {
  leadId: string;
  hasRecorded: boolean;
  onRecorded: () => void;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
  onProgressActionChange?: (run: (() => Promise<void>) | null) => void;
}) {
  const [recording, setRecording] = useState(false);

  // Register the Progress (skip) handler with the dashboard footer so the
  // Progress button shows up next to "Save and Continue" — matches the
  // Payment sub-step pattern. Documents collection is optional for the Reply,
  // so the user can skip when nothing needs collecting.
  useEffect(() => {
    if (!onProgressActionChange) return;
    if (!leadId) {
      onProgressActionChange(null);
      return;
    }
    const run = async () => {
      try {
        await skipStep(
          leadId,
          NewApplicationStep.S3_03b_viii_DocumentsFromClientReplyToObjection
        );
        toast.success("Step skipped successfully.");
        onAdvance();
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to skip step"
        );
      }
    };
    onProgressActionChange(run);
    return () => onProgressActionChange(null);
  }, [leadId, onAdvance, onProgressActionChange]);

  const handleMarkCollected = async () => {
    if (!leadId || recording || hasRecorded) return;
    setRecording(true);
    onSavingChange?.(true);
    try {
      await recordStatusOfObjectionDocumentsFromClient(leadId);
      toast.success("Documents from Client recorded");
      onRecorded();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to record documents"
      );
    } finally {
      setRecording(false);
      onSavingChange?.(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasRecorded) {
      onAdvance();
      return;
    }
    await handleMarkCollected();
  };

  return (
    <form
      id={EXAMINATION_REPORT_DOC_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-row justify-center items-center self-stretch"
      style={{
        fontFamily: "Inter, sans-serif",
        gap: "60px",
        minHeight: "336px",
      }}
    >
      <div
        className="flex flex-col justify-center items-center self-stretch flex-1"
        style={{ gap: "30px" }}
      >
        <img
          src={collectDocumentsGraphic}
          alt=""
          className="w-[220px] h-[220px] flex-shrink-0"
        />
        <p
          className="self-stretch text-center text-[#222222]"
          style={{
            fontSize: "22px",
            lineHeight: "20px",
            fontWeight: 400,
          }}
        >
          Collect any necessary documents from client for the Reply.
        </p>
        <button
          type="button"
          onClick={() => void handleMarkCollected()}
          disabled={recording || hasRecorded}
          className="flex flex-row justify-center items-center bg-white border border-[#222222] rounded-[6px] hover:bg-[#F7F7F7] disabled:opacity-60 disabled:cursor-not-allowed"
          style={{
            width: "137px",
            height: "36px",
            padding: "8px 16px",
            gap: "10px",
          }}
        >
          <span
            className="text-[#222222] text-center"
            style={{
              fontSize: "12px",
              lineHeight: "20px",
              fontWeight: 600,
            }}
          >
            {recording
              ? "Saving…"
              : hasRecorded
              ? "Collected"
              : "Mark as Collected"}
          </span>
        </button>
      </div>
    </form>
  );
}

function FilingReplyObjectionPanel({
  leadId,
  application,
  onSaved,
  onAdvance,
  onSavingChange,
}: {
  leadId: string;
  application: StatusOfObjectionApplication;
  onSaved: () => void;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
}) {
  const [filing, setFiling] = useState<ReplyToObjectionFiledApplication | null>(
    null
  );
  const [filingDate, setFilingDate] = useState<Date | null>(null);
  const [pendingName, setPendingName] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [marking, setMarking] = useState(false);
  const [preview, setPreview] = useState<
    { url: string; name: string; kind: FilePreviewKind } | null
  >(null);

  const loadFiling = async () => {
    if (!leadId) return;
    try {
      const data = await getReplyToObjectionFiledStatus(leadId, [
        application.tmNo,
      ]);
      const row = data.applications.find((a) => a.tmNo === application.tmNo);
      setFiling(row ?? null);
      // Initialize Filing Date from saved value (DD-MM-YYYY)
      const raw = row?.dateOfReplyToObjectionFiled;
      let parsed: Date | null = null;
      if (typeof raw === "string" && raw.length > 0) {
        const nums = raw.split("-").map((p) => Number(p.replace(/\D/g, "")));
        if (nums.length === 3 && nums.every((n) => Number.isFinite(n))) {
          const [a, b, c] = nums;
          const isYMD = String(raw.split("-")[0]).length === 4;
          const year = isYMD ? a : c;
          const month = b;
          const day = isYMD ? c : a;
          if (year >= 1900 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
            parsed = new Date(year, month - 1, day);
          }
        }
      }
      setFilingDate(parsed);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to load Filing Reply"
      );
    }
  };

  useEffect(() => {
    void loadFiling();
    setPendingName(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, application.tmNo]);

  const hasExistingFile = !!filing?.replyToObjectionFileKey;
  const displayName =
    pendingName ?? filing?.replyToObjectionFilename ?? null;
  const fileUrl = filing?.replyToObjectionFileUrl ?? null;

  const uploadFile = async (file: File) => {
    if (!leadId || uploading) return;
    setUploading(true);
    setPendingName(file.name);
    onSavingChange?.(true);
    try {
      const [presigned] = await generateUploadPresignedUrls(
        [{ filename: file.name, mimeType: file.type || "application/pdf" }],
        "private",
        REPLY_TO_OBJECTION_FILED_FOLDER
      );
      const putRes = await fetch(presigned.url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "application/pdf" },
      });
      if (!putRes.ok) throw new Error("Upload failed");

      const updated = await saveReplyToObjectionFiled(leadId, {
        applications: [
          {
            tmNo: application.tmNo,
            dateOfReplyToObjectionFiled: toIsoDate(filingDate) ?? null,
            replyToObjectionFileKey: presigned.key,
            replyToObjectionFilename: file.name,
          },
        ],
      });
      const row = updated.applications.find(
        (a) => a.tmNo === application.tmNo
      );
      setFiling(row ?? null);
      setPendingName(null);
      toast.success("Reply PDF uploaded");
    } catch (err) {
      setPendingName(null);
      toast.error(
        err instanceof Error ? err.message : "Failed to upload Reply PDF"
      );
    } finally {
      setUploading(false);
      onSavingChange?.(false);
    }
  };

  const openPreview = () => {
    if (!fileUrl) {
      toast.error("Reply PDF not available yet");
      return;
    }
    const name = filing?.replyToObjectionFilename ?? "Reply.pdf";
    setPreview({ url: fileUrl, name, kind: detectPreviewKind(name) });
  };

  const persistDateAndAdvance = async () => {
    if (!leadId || marking) return;
    if (!filingDate) {
      toast.error("Filing Date is required");
      return;
    }
    if (!hasExistingFile && !pendingName) {
      toast.error("Upload the Reply PDF before marking Filing Done");
      return;
    }
    setMarking(true);
    onSavingChange?.(true);
    try {
      // Include current file key/filename so the backend doesn't reset them
      // (it rebuilds the row from DTO; omitted fields are persisted as null).
      await saveReplyToObjectionFiled(leadId, {
        applications: [
          {
            tmNo: application.tmNo,
            dateOfReplyToObjectionFiled: toIsoDate(filingDate) ?? null,
            replyToObjectionFileKey: filing?.replyToObjectionFileKey ?? null,
            replyToObjectionFilename:
              filing?.replyToObjectionFilename ?? null,
          },
        ],
      });
      toast.success("Filing Reply saved");
      onSaved();
      onAdvance();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save Filing Reply"
      );
    } finally {
      setMarking(false);
      onSavingChange?.(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void persistDateAndAdvance();
  };

  const dateInputCls =
    "w-full h-9 px-2.5 py-2 text-xs border border-[#EEEEEE] rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-[#222] text-left";

  return (
    <form
      id={EXAMINATION_REPORT_FRO_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-5 self-stretch pt-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Filing Date row (left half; right half hidden per Figma) */}
      <div className="flex flex-row items-center gap-5 self-stretch">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className="text-xs leading-5 text-[#222222]">
            Filing Date
          </label>
          <DateTimePicker
            value={filingDate}
            onChange={setFilingDate}
            placeholder="Select Date"
            buttonClassName={dateInputCls}
          />
        </div>
        <div className="flex-1 min-w-0" aria-hidden />
      </div>

      {/* File Upload card (standard dropzone) */}
      <FileUploadDropzone
        inputId={`reply-to-objection-pdf-${application.tmNo}`}
        hasFile={!!displayName}
        fileLabel={displayName}
        isDragging={dragging}
        uploading={uploading}
        onDropFile={(file) => void uploadFile(file)}
        onPickFile={(file) => void uploadFile(file)}
        onRemove={() => setPendingName(null)}
        onDragEnter={() => setDragging(true)}
        onDragLeave={() => setDragging(false)}
      />

      {hasExistingFile && fileUrl ? (
        <div className="flex flex-row items-center gap-3">
          <button
            type="button"
            onClick={openPreview}
            className="inline-flex items-center justify-center h-9 px-3 text-xs font-medium text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[8px] hover:bg-gray-50"
          >
            Preview
          </button>
        </div>
      ) : null}

      <FilePreviewModal
        open={!!preview}
        onClose={() => setPreview(null)}
        name={preview?.name ?? ""}
        url={preview?.url}
        kind={preview?.kind}
      />
    </form>
  );
}

function InformClientReplyObjectionPanel({
  leadId,
  phone,
  hasSent,
  onSent,
  onAdvance,
  onSavingChange,
  resending,
  onResend,
}: {
  leadId: string;
  phone: string;
  hasSent: boolean;
  onSent: () => void;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
  resending: boolean;
  onResend: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      setMessage(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getReplyToObjectionFiledInformClientPreview(leadId)
      .then(({ message: m }) => {
        if (!cancelled) setMessage(m);
      })
      .catch((err) => {
        if (!cancelled) {
          setMessage(null);
          toast.error(
            err instanceof Error ? err.message : "Failed to load preview"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSent) {
      onAdvance();
      return;
    }
    if (!message) {
      toast.error("No message available to send");
      return;
    }
    onSavingChange?.(true);
    try {
      await sendReplyToObjectionFiledInformClientWhatsApp(leadId);
      toast.success("Inform Client about Reply to Objection sent to client");
      onSent();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to send message"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  return (
    <form
      id={EXAMINATION_REPORT_ICR_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-5 self-stretch pt-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <span
        className="text-[#222222]"
        style={{ fontSize: "16px", lineHeight: "20px", fontWeight: 400 }}
      >
        Message Preview
      </span>

      <div
        className="self-stretch bg-white border border-[#EEEEEE] rounded-[6px]"
        style={{ padding: "14px" }}
      >
        {loading ? (
          <span
            className="text-[#6D6D6D]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            Loading preview…
          </span>
        ) : message ? (
          <p
            className="text-[#222222] whitespace-pre-line"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            {renderWhatsAppFormatting(message)}
          </p>
        ) : (
          <span
            className="text-[#6D6D6D]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            No preview available
          </span>
        )}
      </div>

      {/* Phone chip */}
      <div
        className="flex flex-row items-center bg-[#FCFCFC] border border-[#EEEEEE] rounded-[6px]"
        style={{
          width: "345px",
          maxWidth: "100%",
          height: "44px",
          padding: "4px 8px 4px 16px",
          gap: "10px",
        }}
      >
        <span
          className="flex-1 min-w-0 truncate text-[#222222]"
          style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 500 }}
        >
          {phone || "(No phone number)"}
        </span>
      </div>

      {hasSent && (
        <div className="flex flex-row items-center gap-3">
          <button
            type="button"
            disabled={resending}
            onClick={onResend}
            className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {resending ? "Resending…" : "Resend to WhatsApp"}
          </button>
        </div>
      )}
    </form>
  );
}

function StatusOfAaoPanel({
  leadId,
  application,
  onSaved,
  onAdvance,
}: {
  leadId: string;
  application: AcceptedAfterObjectionApplication;
  onSaved: (next: AcceptedAfterObjectionApplication) => void;
  onAdvance: () => void;
}) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingName, setPendingName] = useState<string | null>(null);
  const [preview, setPreview] = useState<
    { url: string; name: string; kind: FilePreviewKind } | null
  >(null);

  const hasExistingFile = !!application.examinationReportAcceptanceFileKey;
  const displayName =
    pendingName ?? application.examinationReportAcceptanceFilename ?? null;
  const fileUrl = application.examinationReportAcceptanceFileUrl ?? null;

  const openExistingPreview = () => {
    if (!fileUrl) {
      toast.error("Examination Report PDF not available yet");
      return;
    }
    const name =
      application.examinationReportAcceptanceFilename ??
      "Examination Report (after Objection)";
    setPreview({ url: fileUrl, name, kind: detectPreviewKind(name) });
  };

  const uploadFile = async (file: File) => {
    if (!leadId || uploading) return;
    setUploading(true);
    setPendingName(file.name);
    try {
      const [presigned] = await generateUploadPresignedUrls(
        [{ filename: file.name, mimeType: file.type || "application/pdf" }],
        "private",
        EXAMINATION_REPORT_ACCEPTANCE_AFTER_OBJECTION_FOLDER
      );
      const putRes = await fetch(presigned.url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "application/pdf" },
      });
      if (!putRes.ok) throw new Error("Upload failed");

      const updated = await saveAcceptedAfterObjection(leadId, {
        applications: [
          {
            tmNo: application.tmNo,
            examinationReportAcceptanceFileKey: presigned.key,
            examinationReportAcceptanceFilename: file.name,
          },
        ],
      });
      const next =
        updated.applications.find((a) => a.tmNo === application.tmNo) ??
        application;
      onSaved(next);
      setPendingName(null);
      toast.success("Examination Report (after Objection) uploaded");
    } catch (err) {
      setPendingName(null);
      toast.error(
        err instanceof Error ? err.message : "Failed to upload Examination Report"
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <form
      id={EXAMINATION_REPORT_AAO_STATUS_FORM_ID}
      onSubmit={(e) => {
        e.preventDefault();
        if (uploading) {
          toast.error("Please wait for the upload to finish");
          return;
        }
        if (!hasExistingFile && !pendingName) {
          toast.error("Upload the Examination Report PDF first");
          return;
        }
        onAdvance();
      }}
      className="flex flex-col gap-4 self-stretch"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <FileUploadDropzone
        inputId={`accepted-after-objection-pdf-${application.tmNo}`}
        hasFile={!!displayName}
        fileLabel={displayName}
        isDragging={dragging}
        uploading={uploading}
        onDropFile={(file) => void uploadFile(file)}
        onPickFile={(file) => void uploadFile(file)}
        onRemove={() => setPendingName(null)}
        onDragEnter={() => setDragging(true)}
        onDragLeave={() => setDragging(false)}
      />

      {hasExistingFile && !uploading && (
        <div className="flex items-center gap-3">
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="9" cy="9" r="9" fill="#22C55E" />
            <path
              d="M5 9L8 12L13 6"
              stroke="white"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="text-sm font-medium text-[#16A34A]">Uploaded</span>
          {fileUrl ? (
            <button
              type="button"
              onClick={openExistingPreview}
              className="ml-1 inline-flex items-center justify-center h-8 px-3 text-xs font-medium text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[8px] hover:bg-gray-50"
            >
              Preview
            </button>
          ) : null}
        </div>
      )}

      <FilePreviewModal
        open={!!preview}
        onClose={() => setPreview(null)}
        name={preview?.name ?? ""}
        url={preview?.url || undefined}
        kind={preview?.kind}
      />
    </form>
  );
}

function NoticeToClientAaoPanel({
  leadId,
  applications,
  hasSent,
  onSent,
  onAdvance,
  onSavingChange,
  resending,
  onResend,
}: {
  leadId: string;
  applications: AcceptedAfterObjectionApplication[];
  hasSent: boolean;
  onSent: () => void;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
  resending: boolean;
  onResend: () => void;
}) {
  const [preview, setPreview] = useState<
    { url: string; name: string; kind: FilePreviewKind } | null
  >(null);

  const rows = applications
    .filter(
      (a) =>
        a.examinationReportAcceptanceFileKey ||
        a.examinationReportAcceptanceFilename
    )
    .map((a, idx) => ({
      tmNo: a.tmNo,
      name:
        a.examinationReportAcceptanceFilename ??
        (a.mark ? a.mark : `Mark ${String(idx + 1).padStart(2, "0")}`),
      url: a.examinationReportAcceptanceFileUrl ?? null,
    }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSent) {
      onAdvance();
      return;
    }
    if (!rows.length) {
      toast.error(
        "Upload Examination Report PDF in 'Status of Accepted after Objection' for at least one application first"
      );
      return;
    }
    onSavingChange?.(true);
    try {
      await sendAcceptedAfterObjectionPdfToWhatsApp(leadId);
      toast.success("Examination Report (after Objection) sent to client");
      onSent();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to send PDF to client"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const openPreview = (row: { url: string | null; name: string }) => {
    if (!row.url) {
      toast.error("Examination Report PDF not available yet");
      return;
    }
    setPreview({
      url: row.url,
      name: row.name,
      kind: detectPreviewKind(row.name),
    });
  };

  return (
    <form
      id={EXAMINATION_REPORT_AAO_NOTICE_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-2.5 self-stretch py-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {rows.length === 0 ? (
        <p className="text-xs leading-5 text-[#6D6D6D]">
          No Examination Report PDFs uploaded yet. Upload them in "Status of
          Accepted after Objection" first.
        </p>
      ) : (
        rows.map((row) => (
          <div
            key={row.tmNo}
            className="flex flex-row items-center gap-2.5 self-stretch bg-[#FCFCFC] border border-[#EEEEEE] rounded-[6px]"
            style={{ height: "44px", padding: "4px 8px 4px 16px" }}
          >
            <img
              src={pdfIcon}
              alt=""
              className="w-[22px] h-[22px] flex-shrink-0"
            />
            <span
              className="flex-1 min-w-0 truncate text-[#222222]"
              style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 500 }}
            >
              {row.name}
            </span>
            <button
              type="button"
              onClick={() => openPreview(row)}
              className="flex items-center justify-center h-9 px-4 rounded-[6px] bg-[#222222] hover:bg-[#333] text-white text-xs font-medium"
              style={{ width: "78px" }}
            >
              Preview
            </button>
          </div>
        ))
      )}

      {hasSent && (
        <div className="flex flex-row items-center gap-3 pt-2">
          <button
            type="button"
            disabled={resending}
            onClick={onResend}
            className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {resending ? "Resending…" : "Resend to WhatsApp"}
          </button>
        </div>
      )}

      <FilePreviewModal
        open={!!preview}
        onClose={() => setPreview(null)}
        name={preview?.name ?? ""}
        url={preview?.url}
        kind={preview?.kind}
      />
    </form>
  );
}

function InformClientAaoPanel({
  leadId,
  hasSent,
  onSent,
  onAdvance,
  onSavingChange,
  resending,
  onResend,
}: {
  leadId: string;
  hasSent: boolean;
  onSent: () => void;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
  resending: boolean;
  onResend: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState<string[]>([]);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      setMessages([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getAcceptedAfterObjectionInformClientPreview(leadId)
      .then(({ messages: m }) => {
        if (!cancelled) setMessages(m);
      })
      .catch((err) => {
        if (!cancelled) {
          setMessages([]);
          toast.error(
            err instanceof Error ? err.message : "Failed to load preview"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSent) {
      onAdvance();
      return;
    }
    if (!messages.length) {
      toast.error("No message available to send");
      return;
    }
    onSavingChange?.(true);
    try {
      await sendAcceptedAfterObjectionInformClientWhatsApp(leadId);
      toast.success("Inform Client (after Objection) sent to client");
      onSent();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to send message"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const combined = messages.join("\n\n---\n\n");

  return (
    <form
      id={EXAMINATION_REPORT_AAO_INFORM_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-5 self-stretch"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <span
        className="text-[#222222]"
        style={{ fontSize: "16px", lineHeight: "20px", fontWeight: 400 }}
      >
        Message Preview
      </span>

      <div
        className="self-stretch bg-white border border-[#EEEEEE] rounded-[6px]"
        style={{ padding: "14px" }}
      >
        {loading ? (
          <span
            className="text-[#6D6D6D]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            Loading preview…
          </span>
        ) : combined ? (
          <p
            className="text-[#222222] whitespace-pre-line"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            {renderWhatsAppFormatting(combined)}
          </p>
        ) : (
          <span
            className="text-[#6D6D6D]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            No preview available
          </span>
        )}
      </div>

      {hasSent && (
        <div className="flex flex-row items-center gap-3">
          <button
            type="button"
            disabled={resending}
            onClick={onResend}
            className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {resending ? "Resending…" : "Resend to WhatsApp"}
          </button>
        </div>
      )}
    </form>
  );
}

function AcceptedAfterObjectionBranch({
  leadId,
  active,
  onSavingChange,
  onAaoStatusFooterFormModeChange,
  onAaoNoticeFooterSendModeChange,
  onAaoNoticeFooterSaveModeChange,
  onAaoInformFooterSendModeChange,
  onAaoInformFooterSaveModeChange,
}: {
  leadId: string;
  active: boolean;
  onSavingChange?: (saving: boolean) => void;
  onAaoStatusFooterFormModeChange?: (needsSubmit: boolean) => void;
  onAaoNoticeFooterSendModeChange?: (needsSend: boolean) => void;
  onAaoNoticeFooterSaveModeChange?: (needsSave: boolean) => void;
  onAaoInformFooterSendModeChange?: (needsSend: boolean) => void;
  onAaoInformFooterSaveModeChange?: (needsSave: boolean) => void;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTmNo = searchParams.get("tm");
  const urlSubStep = searchParams.get("substep") as AaoSubStep | null;
  const validAaoSubSteps: AaoSubStep[] = useMemo(
    () => AAO_SUB_STEPS.map((s) => s.id),
    []
  );

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<AcceptedAfterObjectionStatus | null>(
    null
  );
  const [selectedTmIdx, setSelectedTmIdxState] = useState(0);
  const [subStep, setSubStepState] = useState<AaoSubStep>(
    urlSubStep && validAaoSubSteps.includes(urlSubStep)
      ? urlSubStep
      : "status-of-aao"
  );
  const [hasSentAaoNotice, setHasSentAaoNotice] = useState(false);
  const [resendingAaoNotice, setResendingAaoNotice] = useState(false);
  const [hasSentAaoInform, setHasSentAaoInform] = useState(false);
  const [resendingAaoInform, setResendingAaoInform] = useState(false);

  // Read AAO Notice + Inform step completion from workflow
  useEffect(() => {
    if (!leadId) {
      setHasSentAaoNotice(false);
      setHasSentAaoInform(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const wf = await getWorkflowStatus(leadId);
        if (cancelled) return;
        const noticeStep = wf.steps.find(
          (s) => s.slug === NOTICE_TO_CLIENT_AAO_STEP_SLUG
        );
        const informStep = wf.steps.find(
          (s) => s.slug === INFORM_CLIENT_AAO_STEP_SLUG
        );
        setHasSentAaoNotice(Boolean(noticeStep?.isCompleted));
        setHasSentAaoInform(Boolean(informStep?.isCompleted));
      } catch {
        if (!cancelled) {
          setHasSentAaoNotice(false);
          setHasSentAaoInform(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const writeUrl = (patch: { tm?: string | null; substep?: string | null }) => {
    const params = new URLSearchParams(searchParams);
    if (patch.tm !== undefined) {
      if (patch.tm) params.set("tm", patch.tm);
      else params.delete("tm");
    }
    if (patch.substep !== undefined) {
      if (patch.substep) params.set("substep", patch.substep);
      else params.delete("substep");
    }
    setSearchParams(params, { replace: true });
  };

  const setSelectedTmIdx = (idx: number) => {
    setSelectedTmIdxState(idx);
    const apps = status?.applications ?? [];
    const tm = apps[idx]?.tmNo ?? null;
    writeUrl({ tm });
  };
  const setSubStep = (next: AaoSubStep) => {
    setSubStepState(next);
    writeUrl({ substep: next });
  };

  // Sliding left-edge indicator: tracks the active sub-step button's vertical
  // position and animates between rows. Also scrolls active row into view when
  // it changes (URL restore or programmatic advance after Send/Save).
  const activeSubStepBtnRef = useRef<HTMLButtonElement | null>(null);
  const [subStepIndicator, setSubStepIndicator] = useState({ top: 0, height: 0 });
  useLayoutEffect(() => {
    const btn = activeSubStepBtnRef.current;
    if (!btn) return;
    setSubStepIndicator({ top: btn.offsetTop, height: btn.offsetHeight });
    btn.scrollIntoView({ block: "nearest" });
  }, [subStep]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getAcceptedAfterObjectionStatus(leadId)
      .then((data) => {
        if (cancelled) return;
        setStatus(data);
        const idx = urlTmNo
          ? data.applications.findIndex((a) => a.tmNo === urlTmNo)
          : -1;
        setSelectedTmIdxState(idx >= 0 ? idx : 0);
      })
      .catch((err) => {
        if (!cancelled)
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Accepted after Objection"
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const applications = useMemo(
    () => status?.applications ?? [],
    [status]
  );
  const selected =
    applications[Math.min(selectedTmIdx, applications.length - 1)] ?? null;

  const hasAnyAaoPdf = useMemo(
    () =>
      applications.some(
        (a) =>
          a.examinationReportAcceptanceFileKey ||
          a.examinationReportAcceptanceFilename
      ),
    [applications]
  );

  // Footer signaling — AAO Status (Save & Continue advances to Notice)
  useLayoutEffect(() => {
    const needsSubmit = active && subStep === "status-of-aao";
    onAaoStatusFooterFormModeChange?.(needsSubmit);
  }, [active, subStep, onAaoStatusFooterFormModeChange]);

  // Footer signaling — AAO Notice to Client (Send & Continue when not sent)
  useLayoutEffect(() => {
    const needsSend =
      active &&
      subStep === "notice-to-client" &&
      !hasSentAaoNotice &&
      hasAnyAaoPdf;
    onAaoNoticeFooterSendModeChange?.(needsSend);
  }, [
    active,
    subStep,
    hasSentAaoNotice,
    hasAnyAaoPdf,
    onAaoNoticeFooterSendModeChange,
  ]);

  // Footer signaling — AAO Notice to Client (Save & Continue when already sent)
  useLayoutEffect(() => {
    const needsSave =
      active && subStep === "notice-to-client" && hasSentAaoNotice;
    onAaoNoticeFooterSaveModeChange?.(needsSave);
  }, [active, subStep, hasSentAaoNotice, onAaoNoticeFooterSaveModeChange]);

  // Footer signaling — AAO Inform Client (Send & Continue when not sent)
  useLayoutEffect(() => {
    const needsSend =
      active && subStep === "inform-client" && !hasSentAaoInform && !!leadId;
    onAaoInformFooterSendModeChange?.(needsSend);
  }, [active, subStep, hasSentAaoInform, leadId, onAaoInformFooterSendModeChange]);

  // Footer signaling — AAO Inform Client (Save & Continue when already sent)
  useLayoutEffect(() => {
    const needsSave =
      active && subStep === "inform-client" && hasSentAaoInform;
    onAaoInformFooterSaveModeChange?.(needsSave);
  }, [active, subStep, hasSentAaoInform, onAaoInformFooterSaveModeChange]);

  const handleAaoNoticeResend = async () => {
    if (!leadId || resendingAaoNotice) return;
    setResendingAaoNotice(true);
    onSavingChange?.(true);
    try {
      await sendAcceptedAfterObjectionPdfToWhatsApp(leadId);
      toast.success("Examination Report (after Objection) resent to client");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to resend");
    } finally {
      setResendingAaoNotice(false);
      onSavingChange?.(false);
    }
  };

  const handleAaoInformResend = async () => {
    if (!leadId || resendingAaoInform) return;
    setResendingAaoInform(true);
    onSavingChange?.(true);
    try {
      await sendAcceptedAfterObjectionInformClientWhatsApp(leadId);
      toast.success("Inform Client (after Objection) resent to client");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to resend");
    } finally {
      setResendingAaoInform(false);
      onSavingChange?.(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-[#6D6D6D]">Loading…</p>;
  }

  if (applications.length === 0 || !selected) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        No applications with status "Examination Report – Accepted after
        Objection" in this acknowledgement yet.
      </p>
    );
  }

  const updateSelected = (next: AcceptedAfterObjectionApplication) => {
    setStatus((prev) =>
      prev
        ? {
            ...prev,
            applications: prev.applications.map((a) =>
              a.tmNo === next.tmNo ? next : a
            ),
          }
        : prev
    );
  };

  return (
    <div className="flex flex-col gap-5">
      {/* TM selector */}
      <div className="flex flex-row flex-wrap gap-3">
        {applications.map((app, idx) => {
          const isActive = selected.tmNo === app.tmNo;
          return (
            <button
              key={app.tmNo}
              type="button"
              onClick={() => setSelectedTmIdx(idx)}
              className={`flex items-center gap-2.5 px-2.5 py-2 rounded-[6px] h-10 min-w-[140px] border transition-colors ${
                isActive
                  ? "bg-[#222222] text-white border-transparent"
                  : "bg-[#FCFCFC] text-[#6D6D6D] border-[#EEEEEE]"
              }`}
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: "12px",
                fontWeight: isActive ? 500 : 400,
                lineHeight: "20px",
              }}
            >
              <RadioCircle active={isActive} />
              {formatTmNoForDisplay(app.tmNo)}
            </button>
          );
        })}
      </div>

      {/* Two-column sub-step panel */}
      <div className="border border-[#EEEEEE] rounded-xl overflow-hidden flex flex-col md:flex-row min-h-0">
        {/* Left: sub-steps */}
        <div className="relative w-full md:w-[235px] md:flex-shrink-0 border-b md:border-b-0 md:border-r border-[#EEEEEE] p-2 flex flex-col gap-1 overflow-y-auto">
          <div
            aria-hidden
            className="pointer-events-none absolute left-0 w-[3px] bg-[#222222] rounded-r-md transition-all duration-200 ease-out"
            style={{
              top: subStepIndicator.top,
              height: subStepIndicator.height,
              opacity: subStepIndicator.height > 0 ? 1 : 0,
            }}
          />
          {AAO_SUB_STEPS.map((step) => {
            const isActive = subStep === step.id;
            return (
              <button
                key={step.id}
                ref={isActive ? activeSubStepBtnRef : null}
                type="button"
                onClick={() => setSubStep(step.id)}
                className={`w-full flex items-center justify-between px-2.5 py-2.5 gap-2.5 rounded-[6px] text-xs transition-colors ${
                  isActive
                    ? "bg-[#222222] text-white font-medium"
                    : "bg-[#F7F7F7] text-[#222222] font-normal hover:bg-[#EEEEEE]"
                }`}
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                <span className="text-left line-clamp-1" title={step.label}>{step.label}</span>
                <ChevronRight active={isActive} />
              </button>
            );
          })}
        </div>

        {/* Right: detail panel */}
        <div className="flex-1 min-w-0 px-5 pt-5 pb-5 overflow-y-auto">
          {subStep === "status-of-aao" ? (
            <StatusOfAaoPanel
              leadId={leadId}
              application={selected}
              onSaved={updateSelected}
              onAdvance={() => setSubStep("notice-to-client")}
            />
          ) : subStep === "notice-to-client" ? (
            <NoticeToClientAaoPanel
              leadId={leadId}
              applications={applications}
              hasSent={hasSentAaoNotice}
              onSent={() => {
                setHasSentAaoNotice(true);
                setSubStep("inform-client");
              }}
              onAdvance={() => setSubStep("inform-client")}
              onSavingChange={onSavingChange}
              resending={resendingAaoNotice}
              onResend={() => void handleAaoNoticeResend()}
            />
          ) : subStep === "inform-client" ? (
            <InformClientAaoPanel
              leadId={leadId}
              hasSent={hasSentAaoInform}
              onSent={() => setHasSentAaoInform(true)}
              onAdvance={() => {
                /* last AAO sub-step */
              }}
              onSavingChange={onSavingChange}
              resending={resendingAaoInform}
              onResend={() => void handleAaoInformResend()}
            />
          ) : (
            <ComingSoonPanel
              label={
                AAO_SUB_STEPS.find((s) => s.id === subStep)?.label ??
                "Coming soon"
              }
            />
          )}
        </div>
      </div>

      {/* onSavingChange is wired through StatusOfAaoPanel via its own internal upload state. */}
      <span className="hidden" data-saving={onSavingChange ? "1" : "0"} />
    </div>
  );
}

function ObjectedBranch({
  leadId,
  phone,
  active,
  onSavingChange,
  onAdvanceToRefusalAbandonment,
  onSooFooterFormModeChange,
  onNtcoFooterSendModeChange,
  onNtcoFooterSaveModeChange,
  onIncoFooterSendModeChange,
  onIncoFooterSaveModeChange,
  onCcroFooterFormModeChange,
  onCcoFooterFormModeChange,
  onInvFooterFormModeChange,
  onPayFooterFormModeChange,
  onPayProgressActionChange,
  onDocFooterFormModeChange,
  onDocProgressActionChange,
  onFroFooterFormModeChange,
  onIcrFooterSendModeChange,
  onIcrFooterSaveModeChange,
}: {
  leadId: string;
  phone: string;
  active: boolean;
  onSavingChange?: (saving: boolean) => void;
  /** 3B(v) NO Abandon Application → 5B(i) Status details for Refusal/Abandonment (item 78). */
  onAdvanceToRefusalAbandonment?: () => void;
  onSooFooterFormModeChange?: (needsSubmit: boolean) => void;
  onNtcoFooterSendModeChange?: (needsSend: boolean) => void;
  onNtcoFooterSaveModeChange?: (needsSave: boolean) => void;
  onIncoFooterSendModeChange?: (needsSend: boolean) => void;
  onIncoFooterSaveModeChange?: (needsSave: boolean) => void;
  onCcroFooterFormModeChange?: (needsSubmit: boolean) => void;
  onCcoFooterFormModeChange?: (needsSubmit: boolean) => void;
  onInvFooterFormModeChange?: (needsSubmit: boolean) => void;
  onPayFooterFormModeChange?: (needsSubmit: boolean) => void;
  onPayProgressActionChange?: (run: (() => Promise<void>) | null) => void;
  onDocFooterFormModeChange?: (needsSubmit: boolean) => void;
  onDocProgressActionChange?: (run: (() => Promise<void>) | null) => void;
  onFroFooterFormModeChange?: (needsSubmit: boolean) => void;
  onIcrFooterSendModeChange?: (needsSend: boolean) => void;
  onIcrFooterSaveModeChange?: (needsSave: boolean) => void;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTmNo = searchParams.get("tm");
  const urlSubStep = searchParams.get("substep") as ObjectedSubStep | null;
  const validObjectedSubSteps: ObjectedSubStep[] = useMemo(
    () => OBJECTED_SUB_STEPS.map((s) => s.id),
    []
  );

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<StatusOfObjectionStatus | null>(null);
  const [selectedTmIdx, setSelectedTmIdxState] = useState(0);
  const [subStep, setSubStepState] = useState<ObjectedSubStep>(
    urlSubStep && validObjectedSubSteps.includes(urlSubStep)
      ? urlSubStep
      : "status-of-objection"
  );
  const [hasSentNtco, setHasSentNtco] = useState(false);
  const [resendingNtco, setResendingNtco] = useState(false);
  const [hasSentInco, setHasSentInco] = useState(false);
  const [resendingInco, setResendingInco] = useState(false);
  const [hasRecordedCcro, setHasRecordedCcro] = useState(false);
  const [hasSavedCco, setHasSavedCco] = useState(false);
  const [ccoChoice, setCcoChoice] = useState<boolean | null>(null);
  const [hasRecordedDoc, setHasRecordedDoc] = useState(false);
  const [hasFiledReply, setHasFiledReply] = useState(false);
  const [hasSentIcr, setHasSentIcr] = useState(false);
  const [resendingIcr, setResendingIcr] = useState(false);

  const writeUrl = (patch: { tm?: string | null; substep?: string | null }) => {
    const params = new URLSearchParams(searchParams);
    if (patch.tm !== undefined) {
      if (patch.tm) params.set("tm", patch.tm);
      else params.delete("tm");
    }
    if (patch.substep !== undefined) {
      if (patch.substep) params.set("substep", patch.substep);
      else params.delete("substep");
    }
    setSearchParams(params, { replace: true });
  };

  const setSelectedTmIdx = (idx: number) => {
    setSelectedTmIdxState(idx);
    const apps = status?.applications ?? [];
    const tm = apps[idx]?.tmNo ?? null;
    writeUrl({ tm });
  };
  const setSubStep = (next: ObjectedSubStep) => {
    setSubStepState(next);
    writeUrl({ substep: next });
  };

  // Sliding left-edge indicator: tracks the active sub-step button's vertical
  // position and animates between rows. Also scrolls active row into view when
  // it changes (URL restore or programmatic advance after Send/Save).
  const activeSubStepBtnRef = useRef<HTMLButtonElement | null>(null);
  const [subStepIndicator, setSubStepIndicator] = useState({ top: 0, height: 0 });
  useLayoutEffect(() => {
    const btn = activeSubStepBtnRef.current;
    if (!btn) return;
    setSubStepIndicator({ top: btn.offsetTop, height: btn.offsetHeight });
    btn.scrollIntoView({ block: "nearest" });
  }, [subStep]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getStatusOfObjection(leadId)
      .then((data) => {
        if (cancelled) return;
        setStatus(data);
        // Restore TM from URL on first load (if present and valid)
        const idx = urlTmNo
          ? data.applications.findIndex((a) => a.tmNo === urlTmNo)
          : -1;
        setSelectedTmIdxState(idx >= 0 ? idx : 0);
      })
      .catch((err) => {
        if (!cancelled)
          toast.error(
            err instanceof Error ? err.message : "Failed to load Status of Objection"
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  // Read step completion from workflow status
  useEffect(() => {
    if (!leadId) {
      setHasSentNtco(false);
      setHasSentInco(false);
      setHasSavedCco(false);
      setHasRecordedDoc(false);
      setHasFiledReply(false);
      setHasSentIcr(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const wf = await getWorkflowStatus(leadId);
        if (cancelled) return;
        const ntcoStep = wf.steps.find(
          (s) => s.slug === NOTICE_TO_CLIENT_OBJECTION_STEP_SLUG
        );
        const incoStep = wf.steps.find(
          (s) => s.slug === INFORM_CLIENT_OBJECTION_STEP_SLUG
        );
        const ccroStep = wf.steps.find(
          (s) => s.slug === CALL_CLIENT_REPLY_TO_OBJECTION_STEP_SLUG
        );
        const ccoStep = wf.steps.find(
          (s) => s.slug === CONFIRMATION_FROM_CLIENT_OBJECTION_STEP_SLUG
        );
        const docStep = wf.steps.find(
          (s) => s.slug === DOCUMENTS_FROM_CLIENT_OBJECTION_STEP_SLUG
        );
        const froStep = wf.steps.find(
          (s) => s.slug === FILING_OF_REPLY_TO_OBJECTION_STEP_SLUG
        );
        const icrStep = wf.steps.find(
          (s) => s.slug === INFORM_CLIENT_REPLY_TO_OBJECTION_STEP_SLUG
        );
        setHasSentNtco(Boolean(ntcoStep?.isCompleted));
        setHasSentInco(Boolean(incoStep?.isCompleted));
        setHasRecordedCcro(Boolean(ccroStep?.isCompleted));
        setHasSavedCco(Boolean(ccoStep?.isCompleted));
        setHasRecordedDoc(Boolean(docStep?.isCompleted));
        setHasFiledReply(Boolean(froStep?.isCompleted));
        setHasSentIcr(Boolean(icrStep?.isCompleted));
      } catch {
        if (!cancelled) {
          setHasSentNtco(false);
          setHasSentInco(false);
          setHasRecordedCcro(false);
          setHasSavedCco(false);
          setHasRecordedDoc(false);
          setHasFiledReply(false);
          setHasSentIcr(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const applications = useMemo(
    () => status?.applications ?? [],
    [status]
  );
  const selected =
    applications[Math.min(selectedTmIdx, applications.length - 1)] ?? null;

  // Load Confirmation choice for the selected TM
  useEffect(() => {
    if (!leadId || !selected) {
      setCcoChoice(null);
      return;
    }
    let cancelled = false;
    getStatusOfObjectionConfirmationFromClient(leadId, [selected.tmNo])
      .then((data) => {
        if (cancelled) return;
        const row = data.applications?.find(
          (a) => a.tmNo === selected.tmNo
        );
        setCcoChoice(row?.clientGoingAhead ?? null);
      })
      .catch(() => {
        if (!cancelled) setCcoChoice(null);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, selected]);
  const hasAnyExamReportPdf = useMemo(
    () =>
      applications.some(
        (a) => a.examinationReportFileKey || a.examinationReportFilename
      ),
    [applications]
  );

  // Footer signaling — Status of Objection sub-step
  useLayoutEffect(() => {
    const needsSubmit = active && subStep === "status-of-objection";
    onSooFooterFormModeChange?.(needsSubmit);
  }, [active, subStep, onSooFooterFormModeChange]);

  // Footer signaling — Notice to Client (Send & Continue when not yet sent)
  useLayoutEffect(() => {
    const needsSend =
      active && subStep === "notice-to-client" && !hasSentNtco && hasAnyExamReportPdf;
    onNtcoFooterSendModeChange?.(needsSend);
  }, [
    active,
    subStep,
    hasSentNtco,
    hasAnyExamReportPdf,
    onNtcoFooterSendModeChange,
  ]);

  // Footer signaling — Notice to Client (Save & Continue when already sent)
  useLayoutEffect(() => {
    const needsSave = active && subStep === "notice-to-client" && hasSentNtco;
    onNtcoFooterSaveModeChange?.(needsSave);
  }, [active, subStep, hasSentNtco, onNtcoFooterSaveModeChange]);

  // Footer signaling — Inform Client (Send & Continue when not yet sent)
  useLayoutEffect(() => {
    const needsSend =
      active && subStep === "inform-client" && !hasSentInco && !!leadId;
    onIncoFooterSendModeChange?.(needsSend);
  }, [active, subStep, hasSentInco, leadId, onIncoFooterSendModeChange]);

  // Footer signaling — Inform Client (Save & Continue when already sent)
  useLayoutEffect(() => {
    const needsSave = active && subStep === "inform-client" && hasSentInco;
    onIncoFooterSaveModeChange?.(needsSave);
  }, [active, subStep, hasSentInco, onIncoFooterSaveModeChange]);

  // Footer signaling — 3B(iv) Call Client (Save & Continue submits the form)
  useLayoutEffect(() => {
    const needsSubmit = active && subStep === "call-client" && !!leadId;
    onCcroFooterFormModeChange?.(needsSubmit);
  }, [active, subStep, leadId, onCcroFooterFormModeChange]);

  // Footer signaling — Client Confirmation (Save & Continue submits the form)
  useLayoutEffect(() => {
    const needsSubmit = active && subStep === "client-confirmation";
    onCcoFooterFormModeChange?.(needsSubmit);
  }, [active, subStep, onCcoFooterFormModeChange]);

  // Footer signaling — Invoice to Client (Save & Continue advances)
  useLayoutEffect(() => {
    const needsSubmit = active && subStep === "invoice-to-client";
    onInvFooterFormModeChange?.(needsSubmit);
  }, [active, subStep, onInvFooterFormModeChange]);

  // Footer signaling — Payment (Save & Continue submits the form / advances)
  useLayoutEffect(() => {
    const needsSubmit = active && subStep === "payment";
    onPayFooterFormModeChange?.(needsSubmit);
  }, [active, subStep, onPayFooterFormModeChange]);

  // Footer signaling — Documents (Save & Continue records / advances)
  useLayoutEffect(() => {
    const needsSubmit = active && subStep === "documents";
    onDocFooterFormModeChange?.(needsSubmit);
  }, [active, subStep, onDocFooterFormModeChange]);

  // Footer signaling — Filing Reply (Save & Continue saves date + advances)
  useLayoutEffect(() => {
    const needsSubmit = active && subStep === "filing-reply";
    onFroFooterFormModeChange?.(needsSubmit);
  }, [active, subStep, onFroFooterFormModeChange]);

  // Footer signaling — Inform Client Reply (Send & Continue when not yet sent)
  useLayoutEffect(() => {
    const needsSend =
      active && subStep === "inform-client-final" && !hasSentIcr && !!leadId;
    onIcrFooterSendModeChange?.(needsSend);
  }, [active, subStep, hasSentIcr, leadId, onIcrFooterSendModeChange]);

  // Footer signaling — Inform Client Reply (Save & Continue when already sent)
  useLayoutEffect(() => {
    const needsSave =
      active && subStep === "inform-client-final" && hasSentIcr;
    onIcrFooterSaveModeChange?.(needsSave);
  }, [active, subStep, hasSentIcr, onIcrFooterSaveModeChange]);

  const handleNtcoResend = async () => {
    if (!leadId || resendingNtco) return;
    setResendingNtco(true);
    onSavingChange?.(true);
    try {
      await sendStatusOfObjectionPdfToWhatsApp(leadId);
      toast.success("Examination Report (Objection) resent to client");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to resend");
    } finally {
      setResendingNtco(false);
      onSavingChange?.(false);
    }
  };

  const handleIncoResend = async () => {
    if (!leadId || resendingInco) return;
    setResendingInco(true);
    onSavingChange?.(true);
    try {
      await sendStatusOfObjectionInformClientToWhatsApp(leadId);
      toast.success("Inform Client about Objection resent to client");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to resend");
    } finally {
      setResendingInco(false);
      onSavingChange?.(false);
    }
  };

  const handleIcrResend = async () => {
    if (!leadId || resendingIcr) return;
    setResendingIcr(true);
    onSavingChange?.(true);
    try {
      await sendReplyToObjectionFiledInformClientWhatsApp(leadId);
      toast.success("Inform Client about Reply to Objection resent");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to resend");
    } finally {
      setResendingIcr(false);
      onSavingChange?.(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-[#6D6D6D]">Loading…</p>;
  }

  if (applications.length === 0 || !selected) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        No applications with status "Examination Report – Objected" in this
        acknowledgement yet.
      </p>
    );
  }

  const updateSelected = (next: StatusOfObjectionApplication) => {
    setStatus((prev) =>
      prev
        ? {
            ...prev,
            applications: prev.applications.map((a) =>
              a.tmNo === next.tmNo ? next : a
            ),
          }
        : prev
    );
  };

  return (
    <div className="flex flex-col gap-5">
      {/* TM selector */}
      <div className="flex flex-row flex-wrap gap-3">
        {applications.map((app, idx) => {
          const isActive = selected.tmNo === app.tmNo;
          return (
            <button
              key={app.tmNo}
              type="button"
              onClick={() => setSelectedTmIdx(idx)}
              className={`flex items-center gap-2.5 px-2.5 py-2 rounded-[6px] h-10 min-w-[140px] border transition-colors ${
                isActive
                  ? "bg-[#222222] text-white border-transparent"
                  : "bg-[#FCFCFC] text-[#6D6D6D] border-[#EEEEEE]"
              }`}
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: "12px",
                fontWeight: isActive ? 500 : 400,
                lineHeight: "20px",
              }}
            >
              <RadioCircle active={isActive} />
              {formatTmNoForDisplay(app.tmNo)}
            </button>
          );
        })}
      </div>

      {/* Two-column sub-step panel */}
      <div className="border border-[#EEEEEE] rounded-xl overflow-hidden flex flex-col md:flex-row min-h-0">
        {/* Left: sub-steps */}
        <div className="relative w-full md:w-[235px] md:flex-shrink-0 border-b md:border-b-0 md:border-r border-[#EEEEEE] p-2 flex flex-col gap-1 overflow-y-auto">
          <div
            aria-hidden
            className="pointer-events-none absolute left-0 w-[3px] bg-[#222222] rounded-r-md transition-all duration-200 ease-out"
            style={{
              top: subStepIndicator.top,
              height: subStepIndicator.height,
              opacity: subStepIndicator.height > 0 ? 1 : 0,
            }}
          />
          {OBJECTED_SUB_STEPS.map((step) => {
            const isActive = subStep === step.id;
            return (
              <button
                key={step.id}
                ref={isActive ? activeSubStepBtnRef : null}
                type="button"
                onClick={() => setSubStep(step.id)}
                className={`w-full flex items-center justify-between px-2.5 py-2.5 gap-2.5 rounded-[6px] text-xs transition-colors ${
                  isActive
                    ? "bg-[#222222] text-white font-medium"
                    : "bg-[#F7F7F7] text-[#222222] font-normal hover:bg-[#EEEEEE]"
                }`}
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                <span className="text-left line-clamp-1" title={step.label}>{step.label}</span>
                <ChevronRight active={isActive} />
              </button>
            );
          })}
        </div>

        {/* Right: detail panel */}
        <div className="flex-1 min-w-0 px-5 pt-5 pb-5 overflow-y-auto">
          {subStep === "status-of-objection" ? (
            <StatusOfObjectionPanel
              leadId={leadId}
              application={selected}
              onSaved={updateSelected}
              onAdvance={() => setSubStep("notice-to-client")}
              onSavingChange={onSavingChange}
            />
          ) : subStep === "notice-to-client" ? (
            <NoticeToClientObjectionPanel
              leadId={leadId}
              applications={applications}
              hasSent={hasSentNtco}
              onSent={() => {
                setHasSentNtco(true);
                setSubStep("inform-client");
              }}
              onAdvance={() => setSubStep("inform-client")}
              onSavingChange={onSavingChange}
              resending={resendingNtco}
              onResend={() => void handleNtcoResend()}
            />
          ) : subStep === "inform-client" ? (
            <InformClientObjectionPanel
              leadId={leadId}
              hasSent={hasSentInco}
              onSent={() => {
                setHasSentInco(true);
                setSubStep("call-client");
              }}
              onAdvance={() => setSubStep("call-client")}
              onSavingChange={onSavingChange}
              resending={resendingInco}
              onResend={() => void handleIncoResend()}
            />
          ) : subStep === "call-client" ? (
            <CallClientReplyToObjectionPanel
              leadId={leadId}
              tmNo={selected.tmNo}
              hasRecorded={hasRecordedCcro}
              onRecorded={() => setHasRecordedCcro(true)}
              onAdvance={() => setSubStep("client-confirmation")}
              onSavingChange={onSavingChange}
            />
          ) : subStep === "client-confirmation" ? (
            <ClientConfirmationPanel
              leadId={leadId}
              application={selected}
              status={status}
              hasSaved={hasSavedCco}
              initialChoice={ccoChoice}
              onSaved={(choice) => {
                setHasSavedCco(true);
                setCcoChoice(choice);
                // Review item 78 — NO abandons the application, so the reply
                // flow (invoice → payment → filing) does not apply. Route to
                // 5B(i), the same destination 3D(vii) NO and 4A(v) NO use.
                if (choice === false) {
                  if (onAdvanceToRefusalAbandonment) {
                    onAdvanceToRefusalAbandonment();
                  } else {
                    toast.info(
                      "Marked as NO. Continue in Final Order → Refused or Abandoned."
                    );
                  }
                  return;
                }
                setSubStep("invoice-to-client");
              }}
              onAdvance={() => {
                if (ccoChoice === false) {
                  if (onAdvanceToRefusalAbandonment) {
                    onAdvanceToRefusalAbandonment();
                  } else {
                    toast.info(
                      "Marked as NO. Continue in Final Order → Refused or Abandoned."
                    );
                  }
                  return;
                }
                setSubStep("invoice-to-client");
              }}
              onSavingChange={onSavingChange}
            />
          ) : subStep === "invoice-to-client" ? (
            <InvoiceToClientObjectionPanel
              leadId={leadId}
              application={selected}
              phone={phone}
              onAdvance={() => setSubStep("payment")}
              onSavingChange={onSavingChange}
            />
          ) : subStep === "payment" ? (
            <PaymentPanel
              leadId={leadId}
              application={selected}
              onAdvance={() => setSubStep("documents")}
              onSavingChange={onSavingChange}
              onProgressActionChange={onPayProgressActionChange}
            />
          ) : subStep === "documents" ? (
            <DocumentsObjectionPanel
              leadId={leadId}
              hasRecorded={hasRecordedDoc}
              onRecorded={() => {
                setHasRecordedDoc(true);
                setSubStep("filing-reply");
              }}
              onAdvance={() => setSubStep("filing-reply")}
              onSavingChange={onSavingChange}
              onProgressActionChange={onDocProgressActionChange}
            />
          ) : subStep === "filing-reply" ? (
            <FilingReplyObjectionPanel
              leadId={leadId}
              application={selected}
              onSaved={() => setHasFiledReply(true)}
              onAdvance={() => setSubStep("inform-client-final")}
              onSavingChange={onSavingChange}
            />
          ) : subStep === "inform-client-final" ? (
            <InformClientReplyObjectionPanel
              leadId={leadId}
              phone={phone}
              hasSent={hasSentIcr}
              onSent={() => setHasSentIcr(true)}
              onAdvance={() => {
                /* last sub-step in Objected branch — no further advance */
              }}
              onSavingChange={onSavingChange}
              resending={resendingIcr}
              onResend={() => void handleIcrResend()}
            />
          ) : (
            <ComingSoonPanel
              label={
                OBJECTED_SUB_STEPS.find((s) => s.id === subStep)?.label ??
                "Coming soon"
              }
            />
          )}
        </div>
      </div>

    </div>
  );
}

// ---------------------------------------------------------------------------
// Hearing after Objection — 3D — Inform Client (Showcause) panel + branch
// ---------------------------------------------------------------------------

function ReadOnlyTextField({ label, value, placeholder }: {
  label: string;
  value: string | null | undefined;
  placeholder?: string;
}) {
  const display = value && String(value).trim() ? String(value) : "";
  return (
    <div className="flex flex-col gap-1 flex-1 min-w-0">
      <label
        className="text-[#222222]"
        style={{
          fontFamily: "Inter, sans-serif",
          fontSize: "12px",
          lineHeight: "20px",
          fontWeight: 400,
        }}
      >
        {label}
      </label>
      <div
        className="box-border flex flex-row items-center px-2.5 py-2 h-9 bg-white border border-[#EEEEEE] rounded-[6px]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <span
          className={`truncate ${display ? "text-[#222222]" : "text-[#6D6D6D]"}`}
          style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
        >
          {display || placeholder || ""}
        </span>
      </div>
    </div>
  );
}

function FetchedDetailsPanel({ rows }: { rows: Array<{ label: string; value: string }> }) {
  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 gap-5 w-full"
      style={{ fontFamily: "Inter, sans-serif", fontFeatureSettings: "'ss01' on, 'cv01' on" }}
    >
      {rows.map(({ label, value }) => (
        <div key={label} className="flex flex-col gap-1 min-w-0">
          <label
            className="text-[#222222]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            {label}
          </label>
          <div
            className="box-border flex flex-row items-center min-h-9 px-2.5 py-2 bg-white border border-[#EEEEEE] rounded-[6px]"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            <span
              className={`break-words ${value ? "text-[#222222]" : "text-[#6D6D6D]"}`}
              style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
            >
              {value || "—"}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function InformClientShowcausePanel({
  leadId,
  status,
  selected,
  hasSent,
  onSent,
  onAdvance,
  onSavingChange,
  resending,
  onResend,
}: {
  leadId: string;
  status: ShowCauseHearingStatus;
  selected: ShowCauseHearingApplication;
  hasSent: boolean;
  onSent: () => void;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
  resending: boolean;
  onResend: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string>("");

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      setMessage("");
      return;
    }
    let cancelled = false;
    setLoading(true);
    getShowCauseHearingInformClientPreview(leadId, [selected.tmNo])
      .then(({ message: m }) => {
        if (!cancelled) setMessage(m);
      })
      .catch((err) => {
        if (!cancelled) {
          setMessage("");
          toast.error(
            err instanceof Error ? err.message : "Failed to load preview"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, selected.tmNo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSent) {
      onAdvance();
      return;
    }
    if (!message.trim()) {
      toast.error(
        'No message available to send. No applications with status "Examination Report - Hearing" on this acknowledgement.'
      );
      return;
    }
    if (!selected) {
      toast.error("No application selected");
      return;
    }
    onSavingChange?.(true);
    try {
      await sendShowCauseHearingInformClient(leadId, [selected.tmNo]);
      toast.success("Inform Client (Showcause) sent to client.");
      onSent();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send message");
    } finally {
      onSavingChange?.(false);
    }
  };

  return (
    <form
      id={EXAMINATION_REPORT_HAO_INFORM_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-5 self-stretch pt-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Top row: Acknowledgment No + Date of Filing */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <ReadOnlyTextField
          label="Acknowledgment No"
          value={status.acknowledgementNo}
          placeholder="Number"
        />
        <ReadOnlyTextField
          label="Date of Filing"
          value={status.dateOfFiling}
          placeholder="Select Date"
        />
      </div>

      {/* Mark / Class / Type of Mark — column headers + read-only inputs */}
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <span
            className="text-[#222222]"
            style={{ fontSize: "14px", lineHeight: "20px", fontWeight: 500 }}
          >
            Mark
          </span>
          <span
            className="text-[#222222] hidden md:inline-flex"
            style={{ fontSize: "14px", lineHeight: "20px", fontWeight: 500 }}
          >
            Selected Class
          </span>
          <span
            className="text-[#222222] hidden md:inline-flex"
            style={{ fontSize: "14px", lineHeight: "20px", fontWeight: 500 }}
          >
            Selected Type of Mark
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <ReadOnlyTextField
            label=""
            value={selected.mark}
            placeholder="Mark Name"
          />
          <ReadOnlyTextField
            label=""
            value={selected.classNumber ? String(selected.classNumber) : ""}
            placeholder="Class"
          />
          <ReadOnlyTextField
            label=""
            value={selected.typeOfMark}
            placeholder="Label"
          />
        </div>
      </div>

      {/* User Date row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <ReadOnlyTextField
          label="User Date"
          value={selected.userDate}
          placeholder="Select Date"
        />
        <div className="hidden md:block" aria-hidden="true" />
      </div>

      {/* Message Preview */}
      <div className="flex flex-col gap-5">
        <span
          className="text-[#222222]"
          style={{ fontSize: "16px", lineHeight: "20px", fontWeight: 400 }}
        >
          Message Preview
        </span>
        <div
          className="self-stretch bg-white border border-[#EEEEEE] rounded-[6px]"
          style={{ padding: "14px" }}
        >
          {loading ? (
            <span
              className="text-[#6D6D6D]"
              style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
            >
              Loading preview…
            </span>
          ) : message ? (
            <p
              className="text-[#222222] whitespace-pre-line"
              style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
            >
              {renderWhatsAppFormatting(message)}
            </p>
          ) : (
            <span
              className="text-[#6D6D6D]"
              style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
            >
              No preview available.
            </span>
          )}
        </div>
      </div>

      {hasSent && (
        <div className="flex flex-row items-center gap-3">
          <button
            type="button"
            disabled={resending}
            onClick={onResend}
            className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {resending ? "Resending…" : "Resend Message"}
          </button>
        </div>
      )}
    </form>
  );
}

function StatusDetailsOfHearingPanel({
  leadId,
  status,
  selected,
  onSaved,
  onAdvance,
  onSavingChange,
}: {
  leadId: string;
  status: ShowCauseHearingStatus;
  selected: ShowCauseHearingApplication;
  onSaved: (next: ShowCauseHearingStatus) => void;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
}) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [pendingName, setPendingName] = useState<string | null>(null);
  const [dateOfHearing, setDateOfHearing] = useState<Date | null>(
    parseDateForHearingInput(selected.dateOfHearing)
  );
  const [preview, setPreview] = useState<
    { url: string; name: string; kind: FilePreviewKind } | null
  >(null);

  // Re-sync local state when the selected TM changes (TM tab switch).
  useEffect(() => {
    setDateOfHearing(parseDateForHearingInput(selected.dateOfHearing));
    setPendingName(null);
  }, [selected.tmNo, selected.dateOfHearing]);

  // Auto-save date when it changes — keeps downstream Notice/Inform previews fresh.
  useEffect(() => {
    if (!leadId || !dateOfHearing) return;
    const newDate = formatDateDDMMYYYYForApi(dateOfHearing);
    if (newDate === selected.dateOfHearing) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const updated = await setShowCauseHearingStatusDetails(leadId, {
          applications: [
            {
              tmNo: selected.tmNo,
              dateOfHearing: newDate,
              hearingNoticeFileKey: selected.hearingNoticeFileKey ?? undefined,
              hearingNoticeFilename:
                selected.hearingNoticeFilename ?? undefined,
            },
          ],
        });
        if (!cancelled) onSaved(updated);
      } catch {
        /* silent — surface only on explicit submit */
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    leadId,
    selected.tmNo,
    dateOfHearing,
    selected.dateOfHearing,
    selected.hearingNoticeFileKey,
    selected.hearingNoticeFilename,
    onSaved,
  ]);

  const hasExistingFile = !!selected.hearingNoticeFileKey;
  const displayName = pendingName ?? selected.hearingNoticeFilename ?? null;
  const fileUrl = selected.hearingNoticeFileUrl ?? null;

  const openExistingPreview = () => {
    if (!fileUrl) {
      toast.error("Hearing Notice PDF not available yet");
      return;
    }
    const name = selected.hearingNoticeFilename ?? "Hearing Notice";
    setPreview({ url: fileUrl, name, kind: detectPreviewKind(name) });
  };

  const buildPayloadDate = () =>
    dateOfHearing ? formatDateDDMMYYYYForApi(dateOfHearing) : null;

  const uploadFile = async (file: File) => {
    if (!leadId || uploading) return;
    setUploading(true);
    setPendingName(file.name);
    try {
      const [presigned] = await generateUploadPresignedUrls(
        [{ filename: file.name, mimeType: file.type || "application/pdf" }],
        "private",
        HEARING_NOTICE_FOLDER
      );
      const putRes = await fetch(presigned.url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "application/pdf" },
      });
      if (!putRes.ok) throw new Error("Upload failed");

      const updated = await setShowCauseHearingStatusDetails(leadId, {
        applications: [
          {
            tmNo: selected.tmNo,
            dateOfHearing: buildPayloadDate(),
            hearingNoticeFileKey: presigned.key,
            hearingNoticeFilename: file.name,
          },
        ],
      });
      onSaved(updated);
      setPendingName(null);
      toast.success("Hearing Notice uploaded");
    } catch (err) {
      setPendingName(null);
      toast.error(
        err instanceof Error ? err.message : "Failed to upload Hearing Notice"
      );
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (uploading || removing) return;
    if (!hasExistingFile) {
      setPendingName(null);
      return;
    }
    if (!leadId) return;
    setRemoving(true);
    try {
      const updated = await setShowCauseHearingStatusDetails(leadId, {
        applications: [
          {
            tmNo: selected.tmNo,
            dateOfHearing: buildPayloadDate(),
            hearingNoticeFileKey: null,
            hearingNoticeFilename: null,
          },
        ],
      });
      onSaved(updated);
      setPendingName(null);
      toast.success("Hearing Notice removed");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to remove Hearing Notice"
      );
    } finally {
      setRemoving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (uploading) {
      toast.error("Please wait for the upload to finish");
      return;
    }
    if (!selected.hearingNoticeFileKey) {
      toast.error("Upload the Hearing Notice PDF first");
      return;
    }
    if (!dateOfHearing) {
      toast.error("Date of Hearing is required");
      return;
    }
    onSavingChange?.(true);
    try {
      const updated = await setShowCauseHearingStatusDetails(leadId, {
        applications: [
          {
            tmNo: selected.tmNo,
            dateOfHearing: buildPayloadDate(),
            hearingNoticeFileKey: selected.hearingNoticeFileKey,
            hearingNoticeFilename: selected.hearingNoticeFilename ?? undefined,
          },
        ],
      });
      onSaved(updated);
      toast.success("Status details of Hearing saved");
      onAdvance();
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save Status details of Hearing"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  return (
    <form
      id={EXAMINATION_REPORT_HAO_STATUS_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-5 self-stretch pt-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <FileUploadDropzone
        inputId={`status-details-hearing-pdf-${selected.tmNo}`}
        hasFile={!!displayName}
        fileLabel={displayName}
        isDragging={dragging}
        uploading={uploading || removing}
        onDropFile={(file) => void uploadFile(file)}
        onPickFile={(file) => void uploadFile(file)}
        onRemove={() => void handleRemove()}
        onDragEnter={() => setDragging(true)}
        onDragLeave={() => setDragging(false)}
      />

      {hasExistingFile && !uploading && (
        <div className="flex items-center gap-3">
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="9" cy="9" r="9" fill="#22C55E" />
            <path
              d="M5 9L8 12L13 6"
              stroke="white"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="text-sm font-medium text-[#16A34A]">Uploaded</span>
          {fileUrl ? (
            <>
              <button
                type="button"
                onClick={openExistingPreview}
                className="ml-1 inline-flex items-center justify-center h-8 px-3 text-xs font-medium text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[8px] hover:bg-gray-50"
              >
                Preview
              </button>
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center h-8 px-3 text-xs font-medium text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[8px] hover:bg-gray-50"
              >
                Download
              </a>
            </>
          ) : null}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="flex flex-col gap-1">
          <label
            className="text-[#222222]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            Acknowledgment No
          </label>
          <div
            className="box-border flex flex-row items-center px-2.5 py-2 h-9 bg-white border border-[#EEEEEE] rounded-[6px]"
          >
            <span
              className={`truncate ${
                status.acknowledgementNo ? "text-[#222222]" : "text-[#6D6D6D]"
              }`}
              style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
            >
              {status.acknowledgementNo || "Number"}
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label
            className="text-[#222222]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            Date of Hearing<span className="text-red-500 ml-0.5">*</span>
          </label>
          <DateTimePicker
            value={dateOfHearing}
            onChange={(d) => setDateOfHearing(d)}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="flex flex-col gap-1">
          <label
            className="text-[#222222]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            No. of applications for Hearing
          </label>
          <div
            className="box-border flex flex-row items-center px-2.5 py-2 h-9 bg-white border border-[#EEEEEE] rounded-[6px]"
          >
            <span
              className="truncate text-[#222222]"
              style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
            >
              {status.noOfApplicationsForHearing}
            </span>
          </div>
        </div>
        <div className="hidden md:block" aria-hidden="true" />
      </div>

      <FilePreviewModal
        open={!!preview}
        onClose={() => setPreview(null)}
        name={preview?.name ?? ""}
        url={preview?.url || undefined}
        kind={preview?.kind}
      />
    </form>
  );
}

function NoticeToClientForHearingPanel({
  leadId,
  applications,
  selected,
  hasSent,
  onSent,
  onAdvance,
  onSavingChange,
  resending,
  onResend,
}: {
  leadId: string;
  applications: ShowCauseHearingApplication[];
  selected: ShowCauseHearingApplication;
  hasSent: boolean;
  onSent: () => void;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
  resending: boolean;
  onResend: () => void;
}) {
  const [preview, setPreview] = useState<
    { url: string; name: string; kind: FilePreviewKind } | null
  >(null);

  const apps = applications.filter((a) => !!a.hearingNoticeFileKey);

  const openPreview = (app: ShowCauseHearingApplication) => {
    if (!app.hearingNoticeFileUrl) {
      toast.error("Hearing Notice PDF not available yet");
      return;
    }
    const name = app.hearingNoticeFilename ?? `Hearing Notice — ${app.tmNo}`;
    setPreview({
      url: app.hearingNoticeFileUrl,
      name,
      kind: detectPreviewKind(name),
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSent) {
      onAdvance();
      return;
    }
    if (!apps.length) {
      toast.error(
        "No Hearing Notice PDFs uploaded. Upload via Status details of Hearing first."
      );
      return;
    }
    onSavingChange?.(true);
    try {
      await sendShowCauseHearingNoticeToClient(leadId, [selected.tmNo]);
      toast.success("Hearing Notice sent to client");
      onSent();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send");
    } finally {
      onSavingChange?.(false);
    }
  };

  return (
    <form
      id={EXAMINATION_REPORT_HAO_NOTICE_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-2.5 self-stretch py-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {apps.length === 0 ? (
        <p className="text-sm text-[#6D6D6D]">
          No Hearing Notice PDFs uploaded yet. Upload them via Status details of
          Hearing.
        </p>
      ) : (
        apps.map((app, idx) => {
          const label =
            app.hearingNoticeFilename ?? `Mark ${String(idx + 1).padStart(2, "0")}`;
          return (
            <div
              key={app.tmNo}
              className="flex flex-row items-center gap-2.5 self-stretch bg-[#FCFCFC] border border-[#EEEEEE] rounded-[6px]"
              style={{ padding: "4px 8px 4px 16px", height: "44px" }}
            >
              <img src={pdfIcon} alt="" className="w-[22px] h-[22px] flex-shrink-0" />
              <span
                className="flex-1 min-w-0 truncate text-[#222222]"
                style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 500 }}
              >
                {label}
              </span>
              <button
                type="button"
                onClick={() => openPreview(app)}
                className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-white bg-[#222222] rounded-[6px] hover:bg-[#000000]"
              >
                Preview
              </button>
            </div>
          );
        })
      )}

      {hasSent && (
        <div className="flex flex-row items-center gap-3 pt-3">
          <button
            type="button"
            disabled={resending}
            onClick={onResend}
            className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {resending ? "Resending…" : "Resend Message"}
          </button>
        </div>
      )}

      <FilePreviewModal
        open={!!preview}
        onClose={() => setPreview(null)}
        name={preview?.name ?? ""}
        url={preview?.url || undefined}
        kind={preview?.kind}
      />
    </form>
  );
}

function InformClientAboutHearingPanel({
  leadId,
  selected,
  hasSent,
  onSent,
  onAdvance,
  onSavingChange,
  resending,
  onResend,
}: {
  leadId: string;
  selected: ShowCauseHearingApplication;
  hasSent: boolean;
  onSent: () => void;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
  resending: boolean;
  onResend: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string>("");

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      setMessage("");
      return;
    }
    let cancelled = false;
    setLoading(true);
    getShowCauseHearingInformClientAboutHearingPreview(leadId, [selected.tmNo])
      .then(({ message: m }) => {
        if (!cancelled) setMessage(m);
      })
      .catch((err) => {
        if (!cancelled) {
          setMessage("");
          toast.error(
            err instanceof Error ? err.message : "Failed to load preview"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, selected.tmNo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSent) {
      onAdvance();
      return;
    }
    if (!message.trim()) {
      toast.error(
        "No message available. Set Date of Hearing in Status details of Hearing first."
      );
      return;
    }
    onSavingChange?.(true);
    try {
      await sendShowCauseHearingInformClientAboutHearing(leadId, [selected.tmNo]);
      toast.success("Inform Client about Hearing sent to client");
      onSent();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send message");
    } finally {
      onSavingChange?.(false);
    }
  };

  return (
    <form
      id={EXAMINATION_REPORT_HAO_INFORM_HEARING_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-5 self-stretch pt-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <span
        className="text-[#222222]"
        style={{ fontSize: "16px", lineHeight: "20px", fontWeight: 400 }}
      >
        Message Preview
      </span>
      <div
        className="self-stretch bg-white border border-[#EEEEEE] rounded-[6px]"
        style={{ padding: "14px" }}
      >
        {loading ? (
          <span
            className="text-[#6D6D6D]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            Loading preview…
          </span>
        ) : message ? (
          <p
            className="text-[#222222] whitespace-pre-line"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            {renderWhatsAppFormatting(message)}
          </p>
        ) : (
          <span
            className="text-[#6D6D6D]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            No preview available. Set Date of Hearing in Status details of Hearing
            first.
          </span>
        )}
      </div>
      {hasSent && (
        <div className="flex flex-row items-center gap-3">
          <button
            type="button"
            disabled={resending}
            onClick={onResend}
            className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {resending ? "Resending…" : "Resend Message"}
          </button>
        </div>
      )}
    </form>
  );
}

function CallClientForHearingPanel({
  leadId,
  selected,
  hasRecorded,
  onRecorded,
  onAdvance,
  onSavingChange,
}: {
  leadId: string;
  selected: ShowCauseHearingApplication;
  hasRecorded: boolean;
  onRecorded: () => void;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
}) {
  const [recording, setRecording] = useState(false);

  const handleRecord = async () => {
    if (!leadId || recording || hasRecorded) return;
    setRecording(true);
    onSavingChange?.(true);
    try {
      await recordShowCauseHearingCallClient(leadId, [selected.tmNo]);
      onRecorded();
      toast.success("Call Client step recorded");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to record call"
      );
    } finally {
      setRecording(false);
      onSavingChange?.(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasRecorded) {
      toast.error("Mark the call as completed first.");
      return;
    }
    onAdvance();
  };

  return (
    <form
      id={EXAMINATION_REPORT_HAO_CALL_CLIENT_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-5 self-stretch pt-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div
        className="flex flex-row items-center gap-4 p-4 rounded-xl"
        style={{
          background: "#FFECD3",
          border: "1px dashed #E18308",
          borderRadius: "16px",
        }}
      >
        <img
          src={legalAdviceHeadset}
          alt=""
          className="w-[60px] h-[60px] flex-shrink-0"
        />
        <div className="flex flex-col gap-3 flex-1 min-w-0">
          <p
            className="text-sm font-normal text-[#222222]"
            style={{ lineHeight: "20px" }}
          >
            Call the client to explain the hearing scheduled for the objection
            and confirm whether they wish to attend or postpone it.
          </p>
          {hasRecorded ? (
            <div className="flex items-center gap-2">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="flex-shrink-0"
              >
                <circle cx="8" cy="8" r="7.5" fill="#222222" />
                <path
                  d="M5.33325 8.00008L7.33325 10.0001L10.6666 6.00008"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span
                className="text-xs font-semibold text-[#222222]"
                style={{ lineHeight: "20px" }}
              >
                Call Completed
              </span>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => void handleRecord()}
              disabled={recording || !leadId}
              className="self-start h-8 px-4 text-xs font-medium text-[#222222] bg-white border border-[#D1D5DB] rounded-md hover:bg-[#F9FAFB] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              style={{ lineHeight: "20px" }}
            >
              {recording ? "Recording…" : "Call Completed"}
            </button>
          )}
        </div>
      </div>
    </form>
  );
}

function HearingInvoiceAmountTooltip({
  defaultAmount,
  noOfApps,
}: {
  defaultAmount: number | null;
  noOfApps: number;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div
      ref={containerRef}
      className="relative inline-flex items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label="How is the invoice amount calculated?"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex items-center justify-center w-4 h-4 text-[#6D6D6D] hover:text-[#222] focus:outline-none focus:ring-1 focus:ring-[#222] rounded-full"
      >
        <HiOutlineInformationCircle className="w-4 h-4" strokeWidth={1.75} />
      </button>
      {open && (
        <div
          role="tooltip"
          className="absolute left-0 top-5 z-30 w-[300px] rounded-[8px] border border-[#EEEEEE] bg-white p-3 shadow-lg"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          <p className="text-[11px] font-semibold leading-4 text-[#222]">
            How the invoice amount is calculated
          </p>
          <p className="mt-1 text-[11px] leading-4 text-[#6D6D6D]">
            Default amount is taken from the Hearing charges configured in app
            settings. You can override the value if needed.
          </p>
          <p className="mt-2 text-[11px] font-semibold leading-4 text-[#222]">
            Calculation Breakdown
          </p>
          <div className="mt-1 flex flex-col gap-1 text-[11px] leading-4 text-[#222]">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[#6D6D6D]">
                Hearing charge ({noOfApps} app × {formatRupees(defaultAmount ?? 0)})
              </span>
              <span className="font-medium">
                {formatRupees((defaultAmount ?? 0) * noOfApps)}
              </span>
            </div>
            <div className="mt-1 flex items-baseline justify-between gap-2 border-t border-[#EEEEEE] pt-1.5">
              <span className="font-semibold text-[#222]">Invoice amount</span>
              <span className="font-semibold text-[#222]">
                {formatRupees((defaultAmount ?? 0) * noOfApps)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InvoiceForHearingPanel({
  leadId,
  phone,
  status,
  selected,
  hasSent,
  onSent,
  onAdvance,
  onSavingChange,
}: {
  leadId: string;
  phone?: string;
  status: ShowCauseHearingStatus;
  selected: ShowCauseHearingApplication;
  hasSent: boolean;
  onSent: () => void;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [invoiceStatus, setInvoiceStatus] =
    useState<HearingInvoiceStatus | null>(null);
  const [preview, setPreview] = useState<HearingInvoicePreview | null>(null);
  const [invoiceAmount, setInvoiceAmount] = useState<string>("");
  const [invoiceDate, setInvoiceDate] = useState<Date | null>(null);
  const [includeInvoiceNo, setIncludeInvoiceNo] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(phone ?? "");
  const [paymentMessage, setPaymentMessage] = useState<string>("");
  const [emailPreview, setEmailPreview] =
    useState<HearingInvoiceEmailPreview | null>(null);
  const [paymentMessageLoading, setPaymentMessageLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMode, setConfirmMode] = useState<"send" | "resend">("send");
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewTab, setPreviewTab] = useState<
    "details" | "pdf" | "message" | "email"
  >("details");
  const [previewNumPages, setPreviewNumPages] = useState<number | null>(null);

  // Load invoice status + preview for the selected TM
  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getHearingInvoiceStatus(leadId, [selected.tmNo]),
      getHearingInvoicePreview(leadId, [selected.tmNo]).catch(() => null),
      getHearingInvoicePaymentMessagePreview(leadId, [selected.tmNo]).catch(
        () => null
      ),
    ])
      .then(([statusData, previewData, messagePreview]) => {
        if (cancelled) return;
        setInvoiceStatus(statusData);
        setPreview(previewData ?? null);
        const row =
          statusData.tmInvoiceRows.find((r) => r.tmNo === selected.tmNo) ??
          null;
        const initialAmount =
          row?.invoiceAmount ?? row?.defaultInvoiceAmount ?? null;
        setInvoiceAmount(
          initialAmount != null ? String(initialAmount) : ""
        );
        setInvoiceDate(
          parseDateForHearingInput(row?.invoiceDate ?? statusData.invoiceDate)
        );
        setIncludeInvoiceNo(
          Boolean(row?.includeInvoiceNo ?? statusData.includeInvoiceNo)
        );
        if (messagePreview?.paymentMessage) {
          setPaymentMessage(messagePreview.paymentMessage);
        }
        if (messagePreview?.emailPreview) {
          setEmailPreview(messagePreview.emailPreview);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Hearing Invoice"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, selected.tmNo]);

  const refreshPaymentMessagePreview = async () => {
    if (!leadId) return;
    setPaymentMessageLoading(true);
    try {
      const res = await getHearingInvoicePaymentMessagePreview(leadId, [
        selected.tmNo,
      ]);
      setPaymentMessage(res.paymentMessage ?? "");
      setEmailPreview(res.emailPreview ?? null);
    } catch {
      // Non-critical — keep existing values.
    } finally {
      setPaymentMessageLoading(false);
    }
  };

  const persist = async (): Promise<HearingInvoiceStatus | null> => {
    const amountNum = Number(invoiceAmount);
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      toast.error("Invoice Amount must be a positive number");
      return null;
    }
    if (!invoiceDate) {
      toast.error("Date of Invoice is required");
      return null;
    }
    const updated = await saveHearingInvoice(leadId, [
      {
        tmNo: selected.tmNo,
        invoiceAmount: amountNum,
        invoiceDate: formatDateDDMMYYYYForApi(invoiceDate),
        includeInvoiceNo,
      },
    ]);
    setInvoiceStatus(updated);
    return updated;
  };

  const handleGeneratePdf = async () => {
    if (!leadId || generating) return;
    setGenerating(true);
    onSavingChange?.(true);
    try {
      const saved = await persist();
      if (!saved) return;
      const { s3Key, downloadUrl } = await generateHearingInvoicePdf(leadId, [
        selected.tmNo,
      ]);
      // Reflect the generated PDF on the status view immediately.
      setInvoiceStatus((prev) =>
        prev
          ? {
              ...prev,
              hearingInvoicePdfKey: s3Key,
              hearingInvoicePdfUrl: downloadUrl,
            }
          : prev
      );
      toast.success("Hearing Invoice PDF generated");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to generate PDF"
      );
    } finally {
      setGenerating(false);
      onSavingChange?.(false);
    }
  };

  const handlePreview = () => {
    setPreviewTab("details");
    setPreviewNumPages(null);
    setPreviewModalOpen(true);
  };

  const closePreviewModal = () => {
    setPreviewModalOpen(false);
    setPreviewNumPages(null);
    setPreviewTab("details");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || sending) return;
    if (hasSent) {
      // Already sent — silently save and advance without re-sending.
      onSavingChange?.(true);
      try {
        const saved = await persist();
        if (!saved) return;
        toast.success("Hearing Invoice saved");
        onAdvance();
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to save Hearing Invoice"
        );
      } finally {
        onSavingChange?.(false);
      }
      return;
    }

    // First-time send: save → refresh preview → open confirm modal.
    setSending(true);
    onSavingChange?.(true);
    try {
      const saved = await persist();
      if (!saved) return;
      if (!saved.hearingInvoicePdfKey) {
        toast.error("Generate the Hearing Invoice PDF before sending.");
        return;
      }
      await refreshPaymentMessagePreview();
      setConfirmMode("send");
      setShowConfirmModal(true);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save Hearing Invoice"
      );
    } finally {
      setSending(false);
      onSavingChange?.(false);
    }
  };

  const handleConfirmProceed = async () => {
    if (!leadId) return;
    setShowConfirmModal(false);
    setSending(true);
    onSavingChange?.(true);
    try {
      const [invoiceResult, paymentResult] = await Promise.allSettled([
        sendHearingInvoiceToClient(leadId, {
          sendWhatsApp: true,
          sendEmail: true,
          tmNos: [selected.tmNo],
        }),
        sendHearingInvoicePaymentMessage(leadId, [selected.tmNo]),
      ]);
      const invoiceOk = invoiceResult.status === "fulfilled";
      const paymentOk = paymentResult.status === "fulfilled";
      const invoiceErr =
        invoiceResult.status === "rejected"
          ? invoiceResult.reason instanceof Error
            ? invoiceResult.reason.message
            : "Failed to send Hearing Invoice"
          : null;
      const paymentErr =
        paymentResult.status === "rejected"
          ? paymentResult.reason instanceof Error
            ? paymentResult.reason.message
            : "Failed to send payment message"
          : null;
      const isResend = confirmMode === "resend";

      if (invoiceOk && paymentOk) {
        onSent();
        toast.success(
          isResend
            ? "Hearing Invoice resent to client."
            : "Hearing Invoice sent to client."
        );
        onAdvance();
      } else if (invoiceOk && !paymentOk) {
        onSent();
        toast.warning(
          `Invoice sent, but payment message failed: ${paymentErr}`
        );
        onAdvance();
      } else if (!invoiceOk && paymentOk) {
        toast.warning(
          `Payment message sent, but sending invoice failed: ${invoiceErr}`
        );
      } else {
        toast.error(invoiceErr ?? "Failed to send Hearing Invoice");
      }
    } finally {
      setSending(false);
      onSavingChange?.(false);
    }
  };

  const handleResend = async () => {
    if (!leadId || sending) return;
    setSending(true);
    onSavingChange?.(true);
    try {
      const saved = await persist();
      if (!saved) return;
      await refreshPaymentMessagePreview();
      setConfirmMode("resend");
      setShowConfirmModal(true);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save Hearing Invoice"
      );
    } finally {
      setSending(false);
      onSavingChange?.(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-[#6D6D6D] py-5">Loading…</p>;
  }

  const hasPdf = !!invoiceStatus?.hearingInvoicePdfUrl;
  const noOfApps = String(status.noOfApplicationsForHearing);
  const dateOfHearingDisplay = selected.dateOfHearing ?? "";
  const addressee = preview?.addressee ?? "";
  const formOfAddress = preview?.formOfAddress ?? "";
  const dateOfFiling = status.dateOfFiling ?? "";

  const FORM_STYLES = { fontFamily: "Inter, sans-serif" } as const;
  const LABEL_CLASS = "text-[12px] font-normal leading-5 text-[#222222]";
  const READONLY_CLASS =
    "h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-[#F7F7F7] px-2.5 py-2 text-[12px] leading-5 text-[#6D6D6D] box-border";
  const INPUT_CLASS =
    "h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-white px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222] box-border";

  const parsedAmount = parseFloat(invoiceAmount);
  const amountForDisplay =
    Number.isFinite(parsedAmount) && parsedAmount >= 0 ? parsedAmount : 0;

  return (
    <form
      id={EXAMINATION_REPORT_HAO_INVOICE_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex w-full max-w-[1085px] flex-col gap-5 pt-4"
      style={FORM_STYLES}
    >
      {/* Row 1 (Editable): Date of Invoice | Invoice Amount */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Date of Invoice for Hearing</label>
          <DateTimePicker
            value={invoiceDate}
            onChange={(d) => setInvoiceDate(d)}
            showTime={false}
            placeholder="Select Date"
            buttonClassName="!h-9 !min-h-0 !text-[12px] rounded-[6px]"
          />
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5">
            <label className={LABEL_CLASS}>Invoice Amount for Hearing</label>
            <HearingInvoiceAmountTooltip
              defaultAmount={
                invoiceStatus?.tmInvoiceRows.find(
                  (r) => r.tmNo === selected.tmNo
                )?.defaultInvoiceAmount ??
                invoiceStatus?.defaultInvoiceAmount ??
                null
              }
              noOfApps={status.noOfApplicationsForHearing}
            />
          </div>
          <input
            type="text"
            inputMode="decimal"
            value={invoiceAmount}
            onChange={(e) => {
              const raw = e.target.value.replace(/[^\d.]/g, "");
              const parts = raw.split(".");
              const v =
                parts.length > 1
                  ? `${parts[0]}.${parts.slice(1).join("")}`
                  : raw;
              setInvoiceAmount(v);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.preventDefault();
            }}
            placeholder="0"
            className={INPUT_CLASS}
          />
        </div>
      </div>

      {/* Row 2 (Readonly): Invoice No. */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Invoice No. for Hearing</label>
          <input
            type="text"
            value={invoiceStatus?.invoiceNo ?? ""}
            placeholder="Auto-generated on save"
            readOnly
            className={READONLY_CLASS}
          />
        </div>
      </div>

      <IncludeInvoiceNoCheckbox
        id="hearing-invoice-include-invoice-no"
        checked={includeInvoiceNo}
        onChange={setIncludeInvoiceNo}
      />

      {/* Invoice Amount summary bar */}
      <div
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-3 rounded-[8px] px-4 sm:px-5 py-4 sm:py-0 min-h-[72px] sm:h-[72px]"
        style={{ background: "#222222" }}
      >
        <div className="flex flex-row items-center gap-2 min-w-0">
          <img
            src={invoiceAmountCheck}
            alt=""
            className="w-5 h-5 flex-shrink-0"
          />
          <span
            className="text-[12px] md:text-sm font-normal leading-5 text-white/90 truncate"
            style={FORM_STYLES}
          >
            INVOICE AMOUNT FOR HEARING
          </span>
        </div>
        <span
          className="text-lg md:text-[22px] font-bold leading-8 md:leading-10 text-white shrink-0"
          style={FORM_STYLES}
        >
          {formatRupees(amountForDisplay)}
        </span>
      </div>

      {/* Generate PDF + Download + Preview */}
      <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2.5">
        <div className="flex flex-row items-center gap-2.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] pl-4 pr-2 py-2 min-h-[44px] sm:h-11">
          <img
            src={invoiceGenerate}
            alt=""
            className="w-[22px] h-[22px] flex-shrink-0"
          />
          <button
            type="button"
            onClick={() => void handleGeneratePdf()}
            disabled={generating}
            className="flex flex-row items-center justify-center min-h-[40px] h-9 px-4 rounded-[6px] bg-[#222222] text-[12px] md:text-xs font-medium text-white hover:bg-[#333333] transition-colors shrink-0 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
            style={FORM_STYLES}
          >
            {generating
              ? "Generating…"
              : hasPdf
                ? "Regenerate PDF"
                : "Generate PDF"}
          </button>
        </div>
        {hasPdf ? (
          <a
            href={invoiceStatus!.hearingInvoicePdfUrl!}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-row items-center justify-center min-h-[44px] sm:h-11 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] md:text-xs font-medium text-[#222222] hover:bg-[#F7F7F7] transition-colors shrink-0 touch-manipulation"
            style={FORM_STYLES}
          >
            Download
          </a>
        ) : (
          <button
            type="button"
            disabled
            className="flex flex-row items-center justify-center min-h-[44px] sm:h-11 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] md:text-xs font-medium text-[#222222] opacity-40 shrink-0 touch-manipulation"
            style={FORM_STYLES}
          >
            Download
          </button>
        )}
        <button
          type="button"
          onClick={handlePreview}
          disabled={generating || !leadId}
          className="flex flex-row items-center justify-center min-h-[44px] sm:h-11 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] md:text-xs font-medium text-[#222222] hover:bg-[#F7F7F7] transition-colors shrink-0 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
          style={FORM_STYLES}
        >
          Preview
        </button>
      </div>

      {/* Phone number + optional Resend */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="tel"
          inputMode="numeric"
          value={phoneNumber}
          readOnly
          placeholder="e.g. +919876543210"
          className="w-full min-w-0 sm:w-[200px] h-9 px-[10px] py-2 text-[12px] text-[#222] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] placeholder:text-[#6D6D6D]"
        />
        {hasSent && (
          <button
            type="button"
            disabled={sending}
            onClick={() => void handleResend()}
            className="inline-flex items-center justify-center h-9 px-4 text-[12px] font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
            style={FORM_STYLES}
          >
            {sending ? "Sending…" : "Resend Message"}
          </button>
        )}
      </div>

      {previewModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4"
          role="dialog"
          aria-modal="true"
          onClick={closePreviewModal}
        >
          <div
            className="relative flex h-[95vh] sm:h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 items-center justify-between border-b border-[#e3e3e3] px-3 py-3 sm:px-4">
              <span
                className="truncate pr-2 text-xs font-medium text-[#222] sm:text-sm"
                style={FORM_STYLES}
              >
                Invoice for Hearing — Preview
              </span>
              <div className="flex items-center gap-3 shrink-0">
                {hasPdf && (
                  <a
                    href={invoiceStatus!.hearingInvoicePdfUrl!}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-[#2E3037] underline underline-offset-2"
                  >
                    Open in new tab
                  </a>
                )}
                <button
                  type="button"
                  onClick={closePreviewModal}
                  className="flex size-10 shrink-0 items-center justify-center rounded text-[#6d6d6d] hover:bg-[#eee] hover:text-[#222] sm:size-8 touch-manipulation"
                  aria-label="Close preview"
                >
                  <HiOutlineX className="size-5" strokeWidth={2} />
                </button>
              </div>
            </div>

            <div
              className="flex shrink-0 gap-1 border-b border-[#e3e3e3] bg-white px-2 sm:px-3"
              role="tablist"
            >
              <button
                type="button"
                role="tab"
                aria-selected={previewTab === "details"}
                onClick={() => setPreviewTab("details")}
                className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation ${
                  previewTab === "details"
                    ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
                    : "text-[#6d6d6d] hover:text-[#222]"
                }`}
              >
                Invoice details
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={previewTab === "pdf"}
                onClick={() => setPreviewTab("pdf")}
                className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation ${
                  previewTab === "pdf"
                    ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
                    : "text-[#6d6d6d] hover:text-[#222]"
                }`}
              >
                Generated PDF
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={previewTab === "message"}
                onClick={() => setPreviewTab("message")}
                className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation ${
                  previewTab === "message"
                    ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
                    : "text-[#6d6d6d] hover:text-[#222]"
                }`}
              >
                WhatsApp Message
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={previewTab === "email"}
                onClick={() => setPreviewTab("email")}
                className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation ${
                  previewTab === "email"
                    ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
                    : "text-[#6d6d6d] hover:text-[#222]"
                }`}
              >
                Email
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-auto bg-[#fafafa] p-2 sm:p-4 flex justify-center">
              {previewTab === "details" ? (
                <div
                  className="flex w-full max-w-3xl flex-col gap-3"
                  style={FORM_STYLES}
                >
                  <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                      Invoice details
                    </p>
                    <div className="grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-3 gap-y-1.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2.5 text-[12px] leading-5 text-[#222]">
                      <ReplyInvoicePreviewRow label="Acknowledgment No">
                        {status.acknowledgementNo?.trim() || (
                          <span className="text-[#6D6D6D]">—</span>
                        )}
                      </ReplyInvoicePreviewRow>
                      <ReplyInvoicePreviewRow label="Date of Filing">
                        {dateOfFiling?.trim() || (
                          <span className="text-[#6D6D6D]">—</span>
                        )}
                      </ReplyInvoicePreviewRow>
                      <ReplyInvoicePreviewRow label="Addressee" alignTop>
                        {addressee?.trim() ? (
                          <div className="flex flex-col">
                            {addressee.split("\n").map((line, i) => (
                              <span key={i}>{line}</span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-[#6D6D6D]">—</span>
                        )}
                      </ReplyInvoicePreviewRow>
                      <ReplyInvoicePreviewRow label="Form of Address">
                        {formOfAddress?.trim() || (
                          <span className="text-[#6D6D6D]">—</span>
                        )}
                      </ReplyInvoicePreviewRow>
                      <ReplyInvoicePreviewRow label="Mark">
                        {selected.mark?.trim() || (
                          <span className="text-[#6D6D6D]">—</span>
                        )}
                      </ReplyInvoicePreviewRow>
                      <ReplyInvoicePreviewRow label="Selected Class">
                        {selected.classNumber != null ? (
                          String(selected.classNumber)
                        ) : (
                          <span className="text-[#6D6D6D]">—</span>
                        )}
                      </ReplyInvoicePreviewRow>
                      <ReplyInvoicePreviewRow label="Selected Type of Mark">
                        {selected.typeOfMark?.trim() || (
                          <span className="text-[#6D6D6D]">—</span>
                        )}
                      </ReplyInvoicePreviewRow>
                      <ReplyInvoicePreviewRow label="Date of Hearing">
                        {dateOfHearingDisplay?.trim() || (
                          <span className="text-[#6D6D6D]">—</span>
                        )}
                      </ReplyInvoicePreviewRow>
                      <ReplyInvoicePreviewRow label="No. of applications for Hearing">
                        {noOfApps?.trim() || (
                          <span className="text-[#6D6D6D]">—</span>
                        )}
                      </ReplyInvoicePreviewRow>
                      <ReplyInvoicePreviewRow label="Date of Invoice for Hearing">
                        {invoiceDate ? (
                          formatInvoiceDateDDMMYYYY(invoiceDate)
                        ) : (
                          <span className="text-[#6D6D6D]">—</span>
                        )}
                      </ReplyInvoicePreviewRow>
                      <ReplyInvoicePreviewRow label="Invoice Amount for Hearing">
                        {formatInvoiceRupees(invoiceAmount)}
                      </ReplyInvoicePreviewRow>
                      <ReplyInvoicePreviewRow label="Invoice No. for Hearing">
                        {invoiceStatus?.invoiceNo?.trim() || (
                          <span className="text-[#6D6D6D]">
                            Auto-generated on save
                          </span>
                        )}
                      </ReplyInvoicePreviewRow>
                    </div>
                  </section>
                </div>
              ) : previewTab === "pdf" ? (
                hasPdf ? (
                  <Document
                    file={invoiceStatus!.hearingInvoicePdfUrl!}
                    onLoadSuccess={({ numPages: n }) => setPreviewNumPages(n)}
                    loading={
                      <p className="text-sm text-[#6d6d6d]">Loading PDF…</p>
                    }
                    error={
                      <p className="text-sm text-red-600">Failed to load PDF.</p>
                    }
                    className="flex flex-col items-center gap-4"
                  >
                    {previewNumPages != null &&
                      Array.from({ length: previewNumPages }, (_, i) => (
                        <Page
                          key={i + 1}
                          pageNumber={i + 1}
                          width={Math.min(
                            800,
                            typeof window !== "undefined"
                              ? window.innerWidth - 48
                              : 800
                          )}
                          renderTextLayer
                          renderAnnotationLayer
                        />
                      ))}
                  </Document>
                ) : (
                  <div className="flex flex-col items-center justify-center gap-3 py-12">
                    <p className="text-sm text-[#6d6d6d]" style={FORM_STYLES}>
                      No invoice PDF has been generated yet.
                    </p>
                    <button
                      type="button"
                      onClick={() => void handleGeneratePdf()}
                      disabled={generating}
                      className="inline-flex items-center justify-center h-9 px-4 rounded-[6px] bg-[#222222] text-xs font-medium text-white hover:bg-[#333] disabled:opacity-60 disabled:cursor-not-allowed"
                      style={FORM_STYLES}
                    >
                      {generating ? "Generating…" : "Generate PDF"}
                    </button>
                  </div>
                )
              ) : previewTab === "message" ? (
                <div
                  className="w-full max-w-2xl rounded-lg border border-[#EEEEEE] bg-white p-4 shadow-sm sm:p-5"
                  style={FORM_STYLES}
                >
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                    Will be sent to client WhatsApp after the invoice PDF
                  </p>
                  {paymentMessageLoading ? (
                    <p className="text-sm text-[#6d6d6d]">
                      Loading message preview…
                    </p>
                  ) : paymentMessage ? (
                    renderInvoicePaymentMessageLines(paymentMessage)
                  ) : (
                    <p className="text-sm text-[#6D6D6D]">
                      Message preview not available.
                    </p>
                  )}
                </div>
              ) : (
                <div
                  className="w-full max-w-2xl rounded-lg border border-[#EEEEEE] bg-white p-4 shadow-sm sm:p-5"
                  style={FORM_STYLES}
                >
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                    Will be emailed to the client with the invoice PDF
                  </p>
                  <HearingEmailPreviewBlock
                    preview={emailPreview}
                    loading={paymentMessageLoading}
                    attachmentFallback={
                      hasPdf
                        ? {
                            filename: "Invoice_for_Hearing.pdf",
                            url: invoiceStatus!.hearingInvoicePdfUrl!,
                          }
                        : null
                    }
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmMessageModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title={
          confirmMode === "resend"
            ? "Resend Hearing Invoice to client"
            : "Confirm Hearing Invoice to client"
        }
        previewSlot={
          <div className="flex flex-col gap-4 p-4">
            <section>
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                WhatsApp message (after invoice PDF)
              </p>
              {paymentMessageLoading ? (
                <p className="text-sm text-[#6d6d6d]">
                  Loading message preview…
                </p>
              ) : paymentMessage ? (
                renderInvoicePaymentMessageLines(paymentMessage)
              ) : (
                <p className="text-sm text-[#6D6D6D]">
                  Message preview not available.
                </p>
              )}
            </section>
            <div className="border-t border-dashed border-[#EEEEEE]" />
            <section>
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Email (with invoice PDF)
              </p>
              <HearingEmailPreviewBlock
                preview={emailPreview}
                loading={paymentMessageLoading}
                attachmentFallback={
                  hasPdf
                    ? {
                        filename: "Invoice_for_Hearing.pdf",
                        url: invoiceStatus!.hearingInvoicePdfUrl!,
                      }
                    : null
                }
              />
            </section>
          </div>
        }
        onConfirm={() => void handleConfirmProceed()}
        disableConfirm={sending}
      />
    </form>
  );
}

function HearingEmailPreviewBlock({
  preview,
  loading,
  attachmentFallback,
}: {
  preview: HearingInvoiceEmailPreview | null;
  loading?: boolean;
  attachmentFallback?: { filename: string; url: string } | null;
}) {
  if (loading) {
    return (
      <p
        className="text-sm text-[#6d6d6d]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        Loading email preview…
      </p>
    );
  }
  if (!preview) {
    return (
      <p
        className="text-sm text-[#6D6D6D]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        Email preview not available.
      </p>
    );
  }
  if (!preview.to) {
    return (
      <p
        className="text-sm text-[#D92D20]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        No email on file for this client. Email will not be sent.
      </p>
    );
  }
  return (
    <div
      className="flex flex-col gap-1.5 font-sans text-[13px] leading-relaxed text-[#222]"
      style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
    >
      <div className="flex flex-row items-baseline gap-2 break-words">
        <span className="text-[#222]">To</span>
        <span className="text-[#222]">:</span>
        <span className="text-[#222] break-all">{preview.to}</span>
      </div>
      <div className="flex flex-row items-baseline gap-2 break-words">
        <span className="text-[#222]">Subject</span>
        <span className="text-[#222]">:</span>
        <span className="text-[#222] break-words">{preview.subject}</span>
      </div>
      <div className="mt-2 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] p-3">
        <p className="m-0 mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
          Body
        </p>
        <p className="m-0 whitespace-pre-wrap break-words text-[#222]">
          {preview.body}
        </p>
      </div>
      {(() => {
        const filename = preview.filename ?? attachmentFallback?.filename ?? null;
        const downloadUrl =
          preview.downloadUrl ?? attachmentFallback?.url ?? null;
        if (!filename) return null;
        return (
          <div className="mt-2 flex flex-row items-baseline gap-2 break-words">
            <span className="text-[#222]">Attachment</span>
            <span className="text-[#222]">:</span>
            {downloadUrl ? (
              <a
                href={downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#2E3037] underline underline-offset-2 break-all"
              >
                {filename}
              </a>
            ) : (
              <span className="text-[#222] break-all">{filename}</span>
            )}
          </div>
        );
      })()}
    </div>
  );
}

function ProcessToNextStepIllustration() {
  return (
    <img
      src={processToNextStepGraphic}
      alt=""
      className="w-[220px] h-[220px] flex-shrink-0"
      aria-hidden="true"
    />
  );
}

// ---------------------------------------------------------------------------
// 3D(vii)a/b — Revised Invoice for Hearing 1 (review item 68).
// Reached from 3D(vii) when the client confirms the hearing need not be attended
// for every application: the marks still attending get a revised 3D(v) invoice.
// Original 3ab–3ae are shown read-only; 3abr–3aer are editable per TM.
// The endpoints are independent of the 3D(vii) answer — they need only hearing
// applications and a saved 3D(v) invoice.
// ---------------------------------------------------------------------------

type RevisedRowDraft = {
  tmNo: string;
  invoiceAmount: string;
  invoiceDate: Date | null;
  invoiceNo: string;
};

function RevisedInvoiceForHearingPanel({
  leadId,
  onAdvance,
  onSavingChange,
}: {
  leadId: string;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<RevisedHearingInvoiceStatus | null>(null);
  const [rows, setRows] = useState<RevisedRowDraft[]>([]);
  const [includeInvoiceNo, setIncludeInvoiceNo] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const FORM_STYLES = { fontFamily: "Inter, sans-serif" } as const;
  const LABEL_CLASS = "text-[12px] font-normal leading-5 text-[#222222]";
  const READONLY_CLASS =
    "h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-[#F7F7F7] px-2.5 py-2 text-[12px] leading-5 text-[#6D6D6D] box-border";
  const INPUT_CLASS =
    "h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-white px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222] box-border";

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    // The revised endpoint only returns `revisedApplications` once a revised invoice
    // exists, so on the first visit the rows are seeded from the original 3D(v)
    // invoice — "Data to be fetched: 3ab–3ae → Data to be edited as: 3abr–3aer".
    Promise.all([
      getRevisedHearingInvoiceStatus(leadId),
      getHearingInvoiceStatus(leadId).catch(() => null),
      getShowCauseHearingConfirmation(leadId).catch(() => null),
      getHearingInvoicePreview(leadId).catch(() => null),
    ])
      .then(([data, original, confirmation, originalPreview]) => {
        if (cancelled) return;
        setStatus(data);
        const saved = data.revisedApplications ?? [];
        if (saved.length > 0) {
          setRows(
            saved.map((row) => ({
              tmNo: row.tmNo,
              invoiceAmount:
                row.invoiceAmount != null ? String(row.invoiceAmount) : "",
              invoiceDate: parseDdMmYyyy(row.invoiceDate),
              invoiceNo: row.invoiceNo ?? "",
            }))
          );
          setIncludeInvoiceNo(Boolean(data.revisedIncludeInvoiceNo));
          return;
        }
        const allRows: HearingInvoiceTmRow[] = original?.tmInvoiceRows ?? [];
        // `tmInvoiceRows` includes default rows for hearing TMs that were never on
        // the saved 3D(v) invoice, and the backend rejects those ("TM No.(s) … are
        // not included in this invoice selection"). The preview's `selectedTmNos` is
        // the saved selection, so that is the only set the revised invoice may cover.
        const savedSelection = new Set(originalPreview?.selectedTmNos ?? []);
        const originalRows = savedSelection.size
          ? allRows.filter((r) => savedSelection.has(r.tmNo))
          : allRows;
        // Prefer the marks still attending: the revision exists because the hearing
        // is no longer for every application, so seeding with all of them would
        // default to a "revised" invoice identical to the original. Fall back to the
        // full saved selection when that intersection is empty, so the panel is never
        // blank (e.g. the invoiced mark is the one that postponed).
        const confirmationRows: HearingConfirmationApplication[] =
          confirmation?.applications ?? [];
        const attendingTmNos = new Set(
          confirmationRows
            .filter((a) => a.clientConfirmation === "YES")
            .map((a) => a.tmNo)
        );
        const attendingRows = originalRows.filter((r) =>
          attendingTmNos.has(r.tmNo)
        );
        const seedRows = attendingRows.length ? attendingRows : originalRows;
        setRows(
          seedRows.map((row) => ({
            tmNo: row.tmNo,
            invoiceAmount:
              row.invoiceAmount != null
                ? String(row.invoiceAmount)
                : row.defaultInvoiceAmount != null
                ? String(row.defaultInvoiceAmount)
                : "",
            invoiceDate: parseDdMmYyyy(row.invoiceDate),
            invoiceNo: "",
          }))
        );
        setIncludeInvoiceNo(Boolean(original?.includeInvoiceNo));
      })
      .catch((err) => {
        if (!cancelled)
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Revised Hearing Invoice"
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const patchRow = (tmNo: string, patch: Partial<RevisedRowDraft>) =>
    setRows((prev) =>
      prev.map((r) => (r.tmNo === tmNo ? { ...r, ...patch } : r))
    );

  const revisedTotal = rows.reduce((sum, r) => {
    const n = parseFloat(r.invoiceAmount);
    return sum + (Number.isFinite(n) && n > 0 ? n : 0);
  }, 0);

  const handleSave = async (): Promise<boolean> => {
    if (!leadId || saving) return false;
    if (!rows.length) {
      toast.error(
        "No applications on the revised invoice. Save the 3D(v) Hearing invoice first."
      );
      return false;
    }
    const invalid = rows.find((r) => {
      const n = parseFloat(r.invoiceAmount);
      return !Number.isFinite(n) || n <= 0;
    });
    if (invalid) {
      toast.error(
        `Enter a revised invoice amount for ${formatTmNoForDisplay(invalid.tmNo)}.`
      );
      return false;
    }
    const missingDate = rows.find((r) => !r.invoiceDate);
    if (missingDate) {
      toast.error(
        `Select a revised date of invoice for ${formatTmNoForDisplay(
          missingDate.tmNo
        )}.`
      );
      return false;
    }
    setSaving(true);
    onSavingChange?.(true);
    try {
      const updated = await saveRevisedHearingInvoice(
        leadId,
        rows.map((r) => ({
          tmNo: r.tmNo,
          invoiceAmount: parseFloat(r.invoiceAmount),
          invoiceDate: formatDateDDMMYYYYForApi(r.invoiceDate as Date),
          includeInvoiceNo,
          ...(r.invoiceNo.trim() ? { invoiceNo: r.invoiceNo.trim() } : {}),
        }))
      );
      setStatus(updated);
      toast.success("Revised Hearing Invoice saved");
      return true;
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save Revised Hearing Invoice"
      );
      return false;
    } finally {
      setSaving(false);
      onSavingChange?.(false);
    }
  };

  const handleGeneratePdf = async () => {
    if (!leadId || generating) return;
    // The backend rejects PDF generation until the revised invoice is saved, so
    // save first rather than surfacing that as an error.
    const ok = await handleSave();
    if (!ok) return;
    setGenerating(true);
    onSavingChange?.(true);
    try {
      await generateRevisedHearingInvoicePdf(leadId);
      const refreshed = await getRevisedHearingInvoiceStatus(leadId);
      setStatus(refreshed);
      toast.success("Revised Hearing Invoice PDF generated");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to generate revised PDF"
      );
    } finally {
      setGenerating(false);
      onSavingChange?.(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void (async () => {
      if (await handleSave()) onAdvance();
    })();
  };

  if (loading) {
    return <p className="text-sm text-[#6D6D6D] py-5">Loading…</p>;
  }

  return (
    <form
      id={EXAMINATION_REPORT_HAO_REVISED_INVOICE_FORM_ID}
      onSubmit={handleSubmit}
      className="flex w-full max-w-[1085px] flex-col gap-5 pt-4"
      style={FORM_STYLES}
    >
      {/* Original 3ab–3ae — read-only */}
      <div className="flex flex-col gap-3">
        <span className="text-sm font-medium leading-5 text-[#222222]">
          Original Invoice for Hearing 1
        </span>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-5">
          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>No. of applications for Hearing</label>
            <input
              type="text"
              readOnly
              className={READONLY_CLASS}
              value={status?.noOfApplicationsForHearing ?? ""}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Invoice Amount for Hearing</label>
            <input
              type="text"
              readOnly
              className={READONLY_CLASS}
              value={
                status?.invoiceAmount != null
                  ? formatRupees(status.invoiceAmount)
                  : ""
              }
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Invoice No. for Hearing</label>
            <input
              type="text"
              readOnly
              className={READONLY_CLASS}
              value={status?.invoiceNo ?? ""}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Date of Invoice for Hearing</label>
            <input
              type="text"
              readOnly
              className={READONLY_CLASS}
              value={status?.invoiceDate ?? ""}
            />
          </div>
        </div>
      </div>

      {/* Revised 3abr–3aer — editable per TM */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-row items-center justify-between gap-3">
          <span className="text-sm font-medium leading-5 text-[#222222]">
            Revised Invoice for Hearing 1
          </span>
          <span className="text-[12px] leading-5 text-[#6D6D6D]">
            No. of applications for Hearing – Revised:{" "}
            <span className="font-medium text-[#222222]">{rows.length}</span>
          </span>
        </div>

        {rows.length === 0 ? (
          <p className="text-[12px] leading-5 text-[#6D6D6D]">
            No applications are on the revised invoice yet. Save the 3D(v)
            Invoice to Client for Hearing 1 first.
          </p>
        ) : (
          rows.map((row) => (
            <div
              key={row.tmNo}
              className="flex flex-col gap-3 rounded-[8px] border border-[#EEEEEE] p-3"
            >
              <div className="flex flex-row items-center justify-between gap-3">
                <span className="text-[12px] font-medium leading-5 text-[#222222]">
                  {formatTmNoForDisplay(row.tmNo)}
                </span>
                {rows.length > 1 && (
                  <button
                    type="button"
                    onClick={() =>
                      setRows((prev) => prev.filter((r) => r.tmNo !== row.tmNo))
                    }
                    className="text-[12px] font-medium leading-5 text-[#B42318] hover:underline"
                  >
                    Remove from revised invoice
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                <div className="flex flex-col gap-1">
                  <label className={LABEL_CLASS}>
                    Invoice Amount for Hearing – Revised
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={row.invoiceAmount}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/[^\d.]/g, "");
                      const parts = raw.split(".");
                      patchRow(row.tmNo, {
                        invoiceAmount:
                          parts.length > 1
                            ? `${parts[0]}.${parts.slice(1).join("")}`
                            : raw,
                      });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") e.preventDefault();
                    }}
                    placeholder="0"
                    className={INPUT_CLASS}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className={LABEL_CLASS}>
                    Date of Invoice for Hearing – Revised
                  </label>
                  <DateTimePicker
                    value={row.invoiceDate}
                    onChange={(d) => patchRow(row.tmNo, { invoiceDate: d })}
                    showTime={false}
                    placeholder="Select Date"
                    buttonClassName="!h-9 !min-h-0 !text-[12px] rounded-[6px]"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className={LABEL_CLASS}>
                    Invoice No. for Hearing – Revised
                  </label>
                  <input
                    type="text"
                    value={row.invoiceNo}
                    onChange={(e) =>
                      patchRow(row.tmNo, { invoiceNo: e.target.value })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") e.preventDefault();
                    }}
                    placeholder="Auto-generated on save"
                    className={INPUT_CLASS}
                  />
                </div>
              </div>
            </div>
          ))
        )}

        <IncludeInvoiceNoCheckbox
          id="hearing-revised-invoice-include-invoice-no"
          checked={includeInvoiceNo}
          onChange={setIncludeInvoiceNo}
        />
      </div>

      {/* Revised total (3acr) */}
      <div
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-3 rounded-[8px] px-4 sm:px-5 py-4 sm:py-0 min-h-[72px] sm:h-[72px]"
        style={{ background: "#222222" }}
      >
        <div className="flex flex-row items-center gap-2 min-w-0">
          <img src={invoiceAmountCheck} alt="" className="w-5 h-5 flex-shrink-0" />
          <span className="text-[12px] md:text-sm font-normal leading-5 text-white/90 truncate">
            INVOICE AMOUNT FOR HEARING – REVISED
          </span>
        </div>
        <span className="text-base md:text-lg font-semibold leading-6 text-white">
          {formatRupees(revisedTotal)}
        </span>
      </div>

      <div className="flex flex-row flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => void handleGeneratePdf()}
          disabled={generating || saving || rows.length === 0}
          className="h-9 px-4 text-xs font-medium text-[#222222] bg-white border border-[#D1D5DB] rounded-md hover:bg-[#F9FAFB] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          {generating ? "Generating…" : "Generate Revised PDF"}
        </button>
        {status?.invoiceNoRevised ? (
          <span className="text-[12px] leading-5 text-[#6D6D6D]">
            Saved as{" "}
            <span className="font-medium text-[#222222]">
              {status.invoiceNoRevised}
            </span>
          </span>
        ) : null}
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// 3D(viii) Invoice for TM-M before Hearing
// ---------------------------------------------------------------------------
function InvoiceForTmMBeforeHearingPanel({
  leadId,
  phone,
  status,
  selected,
  hasSent,
  onSent,
  onAdvance,
  onSavingChange,
  onGatedChange,
  onProgressActionChange,
}: {
  leadId: string;
  phone?: string;
  status: ShowCauseHearingStatus;
  selected: ShowCauseHearingApplication;
  hasSent: boolean;
  onSent: () => void;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
  onGatedChange?: (gated: boolean) => void;
  onProgressActionChange?: (run: (() => Promise<void>) | null) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [invoiceStatus, setInvoiceStatus] =
    useState<TmMBeforeHearingInvoiceStatus | null>(null);
  const [preview, setPreview] = useState<TmMBeforeHearingInvoicePreview | null>(
    null
  );
  const [invoiceAmount, setInvoiceAmount] = useState<string>("");
  const [invoiceDate, setInvoiceDate] = useState<Date | null>(null);
  const [includeInvoiceNo, setIncludeInvoiceNo] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(phone ?? "");
  const [paymentMessage, setPaymentMessage] = useState<string>("");
  const [emailPreview, setEmailPreview] =
    useState<TmMBeforeHearingInvoiceEmailPreview | null>(null);
  const [paymentMessageLoading, setPaymentMessageLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMode, setConfirmMode] = useState<"send" | "resend">("send");
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewTab, setPreviewTab] = useState<
    "details" | "pdf" | "message" | "email"
  >("details");
  const [previewNumPages, setPreviewNumPages] = useState<number | null>(null);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getTmMBeforeHearingInvoiceStatus(leadId, [selected.tmNo]),
      getTmMBeforeHearingInvoicePreview(leadId, [selected.tmNo]).catch(
        () => null
      ),
      getTmMBeforeHearingInvoicePaymentMessagePreview(leadId, [
        selected.tmNo,
      ]).catch(() => null),
    ])
      .then(([statusData, previewData, messagePreview]) => {
        if (cancelled) return;
        setInvoiceStatus(statusData);
        setPreview(previewData ?? null);
        const row =
          statusData.tmInvoiceRows.find((r) => r.tmNo === selected.tmNo) ??
          null;
        const amt = row?.invoiceAmount ?? row?.defaultInvoiceAmount ?? null;
        setInvoiceAmount(amt != null ? String(amt) : "");
        setInvoiceDate(
          parseDateForHearingInput(row?.invoiceDate ?? statusData.invoiceDate)
        );
        setIncludeInvoiceNo(
          Boolean(row?.includeInvoiceNo ?? statusData.includeInvoiceNo)
        );
        if (messagePreview?.paymentMessage) {
          setPaymentMessage(messagePreview.paymentMessage);
        }
        if (messagePreview?.emailPreview) {
          setEmailPreview(messagePreview.emailPreview);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load TM-M before Hearing Invoice"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, selected.tmNo]);

  const persist =
    async (): Promise<TmMBeforeHearingInvoiceStatus | null> => {
      const amt = Number(invoiceAmount);
      if (!Number.isFinite(amt) || amt <= 0) {
        toast.error("Invoice Amount must be a positive number");
        return null;
      }
      if (!invoiceDate) {
        toast.error("Date of Invoice is required");
        return null;
      }
      const updated = await saveTmMBeforeHearingInvoice(leadId, [
        {
          tmNo: selected.tmNo,
          invoiceAmount: amt,
          invoiceDate: formatDateDDMMYYYYForApi(invoiceDate),
          includeInvoiceNo,
        },
      ]);
      setInvoiceStatus(updated);
      return updated;
    };

  const refreshPaymentMessagePreview = async () => {
    if (!leadId) return;
    setPaymentMessageLoading(true);
    try {
      const res = await getTmMBeforeHearingInvoicePaymentMessagePreview(
        leadId,
        [selected.tmNo]
      );
      setPaymentMessage(res.paymentMessage ?? "");
      setEmailPreview(res.emailPreview ?? null);
    } catch {
      // Non-critical — keep existing values.
    } finally {
      setPaymentMessageLoading(false);
    }
  };

  const runGenerate = async (): Promise<string | null> => {
    const saved = await persist();
    if (!saved) return null;
    const { s3Key, downloadUrl } = await generateTmMBeforeHearingInvoicePdf(
      leadId,
      [selected.tmNo]
    );
    setInvoiceStatus((prev) =>
      prev
        ? {
            ...prev,
            tmMBeforeHearingInvoicePdfKey: s3Key,
            tmMBeforeHearingInvoicePdfUrl: downloadUrl,
          }
        : prev
    );
    return downloadUrl;
  };

  const handleGeneratePdf = async () => {
    if (!leadId || generating) return;
    setGenerating(true);
    onSavingChange?.(true);
    try {
      const url = await runGenerate();
      if (!url) return;
      toast.success("TM-M before Hearing Invoice PDF generated");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to generate PDF"
      );
    } finally {
      setGenerating(false);
      onSavingChange?.(false);
    }
  };

  const ensurePdf = async (): Promise<string | null> => {
    const existing = invoiceStatus?.tmMBeforeHearingInvoicePdfUrl;
    if (existing) return existing;
    if (!leadId) return null;
    try {
      return await runGenerate();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to generate PDF"
      );
      return null;
    }
  };

  const handleDownload = async () => {
    if (!leadId || downloading) return;
    setDownloading(true);
    try {
      const url = await ensurePdf();
      if (!url) return;
      const name = "Invoice for TM-M before Hearing.pdf";
      const result = await downloadFileFromUrlWithFallback(url, name);
      if (result === "local") {
        toast.success("PDF saved to your device.");
      } else {
        toast.info(
          "Opened the PDF in a new tab. Use Save or Save as to store it locally."
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const handlePreview = async () => {
    if (!leadId) return;
    const url = await ensurePdf();
    if (!url) return;
    setPreviewTab("details");
    setPreviewNumPages(null);
    setPreviewModalOpen(true);
  };

  const closePreviewModal = () => {
    setPreviewModalOpen(false);
    setPreviewNumPages(null);
    setPreviewTab("details");
  };

  const isGated = !!invoiceStatus && invoiceStatus.isActive === false;

  useEffect(() => {
    onGatedChange?.(isGated);
    return () => {
      onGatedChange?.(false);
    };
  }, [isGated, onGatedChange]);

  // Register the Progress (skip) handler with the dashboard footer so the
  // Progress button shows up next to "Save and Continue" — matches the
  // Invoice for TM-M after Hearing pattern (InvoiceForTmMAfterHearingPanel).
  useEffect(() => {
    if (!onProgressActionChange) return;
    if (!leadId) {
      onProgressActionChange(null);
      return;
    }
    const run = async () => {
      try {
        await skipStep(
          leadId,
          NewApplicationStep.S3_03d_viii_a_SaveInvoiceTmMBeforeHearing
        );
        toast.success("Step skipped successfully.");
        onAdvance();
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to skip step"
        );
      }
    };
    onProgressActionChange(run);
    return () => onProgressActionChange(null);
  }, [leadId, onAdvance, onProgressActionChange]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || sending) return;

    if (isGated) {
      // Confirmation != "TM-M for Postponement" — nothing to save here, just advance.
      onAdvance();
      return;
    }

    if (hasSent) {
      // Already sent — Save & Continue just persists and advances.
      onSavingChange?.(true);
      try {
        const saved = await persist();
        if (!saved) return;
        toast.success("TM-M before Hearing Invoice saved");
        onAdvance();
      } catch (err) {
        toast.error(
          err instanceof Error
            ? err.message
            : "Failed to save TM-M before Hearing Invoice"
        );
      } finally {
        onSavingChange?.(false);
      }
      return;
    }

    // First-time send: save → ensure PDF exists → refresh preview → open confirm modal.
    setSending(true);
    onSavingChange?.(true);
    try {
      const saved = await persist();
      if (!saved) return;
      const pdfUrl = await ensurePdf();
      if (!pdfUrl) return;
      await refreshPaymentMessagePreview();
      setConfirmMode("send");
      setShowConfirmModal(true);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save TM-M before Hearing Invoice"
      );
    } finally {
      setSending(false);
      onSavingChange?.(false);
    }
  };

  const handleConfirmProceed = async () => {
    if (!leadId) return;
    setShowConfirmModal(false);
    setSending(true);
    onSavingChange?.(true);
    try {
      const [invoiceResult, paymentResult] = await Promise.allSettled([
        sendTmMBeforeHearingInvoiceToClient(leadId, {
          sendWhatsApp: true,
          sendEmail: true,
          tmNos: [selected.tmNo],
        }),
        sendTmMBeforeHearingInvoicePaymentMessage(leadId, [selected.tmNo]),
      ]);
      const invoiceOk = invoiceResult.status === "fulfilled";
      const paymentOk = paymentResult.status === "fulfilled";
      const invoiceErr =
        invoiceResult.status === "rejected"
          ? invoiceResult.reason instanceof Error
            ? invoiceResult.reason.message
            : "Failed to send TM-M before Hearing Invoice"
          : null;
      const paymentErr =
        paymentResult.status === "rejected"
          ? paymentResult.reason instanceof Error
            ? paymentResult.reason.message
            : "Failed to send payment message"
          : null;
      const isResend = confirmMode === "resend";

      if (invoiceOk && paymentOk) {
        onSent();
        toast.success(
          isResend
            ? "TM-M before Hearing Invoice resent to client."
            : "TM-M before Hearing Invoice sent to client."
        );
        onAdvance();
      } else if (invoiceOk && !paymentOk) {
        onSent();
        toast.warning(
          `Invoice sent, but payment message failed: ${paymentErr}`
        );
        onAdvance();
      } else if (!invoiceOk && paymentOk) {
        toast.warning(
          `Payment message sent, but sending invoice failed: ${invoiceErr}`
        );
      } else {
        toast.error(invoiceErr ?? "Failed to send TM-M before Hearing Invoice");
      }
    } finally {
      setSending(false);
      onSavingChange?.(false);
    }
  };

  const handleResend = async () => {
    if (!leadId || sending) return;
    setSending(true);
    onSavingChange?.(true);
    try {
      const saved = await persist();
      if (!saved) return;
      const pdfUrl = await ensurePdf();
      if (!pdfUrl) return;
      await refreshPaymentMessagePreview();
      setConfirmMode("resend");
      setShowConfirmModal(true);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save TM-M before Hearing Invoice"
      );
    } finally {
      setSending(false);
      onSavingChange?.(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-[#6D6D6D] py-5">Loading…</p>;
  }

  // Guard — BE returns isActive=false unless 3D(vii) Confirmation = POSTPONE.
  if (isGated) {
    return (
      <form
        id={EXAMINATION_REPORT_HAO_TMM_INVOICE_FORM_ID}
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col gap-3 py-5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          This step is only available when{" "}
          <span className="font-medium text-[#222222]">
            Confirmation from Client for Hearing
          </span>{" "}
          is set to{" "}
          <span className="font-medium text-[#222222]">
            TM-M for Postponement
          </span>{" "}
          for the selected application. Click "Skip &amp; Continue" to proceed
          to the next step.
        </p>
      </form>
    );
  }

  const hasPdf = !!invoiceStatus?.tmMBeforeHearingInvoicePdfUrl;
  const noOfApps = invoiceStatus?.noOfAppsForTmMBeforeHearing
    ? String(invoiceStatus.noOfAppsForTmMBeforeHearing)
    : "1";
  const dateOfHearingDisplay = selected.dateOfHearing ?? "";
  const addressee = preview?.addressee ?? "";
  const formOfAddress = preview?.formOfAddress ?? "";

  return (
    <form
      id={EXAMINATION_REPORT_HAO_TMM_INVOICE_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-5 self-stretch pt-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="flex flex-col gap-1">
          <div
            className="flex flex-row items-center gap-1"
            style={{ height: "20px" }}
          >
            <label
              className="text-[#222222]"
              style={{
                fontSize: "12px",
                lineHeight: "20px",
                fontWeight: 400,
              }}
            >
              Invoice Amount for TM-M before Hearing
            </label>
            <span className="relative inline-flex items-center group">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
                className="cursor-help"
              >
                <circle
                  cx="8"
                  cy="8"
                  r="6.5"
                  stroke="#666666"
                  strokeWidth="1.25"
                />
                <path
                  d="M8 7.5 V11"
                  stroke="#666666"
                  strokeWidth="1.25"
                  strokeLinecap="round"
                />
                <circle cx="8" cy="5.33" r="0.6" fill="#666666" />
              </svg>
              <span
                role="tooltip"
                className="hidden group-hover:flex absolute z-20 left-0 top-[24px] flex-row justify-center items-center rounded-[6px] text-white"
                style={{
                  width: "474px",
                  maxWidth: "calc(100vw - 40px)",
                  height: "68px",
                  padding: "4px 10px",
                  gap: "10px",
                  background: "#565656",
                }}
              >
                <span
                  className="block"
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontStyle: "normal",
                    fontWeight: 400,
                    fontSize: "12px",
                    lineHeight: "20px",
                    color: "#FFFFFF",
                  }}
                >
                  Invoice Amount for TM-M before Hearing : The charges will be
                  Rs.1500/- per Application Number (TM No.). That is 1500 ×
                  3ai) No. of applications for TM-M before Hearing.
                </span>
              </span>
            </span>
          </div>
          <input
            type="text"
            inputMode="decimal"
            value={invoiceAmount}
            onChange={(e) => {
              const raw = e.target.value.replace(/[^\d.]/g, "");
              const parts = raw.split(".");
              const v =
                parts.length > 1
                  ? `${parts[0]}.${parts.slice(1).join("")}`
                  : raw;
              setInvoiceAmount(v);
            }}
            placeholder="Number"
            className="box-border w-full px-2.5 py-2 h-9 text-xs leading-5 bg-white border border-[#EEEEEE] rounded-[6px] focus:outline-none focus:ring-1 focus:ring-[#222222] placeholder:text-[#6D6D6D]"
          />
        </div>
        <ReadOnlyTextField
          label="Invoice No. for TM-M before Hearing"
          value={invoiceStatus?.invoiceNo ?? ""}
          placeholder="Auto-generated on save"
        />
        <IncludeInvoiceNoCheckbox
          id="tmm-before-hearing-include-invoice-no"
          checked={includeInvoiceNo}
          onChange={setIncludeInvoiceNo}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="flex flex-col gap-1">
          <label
            className="text-[#222222]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            Date of Invoice for TM-M before Hearing
          </label>
          <DateTimePicker
            value={invoiceDate}
            onChange={(d) => setInvoiceDate(d)}
          />
        </div>
        <div className="hidden md:block" aria-hidden="true" />
      </div>
      <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2.5">
        <div
          className="flex flex-row items-center gap-2.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] pl-4 pr-2 py-2 min-h-[44px] sm:h-11"
        >
          <img
            src={invoiceGenerate}
            alt=""
            className="w-[22px] h-[22px] flex-shrink-0"
          />
          <button
            type="button"
            onClick={() => void handleGeneratePdf()}
            disabled={generating}
            className="flex flex-row items-center justify-center min-h-[40px] h-9 px-4 rounded-[6px] bg-[#222222] text-[12px] md:text-xs font-medium text-white hover:bg-[#333333] transition-colors shrink-0 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            {generating
              ? "Generating..."
              : hasPdf
                ? "Regenerate PDF"
                : "Generate PDF"}
          </button>
        </div>
        <button
          type="button"
          onClick={() => void handleDownload()}
          disabled={downloading || generating}
          className="flex flex-row items-center justify-center min-h-[44px] sm:h-11 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] md:text-xs font-medium text-[#222222] hover:bg-[#F7F7F7] transition-colors shrink-0 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          {downloading ? "Downloading…" : "Download"}
        </button>
        <button
          type="button"
          onClick={() => void handlePreview()}
          disabled={generating}
          className="flex flex-row items-center justify-center min-h-[44px] sm:h-11 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] md:text-xs font-medium text-[#222222] hover:bg-[#F7F7F7] transition-colors shrink-0 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          Preview
        </button>
      </div>

      {/* Phone number + optional Resend */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="tel"
          inputMode="numeric"
          value={phoneNumber}
          readOnly
          placeholder="e.g. +919876543210"
          className="w-full min-w-0 sm:w-[200px] h-9 px-[10px] py-2 text-[12px] text-[#222] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] placeholder:text-[#6D6D6D]"
        />
        {hasSent && (
          <button
            type="button"
            disabled={sending}
            onClick={() => void handleResend()}
            className="inline-flex items-center justify-center h-9 px-4 text-[12px] font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            {sending ? "Sending…" : "Resend Message"}
          </button>
        )}
      </div>

      <ConfirmMessageModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title={
          confirmMode === "resend"
            ? "Resend TM-M before Hearing Invoice to client"
            : "Confirm TM-M before Hearing Invoice to client"
        }
        previewSlot={
          <div className="flex flex-col gap-4 p-4">
            <section>
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                WhatsApp message (after invoice PDF)
              </p>
              {paymentMessageLoading ? (
                <p className="text-sm text-[#6d6d6d]">
                  Loading message preview…
                </p>
              ) : paymentMessage ? (
                renderInvoicePaymentMessageLines(paymentMessage)
              ) : (
                <p className="text-sm text-[#6D6D6D]">
                  Message preview not available.
                </p>
              )}
            </section>
            <div className="border-t border-dashed border-[#EEEEEE]" />
            <section>
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Email (with invoice PDF)
              </p>
              <HearingEmailPreviewBlock
                preview={emailPreview}
                loading={paymentMessageLoading}
                attachmentFallback={
                  hasPdf
                    ? {
                        filename: "Invoice_for_TM-M_before_Hearing.pdf",
                        url: invoiceStatus!.tmMBeforeHearingInvoicePdfUrl!,
                      }
                    : null
                }
              />
            </section>
          </div>
        }
        onConfirm={() => void handleConfirmProceed()}
        disableConfirm={sending}
      />

      {previewModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4"
          role="dialog"
          aria-modal="true"
          onClick={closePreviewModal}
        >
          <div
            className="relative flex h-[95vh] sm:h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 items-center justify-between border-b border-[#e3e3e3] px-3 py-3 sm:px-4">
              <span
                className="truncate pr-2 text-xs font-medium text-[#222] sm:text-sm"
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                Invoice for TM-M before Hearing.pdf
              </span>
              <div className="flex items-center gap-3 shrink-0">
                {invoiceStatus?.tmMBeforeHearingInvoicePdfUrl && (
                  <a
                    href={invoiceStatus.tmMBeforeHearingInvoicePdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-[#2E3037] underline underline-offset-2"
                  >
                    Open in new tab
                  </a>
                )}
                <button
                  type="button"
                  onClick={closePreviewModal}
                  className="flex size-10 shrink-0 items-center justify-center rounded text-[#6d6d6d] hover:bg-[#eee] hover:text-[#222] sm:size-8 touch-manipulation"
                  aria-label="Close preview"
                >
                  <HiOutlineX className="size-5" strokeWidth={2} />
                </button>
              </div>
            </div>

            <div
              className="flex shrink-0 gap-1 border-b border-[#e3e3e3] bg-white px-2 sm:px-3"
              role="tablist"
            >
              {(["details", "pdf", "message", "email"] as const).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  role="tab"
                  aria-selected={previewTab === tab}
                  onClick={() => setPreviewTab(tab)}
                  disabled={
                    tab === "pdf" &&
                    !invoiceStatus?.tmMBeforeHearingInvoicePdfUrl
                  }
                  className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation disabled:cursor-not-allowed disabled:opacity-50 ${
                    previewTab === tab
                      ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
                      : "text-[#6d6d6d] hover:text-[#222]"
                  }`}
                >
                  {tab === "details"
                    ? "Details"
                    : tab === "pdf"
                      ? "Generated PDF"
                      : tab === "message"
                        ? "WhatsApp Message"
                        : "Email"}
                </button>
              ))}
            </div>

            <div className="min-h-0 flex-1 overflow-auto bg-[#fafafa] p-2 sm:p-4 flex justify-center">
              {previewTab === "details" ? (
                <div
                  className="flex w-full max-w-3xl flex-col gap-3"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                      Invoice details
                    </p>
                    <div className="grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-3 gap-y-1.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2.5 text-[12px] leading-5">
                      <div className="font-medium text-[#222]">Addressee</div>
                      <div className="text-[#222]">:</div>
                      <div className="text-[#222] break-words min-w-0">
                        {addressee || <span className="text-[#6D6D6D]">—</span>}
                      </div>
                      <div className="font-medium text-[#222]">
                        Form of Address
                      </div>
                      <div className="text-[#222]">:</div>
                      <div className="text-[#222] break-words min-w-0">
                        {formOfAddress || (
                          <span className="text-[#6D6D6D]">—</span>
                        )}
                      </div>
                      {/*
                        Spec pg 88 opens each application's block in this window with Application
                        Number (TM No.) then Date of Filing, before Mark / Class / Date of Hearing.
                        Both were absent (review item 42), so the window never said which
                        application it described.
                      */}
                      <div className="font-medium text-[#222]">
                        Application Number (TM No.)
                      </div>
                      <div className="text-[#222]">:</div>
                      <div className="text-[#222] break-words min-w-0">
                        {formatTmNoForDisplay(selected.tmNo) || (
                          <span className="text-[#6D6D6D]">—</span>
                        )}
                      </div>
                      <div className="font-medium text-[#222]">
                        Date of Filing
                      </div>
                      <div className="text-[#222]">:</div>
                      <div className="text-[#222] break-words min-w-0">
                        {selected.applicationDate || (
                          <span className="text-[#6D6D6D]">—</span>
                        )}
                      </div>
                      <div className="font-medium text-[#222]">Mark</div>
                      <div className="text-[#222]">:</div>
                      <div className="text-[#222] break-words min-w-0">
                        {selected.mark || (
                          <span className="text-[#6D6D6D]">—</span>
                        )}
                      </div>
                      <div className="font-medium text-[#222]">
                        Selected Class
                      </div>
                      <div className="text-[#222]">:</div>
                      <div className="text-[#222] break-words min-w-0">
                        {selected.classNumber != null
                          ? String(selected.classNumber)
                          : <span className="text-[#6D6D6D]">—</span>}
                      </div>
                      <div className="font-medium text-[#222]">
                        Selected Type of Mark
                      </div>
                      <div className="text-[#222]">:</div>
                      <div className="text-[#222] break-words min-w-0">
                        {selected.typeOfMark || (
                          <span className="text-[#6D6D6D]">—</span>
                        )}
                      </div>
                      <div className="font-medium text-[#222]">
                        Date of Hearing
                      </div>
                      <div className="text-[#222]">:</div>
                      <div className="text-[#222] break-words min-w-0">
                        {dateOfHearingDisplay || (
                          <span className="text-[#6D6D6D]">—</span>
                        )}
                      </div>
                      <div className="font-medium text-[#222]">
                        No. of applications for TM-M before Hearing
                      </div>
                      <div className="text-[#222]">:</div>
                      <div className="text-[#222] break-words min-w-0">
                        {noOfApps}
                      </div>
                      <div className="font-medium text-[#222]">
                        Invoice Amount for TM-M before Hearing 1
                      </div>
                      <div className="text-[#222]">:</div>
                      <div className="text-[#222] break-words min-w-0">
                        {invoiceAmount
                          ? `Rs. ${Number(invoiceAmount).toLocaleString("en-IN")}/-`
                          : <span className="text-[#6D6D6D]">—</span>}
                      </div>
                      <div className="font-medium text-[#222]">
                        Invoice No. for TM-M before Hearing
                      </div>
                      <div className="text-[#222]">:</div>
                      <div className="text-[#222] break-words min-w-0">
                        {invoiceStatus?.invoiceNo?.trim() || (
                          <span className="text-[#6D6D6D]">
                            Auto-generated on save
                          </span>
                        )}
                      </div>
                      <div className="font-medium text-[#222]">
                        Date of Invoice for TM-M before Hearing 1
                      </div>
                      <div className="text-[#222]">:</div>
                      <div className="text-[#222] break-words min-w-0">
                        {invoiceDate
                          ? formatDateDDMMYYYYForApi(invoiceDate)
                          : <span className="text-[#6D6D6D]">—</span>}
                      </div>
                    </div>
                  </section>
                </div>
              ) : previewTab === "pdf" ? (
                invoiceStatus?.tmMBeforeHearingInvoicePdfUrl ? (
                  <Document
                    file={invoiceStatus.tmMBeforeHearingInvoicePdfUrl}
                    onLoadSuccess={({ numPages: n }) => setPreviewNumPages(n)}
                    loading={
                      <p className="text-sm text-[#6d6d6d]">Loading PDF…</p>
                    }
                    error={
                      <p className="text-sm text-red-600">Failed to load PDF.</p>
                    }
                    className="flex flex-col items-center gap-4"
                  >
                    {previewNumPages != null &&
                      Array.from({ length: previewNumPages }, (_, i) => (
                        <Page
                          key={i + 1}
                          pageNumber={i + 1}
                          width={Math.min(
                            800,
                            typeof window !== "undefined"
                              ? window.innerWidth - 48
                              : 800
                          )}
                          renderTextLayer
                          renderAnnotationLayer
                        />
                      ))}
                  </Document>
                ) : (
                  <p className="text-sm text-[#6D6D6D]">No PDF available.</p>
                )
              ) : previewTab === "message" ? (
                <div
                  className="w-full max-w-2xl rounded-lg border border-[#EEEEEE] bg-white p-4 shadow-sm sm:p-5"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                    Will be sent to client WhatsApp after the invoice PDF
                  </p>
                  {paymentMessageLoading ? (
                    <p className="text-sm text-[#6d6d6d]">
                      Loading message preview…
                    </p>
                  ) : paymentMessage ? (
                    renderInvoicePaymentMessageLines(paymentMessage)
                  ) : (
                    <p className="text-sm text-[#6D6D6D]">
                      Message preview not available.
                    </p>
                  )}
                </div>
              ) : (
                <div
                  className="w-full max-w-2xl rounded-lg border border-[#EEEEEE] bg-white p-4 shadow-sm sm:p-5"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                    Will be emailed to the client with the invoice PDF
                  </p>
                  <HearingEmailPreviewBlock
                    preview={emailPreview}
                    loading={paymentMessageLoading}
                    attachmentFallback={
                      hasPdf
                        ? {
                            filename: "Invoice_for_TM-M_before_Hearing.pdf",
                            url: invoiceStatus!.tmMBeforeHearingInvoicePdfUrl!,
                          }
                        : null
                    }
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </form>
  );
}

// ---------------------------------------------------------------------------
// 3D(ix) Payment from Client for TM-M before Hearing
// ---------------------------------------------------------------------------
function TmmPaymentCurrencyField({
  label,
  value,
  onChange,
  readOnly = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  readOnly?: boolean;
}) {
  return (
    <div
      className="flex flex-col items-start gap-1"
      style={{ flex: "1 1 0", minWidth: 0 }}
    >
      <label
        className="flex items-center text-[12px] font-normal leading-5 text-[#222222]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        {label}
      </label>
      <div
        className="flex flex-row justify-between items-center w-full h-9 px-0 gap-2.5"
        style={{
          background: readOnly ? "#F7F7F7" : "#FFFFFF",
          border: "1px solid #EEEEEE",
          borderRadius: "6px",
          boxSizing: "border-box",
        }}
      >
        <input
          type="text"
          inputMode="decimal"
          value={value}
          onChange={(e) => {
            if (readOnly) return;
            const cleaned = e.target.value.replace(/[^\d.]/g, "");
            const parts = cleaned.split(".");
            const next =
              parts.length > 1
                ? `${parts[0]}.${parts.slice(1).join("")}`
                : cleaned;
            onChange(next);
          }}
          onKeyDown={(e) => {
            if (readOnly) return;
            if (
              e.key.length === 1 &&
              !e.ctrlKey &&
              !e.metaKey &&
              !/[\d.]/.test(e.key)
            ) {
              e.preventDefault();
            }
          }}
          readOnly={readOnly}
          placeholder="00.00"
          className="flex-1 min-w-0 h-full pl-2.5 pr-0 py-0 bg-transparent text-[12px] font-normal leading-5 text-[#222222] placeholder:text-[#6D6D6D] border-0 outline-none focus:ring-0 disabled:bg-transparent disabled:cursor-default"
          style={{ fontFamily: "Inter, sans-serif" }}
        />
        <div
          className="flex flex-row justify-center items-center shrink-0 px-2.5 py-2 gap-2.5 h-9 min-w-[76px]"
          style={{ background: "#F7F7F7", borderRadius: "0 6px 6px 0" }}
        >
          <span className="text-[12px] font-normal leading-5 text-[#6D6D6D]">
            Rs(₹)
          </span>
          <img
            src={chevronDownIcon}
            alt=""
            className="w-4 h-4 shrink-0"
            aria-hidden
          />
        </div>
      </div>
    </div>
  );
}

function PaymentForTmMBeforeHearingPanel({
  leadId,
  selected,
  onAdvance,
  onSavingChange,
  onGatedChange,
}: {
  leadId: string;
  selected: ShowCauseHearingApplication;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
  onGatedChange?: (gated: boolean) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] =
    useState<TmMBeforeHearingInvoiceStatus | null>(null);
  const [savedCollected, setSavedCollected] = useState<number>(0);
  const [newInput, setNewInput] = useState<string>("");

  const isGated = !!status && status.isActive === false;

  useEffect(() => {
    onGatedChange?.(isGated);
    return () => {
      onGatedChange?.(false);
    };
  }, [isGated, onGatedChange]);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getTmMBeforeHearingInvoiceStatus(leadId, [selected.tmNo])
      .then((data) => {
        if (cancelled) return;
        setStatus(data);
        const row = data.tmInvoiceRows.find((r) => r.tmNo === selected.tmNo);
        const c = Number(row?.collectedAmount ?? data.collectedAmount ?? 0) || 0;
        setSavedCollected(c);
        setNewInput("");
      })
      .catch((err) => {
        if (!cancelled)
          toast.error(
            err instanceof Error ? err.message : "Failed to load Payment status"
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, selected.tmNo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (isGated) {
      onAdvance();
      return;
    }
    const parsed = parseFloat(newInput.replace(/[^\d.]/g, ""));
    const additional = isNaN(parsed) ? 0 : parsed;
    const total = savedCollected + additional;
    if (!Number.isFinite(total) || total < 0) {
      toast.error("Collected Amount must be a non-negative number");
      return;
    }
    onSavingChange?.(true);
    try {
      const updated = await saveTmMBeforeHearingCollectedAmount(leadId, [
        { tmNo: selected.tmNo, collectedAmount: total },
      ]);
      setStatus(updated);
      setSavedCollected(total);
      setNewInput("");
      toast.success("Payment recorded");
      onAdvance();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to record payment"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-[#6D6D6D] py-5">Loading…</p>;
  }

  if (isGated) {
    return (
      <form
        id={EXAMINATION_REPORT_HAO_TMM_PAYMENT_FORM_ID}
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col gap-3 py-5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          This step is only available when{" "}
          <span className="font-medium text-[#222222]">
            Confirmation from Client for Hearing
          </span>{" "}
          is set to{" "}
          <span className="font-medium text-[#222222]">
            TM-M for Postponement
          </span>
          . Click "Skip &amp; Continue" to proceed to the next step.
        </p>
      </form>
    );
  }

  const invoiceAmount = status?.invoiceAmount ?? 0;
  const additionalNum = parseFloat(newInput.replace(/[^\d.]/g, "")) || 0;
  const outstanding = Math.max(0, invoiceAmount - savedCollected - additionalNum);
  const outstandingFormatted = outstanding.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  const invoiceFormatted = invoiceAmount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return (
    <form
      id={EXAMINATION_REPORT_HAO_TMM_PAYMENT_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col items-start gap-[22px] w-full pt-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col sm:flex-row items-start sm:items-start gap-5 w-full">
        <TmmPaymentCurrencyField
          label="Invoice Amount for TM-M before Hearing"
          value={invoiceFormatted}
          onChange={() => {}}
          readOnly
        />
        <div
          className="flex flex-col gap-1"
          style={{ flex: "1 1 0", minWidth: 0 }}
        >
          <TmmPaymentCurrencyField
            label="Collected Amount for TM-M before Hearing"
            value={newInput}
            onChange={setNewInput}
          />
          {savedCollected > 0 && (
            <span className="text-[11px] leading-4 text-[#6D6D6D]">
              Already collected: Rs.{" "}
              {savedCollected.toLocaleString("en-IN", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })}
              /-
            </span>
          )}
        </div>
      </div>

      <div
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-3 w-full px-4 sm:px-5 py-4 sm:py-0 min-h-[60px] sm:h-[60px]"
        style={{ background: "#222222", borderRadius: "8px" }}
      >
        <span className="text-[12px] sm:text-[14px] font-normal leading-5 text-white/90">
          Outstanding Amount for TM-M before Hearing
        </span>
        <span className="text-[18px] sm:text-[22px] font-bold leading-8 sm:leading-10 text-white whitespace-nowrap">
          Rs. {outstandingFormatted}/-
        </span>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// 3D(x) File TM-M before Hearing
// ---------------------------------------------------------------------------
function FileTmMBeforeHearingPanel({
  leadId,
  selected,
  onAdvance,
  onSavingChange,
}: {
  leadId: string;
  selected: ShowCauseHearingApplication;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<TmMBeforeHearingFiledStatus | null>(
    null
  );
  const [dateOfFiling, setDateOfFiling] = useState<Date | null>(null);
  const [pendingName, setPendingName] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [previewModal, setPreviewModal] = useState<
    { url: string; name: string; kind: FilePreviewKind } | null
  >(null);

  const selectedTmNoNorm = (selected.tmNo ?? "").trim();
  const selectedRow: TmMBeforeHearingFiledApplication | null =
    status?.applications.find(
      (a) => (a.tmNo ?? "").trim() === selectedTmNoNorm
    ) ?? null;

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getTmMBeforeHearingFiledStatus(leadId)
      .then((data) => {
        if (cancelled) return;
        setStatus(data);
        const row = data.applications.find(
          (a) => (a.tmNo ?? "").trim() === (selected.tmNo ?? "").trim()
        );
        setDateOfFiling(
          parseDateForHearingInput(row?.dateOfTmMBeforeHearingFiled ?? null)
        );
      })
      .catch((err) => {
        if (!cancelled)
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load File TM-M status"
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, selected.tmNo]);

  const uploadFile = async (file: File) => {
    if (!leadId || uploading) return;
    setUploading(true);
    setPendingName(file.name);
    try {
      const [presigned] = await generateUploadPresignedUrls(
        [{ filename: file.name, mimeType: file.type || "application/pdf" }],
        "private",
        TM_M_BEFORE_HEARING_FILED_FOLDER
      );
      const putRes = await fetch(presigned.url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "application/pdf" },
      });
      if (!putRes.ok) throw new Error("Upload failed");
      const updated = await saveTmMBeforeHearingFiled(leadId, [
        {
          tmNo: selected.tmNo,
          dateOfTmMBeforeHearingFiled: dateOfFiling
            ? formatDateDDMMYYYYForApi(dateOfFiling)
            : undefined,
          tmMBeforeHearingFileKey: presigned.key,
          tmMBeforeHearingFilename: file.name,
        },
      ]);
      setStatus(updated);
      setPendingName(null);
      toast.success("TM-M document uploaded");
    } catch (err) {
      setPendingName(null);
      toast.error(
        err instanceof Error ? err.message : "Failed to upload TM-M document"
      );
    } finally {
      setUploading(false);
    }
  };

  const openPreview = () => {
    const url = selectedRow?.tmMBeforeHearingFileUrl;
    if (!url) {
      toast.error("Upload the file first");
      return;
    }
    const name =
      selectedRow?.tmMBeforeHearingFilename ?? `TM-M ${selected.tmNo}`;
    setPreviewModal({ url, name, kind: detectPreviewKind(name) });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (!dateOfFiling) {
      toast.error("Date of Filing of TM-M before Hearing is required");
      return;
    }
    if (!selectedRow?.tmMBeforeHearingFileKey) {
      toast.error("Upload the TM-M document first");
      return;
    }
    onSavingChange?.(true);
    try {
      const updated = await saveTmMBeforeHearingFiled(leadId, [
        {
          tmNo: selected.tmNo,
          dateOfTmMBeforeHearingFiled: formatDateDDMMYYYYForApi(dateOfFiling),
          tmMBeforeHearingFileKey: selectedRow.tmMBeforeHearingFileKey,
          tmMBeforeHearingFilename: selectedRow.tmMBeforeHearingFilename,
        },
      ]);
      setStatus(updated);
      toast.success("File TM-M before Hearing saved");
      onAdvance();
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save File TM-M before Hearing"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-[#6D6D6D] py-5">Loading…</p>;
  }

  // Review item 62 — the status endpoint only returns the marks whose 3D(vii) answer is
  // POSTPONE, so a missing row means this mark is not eligible. Previously the form still
  // rendered and every upload failed with the backend's 400; now the step gates up front.
  if (!selectedRow) {
    return (
      <div
        className="flex flex-col gap-3 py-5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          This step is only available when{" "}
          <span className="font-medium text-[#222222]">
            Confirmation from Client for Hearing
          </span>{" "}
          is{" "}
          <span className="font-medium text-[#222222]">
            TM-M for Postponement
          </span>{" "}
          for this application.
        </p>
      </div>
    );
  }

  const fileLabel =
    pendingName ?? selectedRow?.tmMBeforeHearingFilename ?? null;

  return (
    <form
      id={EXAMINATION_REPORT_HAO_TMM_FILE_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-5 self-stretch pt-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="flex flex-col gap-1">
          <label
            className="text-[#222222]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            Date of Filing of TM-M before Hearing
          </label>
          <DateTimePicker
            value={dateOfFiling}
            onChange={(d) => setDateOfFiling(d)}
          />
        </div>
        <div className="hidden md:block" aria-hidden="true" />
      </div>

      <div className="flex flex-col gap-3">
        <span
          className="text-[#222222]"
          style={{ fontSize: "14px", lineHeight: "20px", fontWeight: 500 }}
        >
          Upload Document
        </span>

        <div
          className="flex flex-row items-center justify-between p-3 rounded-[10px] cursor-pointer transition-colors"
          style={{
            background: dragging ? "#F7F7F7" : "#FFFFFF",
            border: "1px dashed #D8DAEB",
          }}
          onClick={() => {
            const input = document.getElementById(
              `tmm-filed-pdf-${selected.tmNo}`
            ) as HTMLInputElement | null;
            input?.click();
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setDragging(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            const f = e.dataTransfer.files?.[0];
            if (f) void uploadFile(f);
          }}
        >
          <input
            id={`tmm-filed-pdf-${selected.tmNo}`}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void uploadFile(f);
            }}
          />
          <div className="flex flex-row items-center gap-3 min-w-0">
            <img
              src={uploadDropzoneGraphic}
              alt=""
              className="w-8 h-8 flex-shrink-0"
            />
            <span
              className="text-[#101111] truncate"
              style={{
                fontSize: "12px",
                lineHeight: "100%",
                fontWeight: 500,
                letterSpacing: "0.01em",
              }}
            >
              {fileLabel ?? "Drag and drop your files"}
            </span>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              const input = document.getElementById(
                `tmm-filed-pdf-${selected.tmNo}`
              ) as HTMLInputElement | null;
              input?.click();
            }}
            disabled={uploading}
            className="inline-flex items-center justify-center h-9 px-4 text-sm font-medium leading-4 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] hover:bg-gray-50 disabled:opacity-60 whitespace-nowrap"
            style={{
              boxShadow: "0px 5px 20px rgba(0, 0, 0, 0.02)",
              fontFamily: "Inter, sans-serif",
              letterSpacing: "0.04em",
              textTransform: "capitalize",
            }}
          >
            {uploading ? "Uploading…" : "Browse"}
          </button>
        </div>

        {selectedRow?.tmMBeforeHearingFileUrl && (
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={openPreview}
              className="inline-flex items-center justify-center h-8 px-3 text-xs font-medium text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[8px] hover:bg-gray-50"
            >
              Preview
            </button>
            <a
              href={selectedRow.tmMBeforeHearingFileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center h-8 px-3 text-xs font-medium text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[8px] hover:bg-gray-50"
            >
              Download
            </a>
          </div>
        )}
      </div>

      <FilePreviewModal
        open={!!previewModal}
        onClose={() => setPreviewModal(null)}
        name={previewModal?.name ?? ""}
        url={previewModal?.url || undefined}
        kind={previewModal?.kind}
      />
    </form>
  );
}

// ---------------------------------------------------------------------------
// 3D(xi) Inform Client about Filing of TM-M before Hearing
// ---------------------------------------------------------------------------
function InformClientTmMBeforeHearingPanel({
  leadId,
  selected,
  hasSent,
  onSent,
  onAdvance,
  onSavingChange,
  resending,
  onResend,
}: {
  leadId: string;
  selected: ShowCauseHearingApplication;
  hasSent: boolean;
  onSent: () => void;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
  resending: boolean;
  onResend: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string>("");

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getTmMBeforeHearingInformClientPreview(leadId, [selected.tmNo])
      .then(({ message: m }) => {
        if (!cancelled) setMessage(m);
      })
      .catch((err) => {
        if (!cancelled) {
          setMessage("");
          toast.error(
            err instanceof Error ? err.message : "Failed to load preview"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, selected.tmNo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSent) {
      onAdvance();
      return;
    }
    if (!message.trim()) {
      toast.error(
        "No message available. File the TM-M document first."
      );
      return;
    }
    onSavingChange?.(true);
    try {
      await sendTmMBeforeHearingInformClient(leadId, [selected.tmNo]);
      toast.success("Inform Client (TM-M filing) sent to client");
      onSent();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send message");
    } finally {
      onSavingChange?.(false);
    }
  };

  return (
    <form
      id={EXAMINATION_REPORT_HAO_TMM_INFORM_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-5 self-stretch pt-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <span
        className="text-[#222222]"
        style={{ fontSize: "16px", lineHeight: "20px", fontWeight: 400 }}
      >
        Message Preview
      </span>
      <div
        className="self-stretch bg-white border border-[#EEEEEE] rounded-[6px]"
        style={{ padding: "14px" }}
      >
        {loading ? (
          <span
            className="text-[#6D6D6D]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            Loading preview…
          </span>
        ) : message ? (
          <p
            className="text-[#222222] whitespace-pre-line"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            {renderWhatsAppFormatting(message)}
          </p>
        ) : (
          <span
            className="text-[#6D6D6D]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            No preview available. File the TM-M document first.
          </span>
        )}
      </div>
      {hasSent && (
        <div className="flex flex-row items-center gap-3">
          <button
            type="button"
            disabled={resending}
            onClick={onResend}
            className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {resending ? "Resending…" : "Resend Message"}
          </button>
        </div>
      )}
    </form>
  );
}

// ---------------------------------------------------------------------------
// 3D(xii) Payment from Client for Hearing
// ---------------------------------------------------------------------------
function PaymentFromClientHearingPanel({
  leadId,
  selected,
  onAdvance,
  onSavingChange,
  onProgressActionChange,
}: {
  leadId: string;
  selected: ShowCauseHearingApplication;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
  onProgressActionChange?: (run: (() => Promise<void>) | null) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<HearingInvoiceStatus | null>(null);
  const [savedCollected, setSavedCollected] = useState<number>(0);
  const [collected, setCollected] = useState<string>("");

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getHearingInvoiceStatus(leadId, [selected.tmNo])
      .then((data) => {
        if (cancelled) return;
        setStatus(data);
        const row = data.tmInvoiceRows.find((r) => r.tmNo === selected.tmNo);
        const saved = Number(
          row?.collectedAmount ?? data.collectedAmount ?? 0
        );
        setSavedCollected(Number.isFinite(saved) ? saved : 0);
        setCollected("");
      })
      .catch((err) => {
        if (!cancelled)
          toast.error(
            err instanceof Error ? err.message : "Failed to load Payment status"
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, selected.tmNo]);

  const invoiceAmount = status?.invoiceAmount ?? 0;
  const thisPayment = Number(collected) || 0;
  const totalCollected = savedCollected + thisPayment;
  const outstanding = Math.max(0, invoiceAmount - totalCollected);

  // Register the Progress (skip) handler with the dashboard footer so the
  // Progress button shows up next to "Save and Continue" — matches the
  // Payment-Reply-to-Objection pattern (PaymentPanel above).
  useEffect(() => {
    if (!onProgressActionChange) return;
    if (!leadId) {
      onProgressActionChange(null);
      return;
    }
    const run = async () => {
      try {
        await skipStep(
          leadId,
          NewApplicationStep.S3_03d_xii_PaymentFromClientHearing
        );
        toast.success("Step skipped successfully.");
        onAdvance();
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to skip step"
        );
      }
    };
    onProgressActionChange(run);
    return () => onProgressActionChange(null);
  }, [leadId, onAdvance, onProgressActionChange]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (!Number.isFinite(thisPayment) || thisPayment < 0) {
      toast.error("Collected Amount must be a non-negative number");
      return;
    }
    if (thisPayment <= 0) {
      toast.error(
        "Collected amount should be greater than 0. Use Progress to skip this step."
      );
      return;
    }
    if (invoiceAmount > 0 && savedCollected >= invoiceAmount) {
      toast.error("This invoice is already fully paid.");
      return;
    }
    if (invoiceAmount > 0 && totalCollected > invoiceAmount) {
      toast.error(
        "Collected amount cannot exceed the outstanding invoice balance."
      );
      return;
    }
    onSavingChange?.(true);
    try {
      const updated = await saveHearingCollectedAmount(leadId, [
        { tmNo: selected.tmNo, collectedAmount: totalCollected },
      ]);
      setStatus(updated);
      setSavedCollected(totalCollected);
      setCollected("");
      toast.success("Payment recorded");
      onAdvance();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to record payment"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-[#6D6D6D] py-5">Loading…</p>;
  }

  return (
    <form
      id={EXAMINATION_REPORT_HAO_PAYMENT_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-5 self-stretch pt-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="flex flex-col gap-1">
          <label
            className="text-[#222222]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            Invoice Amount for Hearing
          </label>
          <div
            className="flex flex-row justify-between items-center bg-white border border-[#EEEEEE] rounded-[6px] overflow-hidden"
            style={{ height: "36px" }}
          >
            <span
              className="px-2.5 text-[#6D6D6D] flex-1"
              style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
            >
              {invoiceAmount ? invoiceAmount.toLocaleString("en-IN") : "00.00"}
            </span>
            <div
              className="flex items-center justify-center bg-[#F7F7F7] px-2.5"
              style={{ height: "36px", width: "76px", borderRadius: "0 6px 6px 0" }}
            >
              <span
                className="text-[#6D6D6D]"
                style={{
                  fontSize: "12px",
                  lineHeight: "20px",
                  fontWeight: 400,
                }}
              >
                Rs(₹)
              </span>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label
            className="text-[#222222]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            Collected Amount for Hearing
          </label>
          <div
            className="flex flex-row justify-between items-center bg-white border border-[#EEEEEE] rounded-[6px] overflow-hidden"
            style={{ height: "36px" }}
          >
            <input
              type="text"
              inputMode="decimal"
              value={collected}
              onChange={(e) => {
                const cleaned = e.target.value.replace(/[^\d.]/g, "");
                const parts = cleaned.split(".");
                const next =
                  parts.length > 1
                    ? `${parts[0]}.${parts.slice(1).join("")}`
                    : cleaned;
                setCollected(next);
              }}
              onKeyDown={(e) => {
                if (
                  e.key.length === 1 &&
                  !e.ctrlKey &&
                  !e.metaKey &&
                  !/[\d.]/.test(e.key)
                ) {
                  e.preventDefault();
                }
              }}
              placeholder="00.00"
              className="px-2.5 flex-1 bg-transparent focus:outline-none text-[#6D6D6D] placeholder:text-[#6D6D6D]"
              style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
            />
            <div
              className="flex items-center justify-center bg-[#F7F7F7] px-2.5"
              style={{ height: "36px", width: "76px", borderRadius: "0 6px 6px 0" }}
            >
              <span
                className="text-[#6D6D6D]"
                style={{
                  fontSize: "12px",
                  lineHeight: "20px",
                  fontWeight: 400,
                }}
              >
                Rs(₹)
              </span>
            </div>
          </div>
          {savedCollected > 0 && (
            <span className="text-[11px] leading-4 text-[#6D6D6D]">
              Already collected: Rs.{" "}
              {savedCollected.toLocaleString("en-IN", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })}
              /-
            </span>
          )}
        </div>
      </div>
      <div
        className="flex flex-row items-center justify-between bg-[#222222] rounded-[8px] px-5"
        style={{ minHeight: "60px" }}
      >
        <span
          className="text-white"
          style={{ fontSize: "14px", lineHeight: "20px", fontWeight: 400, opacity: 0.9 }}
        >
          Outstanding Amount
        </span>
        <span
          className="text-white"
          style={{ fontSize: "22px", lineHeight: "40px", fontWeight: 700 }}
        >
          : Rs. {outstanding.toLocaleString("en-IN")}/-
        </span>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// 3D(xiii) Stamp Paper details to Vendor for Hearing
// ---------------------------------------------------------------------------
function StampPaperVendorForHearingPanel({
  leadId,
  selected,
  hasSent,
  onSent,
  onAdvance,
  onSavingChange,
  resending,
  onResend,
  onGatedChange,
}: {
  leadId: string;
  selected: ShowCauseHearingApplication;
  hasSent: boolean;
  onSent: () => void;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
  resending: boolean;
  onResend: () => void;
  onGatedChange?: (gated: boolean) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<StampPaperForHearingStatus | null>(null);
  const [stampVendors, setStampVendors] = useState<StampVendorOption[]>([]);
  const [stampPapers, setStampPapers] = useState<string>("");
  const [vendorName, setVendorName] = useState<string>("");
  const [vendorPhone, setVendorPhone] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [previewLoading, setPreviewLoading] = useState(true);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setPreviewLoading(true);
    Promise.all([
      getStampPaperForHearingStatus(leadId, selected.tmNo),
      getStampPaperForHearingMessagePreview(leadId, [selected.tmNo]).catch(
        () => ({ message: "" })
      ),
      getStampVendors().catch((): StampVendorOption[] => []),
    ])
      .then(([statusData, previewData, vendors]) => {
        if (cancelled) return;
        setStatus(statusData);
        const mergedVendors =
          vendors.length > 0 ? vendors : (statusData.stampVendors ?? []);
        setStampVendors(mergedVendors);
        const row =
          statusData.applications?.find((a) => a.tmNo === selected.tmNo) ?? null;
        setStampPapers(
          row?.stampPapersForHearing != null
            ? String(row.stampPapersForHearing)
            : statusData.stampPapersForHearing != null
              ? String(statusData.stampPapersForHearing)
              : ""
        );
        const defaultVendor =
          mergedVendors.find((v) => v.isDefault) ??
          mergedVendors[0] ??
          null;
        setVendorName(
          row?.stampVendorNameForHearing ?? defaultVendor?.name ?? ""
        );
        setVendorPhone(
          row?.stampVendorPhoneForHearing ?? defaultVendor?.phone ?? ""
        );
        setMessage(previewData.message ?? "");
      })
      .catch((err) => {
        if (!cancelled)
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Stamp Paper for Hearing"
          );
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setPreviewLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, selected.tmNo]);

  // Auto-save + refresh preview when stamp count / vendor changes (debounced).
  useEffect(() => {
    if (!leadId) return;
    const n = Number(stampPapers);
    if (!Number.isInteger(n) || n < 1) return;
    if (!vendorName.trim() || !vendorPhone.trim()) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        await saveStampPaperForHearing(leadId, [
          {
            tmNo: selected.tmNo,
            stampPapersForHearing: n,
            stampVendorNameForHearing: vendorName.trim(),
            stampVendorPhoneForHearing: vendorPhone.trim(),
          },
        ]);
      } catch {
        /* save error — surface only on explicit submit */
      }
      try {
        const p = await getStampPaperForHearingMessagePreview(leadId, [
          selected.tmNo,
        ]);
        if (!cancelled) setMessage(p.message ?? "");
      } catch {
        if (!cancelled) setMessage("");
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [leadId, selected.tmNo, stampPapers, vendorName, vendorPhone]);

  const handleVendorQuickSelect = (value: string) => {
    if (value === "__none__") return;
    const match = stampVendors.find((v) => v.name === value);
    setVendorName(value);
    setVendorPhone(match?.phone ?? "");
  };

  const vendorQuickSelectValue =
    vendorName && stampVendors.some((v) => v.name === vendorName)
      ? vendorName
      : "__none__";

  const persist = async (): Promise<StampPaperForHearingStatus | null> => {
    const n = Number(stampPapers);
    if (!Number.isInteger(n) || n < 1) {
      toast.error("Number of Stamp Papers must be a positive integer");
      return null;
    }
    if (!vendorName.trim()) {
      toast.error("Stamp Vendor Name is required");
      return null;
    }
    if (!vendorPhone.trim()) {
      toast.error("Stamp Vendor phone is required");
      return null;
    }
    const updated = await saveStampPaperForHearing(leadId, [
      {
        tmNo: selected.tmNo,
        stampPapersForHearing: n,
        stampVendorNameForHearing: vendorName.trim(),
        stampVendorPhoneForHearing: vendorPhone.trim(),
      },
    ]);
    setStatus(updated);
    return updated;
  };

  const isGated = !!status && status.isActive === false;

  useEffect(() => {
    onGatedChange?.(isGated);
    return () => {
      onGatedChange?.(false);
    };
  }, [isGated, onGatedChange]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (isGated || hasSent) {
      onAdvance();
      return;
    }
    onSavingChange?.(true);
    try {
      const saved = await persist();
      if (!saved) return;
      await sendStampPaperForHearingToVendor(leadId, [selected.tmNo]);
      toast.success("Stamp Paper details sent to vendor");
      onSent();
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to send Stamp Paper details"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-[#6D6D6D] py-5">Loading…</p>;
  }

  if (isGated) {
    return (
      <form
        id={EXAMINATION_REPORT_HAO_STAMP_VENDOR_FORM_ID}
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col gap-3 py-5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          This step is only available when{" "}
          <span className="font-medium text-[#222222]">User Date</span> at filing
          was set to{" "}
          <span className="font-medium text-[#222222]">"Proposed to be used"</span>
          . Click "Skip &amp; Continue" to proceed to the next step.
        </p>
      </form>
    );
  }

  return (
    <form
      id={EXAMINATION_REPORT_HAO_STAMP_VENDOR_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-5 self-stretch pt-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="flex flex-col gap-1">
          <label
            className="text-[#222222]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            Number of Stamp Papers needed for Hearing<span className="text-red-500 ml-0.5">*</span>
          </label>
          <input
            type="number"
            inputMode="numeric"
            min="1"
            value={stampPapers}
            onChange={(e) => setStampPapers(e.target.value)}
            placeholder="Number"
            className="box-border w-full px-2.5 py-2 h-9 text-xs leading-5 bg-white border border-[#EEEEEE] rounded-[6px] focus:outline-none focus:ring-1 focus:ring-[#222222] placeholder:text-[#6D6D6D]"
          />
        </div>
        <div className="flex flex-col gap-1">
            <label
              className="text-[#222222]"
              style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
            >
              Quick select vendor (optional)
            </label>
            <Select
              value={vendorQuickSelectValue}
              onValueChange={handleVendorQuickSelect}
            >
              <SelectTrigger className="data-[placeholder]:text-[#6D6D6D]">
                <SelectValue placeholder="Select to autofill" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Not from list —</SelectItem>
                {stampVendors
                  .filter(
                    (v) => typeof v.name === "string" && v.name.trim().length > 0
                  )
                  .map((v) => (
                    <SelectItem key={v.name} value={v.name}>
                      {v.name}
                      {v.isDefault ? " (default)" : ""}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="flex flex-col gap-1">
          <label
            className="text-[#222222]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            Name of Stamp Vendor for Hearing<span className="text-red-500 ml-0.5">*</span>
          </label>
          <input
            type="text"
            value={vendorName}
            onChange={(e) => setVendorName(e.target.value)}
            placeholder="Vendor Name"
            className="box-border w-full px-2.5 py-2 h-9 text-xs leading-5 bg-white border border-[#EEEEEE] rounded-[6px] focus:outline-none focus:ring-1 focus:ring-[#222222] placeholder:text-[#6D6D6D]"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label
            className="text-[#222222]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            Phone of Stamp Vendor for Hearing
          </label>
          <input
            type="tel"
            inputMode="tel"
            value={vendorPhone}
            onChange={(e) => setVendorPhone(e.target.value)}
            placeholder="Phone number"
            className="box-border w-full px-2.5 py-2 h-9 text-xs leading-5 bg-white border border-[#EEEEEE] rounded-[6px] focus:outline-none focus:ring-1 focus:ring-[#222222] placeholder:text-[#6D6D6D]"
          />
        </div>
      </div>

      <FetchedDetailsPanel
        rows={[
          { label: "Applicant", value: status?.applicant ?? "" },
          { label: "Applicant Address", value: status?.applicantAddress ?? "" },
          { label: "Applicant Type", value: formatApplicantType(status?.applicantType) },
          { label: "User Date", value: status?.userDate ?? "" },
          { label: "Signatory's Name", value: status?.signatoryName ?? "" },
          { label: "Signatory's Designation", value: status?.signatoryDesignation ?? "" },
        ]}
      />

      {/* Message Preview */}
      <div className="flex flex-col gap-5">
        <span
          className="text-[#222222]"
          style={{ fontSize: "16px", lineHeight: "20px", fontWeight: 400 }}
        >
          Message Preview
        </span>
        <div
          className="self-stretch bg-white border border-[#EEEEEE] rounded-[6px]"
          style={{ padding: "14px" }}
        >
          {previewLoading ? (
            <span
              className="text-[#6D6D6D]"
              style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
            >
              Loading preview…
            </span>
          ) : message ? (
            <p
              className="text-[#222222] whitespace-pre-line"
              style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
            >
              {renderWhatsAppFormatting(message)}
            </p>
          ) : (
            <span
              className="text-[#6D6D6D]"
              style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
            >
              Preview will appear after Number of Stamp Papers and Vendor are
              filled.
            </span>
          )}
        </div>
      </div>

      {hasSent && (
        <div className="flex flex-row items-center gap-3">
          <button
            type="button"
            disabled={resending}
            onClick={onResend}
            className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {resending ? "Resending…" : "Resend Message"}
          </button>
        </div>
      )}
    </form>
  );
}

// ---------------------------------------------------------------------------
// 3D(xiv) Stamp Paper from Vendor for hearing — Amount Payable + Paid Yes/No
// ---------------------------------------------------------------------------
function StampPaperFromVendorForHearingPanel({
  leadId,
  selected,
  onAdvance,
  onSavingChange,
  onGatedChange,
}: {
  leadId: string;
  selected: ShowCauseHearingApplication;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
  onGatedChange?: (gated: boolean) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<StampPaperForHearingStatus | null>(null);
  const [amount, setAmount] = useState<string>("");
  const [paid, setPaid] = useState<boolean | null>(null);

  const isGated = !!status && status.isActive === false;

  useEffect(() => {
    onGatedChange?.(isGated);
    return () => {
      onGatedChange?.(false);
    };
  }, [isGated, onGatedChange]);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getStampPaperForHearingStatus(leadId, selected.tmNo)
      .then((data) => {
        if (cancelled) return;
        setStatus(data);
        const row =
          data.applications?.find((a) => a.tmNo === selected.tmNo) ?? null;
        const a =
          row?.amountPayableToStampVendorForHearing ??
          data.amountPayableToStampVendorForHearing;
        setAmount(a != null ? String(a) : "");
        setPaid(row?.paidToVendorForHearing ?? data.paidToVendorForHearing);
      })
      .catch((err) => {
        if (!cancelled)
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Stamp Paper from Vendor"
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, selected.tmNo]);

  const tmRow =
    status?.applications?.find((a) => a.tmNo === selected.tmNo) ?? null;
  const stampPapers =
    tmRow?.stampPapersForHearing ?? status?.stampPapersForHearing ?? null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (isGated) {
      onAdvance();
      return;
    }
    if (paid == null) {
      toast.error("Select Paid to Vendor (Yes or No)");
      return;
    }
    const trimmed = amount.trim();
    let parsedAmount: number | null = null;
    if (trimmed !== "") {
      const n = Number(trimmed);
      if (!Number.isFinite(n) || n < 0) {
        toast.error("Amount must be a non-negative number");
        return;
      }
      parsedAmount = n;
    }
    onSavingChange?.(true);
    try {
      const updated = await saveStampPaperPaymentToVendorHearing(leadId, [
        {
          tmNo: selected.tmNo,
          amountPayableToStampVendorForHearing: parsedAmount,
          paidToVendorForHearing: paid,
        },
      ]);
      setStatus(updated);
      toast.success("Stamp Paper payment to Vendor saved");
      onAdvance();
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save Stamp Paper payment"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-[#6D6D6D] py-5">Loading…</p>;
  }

  if (isGated) {
    return (
      <form
        id={EXAMINATION_REPORT_HAO_STAMP_FROM_VENDOR_FORM_ID}
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col gap-3 py-5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          This step is only available when{" "}
          <span className="font-medium text-[#222222]">User Date</span> at filing
          was set to{" "}
          <span className="font-medium text-[#222222]">"Proposed to be used"</span>
          . Click "Skip &amp; Continue" to proceed to the next step.
        </p>
      </form>
    );
  }

  return (
    <form
      id={EXAMINATION_REPORT_HAO_STAMP_FROM_VENDOR_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-5 self-stretch pt-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <ReadOnlyTextField
          label="Number of Stamp Papers needed for Hearing"
          value={stampPapers != null ? String(stampPapers) : ""}
          placeholder="Number"
        />
        <div className="flex flex-col gap-1">
          <label
            className="text-[#222222]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            Amount Payable to Stamp Vendor for hearing
          </label>
          <div
            className="box-border flex flex-row items-center justify-between px-2.5 py-2 h-9 bg-white border border-[#EEEEEE] rounded-[6px] focus-within:ring-1 focus-within:ring-[#222222]"
          >
            <span className="text-[#6D6D6D] text-xs leading-5">Rs.</span>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Amount"
              className="flex-1 ml-2 min-w-0 bg-transparent border-0 text-xs leading-5 text-[#222222] focus:outline-none placeholder:text-[#6D6D6D]"
            />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="flex flex-col gap-1">
          <label
            className="text-[#222222]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            Paid to Vendor
          </label>
          <Select
            value={paid == null ? undefined : paid ? "yes" : "no"}
            onValueChange={(v) => setPaid(v === "yes")}
          >
            <SelectTrigger className="data-[placeholder]:text-[#6D6D6D]">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="yes">Yes</SelectItem>
              <SelectItem value="no">No</SelectItem>
            </SelectContent>
          </Select>
          {paid === false ? (
            <p
              className="text-xs font-medium leading-5 text-[#222222] mt-0.5"
              style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
            >
              Status: Pending Payment
            </p>
          ) : paid === true ? (
            <p
              className="text-xs font-medium leading-5 text-[#222222] mt-0.5"
              style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
            >
              Status: Paid
            </p>
          ) : null}
        </div>
        <div className="hidden md:block" aria-hidden="true" />
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// "Process to next step" record-only panel — used by 3D(xv), 3D(xvii), 3D(xix), 3D(xx)
// ---------------------------------------------------------------------------
function ProcessToNextStepPanel({
  formId,
  message,
  onSubmit,
}: {
  formId: string;
  message: string;
  onSubmit: () => Promise<void> | void;
}) {
  return (
    <form
      id={formId}
      onSubmit={async (e) => {
        e.preventDefault();
        await onSubmit();
      }}
      className="flex flex-col items-center justify-center self-stretch pt-5 gap-[30px]"
      style={{ fontFamily: "Inter, sans-serif", minHeight: 336 }}
    >
      <img
        src={processToNextStepGraphic}
        alt=""
        width={220}
        height={220}
        className="flex-shrink-0"
      />
      <p
        className="text-center text-[#222222]"
        style={{ fontSize: "22px", lineHeight: "20px", fontWeight: 400 }}
      >
        {message}
      </p>
    </form>
  );
}

// ---------------------------------------------------------------------------
// 3D(xv) Documents from Client for hearing — record only
// ---------------------------------------------------------------------------
function DocumentsFromClientForHearingPanel({
  leadId,
  selected,
  onAdvance,
  onSavingChange,
  onProgressActionChange,
}: {
  leadId: string;
  selected: ShowCauseHearingApplication;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
  onProgressActionChange?: (run: (() => Promise<void>) | null) => void;
}) {
  const [recording, setRecording] = useState(false);

  const handleSubmit = async () => {
    if (!leadId || recording) return;
    setRecording(true);
    onSavingChange?.(true);
    try {
      await recordDocumentsFromClientHearing(leadId, [selected.tmNo]);
      toast.success("Documents from Client for hearing recorded");
      onAdvance();
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to record Documents from Client"
      );
    } finally {
      setRecording(false);
      onSavingChange?.(false);
    }
  };

  useEffect(() => {
    if (!onProgressActionChange) return;
    if (!leadId) {
      onProgressActionChange(null);
      return;
    }
    const run = async () => {
      try {
        await skipStep(
          leadId,
          NewApplicationStep.S3_03d_xv_DocumentsFromClientHearing
        );
        toast.success("Step skipped successfully.");
        onAdvance();
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to skip step"
        );
      }
    };
    onProgressActionChange(run);
    return () => onProgressActionChange(null);
  }, [leadId, onAdvance, onProgressActionChange]);

  return (
    <ProcessToNextStepPanel
      formId={EXAMINATION_REPORT_HAO_DOCS_FROM_CLIENT_FORM_ID}
      message="Process to next step"
      onSubmit={handleSubmit}
    />
  );
}

// ---------------------------------------------------------------------------
// 3D(xvi) Preparation of User Affidavit for hearing — complex form
// ---------------------------------------------------------------------------
function PreparationOfUserAffidavitForHearingPanel({
  leadId,
  selected,
  onAdvance,
  onSavingChange,
  onGatedChange,
}: {
  leadId: string;
  selected: ShowCauseHearingApplication;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
  onGatedChange?: (gated: boolean) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<UserAffidavitForHearingStatus | null>(
    null
  );
  const [item, setItem] = useState<UserAffidavitForHearingItem | null>(null);
  const [dateOfUa, setDateOfUa] = useState<Date | null>(null);
  const [userDate, setUserDate] = useState<Date | null>(null);
  const [turnover, setTurnover] = useState<UserAffidavitForHearingTurnover[]>(
    []
  );
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState<
    { url: string; name: string; kind: FilePreviewKind } | null
  >(null);
  // Review item 37 — the "view all the data" window required by spec pg 96.
  const [uaHearingReviewOpen, setUaHearingReviewOpen] = useState(false);

  const isGated = !!status && status.isActive === false;

  useEffect(() => {
    onGatedChange?.(isGated);
    return () => {
      onGatedChange?.(false);
    };
  }, [isGated, onGatedChange]);

  const reload = (cancelledRef: { cancelled: boolean }) => {
    if (!leadId) return;
    setLoading(true);
    getUserAffidavitForHearingStatus(leadId, [selected.tmNo])
      .then((data) => {
        if (cancelledRef.cancelled) return;
        setStatus(data);
        const row =
          data.userAffidavits.find((u) => u.tmNo === selected.tmNo) ?? null;
        setItem(row);
        setDateOfUa(parseDateForHearingInput(row?.dateOfUserAffidavitForHearing));
        setUserDate(parseDateForHearingInput(row?.userDateForHearing));
        setTurnover(
          row?.turnoverDetails && row.turnoverDetails.length > 0
            ? row.turnoverDetails.map((t) => ({ ...t }))
            : []
        );
      })
      .catch((err) => {
        if (!cancelledRef.cancelled)
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load User Affidavit for Hearing"
          );
      })
      .finally(() => {
        if (!cancelledRef.cancelled) setLoading(false);
      });
  };

  useEffect(() => {
    const ref = { cancelled: false };
    reload(ref);
    return () => {
      ref.cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, selected.tmNo]);

  const persist = async (): Promise<UserAffidavitForHearingItem | null> => {
    if (!dateOfUa) {
      toast.error("Date of User Affidavit for Hearing is required");
      return null;
    }
    if (!userDate) {
      toast.error("User Date for Hearing is required");
      return null;
    }
    for (const t of turnover) {
      if (!t.financialYear?.trim()) {
        toast.error("Financial Year is required for each turnover row");
        return null;
      }
      if (!Number.isFinite(t.amount) || t.amount < 0) {
        toast.error("Turnover amount must be a non-negative number");
        return null;
      }
    }
    const updated = await saveUserAffidavitForHearing(leadId, [
      {
        tmNo: selected.tmNo,
        dateOfUserAffidavitForHearing: dateOfUa
          ? formatDateDDMMYYYYForApi(dateOfUa)
          : null,
        userDateForHearing: userDate ? formatDateDDMMYYYYForApi(userDate) : null,
        turnoverDetails: turnover.length > 0 ? turnover : null,
      },
    ]);
    setStatus(updated);
    const row =
      updated.userAffidavits.find((u) => u.tmNo === selected.tmNo) ?? null;
    setItem(row);
    return row;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (isGated) {
      onAdvance();
      return;
    }
    onSavingChange?.(true);
    try {
      const saved = await persist();
      if (!saved) return;
      toast.success("User Affidavit for Hearing saved");
      onAdvance();
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save User Affidavit for Hearing"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const handleGeneratePdf = async () => {
    if (!leadId || generating) return;
    setGenerating(true);
    onSavingChange?.(true);
    try {
      // Persist current entries first so the PDF reflects latest values.
      const saved = await persist();
      if (!saved) {
        setGenerating(false);
        onSavingChange?.(false);
        return;
      }
      await generateUserAffidavitForHearingPdf(leadId, selected.tmNo);
      toast.success("User Affidavit PDF generated");
      // Refresh to pick up pdf metadata
      const ref = { cancelled: false };
      reload(ref);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to generate PDF"
      );
    } finally {
      setGenerating(false);
      onSavingChange?.(false);
    }
  };

  const openPreview = () => {
    const url = item?.pdf?.url;
    if (!url) return;
    const name =
      item?.pdf?.filename ?? `User_Affidavit_${selected.tmNo}.pdf`;
    setPreview({ url, name, kind: detectPreviewKind(name) });
  };

  const handleDownload = () => {
    const url = item?.pdf?.url;
    if (!url) return;
    const link = document.createElement("a");
    link.href = url;
    link.download =
      item?.pdf?.filename ?? `User_Affidavit_${selected.tmNo}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const addTurnoverRow = () =>
    setTurnover((prev) => [...prev, { financialYear: "", amount: 0 }]);
  const removeTurnoverRow = (i: number) =>
    setTurnover((prev) => prev.filter((_, idx) => idx !== i));

  // Single source for both the inline read-only panel and the spec pg 96 review window (item 37).
  // ⚠️ Spec pg 96 also lists "List of Supporting Documents" here. The hearing UA payload
  // (`UserAffidavitForHearingItem`) carries no supportingDocuments field — the same data gap as
  // review item 47 ("2m) List of Supporting Documents table missing" in the generated PDF). Add the
  // row here once the backend exposes it, so both are fixed from one source.
  const uaHearingReviewRows: Array<{ label: string; value: string }> = [
    { label: "Mark", value: item?.mark ?? "" },
    { label: "Applicant", value: item?.applicant ?? "" },
    { label: "Applicant Address", value: item?.applicantAddress ?? "" },
    { label: "Applicant Type", value: formatApplicantType(item?.applicantType) },
    { label: "User Date", value: item?.userDate ?? "" },
    { label: "Signatory's Name", value: item?.signatoryName ?? "" },
    {
      label: "Signatory's Designation",
      value: item?.signatoryDesignation ?? "",
    },
    {
      label: "Signatory's Guardian's Name",
      value: item?.signatoryGuardianName ?? "",
    },
    {
      label: "Signatory's Relation with Guardian",
      value: item?.signatoryRelationWithGuardian ?? "",
    },
    {
      label: "Description of Applicant",
      value: item?.descriptionOfApplicant ?? "",
    },
    { label: "Goods / Services", value: item?.goodsOrServices ?? "" },
    { label: "Selected Class", value: item?.selectedClass ?? "" },
    { label: "No of Applications", value: String(item?.noOfApplications ?? 1) },
    { label: "GS Type A", value: item?.gsTypeA ?? "" },
    { label: "GS Type B", value: item?.gsTypeB ?? "" },
    { label: "GS Type C", value: item?.gsTypeC ?? "" },
  ];

  // Prefer the date being edited on the form over the saved one, so the window tracks the picker.
  const uaHearingDateForReview = dateOfUa ?? null;

  if (loading) {
    return <p className="text-sm text-[#6D6D6D] py-5">Loading…</p>;
  }

  if (isGated) {
    return (
      <form
        id={EXAMINATION_REPORT_HAO_USER_AFFIDAVIT_FORM_ID}
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col gap-3 py-5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          This step is only available when{" "}
          <span className="font-medium text-[#222222]">User Date</span> at filing
          was set to{" "}
          <span className="font-medium text-[#222222]">"Proposed to be used"</span>
          . Click "Skip &amp; Continue" to proceed to the next step.
        </p>
      </form>
    );
  }

  return (
    <>
      <form
        id={EXAMINATION_REPORT_HAO_USER_AFFIDAVIT_FORM_ID}
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col gap-5 self-stretch pt-5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        {/* Editable fields — grouped at the top for entry */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="flex flex-col gap-1">
            <label
              className="text-[#222222]"
              style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
            >
              User Date for Hearing <span className="text-red-500">*</span>
            </label>
            <DateTimePicker
              value={userDate}
              onChange={setUserDate}
              showTime={false}
              placeholder="Select Date"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label
              className="text-[#222222]"
              style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
            >
              Date of User Affidavit for Hearing <span className="text-red-500">*</span>
            </label>
            <DateTimePicker
              value={dateOfUa}
              onChange={setDateOfUa}
              showTime={false}
              placeholder="Select Date"
            />
          </div>
        </div>

        {/* Turnover details — editable */}
        <div className="flex flex-col gap-2">
          <span
            className="text-[#222222]"
            style={{ fontSize: "14px", lineHeight: "20px", fontWeight: 500 }}
          >
            Turnover details
          </span>
          <div className="flex flex-row items-center gap-5">
            <span
              className="text-[#222222] flex-1"
              style={{
                fontSize: "12px",
                lineHeight: "20px",
                fontWeight: 400,
              }}
            >
              Financial Year
            </span>
            <span
              className="text-[#222222]"
              style={{
                fontSize: "12px",
                lineHeight: "20px",
                fontWeight: 400,
                width: "264.5px",
              }}
            >
              Amount
            </span>
            <div style={{ width: 36 }} aria-hidden="true" />
          </div>
          {turnover.map((row, i) => (
            <div key={i} className="flex flex-row items-center gap-5">
              <input
                type="text"
                value={row.financialYear}
                onChange={(e) =>
                  setTurnover((prev) =>
                    prev.map((t, idx) =>
                      idx === i ? { ...t, financialYear: e.target.value } : t
                    )
                  )
                }
                placeholder="e.g. 2022-23"
                className="flex-1 box-border px-2.5 py-2 h-9 text-xs leading-5 bg-white border border-[#EEEEEE] rounded-[6px] focus:outline-none focus:ring-1 focus:ring-[#222222] placeholder:text-[#6D6D6D]"
              />
              <input
                type="number"
                inputMode="decimal"
                min="0"
                value={Number.isFinite(row.amount) ? row.amount : ""}
                onChange={(e) =>
                  setTurnover((prev) =>
                    prev.map((t, idx) =>
                      idx === i
                        ? { ...t, amount: Number(e.target.value) }
                        : t
                    )
                  )
                }
                placeholder="Amount"
                className="box-border px-2.5 py-2 h-9 text-xs leading-5 bg-white border border-[#EEEEEE] rounded-[6px] focus:outline-none focus:ring-1 focus:ring-[#222222] placeholder:text-[#6D6D6D]"
                style={{ width: "264.5px" }}
              />
              <button
                type="button"
                onClick={() => removeTurnoverRow(i)}
                className="h-9 w-9 flex items-center justify-center rounded-[6px] border border-[#EEEEEE] text-[#6D6D6D] hover:bg-[#F7F7F7]"
                aria-label="Remove row"
              >
                ×
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addTurnoverRow}
            className="self-start text-xs font-medium text-[#222222] hover:underline"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 500 }}
          >
            + Add New
          </button>
        </div>

        {/* Fetched fields — read-only context */}
        <FetchedDetailsPanel
          rows={uaHearingReviewRows}
        />

        {/*
          Review item 37: spec pg 96 requires "an option to view all the data like below in a window.
          If any editing required, there should be an option to do it." Only the inline read-only
          panel above existed, which omits the Turnover table and the Date and offers no Generate PDF.
          Editing stays on the form behind the window, matching the other review windows.
        */}
        <FilePreviewModal
          open={uaHearingReviewOpen}
          onClose={() => setUaHearingReviewOpen(false)}
          name={`User Affidavit Preview${item?.tmNo ? ` — ${formatTmNoForDisplay(item.tmNo)}` : ""}`}
        >
          <div
            className="flex w-full max-w-3xl flex-col gap-3"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Data for User Affidavit
              </p>
              <div className="grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-3 gap-y-1.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2.5 text-[12px] leading-5">
                {uaHearingReviewRows.map((row) => (
                  <Fragment key={row.label}>
                    <div className="flex items-baseline font-medium text-[#222]">
                      {row.label}
                    </div>
                    <div className="flex items-baseline text-[#222]">:</div>
                    <div className="text-[#222] break-words min-w-0">
                      {row.value?.trim() ? (
                        row.value
                      ) : (
                        <span className="text-[#6D6D6D]">—</span>
                      )}
                    </div>
                  </Fragment>
                ))}
              </div>

              <p className="mt-4 mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Turnover Details
              </p>
              <div className="rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2.5 text-[12px] leading-5">
                {turnover.length === 0 ? (
                  <span className="text-[#6D6D6D]">—</span>
                ) : (
                  <ul className="flex flex-col gap-1.5">
                    {turnover.map((t, i) => (
                      <li key={i} className="flex flex-row gap-2 text-[#222]">
                        <span className="w-5 shrink-0 text-[#6D6D6D]">
                          {i + 1}
                        </span>
                        <span className="w-28 shrink-0 font-medium">
                          {t.financialYear?.trim() || "—"}
                        </span>
                        <span className="min-w-0 break-words">
                          Rs.
                          {Number(t.amount ?? 0).toLocaleString("en-IN")}/-
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <p className="mt-4 mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                Date
              </p>
              <div className="rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2.5 text-[12px] leading-5 text-[#222]">
                {uaHearingDateForReview ? (
                  formatOrdinalLongDate(uaHearingDateForReview)
                ) : (
                  <span className="text-[#6D6D6D]">—</span>
                )}
              </div>
            </section>

            {/* Spec pg 96 puts Generate PDF inside this window. */}
            <div className="flex flex-row items-center gap-2.5">
              <button
                type="button"
                onClick={() => void handleGeneratePdf()}
                disabled={generating}
                className="inline-flex items-center justify-center h-9 px-4 rounded-[6px] bg-[#222222] text-[12px] font-medium text-white hover:bg-[#333333] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {generating
                  ? "Generating…"
                  : item?.pdf?.url
                    ? "Regenerate PDF"
                    : "Generate PDF"}
              </button>
            </div>
          </div>
        </FilePreviewModal>

        {/* Generate PDF + Preview/Download */}
        <div className="flex flex-row flex-wrap items-center gap-3">
          <div
            className="flex flex-row items-center bg-[#FCFCFC] border border-[#EEEEEE] rounded-[6px]"
            style={{
              width: "327px",
              maxWidth: "100%",
              height: "44px",
              padding: "4px 8px 4px 16px",
              gap: "10px",
            }}
          >
            <button
              type="button"
              onClick={() => void handleGeneratePdf()}
              disabled={generating}
              className="flex flex-row items-center gap-2.5 flex-1 min-w-0 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <img
                src={pdfIcon}
                alt=""
                className="w-[22px] h-[22px] flex-shrink-0"
              />
              <span
                className="flex-1 min-w-0 truncate text-left text-[#222222]"
                style={{
                  fontSize: "12px",
                  lineHeight: "20px",
                  fontWeight: 500,
                }}
              >
                {generating ? "Generating…" : "Generate PDF"}
              </span>
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={!item?.pdf?.url}
              className="h-9 px-4 rounded-[6px] bg-[#222222] hover:bg-[#333] text-white text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Download
            </button>
          </div>
          {/* Data review window (spec pg 96, review item 37) — available before any PDF exists. */}
          <button
            type="button"
            onClick={() => setUaHearingReviewOpen(true)}
            className="h-9 px-4 rounded-[6px] bg-white border border-[#D5D3E0] text-[#2E3037] text-xs font-medium hover:bg-gray-50"
          >
            Preview
          </button>
          <button
            type="button"
            onClick={openPreview}
            disabled={!item?.pdf?.url}
            className="h-9 px-4 rounded-[6px] bg-white border border-[#D5D3E0] text-[#2E3037] text-xs font-medium hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Preview PDF
          </button>
        </div>
      </form>

      <FilePreviewModal
        open={!!preview}
        onClose={() => setPreview(null)}
        url={preview?.url ?? undefined}
        name={preview?.name ?? ""}
        kind={preview?.kind ?? "pdf"}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// 3D(xvii) Signature from Client for hearing — record only
// ---------------------------------------------------------------------------
function SignatureFromClientForHearingPanel({
  leadId,
  selected,
  onAdvance,
  onSavingChange,
}: {
  leadId: string;
  selected: ShowCauseHearingApplication;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
}) {
  const [recording, setRecording] = useState(false);

  const handleSubmit = async () => {
    if (!leadId || recording) return;
    setRecording(true);
    onSavingChange?.(true);
    try {
      await recordSignatureFromClientHearing(leadId, [selected.tmNo]);
      toast.success("Signature from Client for hearing recorded");
      onAdvance();
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to record Signature from Client"
      );
    } finally {
      setRecording(false);
      onSavingChange?.(false);
    }
  };

  return (
    <ProcessToNextStepPanel
      formId={EXAMINATION_REPORT_HAO_SIGNATURE_FORM_ID}
      message="Process to next step"
      onSubmit={handleSubmit}
    />
  );
}

// ---------------------------------------------------------------------------
// 3D(xviii) Notarisation of Documents for hearing — per-TM table (form per TM)
// ---------------------------------------------------------------------------
function NotarisationOfDocumentsForHearingPanel({
  leadId,
  selected,
  onAdvance,
  onSavingChange,
  onGatedChange,
}: {
  leadId: string;
  selected: ShowCauseHearingApplication;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
  onGatedChange?: (gated: boolean) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<NotarisationForHearingStatus | null>(
    null
  );
  const [amount, setAmount] = useState<string>("");
  const [paid, setPaid] = useState<boolean | null>(null);

  const isGated = !!status && status.isActive === false;

  useEffect(() => {
    onGatedChange?.(isGated);
    return () => {
      onGatedChange?.(false);
    };
  }, [isGated, onGatedChange]);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getNotarisationForHearingStatus(leadId)
      .then((data) => {
        if (cancelled) return;
        setStatus(data);
        const row =
          data.applications.find((a) => a.tmNo === selected.tmNo) ?? null;
        setAmount(
          row?.amountPayableToNotaryForHearing != null
            ? String(row.amountPayableToNotaryForHearing)
            : ""
        );
        setPaid(row?.paidToNotaryForHearing ?? null);
      })
      .catch((err) => {
        if (!cancelled)
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Notarisation for Hearing"
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, selected.tmNo]);

  const tmRow: NotarisationForHearingApplication | null =
    status?.applications.find((a) => a.tmNo === selected.tmNo) ?? null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (isGated) {
      onAdvance();
      return;
    }
    if (paid == null) {
      toast.error("Select Paid to Notary (Yes or No)");
      return;
    }
    const trimmed = amount.trim();
    let parsed: number | null = null;
    if (trimmed !== "") {
      const n = Number(trimmed);
      if (!Number.isFinite(n) || n < 0) {
        toast.error("Amount must be a non-negative number");
        return;
      }
      parsed = n;
    }
    onSavingChange?.(true);
    try {
      const updated = await saveNotaryPaymentForHearing(leadId, [
        {
          tmNo: selected.tmNo,
          amountPayableToNotaryForHearing: parsed,
          paidToNotaryForHearing: paid,
        },
      ]);
      setStatus(updated);
      toast.success("Notary payment for Hearing saved");
      onAdvance();
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save Notary payment"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-[#6D6D6D] py-5">Loading…</p>;
  }

  if (isGated) {
    return (
      <form
        id={EXAMINATION_REPORT_HAO_NOTARISATION_FORM_ID}
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col gap-3 py-5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          This step is only available when{" "}
          <span className="font-medium text-[#222222]">User Date</span> at filing
          was set to{" "}
          <span className="font-medium text-[#222222]">"Proposed to be used"</span>
          . Click "Skip &amp; Continue" to proceed to the next step.
        </p>
      </form>
    );
  }

  return (
    <form
      id={EXAMINATION_REPORT_HAO_NOTARISATION_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-5 self-stretch pt-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <ReadOnlyTextField
          label="User Date"
          value={tmRow?.userDate ?? null}
          placeholder="Select Date"
        />
        {/* 3am) — fetched from the Stamp Paper to Vendor step; was missing from this step entirely
            (review item 20), so staff could not see how many stamp papers the notarisation covers. */}
        <ReadOnlyTextField
          label="Number of Stamp Papers needed for Hearing"
          value={
            status?.stampPapersForHearing != null
              ? String(status.stampPapersForHearing)
              : null
          }
          placeholder="—"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="flex flex-col gap-1">
          <label
            className="text-[#222222]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            Amount Payable to Notary for Hearing
          </label>
          <div className="box-border flex flex-row items-center justify-between px-2.5 py-2 h-9 bg-white border border-[#EEEEEE] rounded-[6px] focus-within:ring-1 focus-within:ring-[#222222]">
            <span className="text-[#6D6D6D] text-xs leading-5">Rs.</span>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Amount"
              className="flex-1 ml-2 min-w-0 bg-transparent border-0 text-xs leading-5 text-[#222222] focus:outline-none placeholder:text-[#6D6D6D]"
            />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="flex flex-col gap-1">
          <label
            className="text-[#222222]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            Paid to Notary for Hearing
          </label>
          <Select
            value={paid == null ? undefined : paid ? "yes" : "no"}
            onValueChange={(v) => setPaid(v === "yes")}
          >
            <SelectTrigger className="data-[placeholder]:text-[#6D6D6D]">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="yes">Yes</SelectItem>
              <SelectItem value="no">No</SelectItem>
            </SelectContent>
          </Select>
          {paid === false ? (
            <p
              className="text-xs font-medium leading-5 text-[#222222] mt-0.5"
              style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
            >
              Status: Pending Payment
            </p>
          ) : paid === true ? (
            <p
              className="text-xs font-medium leading-5 text-[#222222] mt-0.5"
              style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
            >
              Status: Paid
            </p>
          ) : null}
        </div>
        <div className="hidden md:block" aria-hidden="true" />
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// 3D(xix) Scanning of Documents for hearing — record only
// ---------------------------------------------------------------------------
function ScanningOfDocumentsForHearingPanel({
  leadId,
  selected,
  onAdvance,
  onSavingChange,
}: {
  leadId: string;
  selected: ShowCauseHearingApplication;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
}) {
  const [recording, setRecording] = useState(false);

  const handleSubmit = async () => {
    if (!leadId || recording) return;
    setRecording(true);
    onSavingChange?.(true);
    try {
      await recordScanningOfDocumentsForHearing(leadId, [selected.tmNo]);
      toast.success("Scanning of Documents for hearing recorded");
      onAdvance();
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to record Scanning of Documents"
      );
    } finally {
      setRecording(false);
      onSavingChange?.(false);
    }
  };

  return (
    <ProcessToNextStepPanel
      formId={EXAMINATION_REPORT_HAO_SCANNING_FORM_ID}
      message="Process to next step"
      onSubmit={handleSubmit}
    />
  );
}

// ---------------------------------------------------------------------------
// 3D(xx) File Hearing Document — record only
// ---------------------------------------------------------------------------
function FileHearingDocumentPanel({
  leadId,
  selected,
  onAdvance,
  onSavingChange,
}: {
  leadId: string;
  selected: ShowCauseHearingApplication;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
}) {
  const [recording, setRecording] = useState(false);

  const handleSubmit = async () => {
    if (!leadId || recording) return;
    setRecording(true);
    onSavingChange?.(true);
    try {
      await recordFilingHearingDocument(leadId, [selected.tmNo]);
      toast.success("File Hearing Document recorded");
      onAdvance();
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to record File Hearing Document"
      );
    } finally {
      setRecording(false);
      onSavingChange?.(false);
    }
  };

  return (
    <ProcessToNextStepPanel
      formId={EXAMINATION_REPORT_HAO_FILE_HEARING_DOC_FORM_ID}
      message="Process to next step"
      onSubmit={handleSubmit}
    />
  );
}

// ---------------------------------------------------------------------------
// 3D(xxi) Note on Hearing — multi-section form per application
// ---------------------------------------------------------------------------
function PreviewLine({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  const display = value && String(value).trim() ? String(value) : "";
  return (
    <div className="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-4">
      <span className="text-[#6D6D6D] md:w-[220px] md:shrink-0">{label}</span>
      <span className={display ? "text-[#222222]" : "text-[#6D6D6D]"}>
        {display || "—"}
      </span>
    </div>
  );
}

function NoteOnHearingPanel({
  leadId,
  selected,
  onAdvance,
  onSavingChange,
}: {
  leadId: string;
  selected: ShowCauseHearingApplication;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [ackNo, setAckNo] = useState<string>("");
  const [applicant, setApplicant] = useState<string>("");
  const [appData, setAppData] = useState<NoteOnHearingApplicationData | null>(
    null
  );
  const [nonDist1, setNonDist1] = useState("");
  const [nonDist2, setNonDist2] = useState("");
  const [supportingUploaded, setSupportingUploaded] = useState<boolean | null>(
    null
  );
  const [other1, setOther1] = useState("");
  const [other2, setOther2] = useState("");
  const [similarNotes, setSimilarNotes] = useState<NoteOnHearingSimilarMarkNote[]>(
    []
  );
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState<
    { url: string; name: string; kind: FilePreviewKind } | null
  >(null);
  const [dataPreviewOpen, setDataPreviewOpen] = useState(false);
  const [fetchedExpanded, setFetchedExpanded] = useState(false);

  const reload = (cancelledRef: { cancelled: boolean }) => {
    if (!leadId) return;
    setLoading(true);
    getNoteOnHearingStatus(leadId)
      .then((data) => {
        if (cancelledRef.cancelled) return;
        const app =
          data.applications.find((a) => a.tmNo === selected.tmNo) ?? null;
        setAckNo(data.acknowledgementNo ?? "");
        setApplicant(data.applicant ?? "");
        setAppData(app);
        setNonDist1(app?.noteNonDistinctive1 ?? "");
        setNonDist2(app?.noteNonDistinctive2 ?? "");
        setSupportingUploaded(app?.supportingDocsUploaded ?? null);
        setOther1(app?.noteOtherObjections1 ?? "");
        setOther2(app?.noteOtherObjections2 ?? "");
        // Hydrate similarNotes: prefer saved notes, else seed one row per similarMark
        const seeded: NoteOnHearingSimilarMarkNote[] =
          app && app.similarMarkNotes && app.similarMarkNotes.length > 0
            ? app.similarMarkNotes.map((n) => ({ ...n }))
            : (app?.similarMarks ?? []).map((m, i) => ({
                similarMarkId: m.id,
                sortOrder: m.sortOrder ?? i,
                mark: m.mark,
                tmNo: m.tmNo,
                imageKey: null,
                imageUrl: null,
                status: null,
                expired: null,
                goodsServicesDifferent: null,
                userDateEarlier: null,
                state: null,
                note1: null,
                note2: null,
              }));
        setSimilarNotes(seeded);
      })
      .catch((err) => {
        if (!cancelledRef.cancelled)
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Note on Hearing"
          );
      })
      .finally(() => {
        if (!cancelledRef.cancelled) setLoading(false);
      });
  };

  useEffect(() => {
    const ref = { cancelled: false };
    reload(ref);
    return () => {
      ref.cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, selected.tmNo]);

  const persist = async (): Promise<NoteOnHearingApplicationData | null> => {
    if (!appData) return null;
    const updated = await saveNoteOnHearing(leadId, {
      ackApplicationId: appData.ackApplicationId,
      noteNonDistinctive1: nonDist1.trim() || null,
      noteNonDistinctive2: nonDist2.trim() || null,
      supportingDocsUploaded: supportingUploaded,
      noteOtherObjections1: other1.trim() || null,
      noteOtherObjections2: other2.trim() || null,
      similarMarkNotes: similarNotes
        .filter((n) => n.mark && n.tmNo)
        .map((n) => ({
          mark: n.mark!,
          tmNo: n.tmNo!,
          imageKey: n.imageKey,
          status: n.status,
          expired: n.expired,
          goodsServicesDifferent: n.goodsServicesDifferent,
          userDateEarlier: n.userDateEarlier,
          state: n.state,
          note1: n.note1,
          note2: n.note2,
        })),
    });
    setAppData(updated);
    return updated;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    onSavingChange?.(true);
    try {
      const saved = await persist();
      if (!saved) return;
      toast.success("Note on Hearing saved");
      onAdvance();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save Note on Hearing"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const handleGeneratePdf = async () => {
    if (!leadId || !appData || generating) return;
    setGenerating(true);
    onSavingChange?.(true);
    try {
      const saved = await persist();
      if (!saved) {
        setGenerating(false);
        onSavingChange?.(false);
        return;
      }
      await generateNoteOnHearingPdf(leadId, saved.ackApplicationId);
      toast.success("Note on Hearing PDF generated");
      const ref = { cancelled: false };
      reload(ref);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to generate PDF"
      );
    } finally {
      setGenerating(false);
      onSavingChange?.(false);
    }
  };

  const updateSimilar = (
    i: number,
    patch: Partial<NoteOnHearingSimilarMarkNote>
  ) => {
    setSimilarNotes((prev) =>
      prev.map((n, idx) => (idx === i ? { ...n, ...patch } : n))
    );
  };

  const handleSimilarMarkImageUpload = async (
    i: number,
    file: File
  ): Promise<void> => {
    if (!leadId || !appData) return;
    const note = similarNotes[i];
    if (!note?.mark || !note?.tmNo) {
      toast.error("Mark and TM No. required for image upload");
      return;
    }
    onSavingChange?.(true);
    try {
      const out = await uploadNoteOnHearingSimilarMarkImage(leadId, {
        file,
        ackApplicationId: appData.ackApplicationId,
        mark: note.mark,
        tmNo: note.tmNo,
      });
      updateSimilar(i, { imageKey: out.imageKey, imageUrl: out.imageUrl });
      toast.success("Similar mark image uploaded");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to upload image"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  const handleDownload = () => {
    const url = appData?.notePdfUrl;
    if (!url) return;
    const link = document.createElement("a");
    link.href = url;
    link.download = `Note_on_Hearing_${selected.tmNo}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return <p className="text-sm text-[#6D6D6D] py-5">Loading…</p>;
  }

  if (!appData) {
    return (
      <p className="text-sm text-[#6D6D6D] py-5">
        Note on Hearing data is not yet available for this application.
      </p>
    );
  }

  const reasons = appData.objectionReasons;
  const reasonText = [
    reasons.similarity ? "Similarity" : null,
    reasons.nonDistinctive ? "Non Distinctive" : null,
    reasons.supportingDocuments ? "Supporting Documents" : null,
    reasons.tmMYes ? "TM-M" : null,
    reasons.others ? `Others${reasons.othersSpecify ? `: ${reasons.othersSpecify}` : ""}` : null,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <>
      <form
        id={EXAMINATION_REPORT_HAO_NOTE_ON_HEARING_FORM_ID}
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col gap-5 self-stretch pt-5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        {/* Fetched details — collapsible, collapsed by default */}
        <div className="flex flex-col gap-3 self-stretch">
          <button
            type="button"
            onClick={() => setFetchedExpanded((v) => !v)}
            className="flex items-center justify-between w-full px-1 text-left"
            aria-expanded={fetchedExpanded}
          >
            <span
              className="text-[#222222]"
              style={{ fontSize: "14px", lineHeight: "20px", fontWeight: 500 }}
            >
              Fetched Details
            </span>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`text-[#6D6D6D] transition-transform ${
                fetchedExpanded ? "rotate-180" : ""
              }`}
              aria-hidden="true"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
          {fetchedExpanded && (
            <>
              <FetchedDetailsPanel
                rows={[
                  { label: "Mark", value: appData.mark ?? "" },
                  { label: "Selected Class", value: appData.classNumber ? String(appData.classNumber) : "" },
                  { label: "Selected Type of Mark", value: appData.typeOfMark ?? "" },
                  { label: "Goods / Services", value: appData.goodsServices ?? "" },
                  { label: "User Date", value: appData.userDate ?? "" },
                  { label: "User Date for Hearing", value: appData.userDateForHearing ?? "" },
                  { label: "Reason for Objection", value: reasonText },
                  { label: "Similar Marks", value: appData.similarMarks.length ? `${appData.similarMarks.length} mark(s)` : "" },
                  { label: "Acknowledgement No.", value: ackNo },
                ]}
              />
              {appData.imageOfMarkUrl && (
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-normal leading-5 text-[#6D6D6D]">
                    Image of Mark
                  </span>
                  <img
                    src={appData.imageOfMarkUrl}
                    alt="Mark"
                    className="rounded-[6px] border border-[#EEEEEE]"
                    style={{ maxHeight: 155, objectFit: "contain" }}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Note on Similarity — table layout */}
        <div className="flex flex-col gap-3 self-stretch">
          <span
            className="text-[#222222]"
            style={{ fontSize: "14px", lineHeight: "20px", fontWeight: 500 }}
          >
            Note on Similarity
          </span>
          {similarNotes.length === 0 ? (
            <p className="text-xs text-[#6D6D6D]">
              No similar marks for this application.
            </p>
          ) : (
            <>
              {/* Mobile: stacked cards (below md) */}
              <div className="flex flex-col gap-3 md:hidden self-stretch">
                {similarNotes.map((note, i) => {
                  const inputCls =
                    "box-border w-full px-2.5 py-2 h-9 text-xs leading-5 bg-white border border-[#EEEEEE] rounded-[6px] focus:outline-none focus:ring-1 focus:ring-[#222222] placeholder:text-[#6D6D6D]";
                  return (
                    <div
                      key={i}
                      className="flex flex-col gap-3 p-3 border border-[#EEEEEE] rounded-[6px] bg-white"
                    >
                      <span
                        className="text-[#222222]"
                        style={{
                          fontSize: "13px",
                          lineHeight: "20px",
                          fontWeight: 500,
                        }}
                      >
                        Mark {i + 1}
                      </span>
                      {/* Image */}
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-[#222222]">
                          Image of Mark
                        </label>
                        {note.imageUrl ? (
                          <img
                            src={note.imageUrl}
                            alt={`Similar mark ${i + 1}`}
                            className="rounded-[6px] border border-[#EEEEEE]"
                            style={{ maxHeight: 120, objectFit: "contain" }}
                          />
                        ) : (
                          <label className="flex items-center justify-center h-[80px] border border-dashed border-[#D8DAEB] rounded-[10px] cursor-pointer text-xs text-[#6D6D6D] bg-white hover:bg-[#FCFCFC]">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) void handleSimilarMarkImageUpload(i, f);
                                e.target.value = "";
                              }}
                            />
                            Click to upload mark image
                          </label>
                        )}
                      </div>
                      {/* Status */}
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-[#222222]">Status</label>
                        <input
                          type="text"
                          value={note.status ?? ""}
                          onChange={(e) =>
                            updateSimilar(i, { status: e.target.value })
                          }
                          placeholder="Status"
                          className={inputCls}
                        />
                      </div>
                      {/* Expired */}
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-[#222222]">Expired</label>
                        <Select
                          value={
                            note.expired == null
                              ? undefined
                              : note.expired
                                ? "yes"
                                : "no"
                          }
                          onValueChange={(v) =>
                            updateSimilar(i, { expired: v === "yes" })
                          }
                        >
                          <SelectTrigger className="data-[placeholder]:text-[#6D6D6D]">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="yes">Yes</SelectItem>
                            <SelectItem value="no">No</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {/* G/S Different */}
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-[#222222]">
                          Goods / Services
                        </label>
                        <Select
                          value={
                            note.goodsServicesDifferent == null
                              ? undefined
                              : note.goodsServicesDifferent
                                ? "yes"
                                : "no"
                          }
                          onValueChange={(v) =>
                            updateSimilar(i, {
                              goodsServicesDifferent: v === "yes",
                            })
                          }
                        >
                          <SelectTrigger className="data-[placeholder]:text-[#6D6D6D]">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="yes">Different</SelectItem>
                            <SelectItem value="no">Not Different</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {/* User Date Earlier */}
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-[#222222]">
                          User Date
                        </label>
                        <Select
                          value={
                            note.userDateEarlier == null
                              ? undefined
                              : note.userDateEarlier
                                ? "yes"
                                : "no"
                          }
                          onValueChange={(v) =>
                            updateSimilar(i, { userDateEarlier: v === "yes" })
                          }
                        >
                          <SelectTrigger className="data-[placeholder]:text-[#6D6D6D]">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="yes">Earlier</SelectItem>
                            <SelectItem value="no">Not Earlier</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {/* State */}
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-[#222222]">State</label>
                        <input
                          type="text"
                          value={note.state ?? ""}
                          onChange={(e) =>
                            updateSimilar(i, { state: e.target.value })
                          }
                          placeholder="State"
                          className={inputCls}
                        />
                      </div>
                      {/* Note 1 */}
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-[#222222]">Note 1</label>
                        <input
                          type="text"
                          value={note.note1 ?? ""}
                          onChange={(e) =>
                            updateSimilar(i, { note1: e.target.value })
                          }
                          placeholder="Note 1"
                          className={inputCls}
                        />
                      </div>
                      {/* Note 2 */}
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-[#222222]">Note 2</label>
                        <input
                          type="text"
                          value={note.note2 ?? ""}
                          onChange={(e) =>
                            updateSimilar(i, { note2: e.target.value })
                          }
                          placeholder="Note 2"
                          className={inputCls}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop: grid table (md and up) */}
              <div className="hidden md:block overflow-x-auto self-stretch">
                <div style={{ minWidth: 900 }}>
                {/* Header */}
                <div
                  className="grid items-center gap-2 px-2.5 py-2 bg-[#F7F7F7] rounded-t-[6px] border border-[#EEEEEE]"
                  style={{
                    gridTemplateColumns:
                      "56px 64px minmax(110px,1fr) 90px 100px 100px minmax(100px,1fr) minmax(120px,1fr) minmax(120px,1fr)",
                  }}
                >
                  <span className="text-xs font-medium text-[#222222]">#</span>
                  <span className="text-xs font-medium text-[#222222]">Image</span>
                  <span className="text-xs font-medium text-[#222222]">Status</span>
                  <span className="text-xs font-medium text-[#222222]">Expired</span>
                  <span className="text-xs font-medium text-[#222222]">G/S Diff.</span>
                  <span className="text-xs font-medium text-[#222222]">User Date</span>
                  <span className="text-xs font-medium text-[#222222]">State</span>
                  <span className="text-xs font-medium text-[#222222]">Note 1</span>
                  <span className="text-xs font-medium text-[#222222]">Note 2</span>
                </div>
                {/* Rows */}
                {similarNotes.map((note, i) => {
                  const cellCls =
                    "w-full h-9 px-2.5 py-2 text-xs leading-5 bg-white border border-[#EEEEEE] rounded-[6px] focus:outline-none focus:ring-1 focus:ring-[#222222] placeholder:text-[#6D6D6D]";
                  return (
                    <div
                      key={i}
                      className="grid items-center gap-2 px-2.5 py-2 border-b border-x border-[#EEEEEE] last:rounded-b-[6px]"
                      style={{
                        gridTemplateColumns:
                          "56px 64px minmax(110px,1fr) 90px 100px 100px minmax(100px,1fr) minmax(120px,1fr) minmax(120px,1fr)",
                      }}
                    >
                      {/* # label */}
                      <span className="text-xs font-medium text-[#222222]">
                        Mark {i + 1}
                      </span>
                      {/* Image */}
                      <div className="flex justify-center items-center">
                        {note.imageUrl ? (
                          <img
                            src={note.imageUrl}
                            alt={`Similar mark ${i + 1}`}
                            className="rounded-[4px] border border-[#EEEEEE] object-contain"
                            style={{ width: 40, height: 40 }}
                          />
                        ) : (
                          <label
                            className="flex items-center justify-center w-10 h-10 border border-dashed border-[#D8DAEB] rounded-[6px] cursor-pointer bg-white hover:bg-[#FCFCFC]"
                            title="Upload image"
                          >
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) void handleSimilarMarkImageUpload(i, f);
                                e.target.value = "";
                              }}
                            />
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              className="text-[#6D6D6D]"
                            >
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                              <polyline points="17 8 12 3 7 8" />
                              <line x1="12" y1="3" x2="12" y2="15" />
                            </svg>
                          </label>
                        )}
                      </div>
                      {/* Status */}
                      <input
                        type="text"
                        value={note.status ?? ""}
                        onChange={(e) =>
                          updateSimilar(i, { status: e.target.value })
                        }
                        placeholder="Status"
                        className={cellCls}
                      />
                      {/* Expired */}
                      <Select
                        value={
                          note.expired == null
                            ? undefined
                            : note.expired
                              ? "yes"
                              : "no"
                        }
                        onValueChange={(v) =>
                          updateSimilar(i, { expired: v === "yes" })
                        }
                      >
                        <SelectTrigger className="h-9 text-xs data-[placeholder]:text-[#6D6D6D]">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                      </Select>
                      {/* G/S Different */}
                      <Select
                        value={
                          note.goodsServicesDifferent == null
                            ? undefined
                            : note.goodsServicesDifferent
                              ? "yes"
                              : "no"
                        }
                        onValueChange={(v) =>
                          updateSimilar(i, {
                            goodsServicesDifferent: v === "yes",
                          })
                        }
                      >
                        <SelectTrigger className="h-9 text-xs data-[placeholder]:text-[#6D6D6D]">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Different</SelectItem>
                          <SelectItem value="no">Not Diff.</SelectItem>
                        </SelectContent>
                      </Select>
                      {/* User Date Earlier */}
                      <Select
                        value={
                          note.userDateEarlier == null
                            ? undefined
                            : note.userDateEarlier
                              ? "yes"
                              : "no"
                        }
                        onValueChange={(v) =>
                          updateSimilar(i, { userDateEarlier: v === "yes" })
                        }
                      >
                        <SelectTrigger className="h-9 text-xs data-[placeholder]:text-[#6D6D6D]">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Earlier</SelectItem>
                          <SelectItem value="no">Not Earlier</SelectItem>
                        </SelectContent>
                      </Select>
                      {/* State */}
                      <input
                        type="text"
                        value={note.state ?? ""}
                        onChange={(e) =>
                          updateSimilar(i, { state: e.target.value })
                        }
                        placeholder="State"
                        className={cellCls}
                      />
                      {/* Note 1 */}
                      <input
                        type="text"
                        value={note.note1 ?? ""}
                        onChange={(e) =>
                          updateSimilar(i, { note1: e.target.value })
                        }
                        placeholder="Note 1"
                        className={cellCls}
                      />
                      {/* Note 2 */}
                      <input
                        type="text"
                        value={note.note2 ?? ""}
                        onChange={(e) =>
                          updateSimilar(i, { note2: e.target.value })
                        }
                        placeholder="Note 2"
                        className={cellCls}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
            </>
          )}
        </div>

        {/* Note on Non distinctiveness */}
        <div className="flex flex-col gap-4">
          <span
            className="text-[#222222]"
            style={{ fontSize: "14px", lineHeight: "20px", fontWeight: 500 }}
          >
            Note on Non distinctiveness
          </span>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="flex flex-col gap-1">
              <label
                className="text-[#222222]"
                style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
              >
                Note 1
              </label>
              <input
                type="text"
                value={nonDist1}
                onChange={(e) => setNonDist1(e.target.value)}
                placeholder="Note 1"
                className="box-border w-full px-2.5 py-2 h-9 text-xs leading-5 bg-white border border-[#EEEEEE] rounded-[6px] focus:outline-none focus:ring-1 focus:ring-[#222222] placeholder:text-[#6D6D6D]"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label
                className="text-[#222222]"
                style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
              >
                Note 2
              </label>
              <input
                type="text"
                value={nonDist2}
                onChange={(e) => setNonDist2(e.target.value)}
                placeholder="Note 2"
                className="box-border w-full px-2.5 py-2 h-9 text-xs leading-5 bg-white border border-[#EEEEEE] rounded-[6px] focus:outline-none focus:ring-1 focus:ring-[#222222] placeholder:text-[#6D6D6D]"
              />
            </div>
          </div>
        </div>

        {/* Note on Supporting Documents (Yes/No toggle) */}
        <div className="flex flex-col gap-4">
          <span
            className="text-[#222222]"
            style={{ fontSize: "14px", lineHeight: "20px", fontWeight: 500 }}
          >
            Note on Supporting Documents
          </span>
          <div className="flex flex-col gap-1">
            <label
              className="text-[#222222]"
              style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
            >
              Documents Uploaded
            </label>
            <div className="w-full max-w-[380px]">
              <Select
                value={
                  supportingUploaded == null
                    ? undefined
                    : supportingUploaded
                      ? "yes"
                      : "no"
                }
                onValueChange={(v) => setSupportingUploaded(v === "yes")}
              >
                <SelectTrigger className="data-[placeholder]:text-[#6D6D6D]">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Note on Other Objections */}
        <div className="flex flex-col gap-4">
          <span
            className="text-[#222222]"
            style={{ fontSize: "14px", lineHeight: "20px", fontWeight: 500 }}
          >
            Note on Other Objections
          </span>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="flex flex-col gap-1">
              <label
                className="text-[#222222]"
                style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
              >
                Note 1
              </label>
              <input
                type="text"
                value={other1}
                onChange={(e) => setOther1(e.target.value)}
                placeholder="Note 1"
                className="box-border w-full px-2.5 py-2 h-9 text-xs leading-5 bg-white border border-[#EEEEEE] rounded-[6px] focus:outline-none focus:ring-1 focus:ring-[#222222] placeholder:text-[#6D6D6D]"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label
                className="text-[#222222]"
                style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
              >
                Note 2
              </label>
              <input
                type="text"
                value={other2}
                onChange={(e) => setOther2(e.target.value)}
                placeholder="Note 2"
                className="box-border w-full px-2.5 py-2 h-9 text-xs leading-5 bg-white border border-[#EEEEEE] rounded-[6px] focus:outline-none focus:ring-1 focus:ring-[#222222] placeholder:text-[#6D6D6D]"
              />
            </div>
          </div>
        </div>

        {/* Generate PDF + Preview/Download */}
        <div className="flex flex-row flex-wrap items-center gap-3">
          <div
            className="flex flex-row items-center bg-[#FCFCFC] border border-[#EEEEEE] rounded-[6px]"
            style={{
              width: "327px",
              maxWidth: "100%",
              height: "44px",
              padding: "4px 8px 4px 16px",
              gap: "10px",
            }}
          >
            <button
              type="button"
              onClick={() => void handleGeneratePdf()}
              disabled={generating}
              className="flex flex-row items-center gap-2.5 flex-1 min-w-0 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <img src={pdfIcon} alt="" className="w-[22px] h-[22px] flex-shrink-0" />
              <span
                className="flex-1 min-w-0 truncate text-left text-[#222222]"
                style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 500 }}
              >
                {generating ? "Generating…" : "Generate PDF"}
              </span>
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={!appData.notePdfUrl}
              className="h-9 px-4 rounded-[6px] bg-[#222222] hover:bg-[#333] text-white text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Download
            </button>
          </div>
          <button
            type="button"
            onClick={() => setDataPreviewOpen(true)}
            className="h-9 px-4 rounded-[6px] bg-white border border-[#D5D3E0] text-[#2E3037] text-xs font-medium hover:bg-gray-50"
          >
            Preview
          </button>
        </div>
      </form>

      <FilePreviewModal
        open={!!preview}
        onClose={() => setPreview(null)}
        url={preview?.url ?? undefined}
        name={preview?.name ?? ""}
        kind={preview?.kind ?? "pdf"}
      />

      {/* Note data preview modal */}
      {dataPreviewOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setDataPreviewOpen(false)}
        >
          <div
            className="relative flex h-[95vh] sm:h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            {/* Header */}
            <div className="flex shrink-0 items-center justify-between border-b border-[#e3e3e3] px-4 py-3">
              <span className="text-sm font-medium text-[#222222]">
                Note on Hearing — Preview
              </span>
              <button
                type="button"
                onClick={() => setDataPreviewOpen(false)}
                className="flex size-8 items-center justify-center rounded text-[#6d6d6d] hover:bg-[#eee] hover:text-[#222]"
                aria-label="Close preview"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div
              className="flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5 flex flex-col gap-3"
              style={{ fontSize: "12px", lineHeight: "20px", color: "#222222" }}
            >
              {/* Header fields */}
              <PreviewLine label="Applicant" value={applicant} />
              <PreviewLine
                label="Application Number (TM No.)"
                value={appData.tmNo}
              />
              <PreviewLine label="Date of Filing" value={appData.dateOfFiling} />
              <PreviewLine
                label="Mark (Type)"
                value={
                  appData.mark
                    ? `${appData.mark}${appData.typeOfMark ? ` (${appData.typeOfMark})` : ""}`
                    : ""
                }
              />
              <div className="flex flex-col md:flex-row md:items-start gap-1 md:gap-4">
                <span className="text-[#6D6D6D] md:w-[220px] md:shrink-0">
                  Image of Mark
                </span>
                {appData.imageOfMarkUrl ? (
                  <img
                    src={appData.imageOfMarkUrl}
                    alt="Mark"
                    className="rounded-[4px] border border-[#EEEEEE]"
                    style={{ maxHeight: 80, objectFit: "contain" }}
                  />
                ) : (
                  <span className="text-[#6D6D6D]">—</span>
                )}
              </div>
              <PreviewLine
                label="Class"
                value={appData.classNumber ? String(appData.classNumber) : ""}
              />
              <PreviewLine
                label="Goods / Services"
                value={appData.goodsServices}
              />
              <PreviewLine
                label="Hearing Date"
                value={appData.dateOfHearing}
              />

              {/* 1. Similarity */}
              {reasons.similarity && (
                <div className="flex flex-col gap-2 pt-3 border-t border-[#EEEEEE]">
                  <span className="font-medium text-[#222222]">
                    1. Similarity
                  </span>
                  {similarNotes.length === 0 ? (
                    <span className="text-[#6D6D6D] pl-3">—</span>
                  ) : (
                    similarNotes.map((n, i) => (
                      <div key={i} className="flex flex-col gap-1 pl-3">
                        <span className="text-[#222222]">
                          - Mark {i + 1}:{" "}
                          <span className="font-medium">
                            {n.mark || "—"}
                            {n.tmNo ? ` (${n.tmNo})` : ""}
                          </span>
                        </span>
                        <span className="text-[#222222] pl-3">
                          Expired :{" "}
                          {n.expired == null ? "—" : n.expired ? "Yes" : "No"}
                        </span>
                        <span className="text-[#222222] pl-3">
                          Goods / Services :{" "}
                          {n.goodsServicesDifferent == null
                            ? "—"
                            : n.goodsServicesDifferent
                              ? "Different"
                              : "Not Different"}
                        </span>
                        <span className="text-[#222222] pl-3">
                          State : {n.state || "—"}
                        </span>
                        <span className="text-[#222222] pl-3">
                          Note 1 : {n.note1 || "—"}
                        </span>
                        <span className="text-[#222222] pl-3">
                          Note 2 : {n.note2 || "—"}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* 2. Non Distinctiveness */}
              {reasons.nonDistinctive && (
                <div className="flex flex-col gap-1 pt-3 border-t border-[#EEEEEE]">
                  <span className="font-medium text-[#222222]">
                    {reasons.similarity ? "2" : "1"}. Non Distinctiveness
                  </span>
                  {[nonDist1, nonDist2]
                    .map((s) => s.trim())
                    .filter(Boolean)
                    .map((s, idx) => (
                      <span key={idx} className="text-[#222222] pl-3">
                        - {s}
                      </span>
                    ))}
                  {!nonDist1.trim() && !nonDist2.trim() && (
                    <span className="text-[#6D6D6D] pl-3">—</span>
                  )}
                </div>
              )}

              {/* 3. More Supporting Documents required */}
              {reasons.supportingDocuments && (
                <div className="flex flex-col gap-1 pt-3 border-t border-[#EEEEEE]">
                  <span className="font-medium text-[#222222]">
                    {[
                      reasons.similarity,
                      reasons.nonDistinctive,
                    ].filter(Boolean).length + 1}
                    . More Supporting Documents required
                  </span>
                  <span className="text-[#222222] pl-3">
                    -{" "}
                    {supportingUploaded == null
                      ? "—"
                      : supportingUploaded
                        ? "Documents uploaded"
                        : "Documents not yet uploaded"}
                  </span>
                </div>
              )}

              {/* 4. Other Submissions */}
              {reasons.others && (
                <div className="flex flex-col gap-1 pt-3 border-t border-[#EEEEEE]">
                  <span className="font-medium text-[#222222]">
                    {[
                      reasons.similarity,
                      reasons.nonDistinctive,
                      reasons.supportingDocuments,
                    ].filter(Boolean).length + 1}
                    . Other Submissions
                  </span>
                  {[other1, other2]
                    .map((s) => s.trim())
                    .filter(Boolean)
                    .map((s, idx) => (
                      <span key={idx} className="text-[#222222] pl-3">
                        - {s}
                      </span>
                    ))}
                  {!other1.trim() && !other2.trim() && (
                    <span className="text-[#6D6D6D] pl-3">—</span>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex shrink-0 items-center justify-end border-t border-[#e3e3e3] px-4 py-3 gap-2">
              <button
                type="button"
                onClick={() => setDataPreviewOpen(false)}
                className="h-9 px-4 rounded-[6px] bg-[#222222] hover:bg-[#333] text-white text-xs font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// 3D(xxii) Attend Hearing — status + officer remark dropdowns
// ---------------------------------------------------------------------------
function AttendHearingPanel({
  leadId,
  selected,
  onAdvance,
  onSavingChange,
}: {
  leadId: string;
  selected: ShowCauseHearingApplication;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [appData, setAppData] = useState<AttendHearingApplicationData | null>(
    null
  );
  const [hearingStatus, setHearingStatus] = useState<HearingStatusEnum | "">("");
  const [officerRemark, setOfficerRemark] = useState<HearingOfficerRemarkEnum | "">("");

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getAttendHearingStatus(leadId)
      .then((data) => {
        if (cancelled) return;
        const row = data.applications.find((a) => a.tmNo === selected.tmNo) ?? null;
        setAppData(row);
        setHearingStatus(row?.hearingStatus ?? "");
        setOfficerRemark(row?.hearingOfficerRemark ?? "");
      })
      .catch((err) => {
        if (!cancelled)
          toast.error(
            err instanceof Error ? err.message : "Failed to load Attend Hearing"
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, selected.tmNo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || !appData) return;
    if (!hearingStatus) {
      toast.error("Select Hearing Status");
      return;
    }
    if (hearingStatus === "ATTENDED" && !officerRemark) {
      toast.error("Select Officer Remark");
      return;
    }
    onSavingChange?.(true);
    try {
      await saveAttendHearing(leadId, {
        ackApplicationId: appData.ackApplicationId,
        hearingStatus,
        hearingOfficerRemark:
          hearingStatus === "ATTENDED" ? officerRemark || null : null,
      });
      toast.success("Attend Hearing saved");
      onAdvance();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save Attend Hearing"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  if (loading) return <p className="text-sm text-[#6D6D6D] py-5">Loading…</p>;

  return (
    <form
      id={EXAMINATION_REPORT_HAO_ATTEND_HEARING_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-5 self-stretch pt-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="flex flex-col gap-1">
          <label
            className="text-[#222222]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            Status
          </label>
          <Select
            value={hearingStatus || undefined}
            onValueChange={(v) => setHearingStatus(v as HearingStatusEnum)}
          >
            <SelectTrigger className="data-[placeholder]:text-[#6D6D6D]">
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ATTENDED">Attended</SelectItem>
              <SelectItem value="NOT_ATTENDED">Not Attended</SelectItem>
              <SelectItem value="DIDNT_HAPPEN">Didn't Happen</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <label
            className="text-[#222222]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            Officer Remark
          </label>
          <Select
            value={officerRemark || undefined}
            disabled={hearingStatus !== "ATTENDED"}
            onValueChange={(v) =>
              setOfficerRemark(v as HearingOfficerRemarkEnum)
            }
          >
            <SelectTrigger className="data-[placeholder]:text-[#6D6D6D]">
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ACCEPTED">Accepted</SelectItem>
              <SelectItem value="REFUSED">Refused</SelectItem>
              <SelectItem value="WILL_PASS_ON_MERITS">
                Will pass on merits
              </SelectItem>
              <SelectItem value="FILE_TM_M">File TM-M</SelectItem>
              <SelectItem value="DIRECTION_OF_OFFICER">
                Direction of Officer
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// 3D(xxiii) Notice to Client for hearing not happened — file upload per TM + Send
// ---------------------------------------------------------------------------
const POSTPONEMENT_NOTICE_FOLDER = "postponement-notice";

function NoticeForHearingNotHappenedPanel({
  leadId,
  applications,
  selected,
  onAdvance,
  onSavingChange,
  hasSent,
  resending,
  onResend,
  onGatedChange,
}: {
  leadId: string;
  applications: ShowCauseHearingApplication[];
  selected: ShowCauseHearingApplication;
  /**
   * `selectedDidntHappen` is true when the selected mark's 3ax) Hearing Status
   * is "Didn't Happen" — the caller uses it to route back to 3D(xxx) Subsequent
   * Hearings instead of continuing into the after-hearing chain.
   */
  onAdvance: (selectedDidntHappen: boolean) => void;
  onSavingChange?: (saving: boolean) => void;
  hasSent: boolean;
  resending: boolean;
  onResend: () => void;
  onGatedChange?: (gated: boolean) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<HearingDidntHappenStatus | null>(null);
  const [uploading, setUploading] = useState<string | null>(null);
  const [preview, setPreview] = useState<
    { url: string; name: string; kind: FilePreviewKind } | null
  >(null);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getHearingDidntHappenStatus(leadId)
      .then((data) => {
        if (!cancelled) setStatus(data);
      })
      .catch((err) => {
        if (!cancelled)
          toast.error(
            err instanceof Error ? err.message : "Failed to load step status"
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const isGated = !loading && (status?.applications.length ?? 0) === 0;

  // The endpoint only returns marks whose Hearing Status is "Didn't Happen",
  // so membership of the selected mark is the routing condition for 3D(xxx).
  const selectedDidntHappen = (status?.applications ?? []).some(
    (a) => a.tmNo === selected.tmNo
  );

  useEffect(() => {
    onGatedChange?.(isGated);
    return () => {
      onGatedChange?.(false);
    };
  }, [isGated, onGatedChange]);

  const onUploadFor = async (tmNo: string, file: File) => {
    if (!leadId || uploading) return;
    setUploading(tmNo);
    onSavingChange?.(true);
    try {
      const [presigned] = await generateUploadPresignedUrls(
        [{ filename: file.name, mimeType: file.type || "application/pdf" }],
        "private",
        POSTPONEMENT_NOTICE_FOLDER
      );
      const putRes = await fetch(presigned.url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "application/pdf" },
      });
      if (!putRes.ok) throw new Error("Upload to S3 failed");
      await saveHearingDidntHappenPostponementNotice(leadId, [
        {
          tmNo,
          postponementNoticeFileKey: presigned.key,
          postponementNoticeFilename: file.name,
        },
      ]);
      const refreshed = await getHearingDidntHappenStatus(leadId);
      setStatus(refreshed);
      toast.success("Notice uploaded");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to upload");
    } finally {
      setUploading(null);
      onSavingChange?.(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (isGated || hasSent) {
      onAdvance(selectedDidntHappen);
      return;
    }
    onSavingChange?.(true);
    try {
      await sendHearingDidntHappenToClient(leadId, [selected.tmNo]);
      toast.success("Notice sent to client");
      onAdvance(selectedDidntHappen);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send notice");
    } finally {
      onSavingChange?.(false);
    }
  };

  if (loading) return <p className="text-sm text-[#6D6D6D] py-5">Loading…</p>;

  const rows = status?.applications ?? [];
  if (rows.length === 0) {
    return (
      <form
        id={EXAMINATION_REPORT_HAO_HEARING_NOT_HAPPENED_FORM_ID}
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col gap-5 self-stretch pt-5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          This step is only available for hearings marked as "Didn't Happen". No
          marks need a postponement notice — click "Skip &amp; Continue" to
          proceed to the next step.
        </p>
      </form>
    );
  }

  // Pick the next row needing a file; if all are filled, target the first row (replace).
  const nextTargetTmNo = (): string | null => {
    if (rows.length === 0) return null;
    const empty = rows.find((r) => !r.postponementNoticeFileKey);
    return empty?.tmNo ?? rows[0]!.tmNo;
  };

  const onPickFromDropzone = async (file: File) => {
    const tmNo = nextTargetTmNo();
    if (!tmNo) return;
    await onUploadFor(tmNo, file);
  };

  return (
    <>
      <form
        id={EXAMINATION_REPORT_HAO_HEARING_NOT_HAPPENED_FORM_ID}
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col gap-5 self-stretch pt-5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <div
          className="flex flex-col gap-5 p-5 border border-[#EEEEEE] rounded-[10px] bg-white"
        >
          <div className="flex flex-col gap-2">
            <span
              className="text-[#101111]"
              style={{
                fontSize: "14px",
                lineHeight: "100%",
                fontWeight: 600,
                letterSpacing: "0.01em",
              }}
            >
              File Upload
            </span>
            <span
              className="text-[#7D7D7D]"
              style={{
                fontSize: "12px",
                lineHeight: "24px",
                fontWeight: 400,
                letterSpacing: "0.04em",
              }}
            >
              Choose a file and upload securely to proceed.
            </span>
          </div>

          {/* Drag and drop zone — matches FileUploadDropzone visual */}
          <div
            className="self-stretch flex flex-row justify-between items-center gap-5 px-5 py-5 rounded-[16px] border border-dashed border-[#D8DAEB] bg-white cursor-pointer transition-colors hover:bg-[#FCFCFC]"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const f = e.dataTransfer.files[0];
              if (f) void onPickFromDropzone(f);
            }}
            onClick={() => {
              if (uploading) return;
              document
                .getElementById(
                  "hearing-not-happened-dropzone-input"
                )
                ?.click();
            }}
          >
            <div className="flex flex-row items-center gap-5 min-w-0">
              <UploaderIllustration />
              <div className="flex flex-col gap-3 min-w-0">
                <p
                  className="font-medium text-[#101111]"
                  style={{
                    fontSize: "16px",
                    lineHeight: "100%",
                    letterSpacing: "0.01em",
                  }}
                >
                  Drag and drop your files
                </p>
                <p
                  className="font-normal text-[#7D7D7D]"
                  style={{ fontSize: "14px", lineHeight: "21px" }}
                >
                  {uploading
                    ? "Uploading…"
                    : "JPEG, PNG, PDF, and MP4 formats, up to 50MB"}
                </p>
              </div>
            </div>
            <button
              type="button"
              disabled={!!uploading}
              onClick={(e) => {
                e.stopPropagation();
                document
                  .getElementById(
                    "hearing-not-happened-dropzone-input"
                  )
                  ?.click();
              }}
              className="flex-shrink-0 flex items-center justify-center h-9 px-4 rounded-[10px] bg-white border border-[#D5D3E0] text-[#2E3037] hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{
                boxShadow: "0px 5px 20px rgba(0, 0, 0, 0.02)",
                fontSize: "14px",
                fontWeight: 500,
                lineHeight: "16px",
                letterSpacing: "0.04em",
              }}
            >
              Select File
            </button>
            <input
              id="hearing-not-happened-dropzone-input"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void onPickFromDropzone(f);
                e.currentTarget.value = "";
              }}
            />
          </div>
        </div>

        {/* Per-mark rows — PDF icon + name + Preview (matches figma) */}
        <div className="flex flex-col gap-3">
          {rows.map((row) => {
            const file = row.postponementNoticeFilename;
            const url = row.postponementNoticeFileUrl;
            const inputId = `hearing-not-happened-row-${row.tmNo}`;
            const label = row.mark || row.tmNo;
            return (
              <div
                key={row.tmNo}
                className="flex flex-row items-center gap-2.5 px-4 py-1 bg-[#FCFCFC] border border-[#EEEEEE] rounded-[6px] h-11"
              >
                <img
                  src={pdfIcon}
                  alt=""
                  className="w-[22px] h-[22px] flex-shrink-0"
                />
                <span
                  className="flex-1 min-w-0 truncate text-[#222222]"
                  style={{
                    fontSize: "12px",
                    lineHeight: "20px",
                    fontWeight: 500,
                  }}
                >
                  {label}
                  {file ? (
                    <span className="text-[#6D6D6D]"> — {file}</span>
                  ) : null}
                </span>
                {url ? (
                  <button
                    type="button"
                    onClick={() => {
                      const name =
                        file ?? `Postponement_Notice_${row.tmNo}.pdf`;
                      setPreview({
                        url,
                        name,
                        kind: detectPreviewKind(name),
                      });
                    }}
                    className="flex-shrink-0 flex items-center justify-center h-9 px-4 rounded-[6px] bg-[#222222] text-white text-xs font-medium hover:bg-[#333]"
                  >
                    Preview
                  </button>
                ) : (
                  <label className="flex-shrink-0 flex items-center justify-center h-9 px-4 rounded-[6px] bg-[#222222] text-white text-xs font-medium cursor-pointer hover:bg-[#333]">
                    <input
                      id={inputId}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void onUploadFor(row.tmNo, f);
                        e.target.value = "";
                      }}
                    />
                    {uploading === row.tmNo ? "Uploading…" : "Upload"}
                  </label>
                )}
              </div>
            );
          })}
        </div>

        {hasSent && (
          <div className="flex flex-row items-center gap-3">
            <button
              type="button"
              disabled={resending}
              onClick={onResend}
              className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {resending ? "Resending…" : "Resend Message"}
            </button>
          </div>
        )}
      </form>

      <FilePreviewModal
        open={!!preview}
        onClose={() => setPreview(null)}
        url={preview?.url ?? undefined}
        name={preview?.name ?? ""}
        kind={preview?.kind ?? "pdf"}
      />
    </>
  );
  // applications param kept for parity with sibling panels even if unused here
  void applications;
}

// ---------------------------------------------------------------------------
// 3D(xxiv) Inform Client after hearing — message preview + Send & Continue
// ---------------------------------------------------------------------------
const INFORM_AFTER_HEARING_STATUS_LABEL: Record<HearingStatusEnum, string> = {
  ATTENDED: "Attended",
  NOT_ATTENDED: "Not Attended",
  DIDNT_HAPPEN: "Didn't Happen",
};

const INFORM_AFTER_HEARING_REMARK_LABEL: Record<
  HearingOfficerRemarkEnum,
  string
> = {
  ACCEPTED: "Accepted",
  REFUSED: "Refused",
  WILL_PASS_ON_MERITS: "Will pass on merits",
  FILE_TM_M: "File TM-M",
  DIRECTION_OF_OFFICER: "Direction of Officer",
};

function InformClientAfterHearingPanel({
  leadId,
  selected,
  onAdvance,
  onSavingChange,
  hasSent,
  resending,
  onResend,
}: {
  leadId: string;
  selected: ShowCauseHearingApplication;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
  hasSent: boolean;
  resending: boolean;
  onResend: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<InformAfterHearingStatus | null>(null);
  const [scenario, setScenario] = useState<InformAfterHearingScenario | null>(
    null
  );
  const [selectedApp, setSelectedApp] = useState<
    InformAfterHearingScenarioGroup["applications"][number] | null
  >(null);
  const [message, setMessage] = useState<string>("");
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getInformAfterHearingStatus(leadId)
      .then((data) => {
        if (cancelled) return;
        setStatus(data);
        // Determine scenario and application data for the selected TM.
        const matchedGroup = data.groups.find((g) =>
          g.applications.some((a) => a.tmNo === selected.tmNo)
        );
        setScenario(matchedGroup?.scenario ?? null);
        setSelectedApp(
          matchedGroup?.applications.find((a) => a.tmNo === selected.tmNo) ??
            null
        );
      })
      .catch((err) => {
        if (!cancelled)
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Inform Client after hearing"
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, selected.tmNo]);

  useEffect(() => {
    if (!leadId || !scenario || scenario === "NOT_APPLICABLE") {
      setMessage("");
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    getInformAfterHearingPreview(leadId, scenario)
      .then((data) => {
        if (!cancelled) setMessage(data.message);
      })
      .catch(() => {
        if (!cancelled) setMessage("");
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, scenario]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSent) {
      onAdvance();
      return;
    }
    if (!scenario || scenario === "NOT_APPLICABLE") {
      toast.error("No applicable scenario for this application yet.");
      return;
    }
    onSavingChange?.(true);
    try {
      await sendInformAfterHearingScenario(leadId, scenario);
      toast.success("Inform Client (after hearing) sent to client");
      onAdvance();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send message");
    } finally {
      onSavingChange?.(false);
    }
  };

  if (loading) return <p className="text-sm text-[#6D6D6D] py-5">Loading…</p>;

  return (
    <form
      id={EXAMINATION_REPORT_HAO_INFORM_AFTER_HEARING_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-5 self-stretch pt-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <ReadOnlyTextField
          label="Acknowledgement No."
          value={status?.acknowledgementNo}
          placeholder="Acknowledgement No."
        />
        <ReadOnlyTextField
          label="Date of Filing"
          value={selectedApp?.dateOfFiling ?? ""}
          placeholder="Date of Filing"
        />
        <ReadOnlyTextField
          label="Mark"
          value={selectedApp?.mark ?? ""}
          placeholder="Mark"
        />
        <ReadOnlyTextField
          label="Hearing Status"
          value={
            selectedApp?.hearingStatus
              ? INFORM_AFTER_HEARING_STATUS_LABEL[selectedApp.hearingStatus]
              : ""
          }
          placeholder="Hearing Status"
        />
        <ReadOnlyTextField
          label="Remark of Hearing Officer"
          value={
            selectedApp?.hearingOfficerRemark
              ? INFORM_AFTER_HEARING_REMARK_LABEL[
                  selectedApp.hearingOfficerRemark
                ]
              : ""
          }
          placeholder="Remark of Hearing Officer"
        />
        <ReadOnlyTextField
          label="Scenario"
          value={scenario && scenario !== "NOT_APPLICABLE" ? scenario : ""}
          placeholder="Scenario"
        />
      </div>

      <div className="flex flex-col gap-5">
        <span
          className="text-[#222222]"
          style={{ fontSize: "16px", lineHeight: "20px", fontWeight: 400 }}
        >
          Message Preview
        </span>
        <div
          className="self-stretch bg-white border border-[#EEEEEE] rounded-[6px]"
          style={{ padding: "14px", minHeight: 540 }}
        >
          {previewLoading ? (
            <span
              className="text-[#6D6D6D]"
              style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
            >
              Loading preview…
            </span>
          ) : message ? (
            <p
              className="text-[#222222] whitespace-pre-line"
              style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
            >
              {renderWhatsAppFormatting(message)}
            </p>
          ) : (
            <span
              className="text-[#6D6D6D]"
              style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
            >
              No applicable message for this application yet.
            </span>
          )}
        </div>
      </div>

      {hasSent && (
        <div className="flex flex-row items-center gap-3">
          <button
            type="button"
            disabled={resending}
            onClick={onResend}
            className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {resending ? "Resending…" : "Resend Message"}
          </button>
        </div>
      )}
    </form>
  );
}

// ---------------------------------------------------------------------------
// 3D(xxv) Upload Documents after Hearing — record only
// ---------------------------------------------------------------------------
function UploadDocumentsAfterHearingPanel({
  leadId,
  selected,
  onAdvance,
  onSavingChange,
  onProgressActionChange,
}: {
  leadId: string;
  selected: ShowCauseHearingApplication;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
  onProgressActionChange?: (run: (() => Promise<void>) | null) => void;
}) {
  const [recording, setRecording] = useState(false);

  const handleSubmit = async () => {
    if (!leadId || recording) return;
    setRecording(true);
    onSavingChange?.(true);
    try {
      await recordUploadDocumentsAfterHearing(leadId, [selected.tmNo]);
      toast.success("Upload Documents after Hearing recorded");
      onAdvance();
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to record Upload Documents after Hearing"
      );
    } finally {
      setRecording(false);
      onSavingChange?.(false);
    }
  };

  // Register Progress (skip) handler so the dashboard footer renders the gray
  // Progress button next to Save & Continue — matches PaymentFromClientHearingPanel.
  useEffect(() => {
    if (!onProgressActionChange) return;
    if (!leadId) {
      onProgressActionChange(null);
      return;
    }
    const run = async () => {
      try {
        await skipStep(
          leadId,
          NewApplicationStep.S3_03d_xxv_UploadDocumentsAfterHearing
        );
        toast.success("Step skipped successfully.");
        onAdvance();
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to skip step"
        );
      }
    };
    onProgressActionChange(run);
    return () => onProgressActionChange(null);
  }, [leadId, onAdvance, onProgressActionChange]);

  return (
    <ProcessToNextStepPanel
      formId={EXAMINATION_REPORT_HAO_UPLOAD_DOCS_AFTER_HEARING_FORM_ID}
      message="Process to next step"
      onSubmit={handleSubmit}
    />
  );
}

// ---------------------------------------------------------------------------
// 3D(xxvi) Invoice for TM-M after Hearing — invoice form + Generate PDF + Preview
// ---------------------------------------------------------------------------
function InvoiceForTmMAfterHearingPanel({
  leadId,
  phone,
  selected,
  hasSent,
  onSent,
  onAdvance,
  onSavingChange,
  onGatedChange,
  onProgressActionChange,
}: {
  leadId: string;
  phone?: string;
  selected: ShowCauseHearingApplication;
  hasSent: boolean;
  onSent: () => void;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
  onGatedChange?: (gated: boolean) => void;
  onProgressActionChange?: (run: (() => Promise<void>) | null) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<TmMAfterHearingInvoiceStatus | null>(null);
  const [invoiceAmount, setInvoiceAmount] = useState<string>("");
  const [invoiceDate, setInvoiceDate] = useState<Date | null>(null);
  const [includeInvoiceNo, setIncludeInvoiceNo] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(phone ?? "");
  const [paymentMessage, setPaymentMessage] = useState<string>("");
  const [emailPreview, setEmailPreview] =
    useState<TmMAfterHearingInvoiceEmailPreview | null>(null);
  const [paymentMessageLoading, setPaymentMessageLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMode, setConfirmMode] = useState<"send" | "resend">("send");
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewTab, setPreviewTab] = useState<
    "details" | "pdf" | "message" | "email"
  >("details");
  const [previewNumPages, setPreviewNumPages] = useState<number | null>(null);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getTmMAfterHearingInvoiceStatus(leadId, [selected.tmNo]),
      getTmMAfterHearingInvoicePaymentMessagePreview(leadId, [
        selected.tmNo,
      ]).catch(() => null),
    ])
      .then(([data, messagePreview]) => {
        if (cancelled) return;
        setStatus(data);
        const row = data.tmInvoiceRows?.find((r) => r.tmNo === selected.tmNo);
        const amt =
          row?.invoiceAmount ??
          row?.computedInvoiceTotal ??
          data.invoiceAmount ??
          data.computedInvoiceTotal ??
          null;
        setInvoiceAmount(amt != null ? String(amt) : "");
        setInvoiceDate(
          parseDateForHearingInput(row?.invoiceDate ?? data.invoiceDate)
        );
        setIncludeInvoiceNo(
          Boolean(row?.includeInvoiceNo ?? data.includeInvoiceNo)
        );
        if (messagePreview?.paymentMessage) {
          setPaymentMessage(messagePreview.paymentMessage);
        }
        if (messagePreview?.emailPreview) {
          setEmailPreview(messagePreview.emailPreview);
        }
      })
      .catch((err) => {
        if (!cancelled)
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load TM-M after Hearing Invoice"
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, selected.tmNo]);

  const tmRow = status?.tmInvoiceRows?.find((r) => r.tmNo === selected.tmNo);
  const pdfUrl =
    tmRow?.tmMAfterHearingInvoicePdfUrl ??
    status?.tmMAfterHearingInvoicePdfUrl ??
    null;
  const hasPdf = !!pdfUrl;
  const pdfFilename = "Invoice for TM-M after Hearing.pdf";

  const persist = async (): Promise<TmMAfterHearingInvoiceStatus | null> => {
    const amt = Number(invoiceAmount);
    if (!Number.isFinite(amt) || amt < 0) {
      toast.error("Invoice Amount must be a non-negative number");
      return null;
    }
    if (!invoiceDate) {
      toast.error("Date of Invoice is required");
      return null;
    }
    const updated = await saveTmMAfterHearingInvoice(leadId, [
      {
        tmNo: selected.tmNo,
        invoiceAmount: amt,
        invoiceDate: formatDateDDMMYYYYForApi(invoiceDate),
        includeInvoiceNo,
      },
    ]);
    setStatus(updated);
    return updated;
  };

  const refreshPaymentMessagePreview = async () => {
    if (!leadId) return;
    setPaymentMessageLoading(true);
    try {
      const res = await getTmMAfterHearingInvoicePaymentMessagePreview(leadId, [
        selected.tmNo,
      ]);
      setPaymentMessage(res.paymentMessage ?? "");
      setEmailPreview(res.emailPreview ?? null);
    } catch {
      // Non-critical — keep existing values.
    } finally {
      setPaymentMessageLoading(false);
    }
  };

  const runGenerate = async (): Promise<string | null> => {
    const saved = await persist();
    if (!saved) return null;
    await generateTmMAfterHearingInvoicePdf(leadId, [selected.tmNo]);
    const refreshed = await getTmMAfterHearingInvoiceStatus(leadId, [
      selected.tmNo,
    ]);
    setStatus(refreshed);
    const row = refreshed.tmInvoiceRows?.find(
      (r) => r.tmNo === selected.tmNo
    );
    return (
      row?.tmMAfterHearingInvoicePdfUrl ??
      refreshed.tmMAfterHearingInvoicePdfUrl ??
      null
    );
  };

  const handleGeneratePdf = async () => {
    if (!leadId || generating) return;
    setGenerating(true);
    onSavingChange?.(true);
    try {
      const url = await runGenerate();
      if (!url) return;
      toast.success("TM-M after Hearing Invoice PDF generated");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to generate PDF"
      );
    } finally {
      setGenerating(false);
      onSavingChange?.(false);
    }
  };

  const ensurePdf = async (): Promise<string | null> => {
    if (pdfUrl) return pdfUrl;
    if (!leadId) return null;
    try {
      return await runGenerate();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to generate PDF"
      );
      return null;
    }
  };

  const handleDownload = async () => {
    if (!leadId || downloading) return;
    setDownloading(true);
    try {
      const url = await ensurePdf();
      if (!url) return;
      const result = await downloadFileFromUrlWithFallback(url, pdfFilename);
      if (result === "local") {
        toast.success("PDF saved to your device.");
      } else {
        toast.info(
          "Opened the PDF in a new tab. Use Save or Save as to store it locally."
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const handlePreview = async () => {
    if (!leadId) return;
    const url = await ensurePdf();
    if (!url) return;
    setPreviewTab("details");
    setPreviewNumPages(null);
    setPreviewModalOpen(true);
  };

  const closePreviewModal = () => {
    setPreviewModalOpen(false);
    setPreviewNumPages(null);
    setPreviewTab("details");
  };

  const isGated = !!status && status.isActive === false;

  useEffect(() => {
    onGatedChange?.(isGated);
    return () => {
      onGatedChange?.(false);
    };
  }, [isGated, onGatedChange]);

  useEffect(() => {
    if (!onProgressActionChange) return;
    if (!leadId) {
      onProgressActionChange(null);
      return;
    }
    const run = async () => {
      try {
        await skipStep(
          leadId,
          NewApplicationStep.S3_03d_xxvi_a_SaveInvoiceTmMAfterHearing
        );
        toast.success("Step skipped successfully.");
        onAdvance();
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to skip step"
        );
      }
    };
    onProgressActionChange(run);
    return () => onProgressActionChange(null);
  }, [leadId, onAdvance, onProgressActionChange]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || sending) return;

    if (isGated) {
      // Direction != "File TM-M" — nothing to save here, just advance.
      onAdvance();
      return;
    }

    if (hasSent) {
      // Already sent — Save & Continue just persists and advances.
      onSavingChange?.(true);
      try {
        const saved = await persist();
        if (!saved) return;
        toast.success("TM-M after Hearing Invoice saved");
        onAdvance();
      } catch (err) {
        toast.error(
          err instanceof Error
            ? err.message
            : "Failed to save TM-M after Hearing Invoice"
        );
      } finally {
        onSavingChange?.(false);
      }
      return;
    }

    // First-time send: save → ensure PDF exists → refresh preview → open confirm modal.
    setSending(true);
    onSavingChange?.(true);
    try {
      const saved = await persist();
      if (!saved) return;
      const url = await ensurePdf();
      if (!url) return;
      await refreshPaymentMessagePreview();
      setConfirmMode("send");
      setShowConfirmModal(true);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save TM-M after Hearing Invoice"
      );
    } finally {
      setSending(false);
      onSavingChange?.(false);
    }
  };

  const handleConfirmProceed = async () => {
    if (!leadId) return;
    setShowConfirmModal(false);
    setSending(true);
    onSavingChange?.(true);
    try {
      const [invoiceResult, paymentResult] = await Promise.allSettled([
        sendTmMAfterHearingInvoiceToClient(leadId, {
          sendWhatsApp: true,
          sendEmail: true,
          tmNos: [selected.tmNo],
        }),
        sendTmMAfterHearingInvoicePaymentMessage(leadId, [selected.tmNo]),
      ]);
      const invoiceOk = invoiceResult.status === "fulfilled";
      const paymentOk = paymentResult.status === "fulfilled";
      const invoiceErr =
        invoiceResult.status === "rejected"
          ? invoiceResult.reason instanceof Error
            ? invoiceResult.reason.message
            : "Failed to send TM-M after Hearing Invoice"
          : null;
      const paymentErr =
        paymentResult.status === "rejected"
          ? paymentResult.reason instanceof Error
            ? paymentResult.reason.message
            : "Failed to send payment message"
          : null;
      const isResend = confirmMode === "resend";

      if (invoiceOk && paymentOk) {
        onSent();
        toast.success(
          isResend
            ? "TM-M after Hearing Invoice resent to client."
            : "TM-M after Hearing Invoice sent to client."
        );
        onAdvance();
      } else if (invoiceOk && !paymentOk) {
        onSent();
        toast.warning(
          `Invoice sent, but payment message failed: ${paymentErr}`
        );
        onAdvance();
      } else if (!invoiceOk && paymentOk) {
        toast.warning(
          `Payment message sent, but sending invoice failed: ${invoiceErr}`
        );
      } else {
        toast.error(invoiceErr ?? "Failed to send TM-M after Hearing Invoice");
      }
    } finally {
      setSending(false);
      onSavingChange?.(false);
    }
  };

  const handleResend = async () => {
    if (!leadId || sending) return;
    setSending(true);
    onSavingChange?.(true);
    try {
      const saved = await persist();
      if (!saved) return;
      const url = await ensurePdf();
      if (!url) return;
      await refreshPaymentMessagePreview();
      setConfirmMode("resend");
      setShowConfirmModal(true);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save TM-M after Hearing Invoice"
      );
    } finally {
      setSending(false);
      onSavingChange?.(false);
    }
  };

  if (loading) return <p className="text-sm text-[#6D6D6D] py-5">Loading…</p>;

  if (isGated) {
    return (
      <form
        id={EXAMINATION_REPORT_HAO_TMM_AFTER_INVOICE_FORM_ID}
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col gap-3 self-stretch pt-5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          This step is only available when the hearing officer's direction is
          "File TM-M". Click "Skip &amp; Continue" to proceed to the next step.
        </p>
      </form>
    );
  }

  const addressee = status?.addressee ?? "";
  const formOfAddress = status?.formOfAddress ?? "";
  const dateOfHearingDisplay = status?.dateOfHearing ?? "";
  const noOfApps =
    status?.noOfAppsForTmMAfterHearing != null
      ? String(status.noOfAppsForTmMAfterHearing)
      : "1";

  return (
    <>
      <form
        id={EXAMINATION_REPORT_HAO_TMM_AFTER_INVOICE_FORM_ID}
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col gap-5 self-stretch pt-5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="flex flex-col gap-1">
            <div
              className="flex flex-row items-center gap-1"
              style={{ height: "20px" }}
            >
              <label
                className="text-[#222222]"
                style={{
                  fontSize: "12px",
                  lineHeight: "20px",
                  fontWeight: 400,
                }}
              >
                Invoice Amount for TM-M after Hearing
              </label>
              <span className="relative inline-flex items-center group">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                  className="cursor-help"
                >
                  <circle
                    cx="8"
                    cy="8"
                    r="6.5"
                    stroke="#666666"
                    strokeWidth="1.25"
                  />
                  <path
                    d="M8 7.5 V11"
                    stroke="#666666"
                    strokeWidth="1.25"
                    strokeLinecap="round"
                  />
                  <circle cx="8" cy="5.33" r="0.6" fill="#666666" />
                </svg>
                <span
                  role="tooltip"
                  className="hidden group-hover:flex absolute z-20 left-0 top-[24px] flex-row justify-center items-center rounded-[6px] text-white"
                  style={{
                    width: "474px",
                    maxWidth: "calc(100vw - 40px)",
                    height: "68px",
                    padding: "4px 10px",
                    gap: "10px",
                    background: "#565656",
                  }}
                >
                  <span
                    className="block"
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontStyle: "normal",
                      fontWeight: 400,
                      fontSize: "12px",
                      lineHeight: "20px",
                      color: "#FFFFFF",
                    }}
                  >
                    Invoice Amount for TM-M after Hearing : The charges will be
                    Rs.1500/- per Application Number (TM No.). That is 1500 ×
                    No. of applications for TM-M after Hearing.
                  </span>
                </span>
              </span>
            </div>
            <input
              type="text"
              inputMode="decimal"
              value={invoiceAmount}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^\d.]/g, "");
                const parts = raw.split(".");
                const v =
                  parts.length > 1
                    ? `${parts[0]}.${parts.slice(1).join("")}`
                    : raw;
                setInvoiceAmount(v);
              }}
              placeholder="Number"
              className="box-border w-full px-2.5 py-2 h-9 text-xs leading-5 bg-white border border-[#EEEEEE] rounded-[6px] focus:outline-none focus:ring-1 focus:ring-[#222222] placeholder:text-[#6D6D6D]"
            />
          </div>
          <ReadOnlyTextField
            label="Invoice No. for TM-M after Hearing"
            value={status?.invoiceNo}
            placeholder="Invoice No."
          />
        </div>
        <IncludeInvoiceNoCheckbox
          id="tmm-after-hearing-include-invoice-no"
          checked={includeInvoiceNo}
          onChange={setIncludeInvoiceNo}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="flex flex-col gap-1">
            <label
              className="text-[#222222]"
              style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
            >
              Date of Invoice for TM-M after Hearing
            </label>
            <DateTimePicker
              value={invoiceDate}
              onChange={(d) => setInvoiceDate(d)}
            />
          </div>
          <ReadOnlyTextField
            label="No. of applications for TM-M after Hearing"
            value={
              status?.noOfAppsForTmMAfterHearing != null
                ? String(status.noOfAppsForTmMAfterHearing)
                : ""
            }
            placeholder="Number"
          />
        </div>

        {/* Generate PDF + Preview/Download */}
        <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2.5">
          <div className="flex flex-row items-center gap-2.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] pl-4 pr-2 py-2 min-h-[44px] sm:h-11">
            <img
              src={invoiceGenerate}
              alt=""
              className="w-[22px] h-[22px] flex-shrink-0"
            />
            <button
              type="button"
              onClick={() => void handleGeneratePdf()}
              disabled={generating}
              className="flex flex-row items-center justify-center min-h-[40px] h-9 px-4 rounded-[6px] bg-[#222222] text-[12px] md:text-xs font-medium text-white hover:bg-[#333333] transition-colors shrink-0 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              {generating
                ? "Generating..."
                : hasPdf
                  ? "Regenerate PDF"
                  : "Generate PDF"}
            </button>
          </div>
          <button
            type="button"
            onClick={() => void handleDownload()}
            disabled={downloading || generating}
            className="flex flex-row items-center justify-center min-h-[44px] sm:h-11 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] md:text-xs font-medium text-[#222222] hover:bg-[#F7F7F7] transition-colors shrink-0 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            {downloading ? "Downloading…" : "Download"}
          </button>
          <button
            type="button"
            onClick={() => void handlePreview()}
            disabled={generating}
            className="flex flex-row items-center justify-center min-h-[44px] sm:h-11 px-4 rounded-[6px] border border-[#EEEEEE] bg-white text-[12px] md:text-xs font-medium text-[#222222] hover:bg-[#F7F7F7] transition-colors shrink-0 disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            Preview
          </button>
        </div>

        {/* Phone number + optional Resend */}
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="tel"
            inputMode="numeric"
            value={phoneNumber}
            readOnly
            placeholder="e.g. +919876543210"
            className="w-full min-w-0 sm:w-[200px] h-9 px-[10px] py-2 text-[12px] text-[#222] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] placeholder:text-[#6D6D6D]"
          />
          {hasSent && (
            <button
              type="button"
              disabled={sending}
              onClick={() => void handleResend()}
              className="inline-flex items-center justify-center h-9 px-4 text-[12px] font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              {sending ? "Sending…" : "Resend Message"}
            </button>
          )}
        </div>

        <ConfirmMessageModal
          open={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          title={
            confirmMode === "resend"
              ? "Resend TM-M after Hearing Invoice to client"
              : "Confirm TM-M after Hearing Invoice to client"
          }
          previewSlot={
            <div className="flex flex-col gap-4 p-4">
              <section>
                <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                  WhatsApp message (after invoice PDF)
                </p>
                {paymentMessageLoading ? (
                  <p className="text-sm text-[#6d6d6d]">
                    Loading message preview…
                  </p>
                ) : paymentMessage ? (
                  renderInvoicePaymentMessageLines(paymentMessage)
                ) : (
                  <p className="text-sm text-[#6D6D6D]">
                    Message preview not available.
                  </p>
                )}
              </section>
              <div className="border-t border-dashed border-[#EEEEEE]" />
              <section>
                <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                  Email (with invoice PDF)
                </p>
                <HearingEmailPreviewBlock
                  preview={emailPreview}
                  loading={paymentMessageLoading}
                  attachmentFallback={
                    hasPdf && pdfUrl
                      ? {
                          filename: pdfFilename,
                          url: pdfUrl,
                        }
                      : null
                  }
                />
              </section>
            </div>
          }
          onConfirm={() => void handleConfirmProceed()}
          disableConfirm={sending}
        />

        {previewModalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4"
            role="dialog"
            aria-modal="true"
            onClick={closePreviewModal}
          >
            <div
              className="relative flex h-[95vh] sm:h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex shrink-0 items-center justify-between border-b border-[#e3e3e3] px-3 py-3 sm:px-4">
                <span
                  className="truncate pr-2 text-xs font-medium text-[#222] sm:text-sm"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  {pdfFilename}
                </span>
                <div className="flex items-center gap-3 shrink-0">
                  {pdfUrl && (
                    <a
                      href={pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-[#2E3037] underline underline-offset-2"
                    >
                      Open in new tab
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={closePreviewModal}
                    className="flex size-10 shrink-0 items-center justify-center rounded text-[#6d6d6d] hover:bg-[#eee] hover:text-[#222] sm:size-8 touch-manipulation"
                    aria-label="Close preview"
                  >
                    <HiOutlineX className="size-5" strokeWidth={2} />
                  </button>
                </div>
              </div>

              <div
                className="flex shrink-0 gap-1 border-b border-[#e3e3e3] bg-white px-2 sm:px-3"
                role="tablist"
              >
                {(["details", "pdf", "message", "email"] as const).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    role="tab"
                    aria-selected={previewTab === tab}
                    onClick={() => setPreviewTab(tab)}
                    disabled={tab === "pdf" && !pdfUrl}
                    className={`rounded-t-md px-3 py-2 text-xs font-medium sm:text-sm touch-manipulation disabled:cursor-not-allowed disabled:opacity-50 ${
                      previewTab === tab
                        ? "border border-b-0 border-[#e3e3e3] bg-[#fafafa] text-[#101111]"
                        : "text-[#6d6d6d] hover:text-[#222]"
                    }`}
                  >
                    {tab === "details"
                      ? "Details"
                      : tab === "pdf"
                        ? "Generated PDF"
                        : tab === "message"
                          ? "WhatsApp Message"
                          : "Email"}
                  </button>
                ))}
              </div>

              <div className="min-h-0 flex-1 overflow-auto bg-[#fafafa] p-2 sm:p-4 flex justify-center">
                {previewTab === "details" ? (
                  <div
                    className="flex w-full max-w-3xl flex-col gap-3"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    <section className="rounded-lg border border-[#EEEEEE] bg-white p-3 shadow-sm sm:p-4">
                      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                        Invoice details
                      </p>
                      <div className="grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-3 gap-y-1.5 rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2.5 text-[12px] leading-5">
                        <div className="font-medium text-[#222]">
                          Acknowledgement No.
                        </div>
                        <div className="text-[#222]">:</div>
                        <div className="text-[#222] break-words min-w-0">
                          {status?.acknowledgementNo || (
                            <span className="text-[#6D6D6D]">—</span>
                          )}
                        </div>
                        <div className="font-medium text-[#222]">
                          Date of Filing
                        </div>
                        <div className="text-[#222]">:</div>
                        <div className="text-[#222] break-words min-w-0">
                          {status?.dateOfFiling || (
                            <span className="text-[#6D6D6D]">—</span>
                          )}
                        </div>
                        <div className="font-medium text-[#222]">Addressee</div>
                        <div className="text-[#222]">:</div>
                        <div className="text-[#222] break-words min-w-0">
                          {status?.addressee || (
                            <span className="text-[#6D6D6D]">—</span>
                          )}
                        </div>
                        <div className="font-medium text-[#222]">
                          Form of Address
                        </div>
                        <div className="text-[#222]">:</div>
                        <div className="text-[#222] break-words min-w-0">
                          {status?.formOfAddress || (
                            <span className="text-[#6D6D6D]">—</span>
                          )}
                        </div>
                        {/*
                          Review item 43: the window never named the application it described.
                          Placed immediately before the application's own attributes, mirroring the
                          spec's per-application block (TM No. → Date of Filing → Mark → Class).
                          Date of Filing is already shown above, next to Acknowledgement No.
                        */}
                        <div className="font-medium text-[#222]">
                          Application Number (TM No.)
                        </div>
                        <div className="text-[#222]">:</div>
                        <div className="text-[#222] break-words min-w-0">
                          {tmRow?.tmNo ? (
                            formatTmNoForDisplay(tmRow.tmNo)
                          ) : (
                            <span className="text-[#6D6D6D]">—</span>
                          )}
                        </div>
                        <div className="font-medium text-[#222]">Mark</div>
                        <div className="text-[#222]">:</div>
                        <div className="text-[#222] break-words min-w-0">
                          {tmRow?.mark || status?.mark || (
                            <span className="text-[#6D6D6D]">—</span>
                          )}
                        </div>
                        <div className="font-medium text-[#222]">
                          Selected Type of Mark
                        </div>
                        <div className="text-[#222]">:</div>
                        <div className="text-[#222] break-words min-w-0">
                          {status?.selectedTypeOfMark || (
                            <span className="text-[#6D6D6D]">—</span>
                          )}
                        </div>
                        <div className="font-medium text-[#222]">
                          Selected Class
                        </div>
                        <div className="text-[#222]">:</div>
                        <div className="text-[#222] break-words min-w-0">
                          {tmRow?.classNumber != null
                            ? String(tmRow.classNumber)
                            : status?.selectedClass || (
                                <span className="text-[#6D6D6D]">—</span>
                              )}
                        </div>
                        <div className="font-medium text-[#222]">
                          Date of Hearing
                        </div>
                        <div className="text-[#222]">:</div>
                        <div className="text-[#222] break-words min-w-0">
                          {status?.dateOfHearing || (
                            <span className="text-[#6D6D6D]">—</span>
                          )}
                        </div>
                        <div className="font-medium text-[#222]">
                          No. of applications for TM-M after Hearing
                        </div>
                        <div className="text-[#222]">:</div>
                        <div className="text-[#222] break-words min-w-0">
                          {status?.noOfAppsForTmMAfterHearing != null
                            ? String(status.noOfAppsForTmMAfterHearing)
                            : <span className="text-[#6D6D6D]">—</span>}
                        </div>
                        <div className="font-medium text-[#222]">
                          Invoice Amount for TM-M after Hearing
                        </div>
                        <div className="text-[#222]">:</div>
                        <div className="text-[#222] break-words min-w-0">
                          {invoiceAmount
                            ? `Rs. ${Number(invoiceAmount).toLocaleString("en-IN")}/-`
                            : <span className="text-[#6D6D6D]">—</span>}
                        </div>
                        <div className="font-medium text-[#222]">
                          Invoice No. for TM-M after Hearing
                        </div>
                        <div className="text-[#222]">:</div>
                        <div className="text-[#222] break-words min-w-0">
                          {status?.invoiceNo?.trim() || (
                            <span className="text-[#6D6D6D]">
                              Auto-generated on save
                            </span>
                          )}
                        </div>
                        <div className="font-medium text-[#222]">
                          Date of Invoice for TM-M after Hearing
                        </div>
                        <div className="text-[#222]">:</div>
                        <div className="text-[#222] break-words min-w-0">
                          {tmRow?.invoiceDate || status?.invoiceDate || (
                            <span className="text-[#6D6D6D]">—</span>
                          )}
                        </div>
                      </div>
                    </section>
                  </div>
                ) : previewTab === "pdf" ? (
                  pdfUrl ? (
                    <Document
                      file={pdfUrl}
                      onLoadSuccess={({ numPages: n }) => setPreviewNumPages(n)}
                      loading={
                        <p className="text-sm text-[#6d6d6d]">Loading PDF…</p>
                      }
                      error={
                        <p className="text-sm text-red-600">
                          Failed to load PDF.
                        </p>
                      }
                      className="flex flex-col items-center gap-4"
                    >
                      {previewNumPages != null &&
                        Array.from({ length: previewNumPages }, (_, i) => (
                          <Page
                            key={i + 1}
                            pageNumber={i + 1}
                            width={Math.min(
                              800,
                              typeof window !== "undefined"
                                ? window.innerWidth - 48
                                : 800
                            )}
                            renderTextLayer
                            renderAnnotationLayer
                          />
                        ))}
                    </Document>
                  ) : (
                    <p className="text-sm text-[#6D6D6D]">No PDF available.</p>
                  )
                ) : previewTab === "message" ? (
                  <div
                    className="w-full max-w-2xl rounded-lg border border-[#EEEEEE] bg-white p-4 shadow-sm sm:p-5"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                      Will be sent to client WhatsApp after the invoice PDF
                    </p>
                    {paymentMessageLoading ? (
                      <p className="text-sm text-[#6d6d6d]">
                        Loading message preview…
                      </p>
                    ) : paymentMessage ? (
                      renderInvoicePaymentMessageLines(paymentMessage)
                    ) : (
                      <p className="text-sm text-[#6D6D6D]">
                        Message preview not available.
                      </p>
                    )}
                  </div>
                ) : (
                  <div
                    className="w-full max-w-2xl rounded-lg border border-[#EEEEEE] bg-white p-4 shadow-sm sm:p-5"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                      Will be emailed to the client with the invoice PDF
                    </p>
                    <HearingEmailPreviewBlock
                      preview={emailPreview}
                      loading={paymentMessageLoading}
                      attachmentFallback={
                        hasPdf && pdfUrl
                          ? {
                              filename: pdfFilename,
                              url: pdfUrl,
                            }
                          : null
                      }
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </form>
    </>
  );
}

// ---------------------------------------------------------------------------
// 3D(xxvii) Payment from Client for TM-M after Hearing — Invoice/Collected/Outstanding
// ---------------------------------------------------------------------------
function PaymentFromClientForTmMAfterHearingPanel({
  leadId,
  selected,
  onAdvance,
  onSavingChange,
  onGatedChange,
}: {
  leadId: string;
  selected: ShowCauseHearingApplication;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
  onGatedChange?: (gated: boolean) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<TmMAfterHearingInvoiceStatus | null>(null);
  const [savedCollected, setSavedCollected] = useState<number>(0);
  const [newInput, setNewInput] = useState<string>("");

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getTmMAfterHearingInvoiceStatus(leadId, [selected.tmNo])
      .then((data) => {
        if (cancelled) return;
        setStatus(data);
        const row = data.tmInvoiceRows?.find((r) => r.tmNo === selected.tmNo);
        const c = Number(row?.collectedAmount ?? data.collectedAmount ?? 0) || 0;
        setSavedCollected(c);
        setNewInput("");
      })
      .catch((err) => {
        if (!cancelled)
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load TM-M after Hearing payment"
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, selected.tmNo]);

  const tmRow = status?.tmInvoiceRows?.find((r) => r.tmNo === selected.tmNo);
  const invoiceAmount =
    tmRow?.invoiceAmount ??
    tmRow?.computedInvoiceTotal ??
    status?.invoiceAmount ??
    0;
  const additionalNum = parseFloat(newInput.replace(/[^\d.]/g, "")) || 0;
  const outstanding = Math.max(0, invoiceAmount - savedCollected - additionalNum);
  const outstandingFormatted = outstanding.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  const invoiceFormatted = invoiceAmount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const isGated = !!status && status.isActive === false;

  useEffect(() => {
    onGatedChange?.(isGated);
    return () => {
      onGatedChange?.(false);
    };
  }, [isGated, onGatedChange]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (isGated) {
      onAdvance();
      return;
    }
    const total = savedCollected + additionalNum;
    if (!Number.isFinite(total) || total < 0) {
      toast.error("Collected Amount must be a non-negative number");
      return;
    }
    if (invoiceAmount > 0 && total > invoiceAmount) {
      toast.error("Collected amount cannot exceed the invoice amount");
      return;
    }
    onSavingChange?.(true);
    try {
      const updated = await saveTmMAfterHearingCollectedAmount(leadId, [
        { tmNo: selected.tmNo, collectedAmount: total },
      ]);
      setStatus(updated);
      setSavedCollected(total);
      setNewInput("");
      toast.success("Payment recorded");
      onAdvance();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to record payment"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  if (loading) return <p className="text-sm text-[#6D6D6D] py-5">Loading…</p>;

  if (isGated) {
    return (
      <form
        id={EXAMINATION_REPORT_HAO_TMM_AFTER_PAYMENT_FORM_ID}
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col gap-3 self-stretch pt-5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          This step is only available when the hearing officer's direction is
          "File TM-M". Click "Skip &amp; Continue" to proceed to the next step.
        </p>
      </form>
    );
  }

  return (
    <form
      id={EXAMINATION_REPORT_HAO_TMM_AFTER_PAYMENT_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col items-start gap-[22px] w-full pt-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col sm:flex-row items-start sm:items-start gap-5 w-full">
        <TmmPaymentCurrencyField
          label="Invoice Amount for TM-M after Hearing"
          value={invoiceFormatted}
          onChange={() => {}}
          readOnly
        />
        <div
          className="flex flex-col gap-1"
          style={{ flex: "1 1 0", minWidth: 0 }}
        >
          <TmmPaymentCurrencyField
            label="Collected Amount for TM-M after Hearing"
            value={newInput}
            onChange={setNewInput}
          />
          {savedCollected > 0 && (
            <span className="text-[11px] leading-4 text-[#6D6D6D]">
              Already collected: Rs.{" "}
              {savedCollected.toLocaleString("en-IN", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })}
              /-
            </span>
          )}
        </div>
      </div>

      <div
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-3 w-full px-4 sm:px-5 py-4 sm:py-0 min-h-[60px] sm:h-[60px]"
        style={{ background: "#222222", borderRadius: "8px" }}
      >
        <span className="text-[12px] sm:text-[14px] font-normal leading-5 text-white/90">
          Outstanding Amount for TM-M after Hearing
        </span>
        <span className="text-[18px] sm:text-[22px] font-bold leading-8 sm:leading-10 text-white whitespace-nowrap">
          Rs. {outstandingFormatted}/-
        </span>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Shared: dropzone + per-row file rows ("File Upload" card with drag-and-drop)
// ---------------------------------------------------------------------------
function MultiFileUploadCard({
  inputIdPrefix,
  rows,
  onPickForRow,
  uploadingTmNo,
  onPreview,
}: {
  inputIdPrefix: string;
  rows: Array<{
    tmNo: string;
    label: string;
    filename: string | null;
    url: string | null;
  }>;
  onPickForRow: (tmNo: string, file: File) => Promise<void> | void;
  uploadingTmNo: string | null;
  onPreview: (row: {
    tmNo: string;
    label: string;
    filename: string | null;
    url: string | null;
  }) => void;
}) {
  const nextTargetTmNo = (): string | null => {
    const empty = rows.find((r) => !r.url && !r.filename);
    return empty?.tmNo ?? rows[0]?.tmNo ?? null;
  };

  const onPickFromDropzone = async (file: File) => {
    const tmNo = nextTargetTmNo();
    if (!tmNo) return;
    await onPickForRow(tmNo, file);
  };

  const dropzoneInputId = `${inputIdPrefix}-dropzone`;

  return (
    <>
      <div
        className="flex flex-col gap-5 p-5 border border-[#EEEEEE] rounded-[10px] bg-white"
      >
        <div className="flex flex-col gap-2">
          <span
            className="text-[#101111]"
            style={{
              fontSize: "14px",
              lineHeight: "100%",
              fontWeight: 600,
              letterSpacing: "0.01em",
            }}
          >
            File Upload
          </span>
          <span
            className="text-[#7D7D7D]"
            style={{
              fontSize: "12px",
              lineHeight: "24px",
              fontWeight: 400,
              letterSpacing: "0.04em",
            }}
          >
            Choose a file and upload securely to proceed.
          </span>
        </div>
        <div
          className="self-stretch flex flex-row justify-between items-center gap-5 px-5 py-5 rounded-[16px] border border-dashed border-[#D8DAEB] bg-white cursor-pointer transition-colors hover:bg-[#FCFCFC]"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const f = e.dataTransfer.files[0];
            if (f) void onPickFromDropzone(f);
          }}
          onClick={() => {
            if (uploadingTmNo) return;
            document.getElementById(dropzoneInputId)?.click();
          }}
        >
          <div className="flex flex-row items-center gap-5 min-w-0">
            <UploaderIllustration />
            <div className="flex flex-col gap-3 min-w-0">
              <p
                className="font-medium text-[#101111]"
                style={{
                  fontSize: "16px",
                  lineHeight: "100%",
                  letterSpacing: "0.01em",
                }}
              >
                Drag and drop your files
              </p>
              <p
                className="font-normal text-[#7D7D7D]"
                style={{ fontSize: "14px", lineHeight: "21px" }}
              >
                {uploadingTmNo
                  ? "Uploading…"
                  : "JPEG, PNG, PDF, and MP4 formats, up to 50MB"}
              </p>
            </div>
          </div>
          <button
            type="button"
            disabled={!!uploadingTmNo}
            onClick={(e) => {
              e.stopPropagation();
              document.getElementById(dropzoneInputId)?.click();
            }}
            className="flex-shrink-0 flex items-center justify-center h-9 px-4 rounded-[10px] bg-white border border-[#D5D3E0] text-[#2E3037] hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              boxShadow: "0px 5px 20px rgba(0, 0, 0, 0.02)",
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: "16px",
              letterSpacing: "0.04em",
            }}
          >
            Select File
          </button>
          <input
            id={dropzoneInputId}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onPickFromDropzone(f);
              e.currentTarget.value = "";
            }}
          />
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {rows.map((row) => {
          const inputId = `${inputIdPrefix}-row-${row.tmNo}`;
          return (
            <div
              key={row.tmNo}
              className="flex flex-row items-center gap-2.5 px-4 py-1 bg-[#FCFCFC] border border-[#EEEEEE] rounded-[6px] h-11"
            >
              <img
                src={pdfIcon}
                alt=""
                className="w-[22px] h-[22px] flex-shrink-0"
              />
              <span
                className="flex-1 min-w-0 truncate text-[#222222]"
                style={{
                  fontSize: "12px",
                  lineHeight: "20px",
                  fontWeight: 500,
                }}
              >
                {row.label}
                {row.filename ? (
                  <span className="text-[#6D6D6D]"> — {row.filename}</span>
                ) : null}
              </span>
              {row.url ? (
                <button
                  type="button"
                  onClick={() => onPreview(row)}
                  className="flex-shrink-0 flex items-center justify-center h-9 px-4 rounded-[6px] bg-[#222222] text-white text-xs font-medium hover:bg-[#333]"
                >
                  Preview
                </button>
              ) : (
                <label className="flex-shrink-0 flex items-center justify-center h-9 px-4 rounded-[6px] bg-[#222222] text-white text-xs font-medium cursor-pointer hover:bg-[#333]">
                  <input
                    id={inputId}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void onPickForRow(row.tmNo, f);
                      e.target.value = "";
                    }}
                  />
                  {uploadingTmNo === row.tmNo ? "Uploading…" : "Upload"}
                </label>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// 3D(xxviii) File TM-M after Hearing 1
// ---------------------------------------------------------------------------
const TM_M_AFTER_HEARING_FILED_FOLDER = "tm-m-after-hearing-filed";

function FileTmMAfterHearingPanel({
  leadId,
  selected,
  onAdvance,
  onSavingChange,
  onGatedChange,
}: {
  leadId: string;
  selected: ShowCauseHearingApplication;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
  onGatedChange?: (gated: boolean) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<TmMAfterHearingFiledStatus | null>(null);
  const [date, setDate] = useState<Date | null>(null);
  const [uploadingTmNo, setUploadingTmNo] = useState<string | null>(null);
  const [preview, setPreview] = useState<
    { url: string; name: string; kind: FilePreviewKind } | null
  >(null);

  const isGated = !!status && status.isActive === false;

  useEffect(() => {
    onGatedChange?.(isGated);
    return () => {
      onGatedChange?.(false);
    };
  }, [isGated, onGatedChange]);

  const reload = (cancelledRef: { cancelled: boolean }) => {
    if (!leadId) return;
    setLoading(true);
    getTmMAfterHearingFiledStatus(leadId)
      .then((data) => {
        if (cancelledRef.cancelled) return;
        setStatus(data);
        const row =
          data.applications.find((a) => a.tmNo === selected.tmNo) ?? null;
        setDate(parseDateForHearingInput(row?.dateOfTmMAfterHearingFiled));
      })
      .catch((err) => {
        if (!cancelledRef.cancelled)
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load File TM-M after Hearing"
          );
      })
      .finally(() => {
        if (!cancelledRef.cancelled) setLoading(false);
      });
  };

  useEffect(() => {
    const ref = { cancelled: false };
    reload(ref);
    return () => {
      ref.cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, selected.tmNo]);

  // Auto-save date when it changes — keeps downstream Inform Client TM-M preview fresh.
  // Always send the row's current file key + filename alongside so concurrent autosaves
  // don't blank the just-uploaded PDF (the DTO's optional-string transform coerces
  // missing fields to null, which would otherwise wipe them).
  useEffect(() => {
    if (!leadId || !date) return;
    const formatted = formatDateDDMMYYYYForApi(date);
    const row = status?.applications.find((a) => a.tmNo === selected.tmNo);
    if (row?.dateOfTmMAfterHearingFiled === formatted) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        await saveTmMAfterHearingFiled(leadId, [
          {
            tmNo: selected.tmNo,
            dateOfTmMAfterHearingFiled: formatted,
            tmMAfterHearingFileKey: row?.tmMAfterHearingFileKey ?? null,
            tmMAfterHearingFilename: row?.tmMAfterHearingFilename ?? null,
          },
        ]);
        if (!cancelled) {
          const refreshed = await getTmMAfterHearingFiledStatus(leadId);
          if (!cancelled) setStatus(refreshed);
        }
      } catch {
        /* silent — surface only on explicit submit */
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [leadId, selected.tmNo, date, status]);

  const onUploadFor = async (tmNo: string, file: File) => {
    if (!leadId || uploadingTmNo) return;
    setUploadingTmNo(tmNo);
    onSavingChange?.(true);
    try {
      const [presigned] = await generateUploadPresignedUrls(
        [{ filename: file.name, mimeType: file.type || "application/pdf" }],
        "private",
        TM_M_AFTER_HEARING_FILED_FOLDER
      );
      const putRes = await fetch(presigned.url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "application/pdf" },
      });
      if (!putRes.ok) throw new Error("Upload to S3 failed");
      const row = status?.applications.find((a) => a.tmNo === tmNo);
      const formattedDate =
        tmNo === selected.tmNo && date
          ? formatDateDDMMYYYYForApi(date)
          : (row?.dateOfTmMAfterHearingFiled ?? null);
      await saveTmMAfterHearingFiled(leadId, [
        {
          tmNo,
          tmMAfterHearingFileKey: presigned.key,
          tmMAfterHearingFilename: file.name,
          dateOfTmMAfterHearingFiled: formattedDate,
        },
      ]);
      const refreshed = await getTmMAfterHearingFiledStatus(leadId);
      setStatus(refreshed);
      toast.success("TM-M PDF uploaded");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to upload");
    } finally {
      setUploadingTmNo(null);
      onSavingChange?.(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (isGated) {
      onAdvance();
      return;
    }
    onSavingChange?.(true);
    try {
      const row = status?.applications.find((a) => a.tmNo === selected.tmNo);
      await saveTmMAfterHearingFiled(leadId, [
        {
          tmNo: selected.tmNo,
          dateOfTmMAfterHearingFiled: date
            ? formatDateDDMMYYYYForApi(date)
            : null,
          tmMAfterHearingFileKey: row?.tmMAfterHearingFileKey ?? null,
          tmMAfterHearingFilename: row?.tmMAfterHearingFilename ?? null,
        },
      ]);
      toast.success("File TM-M after Hearing saved");
      onAdvance();
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save File TM-M after Hearing"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  if (loading) return <p className="text-sm text-[#6D6D6D] py-5">Loading…</p>;

  if (isGated) {
    return (
      <form
        id={EXAMINATION_REPORT_HAO_FILE_TMM_AFTER_HEARING_FORM_ID}
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col gap-3 self-stretch pt-5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          This step is only available when the hearing officer's direction is
          "File TM-M". Click "Skip &amp; Continue" to proceed to the next step.
        </p>
      </form>
    );
  }

  const rows =
    status?.applications.map((a) => ({
      tmNo: a.tmNo,
      label: a.tmNo,
      filename: a.tmMAfterHearingFilename,
      url: a.tmMAfterHearingFileUrl,
    })) ?? [];

  return (
    <>
      <form
        id={EXAMINATION_REPORT_HAO_FILE_TMM_AFTER_HEARING_FORM_ID}
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col gap-5 self-stretch pt-5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="flex flex-col gap-1">
            <label
              className="text-[#222222]"
              style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
            >
              Date of Filing of TM-M after Hearing
            </label>
            <DateTimePicker
              value={date}
              onChange={setDate}
              showTime={false}
              placeholder="Select Date"
            />
          </div>
          <div className="hidden md:block" aria-hidden="true" />
        </div>

        <MultiFileUploadCard
          inputIdPrefix="file-tmm-after-hearing"
          rows={rows}
          onPickForRow={onUploadFor}
          uploadingTmNo={uploadingTmNo}
          onPreview={(row) =>
            setPreview({
              url: row.url!,
              name:
                row.filename ?? `TM_M_After_Hearing_${row.tmNo}.pdf`,
              kind: detectPreviewKind(
                row.filename ?? `TM_M_After_Hearing_${row.tmNo}.pdf`
              ),
            })
          }
        />
      </form>

      <FilePreviewModal
        open={!!preview}
        onClose={() => setPreview(null)}
        url={preview?.url ?? undefined}
        name={preview?.name ?? ""}
        kind={preview?.kind ?? "pdf"}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// 3D(xxix) Inform Client about Filing TM-M after Hearing — message preview
// ---------------------------------------------------------------------------
function InformClientAfterFilingTmMPanel({
  leadId,
  selected,
  hasSent,
  onAdvance,
  onSavingChange,
  resending,
  onResend,
  onGatedChange,
}: {
  leadId: string;
  selected: ShowCauseHearingApplication;
  hasSent: boolean;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
  resending: boolean;
  onResend: () => void;
  onGatedChange?: (gated: boolean) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string>("");
  const [applicationCount, setApplicationCount] = useState<number | null>(null);

  // Gated when no application in this Acknowledgement has TM-M after Hearing filed
  // for the selected TM No. — mirrors 3D(xxviii)'s "officer direction != FILE_TM_M" skip path.
  const isGated = applicationCount === 0;

  useEffect(() => {
    onGatedChange?.(isGated);
    return () => {
      onGatedChange?.(false);
    };
  }, [isGated, onGatedChange]);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getInformClientTmMAfterHearingPreview(leadId, [selected.tmNo])
      .then((data) => {
        if (!cancelled) {
          setMessage(data.message);
          setApplicationCount(data.applicationCount);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMessage("");
          setApplicationCount(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, selected.tmNo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (isGated || hasSent) {
      onAdvance();
      return;
    }
    onSavingChange?.(true);
    try {
      await sendInformClientTmMAfterHearing(leadId, [selected.tmNo]);
      toast.success("Inform Client (TM-M after hearing) sent to client");
      onAdvance();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send message");
    } finally {
      onSavingChange?.(false);
    }
  };

  if (loading) return <p className="text-sm text-[#6D6D6D] py-5">Loading…</p>;

  if (isGated) {
    return (
      <form
        id={EXAMINATION_REPORT_HAO_INFORM_TMM_AFTER_HEARING_FORM_ID}
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col gap-3 self-stretch pt-5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          This step is only available when the hearing officer's direction is
          "File TM-M". Click "Skip &amp; Continue" to proceed to the next step.
        </p>
      </form>
    );
  }

  return (
    <form
      id={EXAMINATION_REPORT_HAO_INFORM_TMM_AFTER_HEARING_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-5 self-stretch pt-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col gap-5">
        <span
          className="text-[#222222]"
          style={{ fontSize: "16px", lineHeight: "20px", fontWeight: 400 }}
        >
          Message Preview
        </span>
        <div
          className="self-stretch bg-white border border-[#EEEEEE] rounded-[6px]"
          style={{ padding: "14px", minHeight: 540 }}
        >
          {message ? (
            <p
              className="text-[#222222] whitespace-pre-line"
              style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
            >
              {renderWhatsAppFormatting(message)}
            </p>
          ) : (
            <span
              className="text-[#6D6D6D]"
              style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
            >
              No preview available — file the TM-M document first.
            </span>
          )}
        </div>
      </div>

      {hasSent && (
        <div className="flex flex-row items-center gap-3">
          <button
            type="button"
            disabled={resending}
            onClick={onResend}
            className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {resending ? "Resending…" : "Resend Message"}
          </button>
        </div>
      )}
    </form>
  );
}

// ---------------------------------------------------------------------------
// 3D(xxx) Subsequent Hearings — Date + Notice upload + message preview/send
// ---------------------------------------------------------------------------
const SUBSEQUENT_HEARING_NOTICE_FOLDER = "subsequent-hearing-notice";

function SubsequentHearingsPanel({
  leadId,
  selected,
  hasSent,
  onAdvance,
  onSavingChange,
  resending,
  onResend,
  onGatedChange,
}: {
  leadId: string;
  selected: ShowCauseHearingApplication;
  hasSent: boolean;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
  resending: boolean;
  onResend: () => void;
  onGatedChange?: (gated: boolean) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<SubsequentHearingStatus | null>(null);
  const [date, setDate] = useState<Date | null>(null);
  const [uploadingTmNo, setUploadingTmNo] = useState<string | null>(null);
  const [message, setMessage] = useState<string>("");
  const [preview, setPreview] = useState<
    { url: string; name: string; kind: FilePreviewKind } | null
  >(null);

  const isGated = !!status && status.isActive === false;

  useEffect(() => {
    onGatedChange?.(isGated);
    return () => {
      onGatedChange?.(false);
    };
  }, [isGated, onGatedChange]);

  const reload = (cancelledRef: { cancelled: boolean }) => {
    if (!leadId) return;
    setLoading(true);
    Promise.all([
      getSubsequentHearingStatus(leadId),
      getSubsequentHearingNoticePreview(leadId, [selected.tmNo]).catch(() => ({
        message: "",
      })),
    ])
      .then(([data, msg]) => {
        if (cancelledRef.cancelled) return;
        setStatus(data);
        const row =
          data.applications.find((a) => a.tmNo === selected.tmNo) ?? null;
        setDate(parseDateForHearingInput(row?.dateOfHearing));
        setMessage(msg.message ?? "");
      })
      .catch((err) => {
        if (!cancelledRef.cancelled)
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Subsequent Hearings"
          );
      })
      .finally(() => {
        if (!cancelledRef.cancelled) setLoading(false);
      });
  };

  useEffect(() => {
    const ref = { cancelled: false };
    reload(ref);
    return () => {
      ref.cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, selected.tmNo]);

  const hearingNumber = status?.hearingNumber ?? 2;

  // Auto-save + refresh preview when date changes (debounced).
  useEffect(() => {
    if (!leadId || !date) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        await saveSubsequentHearingDetails(leadId, hearingNumber, [
          {
            tmNo: selected.tmNo,
            dateOfHearing: formatDateDDMMYYYYForApi(date),
          },
        ]);
        const p = await getSubsequentHearingNoticePreview(leadId, [
          selected.tmNo,
        ]);
        if (!cancelled) setMessage(p.message ?? "");
      } catch {
        /* silent — surface only on explicit submit */
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [leadId, selected.tmNo, date, hearingNumber]);

  const onUploadFor = async (tmNo: string, file: File) => {
    if (!leadId || uploadingTmNo) return;
    setUploadingTmNo(tmNo);
    onSavingChange?.(true);
    try {
      const [presigned] = await generateUploadPresignedUrls(
        [{ filename: file.name, mimeType: file.type || "application/pdf" }],
        "private",
        SUBSEQUENT_HEARING_NOTICE_FOLDER
      );
      const putRes = await fetch(presigned.url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "application/pdf" },
      });
      if (!putRes.ok) throw new Error("Upload to S3 failed");
      await saveSubsequentHearingDetails(leadId, hearingNumber, [
        {
          tmNo,
          hearingNoticeFileKey: presigned.key,
          hearingNoticeFilename: file.name,
        },
      ]);
      const refreshed = await getSubsequentHearingStatus(leadId);
      setStatus(refreshed);
      toast.success("Hearing Notice uploaded");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to upload");
    } finally {
      setUploadingTmNo(null);
      onSavingChange?.(false);
    }
  };

  const persistDate = async (): Promise<boolean> => {
    if (!date) {
      toast.error("Select Date of Hearing");
      return false;
    }
    await saveSubsequentHearingDetails(leadId, hearingNumber, [
      {
        tmNo: selected.tmNo,
        dateOfHearing: formatDateDDMMYYYYForApi(date),
      },
    ]);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (isGated || hasSent) {
      onAdvance();
      return;
    }
    onSavingChange?.(true);
    try {
      const ok = await persistDate();
      if (!ok) return;
      await sendSubsequentHearingNotice(leadId, [selected.tmNo]);
      toast.success("Subsequent Hearing notice sent to client");
      onAdvance();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send notice");
    } finally {
      onSavingChange?.(false);
    }
  };

  if (loading) return <p className="text-sm text-[#6D6D6D] py-5">Loading…</p>;

  if (isGated) {
    return (
      <form
        id={EXAMINATION_REPORT_HAO_SUBSEQUENT_HEARINGS_FORM_ID}
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col gap-3 py-5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Subsequent Hearings is not active for this application. Click "Skip
          &amp; Continue" to proceed to the next step.
        </p>
      </form>
    );
  }

  const rows =
    status?.applications.map((a: SubsequentHearingApplication) => ({
      tmNo: a.tmNo,
      label: a.mark || a.tmNo,
      filename: a.hearingNoticeFilename,
      url: a.hearingNoticeFileUrl,
    })) ?? [];

  return (
    <>
      <form
        id={EXAMINATION_REPORT_HAO_SUBSEQUENT_HEARINGS_FORM_ID}
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col gap-5 self-stretch pt-5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="flex flex-col gap-1">
            <label
              className="text-[#222222]"
              style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
            >
              Date of Hearing {hearingNumber}
            </label>
            <DateTimePicker
              value={date}
              onChange={setDate}
              showTime={false}
              placeholder="Select Date"
            />
          </div>
          <ReadOnlyTextField
            label="Acknowledgement No."
            value={status?.acknowledgementNo ?? null}
            placeholder="Acknowledgement No."
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <ReadOnlyTextField
            label={`No. of applications for Hearing ${hearingNumber}`}
            value={
              status?.applications?.length != null
                ? String(status.applications.length)
                : ""
            }
            placeholder="Number"
          />
          <div className="hidden md:block" aria-hidden="true" />
        </div>

        <MultiFileUploadCard
          inputIdPrefix="subsequent-hearing-notice"
          rows={rows}
          onPickForRow={onUploadFor}
          uploadingTmNo={uploadingTmNo}
          onPreview={(row) =>
            setPreview({
              url: row.url!,
              name: row.filename ?? `Hearing_Notice_${row.tmNo}.pdf`,
              kind: detectPreviewKind(
                row.filename ?? `Hearing_Notice_${row.tmNo}.pdf`
              ),
            })
          }
        />

        {/* Message Preview */}
        <div className="flex flex-col gap-5">
          <span
            className="text-[#222222]"
            style={{ fontSize: "16px", lineHeight: "20px", fontWeight: 400 }}
          >
            Message Preview
          </span>
          <div
            className="self-stretch bg-white border border-[#EEEEEE] rounded-[6px]"
            style={{ padding: "14px", minHeight: 540 }}
          >
            {message ? (
              <p
                className="text-[#222222] whitespace-pre-line"
                style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
              >
                {renderWhatsAppFormatting(message)}
              </p>
            ) : (
              <span
                className="text-[#6D6D6D]"
                style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
              >
                Preview will appear after Date of Hearing is set.
              </span>
            )}
          </div>
        </div>

        {hasSent && (
          <div className="flex flex-row items-center gap-3">
            <button
              type="button"
              disabled={resending}
              onClick={onResend}
              className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {resending ? "Resending…" : "Resend Message"}
            </button>
          </div>
        )}
      </form>

      <FilePreviewModal
        open={!!preview}
        onClose={() => setPreview(null)}
        url={preview?.url ?? undefined}
        name={preview?.name ?? ""}
        kind={preview?.kind ?? "pdf"}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// 3D(v) Subsequent Hearing Invoice — Rs.2500 × No. of apps
// PDF template: "11. Invoice for Subsequent Hearing - 1b) Mark"
// ---------------------------------------------------------------------------

function SubsequentHearingInvoicePanel({
  leadId,
  selected,
  hasSent,
  onSent,
  onAdvance,
  onSavingChange,
  onGatedChange,
}: {
  leadId: string;
  selected: ShowCauseHearingApplication;
  hasSent: boolean;
  onSent: () => void;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
  onGatedChange?: (gated: boolean) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [hearingNumber, setHearingNumber] = useState<number>(2);
  const [conditionalStepsActive, setConditionalStepsActive] =
    useState<boolean>(false);
  const [conditionalStepsReason, setConditionalStepsReason] =
    useState<string>("");
  const [invoice, setInvoice] = useState<SubsequentHearingInvoiceStatus | null>(
    null
  );
  const [previewData, setPreviewData] =
    useState<SubsequentHearingInvoicePreview | null>(null);
  const [invoiceAmount, setInvoiceAmount] = useState<string>("");
  const [invoiceDate, setInvoiceDate] = useState<Date | null>(null);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);
  const [pdfPreview, setPdfPreview] = useState<
    { url: string; name: string; kind: FilePreviewKind } | null
  >(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMode, setConfirmMode] = useState<"send" | "resend">("send");
  const [pdfMessage, setPdfMessage] = useState<string>("");
  const [paymentMessage, setPaymentMessage] = useState<string>("");
  const [messagePreviewLoading, setMessagePreviewLoading] = useState(false);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const status = await getSubsequentHearingStatus(leadId);
        const num = status.currentHearingNumber ?? status.hearingNumber ?? 2;
        if (cancelled) return;
        setHearingNumber(num);
        setConditionalStepsActive(!!status.conditionalStepsActive);
        setConditionalStepsReason(status.conditionalStepsReason ?? "");
        const [invStatus, prev, msgPreview] = await Promise.all([
          getSubsequentHearingInvoiceStatus(leadId, num, [selected.tmNo]),
          getSubsequentHearingInvoicePreview(leadId, num, [
            selected.tmNo,
          ]).catch(() => null),
          getSubsequentHearingInvoiceWhatsAppPreview(leadId, num, [
            selected.tmNo,
          ]).catch(() => null),
        ]);
        if (cancelled) return;
        setInvoice(invStatus);
        setPreviewData(prev);
        if (msgPreview) {
          setPdfMessage(msgPreview.pdfMessage ?? "");
          setPaymentMessage(msgPreview.paymentMessage ?? "");
        }
        const row =
          invStatus.tmInvoiceRows.find((r) => r.tmNo === selected.tmNo) ?? null;
        const initialAmount =
          row?.invoiceAmount ?? row?.defaultInvoiceAmount ?? null;
        setInvoiceAmount(initialAmount != null ? String(initialAmount) : "");
        setInvoiceDate(
          parseDateForHearingInput(row?.invoiceDate ?? invStatus.invoiceDate)
        );
      } catch (err) {
        if (!cancelled)
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Subsequent Hearing Invoice"
          );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, selected.tmNo]);

  const isGated = !!invoice && invoice.isActive === false;

  useEffect(() => {
    onGatedChange?.(isGated);
    return () => {
      onGatedChange?.(false);
    };
  }, [isGated, onGatedChange]);

  const persist = async (): Promise<SubsequentHearingInvoiceStatus | null> => {
    const amt = Number(invoiceAmount);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error("Invoice Amount must be a positive number");
      return null;
    }
    if (!invoiceDate) {
      toast.error("Date of Invoice is required");
      return null;
    }
    const updated = await saveSubsequentHearingInvoice(leadId, [
      {
        tmNo: selected.tmNo,
        hearingNumber,
        invoiceAmount: amt,
        invoiceDate: formatDateDDMMYYYYForApi(invoiceDate),
      },
    ]);
    setInvoice(updated);
    return updated;
  };

  const refreshMessagePreview = async () => {
    if (!leadId) return;
    setMessagePreviewLoading(true);
    try {
      const res = await getSubsequentHearingInvoiceWhatsAppPreview(
        leadId,
        hearingNumber,
        [selected.tmNo]
      );
      setPdfMessage(res.pdfMessage ?? "");
      setPaymentMessage(res.paymentMessage ?? "");
    } catch {
      // Non-critical — keep existing values.
    } finally {
      setMessagePreviewLoading(false);
    }
  };

  const runGenerate = async (): Promise<string | null> => {
    const saved = await persist();
    if (!saved) return null;
    const { s3Key, downloadUrl } = await generateSubsequentHearingInvoicePdf(
      leadId,
      hearingNumber,
      [selected.tmNo]
    );
    setInvoice((prev) =>
      prev
        ? { ...prev, invoicePdfKey: s3Key, invoicePdfUrl: downloadUrl }
        : prev
    );
    return downloadUrl;
  };

  const handleGeneratePdf = async () => {
    if (!leadId || generating) return;
    setGenerating(true);
    onSavingChange?.(true);
    try {
      const url = await runGenerate();
      if (!url) return;
      toast.success("Subsequent Hearing Invoice PDF generated");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to generate PDF");
    } finally {
      setGenerating(false);
      onSavingChange?.(false);
    }
  };

  const ensurePdf = async (): Promise<string | null> => {
    if (invoice?.invoicePdfUrl) return invoice.invoicePdfUrl;
    if (!leadId) return null;
    setGenerating(true);
    onSavingChange?.(true);
    try {
      return await runGenerate();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to generate PDF");
      return null;
    } finally {
      setGenerating(false);
      onSavingChange?.(false);
    }
  };

  const handlePreviewPdf = async () => {
    const url = await ensurePdf();
    if (!url) return;
    const name = `Subsequent_Hearing_Invoice_${selected.tmNo}.pdf`;
    setPdfPreview({ url, name, kind: detectPreviewKind(name) });
  };

  const handleDownloadPdf = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const url = await ensurePdf();
      if (!url) return;
      const name = `Subsequent_Hearing_Invoice_${selected.tmNo}.pdf`;
      const result = await downloadFileFromUrlWithFallback(url, name);
      if (result === "local") {
        toast.success("PDF saved to your device.");
      } else {
        toast.info(
          "Opened the PDF in a new tab. Use Save or Save as to store it locally."
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || sending) return;
    if (isGated) {
      onAdvance();
      return;
    }
    if (hasSent) {
      // Already sent — Save & Continue: persist edits and advance.
      onSavingChange?.(true);
      try {
        const saved = await persist();
        if (!saved) return;
        toast.success("Invoice saved");
        onAdvance();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to save");
      } finally {
        onSavingChange?.(false);
      }
      return;
    }
    // First-time send: validate + persist, then open confirm modal.
    // Sending happens in handleConfirmProceed; we stay on this step.
    setSending(true);
    onSavingChange?.(true);
    try {
      const saved = await persist();
      if (!saved) return;
      if (!saved.invoicePdfKey) {
        toast.error("Generate the Invoice PDF before sending.");
        return;
      }
      await refreshMessagePreview();
      setConfirmMode("send");
      setShowConfirmModal(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save Invoice");
    } finally {
      setSending(false);
      onSavingChange?.(false);
    }
  };

  const handleConfirmProceed = async () => {
    if (!leadId) return;
    setShowConfirmModal(false);
    setSending(true);
    onSavingChange?.(true);
    try {
      await sendSubsequentHearingInvoiceToClient(leadId, {
        hearingNumber,
        sendWhatsApp: true,
        sendEmail: true,
        tmNos: [selected.tmNo],
      });
      onSent();
      toast.success(
        confirmMode === "resend"
          ? "Subsequent Hearing Invoice resent to client."
          : "Subsequent Hearing Invoice sent to client."
      );
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to send Invoice"
      );
    } finally {
      setSending(false);
      onSavingChange?.(false);
    }
  };

  const handleResend = async () => {
    if (!leadId || sending) return;
    setSending(true);
    onSavingChange?.(true);
    try {
      const saved = await persist();
      if (!saved) return;
      if (!saved.invoicePdfKey) {
        toast.error("Generate the Invoice PDF before resending.");
        return;
      }
      await refreshMessagePreview();
      setConfirmMode("resend");
      setShowConfirmModal(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save Invoice");
    } finally {
      setSending(false);
      onSavingChange?.(false);
    }
  };

  if (loading) return <p className="text-sm text-[#6D6D6D] py-5">Loading…</p>;

  if (isGated) {
    return (
      <form
        id={EXAMINATION_REPORT_HAO_SUBSEQUENT_INVOICE_FORM_ID}
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col gap-3 py-5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Subsequent Hearing Invoice is not active for this application. Click
          "Skip &amp; Continue" to proceed to the next step.
        </p>
      </form>
    );
  }

  const noOfApps = invoice?.noOfApps ?? 1;
  const defaultPerApp =
    invoice?.tmInvoiceRows?.[0]?.defaultInvoiceAmount ?? 2500;

  return (
    <>
      <form
        id={EXAMINATION_REPORT_HAO_SUBSEQUENT_INVOICE_FORM_ID}
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col gap-5 self-stretch pt-5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        {conditionalStepsActive ? (
          <div
            className="self-stretch flex flex-col gap-1 px-4 py-3 rounded-[8px] border border-[#FFD580] bg-[#FFF8EC]"
            role="alert"
          >
            <span
              className="text-[#7A4A00] font-medium"
              style={{ fontSize: "12px", lineHeight: "18px" }}
            >
              Conditional documents required before this hearing
            </span>
            <span
              className="text-[#7A4A00]"
              style={{ fontSize: "11px", lineHeight: "16px" }}
            >
              Stamp Paper, User Affidavit and Notarisation steps were not
              completed (User Date was &quot;Proposed to be used&quot; at
              filing). Prepare these via the Stamp Paper / User Affidavit /
              Notarisation sub-steps before attending the hearing.
              {conditionalStepsReason ? ` (${conditionalStepsReason})` : ""}
            </span>
          </div>
        ) : null}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <ReadOnlyTextField
            label="Hearing Number"
            value={String(hearingNumber)}
            placeholder=""
          />
          <ReadOnlyTextField
            label="No. of applications for Subsequent Hearing"
            value={String(noOfApps)}
            placeholder=""
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="flex flex-col gap-1">
            <label
              className="text-[#222222]"
              style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
            >
              Invoice Amount (Rs.)
            </label>
            <input
              type="number"
              inputMode="decimal"
              min={0}
              value={invoiceAmount}
              onChange={(e) => setInvoiceAmount(e.target.value)}
              placeholder={`${defaultPerApp * noOfApps}`}
              className="h-9 w-full rounded-[6px] border border-[#EEEEEE] bg-white px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222]"
            />
            <span
              className="text-[#6D6D6D]"
              style={{ fontSize: "11px", lineHeight: "16px" }}
            >
              Default: Rs.{defaultPerApp} × {noOfApps} application
              {noOfApps === 1 ? "" : "s"} = Rs.{defaultPerApp * noOfApps}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <label
              className="text-[#222222]"
              style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
            >
              Date of Invoice
            </label>
            <DateTimePicker
              value={invoiceDate}
              onChange={setInvoiceDate}
              showTime={false}
              placeholder="Select Date"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <ReadOnlyTextField
            label="Invoice No."
            value={invoice?.invoiceNo ?? ""}
            placeholder="Auto-generated on save"
          />
          <ReadOnlyTextField
            label="Date of Filing"
            value={previewData?.dateOfFiling ?? ""}
            placeholder=""
          />
        </div>

        <div className="flex flex-row flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={generating}
            onClick={() => void handleGeneratePdf()}
            className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-white bg-[#222222] rounded-[10px] hover:bg-[#333] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {generating
              ? "Generating…"
              : invoice?.invoicePdfKey
                ? "Regenerate PDF"
                : "Generate PDF"}
          </button>
          <button
            type="button"
            disabled={generating}
            onClick={() => void handlePreviewPdf()}
            className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Preview PDF
          </button>
          <button
            type="button"
            disabled={downloading || generating}
            onClick={() => void handleDownloadPdf()}
            className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {downloading ? "Downloading…" : "Download PDF"}
          </button>
        </div>

        <div className="flex flex-row flex-wrap items-center gap-3 pt-2">
          {!hasSent && (
            <button
              type="submit"
              disabled={sending}
              className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-white bg-[#222222] rounded-[10px] hover:bg-[#333] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {sending ? "Sending…" : "Send Message"}
            </button>
          )}
          {hasSent && (
            <button
              type="button"
              disabled={sending}
              onClick={() => void handleResend()}
              className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {sending ? "Sending…" : "Resend Message"}
            </button>
          )}
        </div>
      </form>

      <FilePreviewModal
        open={!!pdfPreview}
        onClose={() => setPdfPreview(null)}
        url={pdfPreview?.url ?? undefined}
        name={pdfPreview?.name ?? ""}
        kind={pdfPreview?.kind ?? "pdf"}
      />

      <ConfirmMessageModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title={
          confirmMode === "resend"
            ? "Resend Subsequent Hearing Invoice to client"
            : "Confirm Subsequent Hearing Invoice to client"
        }
        previewSlot={
          <div className="flex flex-col gap-4 p-4">
            <section>
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                WhatsApp message (Invoice PDF)
              </p>
              {messagePreviewLoading ? (
                <p className="text-sm text-[#6d6d6d]">
                  Loading message preview…
                </p>
              ) : pdfMessage ? (
                <pre
                  className="m-0 whitespace-pre-wrap break-words font-sans text-sm leading-5 text-[#222]"
                  style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
                >
                  {renderWhatsAppFormatting(pdfMessage)}
                </pre>
              ) : (
                <p className="text-sm text-[#6D6D6D]">
                  Message preview not available.
                </p>
              )}
            </section>
            {paymentMessage ? (
              <>
                <div className="border-t border-dashed border-[#EEEEEE]" />
                <section>
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-[#6D6D6D]">
                    Payment details message
                  </p>
                  <pre
                    className="m-0 whitespace-pre-wrap break-words font-sans text-sm leading-5 text-[#222]"
                    style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
                  >
                    {renderWhatsAppFormatting(paymentMessage)}
                  </pre>
                </section>
              </>
            ) : null}
          </div>
        }
        disableConfirm={sending}
        onConfirm={() => void handleConfirmProceed()}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// 3D(xii) Payment From Client for Subsequent Hearing — record collected amount.
// ---------------------------------------------------------------------------

function SubsequentHearingPaymentFromClientPanel({
  leadId,
  selected,
  onAdvance,
  onSavingChange,
  onGatedChange,
}: {
  leadId: string;
  selected: ShowCauseHearingApplication;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
  onGatedChange?: (gated: boolean) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [hearingNumber, setHearingNumber] = useState<number>(2);
  const [status, setStatus] = useState<SubsequentHearingInvoiceStatus | null>(
    null
  );
  const [savedCollected, setSavedCollected] = useState<number>(0);
  const [collected, setCollected] = useState<string>("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const hearingStatus = await getSubsequentHearingStatus(leadId);
        const num =
          hearingStatus.currentHearingNumber ?? hearingStatus.hearingNumber ?? 2;
        if (cancelled) return;
        setHearingNumber(num);
        const invoice = await getSubsequentHearingInvoiceStatus(leadId, num, [
          selected.tmNo,
        ]);
        if (cancelled) return;
        setStatus(invoice);
        const row =
          invoice.tmInvoiceRows.find((r) => r.tmNo === selected.tmNo) ?? null;
        const saved = Number(
          row?.collectedAmount ?? invoice.collectedAmount ?? 0
        );
        setSavedCollected(Number.isFinite(saved) ? saved : 0);
        setCollected("");
      } catch (err) {
        if (!cancelled)
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Payment status"
          );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, selected.tmNo]);

  const invoiceAmount = status?.invoiceAmount ?? 0;
  const thisPayment = Number(collected) || 0;
  const totalCollected = savedCollected + thisPayment;
  const outstanding = Math.max(0, invoiceAmount - totalCollected);

  const isGated = !!status && status.isActive === false;

  useEffect(() => {
    onGatedChange?.(isGated);
    return () => {
      onGatedChange?.(false);
    };
  }, [isGated, onGatedChange]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    if (isGated) {
      onAdvance();
      return;
    }
    if (!Number.isFinite(thisPayment) || thisPayment < 0) {
      toast.error("Collected Amount must be a non-negative number");
      return;
    }
    if (thisPayment <= 0) {
      toast.error("Collected amount should be greater than 0.");
      return;
    }
    if (invoiceAmount > 0 && savedCollected >= invoiceAmount) {
      toast.error("This invoice is already fully paid.");
      return;
    }
    if (invoiceAmount > 0 && totalCollected > invoiceAmount) {
      toast.error(
        "Collected amount cannot exceed the outstanding invoice balance."
      );
      return;
    }
    setSaving(true);
    onSavingChange?.(true);
    try {
      const updated = await saveSubsequentHearingCollectedAmount(
        leadId,
        hearingNumber,
        [{ tmNo: selected.tmNo, collectedAmount: totalCollected }]
      );
      setStatus(updated);
      setSavedCollected(totalCollected);
      setCollected("");
      toast.success("Payment recorded");
      onAdvance();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to record payment"
      );
    } finally {
      setSaving(false);
      onSavingChange?.(false);
    }
  };

  if (loading) return <p className="text-sm text-[#6D6D6D] py-5">Loading…</p>;

  if (isGated) {
    return (
      <form
        id={EXAMINATION_REPORT_HAO_SUBSEQUENT_PAYMENT_FORM_ID}
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col gap-3 py-5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Subsequent Hearing Invoice is not active for this application. Click
          "Skip &amp; Continue" to proceed to the next step.
        </p>
      </form>
    );
  }

  return (
    <form
      id={EXAMINATION_REPORT_HAO_SUBSEQUENT_PAYMENT_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-5 self-stretch pt-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <ReadOnlyTextField
          label="Hearing Number"
          value={String(hearingNumber)}
          placeholder=""
        />
        <ReadOnlyTextField
          label="Invoice No."
          value={status?.invoiceNo ?? ""}
          placeholder=""
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="flex flex-col gap-1">
          <label
            className="text-[#222222]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            Invoice Amount (Fetched)
          </label>
          <div
            className="flex flex-row justify-between items-center bg-white border border-[#EEEEEE] rounded-[6px] overflow-hidden"
            style={{ height: "36px" }}
          >
            <span
              className="px-2.5 text-[#6D6D6D] flex-1"
              style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
            >
              {invoiceAmount ? invoiceAmount.toLocaleString("en-IN") : "00.00"}
            </span>
            <div
              className="flex items-center justify-center bg-[#F7F7F7] px-2.5"
              style={{
                height: "36px",
                width: "76px",
                borderRadius: "0 6px 6px 0",
              }}
            >
              <span
                className="text-[#6D6D6D]"
                style={{
                  fontSize: "12px",
                  lineHeight: "20px",
                  fontWeight: 400,
                }}
              >
                Rs(₹)
              </span>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label
            className="text-[#222222]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            Collected Amount
          </label>
          <div
            className="flex flex-row justify-between items-center bg-white border border-[#EEEEEE] rounded-[6px] overflow-hidden"
            style={{ height: "36px" }}
          >
            <input
              type="text"
              inputMode="decimal"
              value={collected}
              onChange={(e) => {
                const cleaned = e.target.value.replace(/[^\d.]/g, "");
                const parts = cleaned.split(".");
                const next =
                  parts.length > 1
                    ? `${parts[0]}.${parts.slice(1).join("")}`
                    : cleaned;
                setCollected(next);
              }}
              onKeyDown={(e) => {
                if (
                  e.key.length === 1 &&
                  !e.ctrlKey &&
                  !e.metaKey &&
                  !/[\d.]/.test(e.key)
                ) {
                  e.preventDefault();
                }
              }}
              placeholder="00.00"
              className="px-2.5 flex-1 bg-transparent focus:outline-none text-[#6D6D6D] placeholder:text-[#6D6D6D]"
              style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
            />
            <div
              className="flex items-center justify-center bg-[#F7F7F7] px-2.5"
              style={{
                height: "36px",
                width: "76px",
                borderRadius: "0 6px 6px 0",
              }}
            >
              <span
                className="text-[#6D6D6D]"
                style={{
                  fontSize: "12px",
                  lineHeight: "20px",
                  fontWeight: 400,
                }}
              >
                Rs(₹)
              </span>
            </div>
          </div>
          {savedCollected > 0 && (
            <span className="text-[11px] leading-4 text-[#6D6D6D]">
              Already collected: Rs.{" "}
              {savedCollected.toLocaleString("en-IN", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })}
              /-
            </span>
          )}
        </div>
      </div>

      <div
        className="flex flex-row items-center justify-between bg-[#222222] rounded-[8px] px-5"
        style={{ minHeight: "60px" }}
      >
        <span
          className="text-white"
          style={{
            fontSize: "14px",
            lineHeight: "20px",
            fontWeight: 400,
            opacity: 0.9,
          }}
        >
          Outstanding Amount
        </span>
        <span
          className="text-white"
          style={{ fontSize: "22px", lineHeight: "40px", fontWeight: 700 }}
        >
          : Rs. {outstanding.toLocaleString("en-IN")}/-
        </span>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// 3D(xxx) Subsequent Hearing Attend (outcome) — Status + Officer Remark
// ---------------------------------------------------------------------------

function SubsequentHearingAttendPanel({
  leadId,
  selected,
  onAdvance,
  onSavingChange,
  onGatedChange,
}: {
  leadId: string;
  selected: ShowCauseHearingApplication;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
  onGatedChange?: (gated: boolean) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [hearingNumber, setHearingNumber] = useState<number>(2);
  const [isActive, setIsActive] = useState<boolean | null>(null);
  const [hearingStatus, setHearingStatus] = useState<HearingStatusEnum | "">(
    ""
  );
  const [officerRemark, setOfficerRemark] = useState<
    HearingOfficerRemarkEnum | ""
  >("");
  const [remarks, setRemarks] = useState<string>("");

  const isGated = isActive === false;

  useEffect(() => {
    onGatedChange?.(isGated);
    return () => {
      onGatedChange?.(false);
    };
  }, [isGated, onGatedChange]);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getSubsequentHearingStatus(leadId)
      .then((data) => {
        if (cancelled) return;
        setHearingNumber(data.currentHearingNumber ?? data.hearingNumber ?? 2);
        setIsActive(data.isActive ?? true);
        const row =
          data.applications.find((a) => a.tmNo === selected.tmNo) ?? null;
        setHearingStatus(row?.hearingStatus ?? "");
        setOfficerRemark(row?.hearingOfficerRemark ?? "");
        setRemarks(row?.remarks ?? "");
      })
      .catch((err) => {
        if (!cancelled)
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Attend Subsequent Hearing"
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, selected.tmNo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (isGated) {
      onAdvance();
      return;
    }
    if (!hearingStatus) {
      toast.error("Select Hearing Status");
      return;
    }
    if (hearingStatus === "ATTENDED" && !officerRemark) {
      toast.error("Select Officer Remark");
      return;
    }
    onSavingChange?.(true);
    try {
      await saveSubsequentHearingOutcome(leadId, hearingNumber, [
        {
          tmNo: selected.tmNo,
          hearingStatus,
          hearingOfficerRemark:
            hearingStatus === "ATTENDED" ? officerRemark || null : null,
          remarks: remarks.trim() || null,
        },
      ]);
      toast.success("Subsequent Hearing outcome saved");
      onAdvance();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save outcome"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  if (loading) return <p className="text-sm text-[#6D6D6D] py-5">Loading…</p>;

  if (isGated) {
    return (
      <form
        id={EXAMINATION_REPORT_HAO_SUBSEQUENT_ATTEND_FORM_ID}
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col gap-3 py-5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Subsequent Hearings is not active for this application. Click "Skip
          &amp; Continue" to proceed to the next step.
        </p>
      </form>
    );
  }

  return (
    <form
      id={EXAMINATION_REPORT_HAO_SUBSEQUENT_ATTEND_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-5 self-stretch pt-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <ReadOnlyTextField
          label="Hearing Number"
          value={String(hearingNumber)}
          placeholder=""
        />
        <div className="hidden md:block" aria-hidden="true" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="flex flex-col gap-1">
          <label
            className="text-[#222222]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            Status
          </label>
          <Select
            value={hearingStatus || undefined}
            onValueChange={(v) => setHearingStatus(v as HearingStatusEnum)}
          >
            <SelectTrigger className="data-[placeholder]:text-[#6D6D6D]">
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ATTENDED">Attended</SelectItem>
              <SelectItem value="NOT_ATTENDED">Not Attended</SelectItem>
              <SelectItem value="DIDNT_HAPPEN">Didn't Happen</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <label
            className="text-[#222222]"
            style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
          >
            Officer Remark
          </label>
          <Select
            value={officerRemark || undefined}
            disabled={hearingStatus !== "ATTENDED"}
            onValueChange={(v) =>
              setOfficerRemark(v as HearingOfficerRemarkEnum)
            }
          >
            <SelectTrigger className="data-[placeholder]:text-[#6D6D6D]">
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ACCEPTED">Accepted</SelectItem>
              <SelectItem value="REFUSED">Refused</SelectItem>
              <SelectItem value="WILL_PASS_ON_MERITS">
                Will pass on merits
              </SelectItem>
              <SelectItem value="FILE_TM_M">File TM-M</SelectItem>
              <SelectItem value="DIRECTION_OF_OFFICER">
                Direction of Officer
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <label
          className="text-[#222222]"
          style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
        >
          Remarks (optional)
        </label>
        <textarea
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          rows={3}
          placeholder="General remarks about the hearing"
          className="w-full rounded-[6px] border border-[#EEEEEE] bg-white px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222]"
        />
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// 3D(xxx) Subsequent Hearing Postponement Notice — upload per TM
// ---------------------------------------------------------------------------
const SUBSEQUENT_HEARING_POSTPONEMENT_FOLDER =
  "subsequent-hearing-postponement";

function SubsequentHearingPostponementPanel({
  leadId,
  selected,
  onAdvance,
  onSavingChange,
}: {
  leadId: string;
  selected: ShowCauseHearingApplication;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [hearingNumber, setHearingNumber] = useState<number>(2);
  const [row, setRow] = useState<SubsequentHearingApplication | null>(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<
    { url: string; name: string; kind: FilePreviewKind } | null
  >(null);

  const reload = async (cancelledRef: { cancelled: boolean }) => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await getSubsequentHearingStatus(leadId);
      if (cancelledRef.cancelled) return;
      setHearingNumber(data.currentHearingNumber ?? data.hearingNumber ?? 2);
      setRow(data.applications.find((a) => a.tmNo === selected.tmNo) ?? null);
    } catch (err) {
      if (!cancelledRef.cancelled)
        toast.error(
          err instanceof Error
            ? err.message
            : "Failed to load Postponement Notice"
        );
    } finally {
      if (!cancelledRef.cancelled) setLoading(false);
    }
  };

  useEffect(() => {
    const ref = { cancelled: false };
    void reload(ref);
    return () => {
      ref.cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, selected.tmNo]);

  const handleUpload = async (file: File) => {
    if (!leadId || uploading) return;
    setUploading(true);
    onSavingChange?.(true);
    try {
      const [presigned] = await generateUploadPresignedUrls(
        [{ filename: file.name, mimeType: file.type || "application/pdf" }],
        "private",
        SUBSEQUENT_HEARING_POSTPONEMENT_FOLDER
      );
      const putRes = await fetch(presigned.url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "application/pdf" },
      });
      if (!putRes.ok) throw new Error("Upload to S3 failed");
      await saveSubsequentHearingPostponement(leadId, hearingNumber, [
        {
          tmNo: selected.tmNo,
          postponementNoticeFileKey: presigned.key,
          postponementNoticeFilename: file.name,
        },
      ]);
      await reload({ cancelled: false });
      toast.success("Postponement Notice uploaded");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to upload");
    } finally {
      setUploading(false);
      onSavingChange?.(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!row?.postponementNoticeFileKey) {
      toast.error("Upload Notice of Postponement first");
      return;
    }
    onAdvance();
  };

  if (loading) return <p className="text-sm text-[#6D6D6D] py-5">Loading…</p>;

  if (row && row.hearingStatus !== "DIDNT_HAPPEN") {
    return (
      <p className="text-sm text-[#6D6D6D] py-5">
        Notice of Postponement is only required when the Subsequent Hearing
        status is "Didn't Happen". Current status: {row.hearingStatus ?? "—"}.
      </p>
    );
  }

  const label = row?.mark || selected.tmNo;
  const file = row?.postponementNoticeFilename ?? null;
  const url = row?.postponementNoticeFileUrl ?? null;

  return (
    <>
      <form
        id={EXAMINATION_REPORT_HAO_SUBSEQUENT_POSTPONEMENT_FORM_ID}
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col gap-5 self-stretch pt-5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <ReadOnlyTextField
            label="Hearing Number"
            value={String(hearingNumber)}
            placeholder=""
          />
          <div className="hidden md:block" aria-hidden="true" />
        </div>

        <div
          className="self-stretch flex flex-row justify-between items-center gap-5 px-5 py-5 rounded-[16px] border border-dashed border-[#D8DAEB] bg-white cursor-pointer transition-colors hover:bg-[#FCFCFC]"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const f = e.dataTransfer.files[0];
            if (f) void handleUpload(f);
          }}
          onClick={() => {
            if (uploading) return;
            document
              .getElementById("subsequent-postponement-dropzone-input")
              ?.click();
          }}
        >
          <div className="flex flex-row items-center gap-5 min-w-0">
            <div className="flex flex-col gap-2 min-w-0">
              <p
                className="font-medium text-[#101111]"
                style={{
                  fontSize: "16px",
                  lineHeight: "100%",
                  letterSpacing: "0.01em",
                }}
              >
                Drag and drop the Notice of Postponement
              </p>
              <p
                className="font-normal text-[#7D7D7D]"
                style={{ fontSize: "14px", lineHeight: "21px" }}
              >
                {uploading
                  ? "Uploading…"
                  : "JPEG, PNG, PDF formats, up to 50MB"}
              </p>
            </div>
          </div>
          <button
            type="button"
            disabled={uploading}
            onClick={(e) => {
              e.stopPropagation();
              document
                .getElementById("subsequent-postponement-dropzone-input")
                ?.click();
            }}
            className="flex-shrink-0 flex items-center justify-center h-9 px-4 rounded-[10px] bg-white border border-[#D5D3E0] text-[#2E3037] hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: "16px",
              letterSpacing: "0.04em",
            }}
          >
            Select File
          </button>
          <input
            id="subsequent-postponement-dropzone-input"
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleUpload(f);
              e.currentTarget.value = "";
            }}
          />
        </div>

        <div className="flex flex-row items-center gap-2.5 px-4 py-1 bg-[#FCFCFC] border border-[#EEEEEE] rounded-[6px] h-11">
          <img
            src={pdfIcon}
            alt=""
            className="w-[22px] h-[22px] flex-shrink-0"
          />
          <span
            className="flex-1 min-w-0 truncate text-[#222222]"
            style={{
              fontSize: "12px",
              lineHeight: "20px",
              fontWeight: 500,
            }}
          >
            {label}
            {file ? <span className="text-[#6D6D6D]"> — {file}</span> : null}
          </span>
          {url ? (
            <button
              type="button"
              onClick={() => {
                const name =
                  file ?? `Subsequent_Postponement_Notice_${selected.tmNo}.pdf`;
                setPreview({
                  url,
                  name,
                  kind: detectPreviewKind(name),
                });
              }}
              className="flex-shrink-0 flex items-center justify-center h-9 px-4 rounded-[6px] bg-[#222222] text-white text-xs font-medium hover:bg-[#333]"
            >
              Preview
            </button>
          ) : null}
        </div>
      </form>

      <FilePreviewModal
        open={!!preview}
        onClose={() => setPreview(null)}
        url={preview?.url ?? undefined}
        name={preview?.name ?? ""}
        kind={preview?.kind ?? "pdf"}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// 3D(xlix) Order of Hearing to Client — file upload per TM + outcome + message
// ---------------------------------------------------------------------------
const ORDER_OF_HEARING_FOLDER = "order-of-hearing";

function OrderOfHearingToClientPanel({
  leadId,
  selected,
  hasSent,
  onSent,
  onAdvance,
  onSavingChange,
  resending,
  onResend,
  onGatedChange,
}: {
  leadId: string;
  selected: ShowCauseHearingApplication;
  hasSent: boolean;
  onSent: () => void;
  onAdvance: () => void;
  onSavingChange?: (saving: boolean) => void;
  resending: boolean;
  onResend: () => void;
  onGatedChange?: (gated: boolean) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<OrderOfHearingStatus | null>(null);
  const [outcome, setOutcome] = useState<OrderOfHearingOutcome | "">("");
  const [uploadingTmNo, setUploadingTmNo] = useState<string | null>(null);
  const [message, setMessage] = useState<string>("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState<
    { url: string; name: string; kind: FilePreviewKind } | null
  >(null);

  const selectedRow = (s: OrderOfHearingStatus | null) =>
    s?.applications.find((a) => a.tmNo === selected.tmNo) ?? null;

  // Review item 71 — activation is scoped to the selected mark by the backend.
  const isGated = !!status && status.isActive === false;

  useEffect(() => {
    onGatedChange?.(isGated);
    return () => {
      onGatedChange?.(false);
    };
  }, [isGated, onGatedChange]);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getOrderOfHearingStatus(leadId, [selected.tmNo])
      .then((data) => {
        if (cancelled) return;
        setStatus(data);
        const row = selectedRow(data);
        setOutcome(row?.outcomeOfHearing ?? "");
      })
      .catch((err) => {
        if (!cancelled)
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Order of Hearing"
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, selected.tmNo]);

  // Auto-save outcome + load message preview (debounced).
  useEffect(() => {
    if (!leadId || !outcome) {
      setMessage("");
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    const timer = setTimeout(async () => {
      try {
        await saveOrderOfHearingDetails(leadId, [
          {
            tmNo: selected.tmNo,
            outcomeOfHearing: outcome as OrderOfHearingOutcome,
          },
        ]);
        const data = await getOrderOfHearingMessagePreview(
          leadId,
          outcome as OrderOfHearingOutcome,
          [selected.tmNo]
        );
        if (!cancelled) setMessage(data.message);
      } catch {
        if (!cancelled) setMessage("");
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [leadId, selected.tmNo, outcome]);

  const onUploadFor = async (tmNo: string, file: File) => {
    if (!leadId || uploadingTmNo) return;
    setUploadingTmNo(tmNo);
    onSavingChange?.(true);
    try {
      const [presigned] = await generateUploadPresignedUrls(
        [{ filename: file.name, mimeType: file.type || "application/pdf" }],
        "private",
        ORDER_OF_HEARING_FOLDER
      );
      const putRes = await fetch(presigned.url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "application/pdf" },
      });
      if (!putRes.ok) throw new Error("Upload to S3 failed");
      await saveOrderOfHearingDetails(leadId, [
        {
          tmNo,
          orderOfHearingFileKey: presigned.key,
          orderOfHearingFilename: file.name,
        },
      ]);
      const refreshed = await getOrderOfHearingStatus(leadId);
      setStatus(refreshed);
      toast.success("Order of Hearing PDF uploaded");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to upload");
    } finally {
      setUploadingTmNo(null);
      onSavingChange?.(false);
    }
  };

  const persistOutcome = async (): Promise<boolean> => {
    if (!outcome) {
      toast.error("Select Outcome of Hearing");
      return false;
    }
    await saveOrderOfHearingDetails(leadId, [
      {
        tmNo: selected.tmNo,
        outcomeOfHearing: outcome as OrderOfHearingOutcome,
      },
    ]);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (hasSent) {
      onAdvance();
      return;
    }
    onSavingChange?.(true);
    try {
      const ok = await persistOutcome();
      if (!ok) return;
      await sendOrderOfHearingMessage(leadId, outcome as OrderOfHearingOutcome, [
        selected.tmNo,
      ]);
      toast.success("Order of Hearing message sent to client");
      onSent();
      onAdvance();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to send message");
    } finally {
      onSavingChange?.(false);
    }
  };

  if (loading) return <p className="text-sm text-[#6D6D6D] py-5">Loading…</p>;

  // Review item 71 — 3D(xlix) is the last HAO sub-step, so there is nothing to skip to:
  // explain why it is inactive and let the suppressed footer actions do the gating.
  if (isGated) {
    return (
      <div
        className="flex flex-col gap-3 py-5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          {status?.inactiveReason ||
            'This step is only available when User Date at filing was set to "Proposed to be used".'}
        </p>
      </div>
    );
  }

  const rows =
    status?.applications.map((a: OrderOfHearingApplication) => ({
      tmNo: a.tmNo,
      label: a.mark || a.tmNo,
      filename: a.orderOfHearingFilename,
      url: a.orderOfHearingFileUrl,
    })) ?? [];

  return (
    <>
      <form
        id={EXAMINATION_REPORT_HAO_ORDER_OF_HEARING_FORM_ID}
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col gap-5 self-stretch pt-5"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <MultiFileUploadCard
          inputIdPrefix="order-of-hearing"
          rows={rows}
          onPickForRow={onUploadFor}
          uploadingTmNo={uploadingTmNo}
          onPreview={(row) =>
            setPreview({
              url: row.url!,
              name: row.filename ?? `Order_of_Hearing_${row.tmNo}.pdf`,
              kind: detectPreviewKind(
                row.filename ?? `Order_of_Hearing_${row.tmNo}.pdf`
              ),
            })
          }
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="flex flex-col gap-1">
            <label
              className="text-[#222222]"
              style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
            >
              Outcome of Hearing
            </label>
            <Select
              value={outcome || undefined}
              onValueChange={(v) =>
                setOutcome(v as OrderOfHearingOutcome)
              }
            >
              <SelectTrigger className="data-[placeholder]:text-[#6D6D6D]">
                <SelectValue placeholder="Select an option" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ACCEPTED_AFTER_HEARING">
                  Accepted after Hearing
                </SelectItem>
                <SelectItem value="REFUSED_AFTER_HEARING">
                  Refused after Hearing
                </SelectItem>
                <SelectItem value="REFUSED_AFTER_NON_APPEARANCE">
                  Refused after non-appearance
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="hidden md:block" aria-hidden="true" />
        </div>

        {/* Message Preview */}
        <div className="flex flex-col gap-5">
          <span
            className="text-[#222222]"
            style={{ fontSize: "16px", lineHeight: "20px", fontWeight: 400 }}
          >
            Message Preview
          </span>
          <div
            className="self-stretch bg-white border border-[#EEEEEE] rounded-[6px]"
            style={{ padding: "14px", minHeight: 540 }}
          >
            {previewLoading ? (
              <span
                className="text-[#6D6D6D]"
                style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
              >
                Loading preview…
              </span>
            ) : message ? (
              <p
                className="text-[#222222] whitespace-pre-line"
                style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
              >
                {renderWhatsAppFormatting(message)}
              </p>
            ) : (
              <span
                className="text-[#6D6D6D]"
                style={{ fontSize: "12px", lineHeight: "20px", fontWeight: 400 }}
              >
                Select an outcome to see the message preview.
              </span>
            )}
          </div>
        </div>

        {hasSent && (
          <div className="flex flex-row items-center gap-3">
            <button
              type="button"
              disabled={resending}
              onClick={onResend}
              className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {resending ? "Resending…" : "Resend Message"}
            </button>
          </div>
        )}
      </form>

      <FilePreviewModal
        open={!!preview}
        onClose={() => setPreview(null)}
        url={preview?.url ?? undefined}
        name={preview?.name ?? ""}
        kind={preview?.kind ?? "pdf"}
      />
    </>
  );
}

function ConfirmationFromClientHearingPanel({
  leadId,
  selected,
  noOfApplicationsForHearing,
  hasSaved,
  onSaved,
  onAdvanceProceed,
  onAdvancePostpone,
  onAdvanceAbandon,
  onAdvanceRevisedInvoice,
  onSavingChange,
}: {
  leadId: string;
  selected: ShowCauseHearingApplication;
  /** 3ab) fetched from 3D(ii) — see the render site for why it is not 1. */
  noOfApplicationsForHearing: number;
  hasSaved: boolean;
  onSaved: (
    next: HearingClientConfirmation,
    suggestedNextStep: HearingSuggestedNextStep
  ) => void;
  onAdvanceProceed: () => void;
  onAdvancePostpone: () => void;
  onAdvanceAbandon: () => void;
  /** YES + "hearing not for all applications" → 3D(vii)a revised invoice (item 68). */
  onAdvanceRevisedInvoice: () => void;
  onSavingChange?: (saving: boolean) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [choice, setChoice] = useState<HearingClientConfirmation | null>(null);
  const [savedChoice, setSavedChoice] =
    useState<HearingClientConfirmation | null>(null);
  const [suggestedNextStep, setSuggestedNextStep] =
    useState<HearingSuggestedNextStep>("PROCEED");
  const [needRevisedInvoice, setNeedRevisedInvoice] = useState(false);
  /**
   * True when another hearing TM on this acknowledgement is POSTPONE or NO — i.e.
   * the hearing is no longer to be attended for *all* applications, which is the
   * review's stated condition for a revised 3D(v) invoice (item 68). Used to gate
   * the "Need Revised Invoice" control so it is not the unexplained always-present
   * option under YES that the client objected to in item 28.
   */
  const [hearingNotForAllApps, setHearingNotForAllApps] = useState(false);

  // Load existing confirmation per-TM
  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getShowCauseHearingConfirmation(leadId, [selected.tmNo])
      .then((data) => {
        if (cancelled) return;
        const row =
          data.applications.find((a) => a.tmNo === selected.tmNo) ?? null;
        const loadedChoice = row?.clientConfirmation ?? null;
        setChoice(loadedChoice);
        setSavedChoice(loadedChoice);
        setSuggestedNextStep(data.suggestedNextStep ?? "PROCEED");
        setNeedRevisedInvoice(row?.needRevisedInvoice === true);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Confirmation from Client"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, selected.tmNo]);

  // Unscoped read of the sibling TMs' confirmations. Kept separate from the scoped
  // fetch above so `suggestedNextStep` semantics for the selected TM are unchanged.
  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    getShowCauseHearingConfirmation(leadId)
      .then((data) => {
        if (cancelled) return;
        setHearingNotForAllApps(
          data.applications.some(
            (a) =>
              a.tmNo !== selected.tmNo &&
              (a.clientConfirmation === "POSTPONE" ||
                a.clientConfirmation === "NO")
          )
        );
      })
      .catch(() => {
        // Non-fatal: without this the revised-invoice control simply stays hidden.
        if (!cancelled) setHearingNotForAllApps(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, selected.tmNo]);

  const advanceForChoice = (
    nextChoice: HearingClientConfirmation,
    nextSuggested: HearingSuggestedNextStep
  ) => {
    if (nextChoice === "NO") {
      onAdvanceAbandon();
      return;
    }
    if (nextChoice === "POSTPONE") {
      onAdvancePostpone();
      return;
    }
    // YES: when the hearing is no longer for every application, the backend
    // suggests the revised 3D(v) invoice — review item 68. Two bugs here before:
    // both arms called onAdvanceProceed(), and the compared literal
    // ("3D(v)_revised") is not what the API returns — it returns the workflow slug.
    if (
      nextSuggested ===
      NewApplicationStep.S3_03d_vii_a_SaveRevisedInvoiceHearing1
    ) {
      onAdvanceRevisedInvoice();
      return;
    }
    onAdvanceProceed();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (!choice) {
      toast.error("Select YES, NO, or POSTPONE first");
      return;
    }
    const isDirty = choice !== savedChoice;
    if (hasSaved && !isDirty) {
      // Already saved with the current selection; just advance.
      advanceForChoice(choice, suggestedNextStep);
      return;
    }
    onSavingChange?.(true);
    try {
      const data = await saveShowCauseHearingConfirmation(leadId, [
        {
          tmNo: selected.tmNo,
          clientConfirmation: choice,
          // Only meaningful on YES — the backend rejects it otherwise
          // (`needRevisedInvoice is only valid when clientConfirmation is YES`).
          // Sent explicitly (including `false`) so unchecking clears a stale `true`
          // that would otherwise keep suggesting 3D(v) revised with no way back.
          needRevisedInvoice:
            choice === "YES"
              ? hearingNotForAllApps && needRevisedInvoice
              : undefined,
        },
      ]);
      setSuggestedNextStep(data.suggestedNextStep ?? "PROCEED");
      setSavedChoice(choice);
      onSaved(choice, data.suggestedNextStep ?? "PROCEED");
      toast.success("Confirmation from Client saved");
      advanceForChoice(choice, data.suggestedNextStep ?? "PROCEED");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save Confirmation from Client"
      );
    } finally {
      onSavingChange?.(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-[#6D6D6D] py-5">Loading…</p>;
  }

  const showProceedView = choice === "NO" || choice === "POSTPONE";

  const ChoicePill = ({
    value,
    label,
  }: {
    value: HearingClientConfirmation;
    label: string;
  }) => {
    const isActive = choice === value;
    return (
      <button
        type="button"
        onClick={() => setChoice(value)}
        className="flex flex-row items-center gap-2.5 px-2.5 py-2 h-9 rounded-[6px] border flex-1 transition-colors"
        style={{
          fontFamily: "Inter, sans-serif",
          fontSize: "12px",
          lineHeight: "20px",
          fontWeight: 400,
          background: isActive ? "#222222" : "#FCFCFC",
          color: isActive ? "#FFFFFF" : "#6D6D6D",
          border: isActive ? "1px solid #222222" : "1px solid #EEEEEE",
        }}
      >
        <RadioCircle active={isActive} />
        <span className="flex-1 text-left">{label}</span>
      </button>
    );
  };

  const dateOfHearingDisplay = selected.dateOfHearing ?? "";
  // 3ab) is "Data to be fetched" here (spec pg 87), and spec pg 84 defines it as "the total number
  // of applications in the same Acknowledgement No. which is set for Hearing" — so it is the
  // acknowledgement-wide count, the same value 3D(ii) shows, not 1 per TM tab. It was hard-coded to
  // 1, which also under-stated the basis of the hearing invoice (3ac = Rs.5000 × 3ab).
  const noOfApps = noOfApplicationsForHearing;

  return (
    <form
      id={EXAMINATION_REPORT_HAO_CONFIRMATION_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-5 self-stretch pt-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Choice selector — Yes (Attended) | No (Abandon) | TM-M for Postponement */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <ChoicePill value="YES" label="Yes (Attended)" />
        <ChoicePill value="NO" label="No(Abandon)" />
        <ChoicePill value="POSTPONE" label="TM-M for Postponement" />
      </div>

      {showProceedView && (
        <div className="flex flex-col items-center gap-7 self-stretch py-5">
          <ProcessToNextStepIllustration />
          <span
            className="text-[#222222] text-center"
            style={{ fontSize: "22px", lineHeight: "20px", fontWeight: 400 }}
          >
            Process to next step
          </span>
          <button
            type="submit"
            className="inline-flex items-center justify-center h-9 px-4 text-xs font-semibold leading-5 text-[#222222] bg-white border border-[#222222] rounded-[6px] hover:bg-gray-50"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            Proceed
          </button>
        </div>
      )}

      {choice === "YES" && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <ReadOnlyTextField
              label="No. of applications for Hearing"
              value={String(noOfApps)}
              placeholder="Number"
            />
            <ReadOnlyTextField
              label="Date of Hearing"
              value={dateOfHearingDisplay}
              placeholder="Select Date"
            />
          </div>

          {/*
            Review item 68 — "Need Revised Invoice" (3abr/3acr/3adr/3aer).
            The review sentence sits under the POSTPONE paragraph, but its condition is
            "if the client confirms that hearing need not be attended for ALL applications":
            the marks *still attending* are the ones whose invoice must shrink, so the flag
            belongs to a YES TM. The backend enforces exactly that — it rejects
            needRevisedInvoice unless clientConfirmation is YES, and only the YES branch
            yields suggestedNextStep "3D(v)_revised".
            Item 28's complaint was an unexplained option always shown under YES, so this is
            gated to the case where another TM is POSTPONE/NO — i.e. the hearing genuinely
            is not for every application.
          */}
          {hearingNotForAllApps && (
            <label
              htmlFor="hearing-need-revised-invoice"
              className="flex flex-row items-start gap-2.5 cursor-pointer"
            >
              <input
                id="hearing-need-revised-invoice"
                type="checkbox"
                checked={needRevisedInvoice}
                onChange={(e) => setNeedRevisedInvoice(e.target.checked)}
                className="mt-0.5 h-4 w-4 flex-shrink-0 accent-[#222222] cursor-pointer"
              />
              <span className="flex flex-col gap-0.5">
                <span className="text-[12px] font-medium leading-5 text-[#222222]">
                  Need Revised Invoice for Hearing 1
                </span>
                <span className="text-[12px] leading-5 text-[#6D6D6D]">
                  The hearing is no longer to be attended for every application, so
                  the invoice for Hearing 1 needs revising. Saving with this ticked
                  goes to Revised Invoice for Hearing 1.
                </span>
              </span>
            </label>
          )}
        </>
      )}
    </form>
  );
}

function HearingAfterObjectionBranch({
  leadId,
  phone,
  active,
  onSavingChange,
  onAdvanceToRefusalAbandonment,
  onHaoInformFooterSendModeChange,
  onHaoInformFooterSaveModeChange,
  onHaoStatusFooterFormModeChange,
  onHaoNoticeFooterSendModeChange,
  onHaoNoticeFooterSaveModeChange,
  onHaoInformHearingFooterSendModeChange,
  onHaoInformHearingFooterSaveModeChange,
  onHaoCallClientFooterFormModeChange,
  onHaoConfirmationFooterFormModeChange,
  onHaoRevisedInvoiceFooterFormModeChange,
  onHaoInvoiceFooterFormModeChange,
  onHaoInvoiceSentStatusChange,
  onHaoTmMInvoiceSentStatusChange,
  onHaoTmMInvoiceFooterFormModeChange,
  onHaoTmMInvoiceFooterSkipModeChange,
  onHaoTmMInvoiceProgressActionChange,
  onHaoTmMPaymentFooterFormModeChange,
  onHaoTmMPaymentFooterSkipModeChange,
  onHaoTmMFileFooterFormModeChange,
  onHaoTmMInformFooterSendModeChange,
  onHaoTmMInformFooterSaveModeChange,
  onHaoPaymentFooterFormModeChange,
  onHaoPaymentProgressActionChange,
  onHaoStampVendorFooterSendModeChange,
  onHaoStampVendorFooterSaveModeChange,
  onHaoStampVendorFooterSkipModeChange,
  onHaoStampFromVendorFooterFormModeChange,
  onHaoStampFromVendorFooterSkipModeChange,
  onHaoDocsFromClientFooterFormModeChange,
  onHaoDocsFromClientProgressActionChange,
  onHaoUserAffidavitFooterFormModeChange,
  onHaoUserAffidavitFooterSkipModeChange,
  onHaoSignatureFooterFormModeChange,
  onHaoNotarisationFooterFormModeChange,
  onHaoNotarisationFooterSkipModeChange,
  onHaoScanningFooterFormModeChange,
  onHaoFileHearingDocFooterFormModeChange,
  onHaoNoteOnHearingFooterFormModeChange,
  onHaoAttendHearingFooterFormModeChange,
  onHaoHearingNotHappenedFooterSendModeChange,
  onHaoHearingNotHappenedFooterSaveModeChange,
  onHaoHearingNotHappenedFooterSkipModeChange,
  onHaoInformAfterHearingFooterSendModeChange,
  onHaoInformAfterHearingFooterSaveModeChange,
  onHaoUploadDocsAfterHearingFooterFormModeChange,
  onHaoUploadDocsAfterHearingProgressActionChange,
  onHaoTmMAfterInvoiceFooterFormModeChange,
  onHaoTmMAfterInvoiceFooterSkipModeChange,
  onHaoTmMAfterInvoiceProgressActionChange,
  onHaoTmMAfterPaymentFooterFormModeChange,
  onHaoTmMAfterPaymentFooterSkipModeChange,
  onHaoFileTmMAfterHearingFooterFormModeChange,
  onHaoFileTmMAfterHearingFooterSkipModeChange,
  onHaoInformTmMAfterHearingFooterSendModeChange,
  onHaoInformTmMAfterHearingFooterSaveModeChange,
  onHaoInformTmMAfterHearingFooterSkipModeChange,
  onHaoSubsequentHearingsFooterSendModeChange,
  onHaoSubsequentHearingsFooterSaveModeChange,
  onHaoSubsequentHearingsFooterSkipModeChange,
  onHaoSubsequentInvoiceFooterSkipModeChange,
  onHaoSubsequentInvoiceFooterFormModeChange,
  onHaoSubsequentInvoiceSentStatusChange,
  onHaoSubsequentPaymentFromClientFooterFormModeChange,
  onHaoSubsequentPaymentFromClientFooterSkipModeChange,
  onHaoSubsequentAttendFooterFormModeChange,
  onHaoSubsequentAttendFooterSkipModeChange,
  onHaoSubsequentPostponementFooterFormModeChange,
  onHaoOrderOfHearingFooterSendModeChange,
  onHaoOrderOfHearingFooterSaveModeChange,
}: {
  leadId: string;
  phone?: string;
  active: boolean;
  onSavingChange?: (saving: boolean) => void;
  /** 3D(vii) NO → 5B(i) Refusal/Abandonment; supplied by the page (item 67). */
  onAdvanceToRefusalAbandonment?: () => void;
  onHaoInformFooterSendModeChange?: (needsSend: boolean) => void;
  onHaoInformFooterSaveModeChange?: (needsSave: boolean) => void;
  onHaoStatusFooterFormModeChange?: (needsSubmit: boolean) => void;
  onHaoNoticeFooterSendModeChange?: (needsSend: boolean) => void;
  onHaoNoticeFooterSaveModeChange?: (needsSave: boolean) => void;
  onHaoInformHearingFooterSendModeChange?: (needsSend: boolean) => void;
  onHaoInformHearingFooterSaveModeChange?: (needsSave: boolean) => void;
  onHaoCallClientFooterFormModeChange?: (needsSubmit: boolean) => void;
  onHaoConfirmationFooterFormModeChange?: (needsSubmit: boolean) => void;
  onHaoRevisedInvoiceFooterFormModeChange?: (needsSubmit: boolean) => void;
  onHaoInvoiceFooterFormModeChange?: (needsSubmit: boolean) => void;
  onHaoInvoiceSentStatusChange?: (sent: boolean) => void;
  onHaoTmMInvoiceSentStatusChange?: (sent: boolean) => void;
  onHaoTmMInvoiceFooterFormModeChange?: (needsSubmit: boolean) => void;
  onHaoTmMInvoiceFooterSkipModeChange?: (needsSkip: boolean) => void;
  onHaoTmMInvoiceProgressActionChange?: (
    run: (() => Promise<void>) | null
  ) => void;
  onHaoTmMPaymentFooterFormModeChange?: (needsSubmit: boolean) => void;
  onHaoTmMPaymentFooterSkipModeChange?: (needsSkip: boolean) => void;
  onHaoTmMFileFooterFormModeChange?: (needsSubmit: boolean) => void;
  onHaoTmMInformFooterSendModeChange?: (needsSend: boolean) => void;
  onHaoTmMInformFooterSaveModeChange?: (needsSave: boolean) => void;
  onHaoPaymentFooterFormModeChange?: (needsSubmit: boolean) => void;
  onHaoPaymentProgressActionChange?: (
    run: (() => Promise<void>) | null
  ) => void;
  onHaoStampVendorFooterSendModeChange?: (needsSend: boolean) => void;
  onHaoStampVendorFooterSaveModeChange?: (needsSave: boolean) => void;
  onHaoStampVendorFooterSkipModeChange?: (needsSkip: boolean) => void;
  onHaoStampFromVendorFooterFormModeChange?: (needsSubmit: boolean) => void;
  onHaoStampFromVendorFooterSkipModeChange?: (needsSkip: boolean) => void;
  onHaoDocsFromClientFooterFormModeChange?: (needsSubmit: boolean) => void;
  onHaoDocsFromClientProgressActionChange?: (
    run: (() => Promise<void>) | null
  ) => void;
  onHaoUserAffidavitFooterFormModeChange?: (needsSubmit: boolean) => void;
  onHaoUserAffidavitFooterSkipModeChange?: (needsSkip: boolean) => void;
  onHaoSignatureFooterFormModeChange?: (needsSubmit: boolean) => void;
  onHaoNotarisationFooterFormModeChange?: (needsSubmit: boolean) => void;
  onHaoNotarisationFooterSkipModeChange?: (needsSkip: boolean) => void;
  onHaoScanningFooterFormModeChange?: (needsSubmit: boolean) => void;
  onHaoFileHearingDocFooterFormModeChange?: (needsSubmit: boolean) => void;
  onHaoNoteOnHearingFooterFormModeChange?: (needsSubmit: boolean) => void;
  onHaoAttendHearingFooterFormModeChange?: (needsSubmit: boolean) => void;
  onHaoHearingNotHappenedFooterSendModeChange?: (needsSend: boolean) => void;
  onHaoHearingNotHappenedFooterSaveModeChange?: (needsSave: boolean) => void;
  onHaoHearingNotHappenedFooterSkipModeChange?: (needsSkip: boolean) => void;
  onHaoInformAfterHearingFooterSendModeChange?: (needsSend: boolean) => void;
  onHaoInformAfterHearingFooterSaveModeChange?: (needsSave: boolean) => void;
  onHaoUploadDocsAfterHearingFooterFormModeChange?: (needsSubmit: boolean) => void;
  onHaoUploadDocsAfterHearingProgressActionChange?: (
    run: (() => Promise<void>) | null
  ) => void;
  onHaoTmMAfterInvoiceFooterFormModeChange?: (needsSubmit: boolean) => void;
  onHaoTmMAfterInvoiceFooterSkipModeChange?: (needsSkip: boolean) => void;
  onHaoTmMAfterInvoiceProgressActionChange?: (
    run: (() => Promise<void>) | null
  ) => void;
  onHaoTmMAfterPaymentFooterFormModeChange?: (needsSubmit: boolean) => void;
  onHaoTmMAfterPaymentFooterSkipModeChange?: (needsSkip: boolean) => void;
  onHaoFileTmMAfterHearingFooterFormModeChange?: (needsSubmit: boolean) => void;
  onHaoFileTmMAfterHearingFooterSkipModeChange?: (needsSkip: boolean) => void;
  onHaoInformTmMAfterHearingFooterSendModeChange?: (needsSend: boolean) => void;
  onHaoInformTmMAfterHearingFooterSaveModeChange?: (needsSave: boolean) => void;
  onHaoInformTmMAfterHearingFooterSkipModeChange?: (needsSkip: boolean) => void;
  onHaoSubsequentHearingsFooterSendModeChange?: (needsSend: boolean) => void;
  onHaoSubsequentHearingsFooterSaveModeChange?: (needsSave: boolean) => void;
  onHaoSubsequentHearingsFooterSkipModeChange?: (needsSkip: boolean) => void;
  onHaoSubsequentInvoiceFooterSkipModeChange?: (needsSkip: boolean) => void;
  /** HAO Subsequent Hearing Invoice (active): footer Send/Save & Continue submits the form */
  onHaoSubsequentInvoiceFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** HAO Subsequent Hearing Invoice: "sent" status — flips footer label between Send & Save */
  onHaoSubsequentInvoiceSentStatusChange?: (sent: boolean) => void;
  onHaoSubsequentPaymentFromClientFooterFormModeChange?: (
    needsSubmit: boolean
  ) => void;
  /** HAO Payment from Client for Subsequent Hearing — Skip & Continue when invoice not active for this application */
  onHaoSubsequentPaymentFromClientFooterSkipModeChange?: (
    needsSkip: boolean
  ) => void;
  onHaoSubsequentAttendFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** HAO Attend Subsequent Hearing — Skip & Continue when Subsequent Hearings not active for this application */
  onHaoSubsequentAttendFooterSkipModeChange?: (needsSkip: boolean) => void;
  onHaoSubsequentPostponementFooterFormModeChange?: (
    needsSubmit: boolean
  ) => void;
  onHaoOrderOfHearingFooterSendModeChange?: (needsSend: boolean) => void;
  onHaoOrderOfHearingFooterSaveModeChange?: (needsSave: boolean) => void;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTmNo = searchParams.get("tm");
  const urlSubStep = searchParams.get("substep") as HaoSubStep | null;
  const validHaoSubSteps: HaoSubStep[] = useMemo(
    () => HAO_SUB_STEPS.map((s) => s.id),
    []
  );

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<ShowCauseHearingStatus | null>(null);
  const [selectedTmIdx, setSelectedTmIdxState] = useState(0);
  const [subStep, setSubStepState] = useState<HaoSubStep>(
    urlSubStep && validHaoSubSteps.includes(urlSubStep)
      ? urlSubStep
      : "inform-client-showcause"
  );
  const [hasSentInform, setHasSentInform] = useState(false);
  const [resendingInform, setResendingInform] = useState(false);
  const [hasSentNotice, setHasSentNotice] = useState(false);
  const [resendingNotice, setResendingNotice] = useState(false);
  const [hasSentInformHearing, setHasSentInformHearing] = useState(false);
  const [resendingInformHearing, setResendingInformHearing] = useState(false);
  const [hasSentInvoice, setHasSentInvoice] = useState(false);
  const [hasSentTmMInvoice, setHasSentTmMInvoice] = useState(false);
  const [hasSentTmMAfterInvoice, setHasSentTmMAfterInvoice] = useState(false);
  const [hasRecordedCall, setHasRecordedCall] = useState(false);
  const [hasSavedConfirmation, setHasSavedConfirmation] = useState(false);
  const [hasSentTmMInform, setHasSentTmMInform] = useState(false);
  const [resendingTmMInform, setResendingTmMInform] = useState(false);
  const [hasSentStampVendor, setHasSentStampVendor] = useState(false);
  const [resendingStampVendor, setResendingStampVendor] = useState(false);
  const [hasSentHearingNotHappened, setHasSentHearingNotHappened] =
    useState(false);
  const [resendingHearingNotHappened, setResendingHearingNotHappened] =
    useState(false);
  const [hasSentInformAfterHearing, setHasSentInformAfterHearing] =
    useState(false);
  const [resendingInformAfterHearing, setResendingInformAfterHearing] =
    useState(false);
  const [hasSentInformTmMAfter, setHasSentInformTmMAfter] = useState(false);
  const [resendingInformTmMAfter, setResendingInformTmMAfter] =
    useState(false);
  const [hasSentSubsequentHearings, setHasSentSubsequentHearings] =
    useState(false);
  const [resendingSubsequentHearings, setResendingSubsequentHearings] =
    useState(false);
  const [hasSentSubsequentInvoice, setHasSentSubsequentInvoice] =
    useState(false);
  const [hasSentOrderOfHearing, setHasSentOrderOfHearing] = useState(false);
  const [resendingOrderOfHearing, setResendingOrderOfHearing] =
    useState(false);
  // Review item 71 — 3D(xlix) is inactive when the selected mark had a specific 2l) User Date at
  // filing, or when its Order of Hearing was already sent in Hearing 1. It is the last HAO
  // sub-step, so there is nowhere to skip to: suppress the footer actions instead.
  const [orderOfHearingGated, setOrderOfHearingGated] = useState(false);

  // Read step completion from workflow for all HAO sub-steps
  useEffect(() => {
    if (!leadId) {
      setHasSentInform(false);
      setHasSentNotice(false);
      setHasSentInformHearing(false);
      setHasSentInvoice(false);
      setHasSentTmMInvoice(false);
      setHasSentTmMAfterInvoice(false);
      setHasRecordedCall(false);
      setHasSavedConfirmation(false);
      setHasSentTmMInform(false);
      setHasSentStampVendor(false);
      setHasSentHearingNotHappened(false);
      setHasSentInformAfterHearing(false);
      setHasSentInformTmMAfter(false);
      setHasSentSubsequentHearings(false);
      setHasSentSubsequentInvoice(false);
      setHasSentOrderOfHearing(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const wf = await getWorkflowStatus(leadId);
        if (cancelled) return;
        const find = (slug: string) => wf.steps.find((s) => s.slug === slug);
        setHasSentInform(Boolean(find(INFORM_CLIENT_HEARING_STEP_SLUG)?.isCompleted));
        setHasSentNotice(Boolean(find(NOTICE_TO_CLIENT_HEARING_STEP_SLUG)?.isCompleted));
        setHasSentInformHearing(
          Boolean(find(INFORM_CLIENT_ABOUT_HEARING_STEP_SLUG)?.isCompleted)
        );
        setHasSentInvoice(
          Boolean(find(INVOICE_FOR_HEARING_STEP_SLUG)?.isCompleted)
        );
        setHasSentTmMInvoice(
          Boolean(find(TMM_BEFORE_HEARING_SEND_INVOICE_STEP_SLUG)?.isCompleted)
        );
        setHasSentTmMAfterInvoice(
          Boolean(find(TMM_AFTER_HEARING_SEND_INVOICE_STEP_SLUG)?.isCompleted)
        );
        setHasRecordedCall(Boolean(find(CALL_CLIENT_HEARING_STEP_SLUG)?.isCompleted));
        setHasSavedConfirmation(
          Boolean(find(CONFIRMATION_FROM_CLIENT_HEARING_STEP_SLUG)?.isCompleted)
        );
        setHasSentTmMInform(
          Boolean(find(TMM_BEFORE_HEARING_INFORM_STEP_SLUG)?.isCompleted)
        );
        setHasSentStampVendor(
          Boolean(find(STAMP_PAPER_VENDOR_HEARING_STEP_SLUG)?.isCompleted)
        );
        setHasSentHearingNotHappened(
          Boolean(
            find("S3_03D_XXIII_NOTICE_TO_CLIENT_HEARING_DIDNT_HAPPEN")?.isCompleted
          )
        );
        setHasSentInformAfterHearing(
          Boolean(find("S3_03D_XXIV_INFORM_CLIENT_AFTER_HEARING")?.isCompleted)
        );
        setHasSentInformTmMAfter(
          Boolean(
            find("S3_03D_XXIX_INFORM_CLIENT_TM_M_AFTER_HEARING_FILED")
              ?.isCompleted
          )
        );
        setHasSentSubsequentHearings(
          Boolean(find("S3_03D_XXX_SUBSEQUENT_HEARINGS")?.isCompleted)
        );
        setHasSentOrderOfHearing(
          Boolean(find("S3_03D_XLIX_ORDER_OF_HEARING_TO_CLIENT")?.isCompleted)
        );
      } catch {
        if (!cancelled) {
          setHasSentInform(false);
          setHasSentNotice(false);
          setHasSentInformHearing(false);
          setHasSentInvoice(false);
          setHasSentTmMInvoice(false);
          setHasSentTmMAfterInvoice(false);
          setHasRecordedCall(false);
          setHasSavedConfirmation(false);
          setHasSentTmMInform(false);
          setHasSentStampVendor(false);
          setHasSentHearingNotHappened(false);
          setHasSentInformAfterHearing(false);
          setHasSentInformTmMAfter(false);
          setHasSentSubsequentHearings(false);
          setHasSentSubsequentInvoice(false);
          setHasSentOrderOfHearing(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const writeUrl = (patch: { tm?: string | null; substep?: string | null }) => {
    const params = new URLSearchParams(searchParams);
    if (patch.tm !== undefined) {
      if (patch.tm) params.set("tm", patch.tm);
      else params.delete("tm");
    }
    if (patch.substep !== undefined) {
      if (patch.substep) params.set("substep", patch.substep);
      else params.delete("substep");
    }
    setSearchParams(params, { replace: true });
  };

  const setSelectedTmIdx = (idx: number) => {
    setSelectedTmIdxState(idx);
    const apps = status?.applications ?? [];
    const tm = apps[idx]?.tmNo ?? null;
    writeUrl({ tm });
  };
  const setSubStep = (next: HaoSubStep) => {
    setSubStepState(next);
    writeUrl({ substep: next });
  };

  // Sliding left-edge indicator: tracks the active sub-step button's vertical
  // position and animates between rows. Also scrolls active row into view when
  // it changes (URL restore or programmatic advance after Send/Save).
  // Critical here because HAO has 35 sub-steps — the active one is often off-screen.
  const activeSubStepBtnRef = useRef<HTMLButtonElement | null>(null);
  const [subStepIndicator, setSubStepIndicator] = useState({ top: 0, height: 0 });
  useLayoutEffect(() => {
    const btn = activeSubStepBtnRef.current;
    if (!btn) return;
    setSubStepIndicator({ top: btn.offsetTop, height: btn.offsetHeight });
    btn.scrollIntoView({ block: "nearest" });
  }, [subStep]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getShowCauseHearingStatusDetails(leadId)
      .then((data) => {
        if (cancelled) return;
        setStatus(data);
        const idx = urlTmNo
          ? data.applications.findIndex((a) => a.tmNo === urlTmNo)
          : -1;
        setSelectedTmIdxState(idx >= 0 ? idx : 0);
      })
      .catch((err) => {
        if (!cancelled)
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load Hearing after Objection"
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const applications = useMemo(
    () => status?.applications ?? [],
    [status]
  );
  const selected =
    applications[Math.min(selectedTmIdx, applications.length - 1)] ?? null;

  // Footer signaling — Inform Client (Showcause): Send & Continue when not sent
  useLayoutEffect(() => {
    const needsSend =
      active &&
      subStep === "inform-client-showcause" &&
      !hasSentInform &&
      !!leadId;
    onHaoInformFooterSendModeChange?.(needsSend);
  }, [active, subStep, hasSentInform, leadId, onHaoInformFooterSendModeChange]);

  // Footer signaling — Inform Client (Showcause): Save & Continue when sent
  useLayoutEffect(() => {
    const needsSave =
      active && subStep === "inform-client-showcause" && hasSentInform;
    onHaoInformFooterSaveModeChange?.(needsSave);
  }, [active, subStep, hasSentInform, onHaoInformFooterSaveModeChange]);

  // Footer signaling — Status details of Hearing: Save & Continue submits form
  useLayoutEffect(() => {
    const needsSubmit =
      active && subStep === "status-details-of-hearing" && !!leadId;
    onHaoStatusFooterFormModeChange?.(needsSubmit);
  }, [active, subStep, leadId, onHaoStatusFooterFormModeChange]);

  // Footer signaling — Notice to Client for Hearing: Send & Continue when not sent
  useLayoutEffect(() => {
    const needsSend =
      active &&
      subStep === "notice-to-client-for-hearing" &&
      !hasSentNotice &&
      !!leadId;
    onHaoNoticeFooterSendModeChange?.(needsSend);
  }, [active, subStep, hasSentNotice, leadId, onHaoNoticeFooterSendModeChange]);

  // Footer signaling — Notice to Client for Hearing: Save & Continue when sent
  useLayoutEffect(() => {
    const needsSave =
      active && subStep === "notice-to-client-for-hearing" && hasSentNotice;
    onHaoNoticeFooterSaveModeChange?.(needsSave);
  }, [active, subStep, hasSentNotice, onHaoNoticeFooterSaveModeChange]);

  // Footer signaling — Inform Client about Hearing: Send & Continue when not sent
  useLayoutEffect(() => {
    const needsSend =
      active &&
      subStep === "inform-client-about-hearing" &&
      !hasSentInformHearing &&
      !!leadId;
    onHaoInformHearingFooterSendModeChange?.(needsSend);
  }, [
    active,
    subStep,
    hasSentInformHearing,
    leadId,
    onHaoInformHearingFooterSendModeChange,
  ]);

  // Footer signaling — Inform Client about Hearing: Save & Continue when sent
  useLayoutEffect(() => {
    const needsSave =
      active &&
      subStep === "inform-client-about-hearing" &&
      hasSentInformHearing;
    onHaoInformHearingFooterSaveModeChange?.(needsSave);
  }, [
    active,
    subStep,
    hasSentInformHearing,
    onHaoInformHearingFooterSaveModeChange,
  ]);

  // Footer signaling — Call Client for Hearing: Save & Continue submits form
  useLayoutEffect(() => {
    const needsSubmit =
      active && subStep === "call-client-for-hearing" && !!leadId;
    onHaoCallClientFooterFormModeChange?.(needsSubmit);
  }, [active, subStep, leadId, onHaoCallClientFooterFormModeChange]);

  // Footer signaling — Confirmation from Client: Save & Continue submits form
  useLayoutEffect(() => {
    const needsSubmit =
      active &&
      subStep === "confirmation-from-client-for-hearing" &&
      !!leadId;
    onHaoConfirmationFooterFormModeChange?.(needsSubmit);
  }, [active, subStep, leadId, onHaoConfirmationFooterFormModeChange]);

  // Footer signaling — 3D(vii)a Revised Invoice: Save & Continue submits the form
  useLayoutEffect(() => {
    const needsSubmit =
      active && subStep === "revised-invoice-for-hearing" && !!leadId;
    onHaoRevisedInvoiceFooterFormModeChange?.(needsSubmit);
  }, [active, subStep, leadId, onHaoRevisedInvoiceFooterFormModeChange]);

  // Footer signaling — Invoice for Hearing: Save & Continue submits form
  useLayoutEffect(() => {
    const needsSubmit =
      active && subStep === "invoice-for-hearing" && !!leadId;
    onHaoInvoiceFooterFormModeChange?.(needsSubmit);
  }, [active, subStep, leadId, onHaoInvoiceFooterFormModeChange]);

  // Bubble Invoice "sent" status up so footer flips between Send & Save labels
  useLayoutEffect(() => {
    onHaoInvoiceSentStatusChange?.(hasSentInvoice);
  }, [hasSentInvoice, onHaoInvoiceSentStatusChange]);

  // Bubble TM-M before Hearing Invoice "sent" status up for the same label flip
  useLayoutEffect(() => {
    onHaoTmMInvoiceSentStatusChange?.(hasSentTmMInvoice);
  }, [hasSentTmMInvoice, onHaoTmMInvoiceSentStatusChange]);

  // Footer signaling — TM-M before invoice / payment / file / inform / payment-hearing
  useLayoutEffect(() => {
    onHaoTmMInvoiceFooterFormModeChange?.(
      !!(active && subStep === "invoice-for-tmm-before-hearing" && leadId)
    );
  }, [active, subStep, leadId, onHaoTmMInvoiceFooterFormModeChange]);

  useLayoutEffect(() => {
    onHaoTmMPaymentFooterFormModeChange?.(
      !!(
        active &&
        subStep === "payment-from-client-for-tmm-before-hearing" &&
        leadId
      )
    );
  }, [active, subStep, leadId, onHaoTmMPaymentFooterFormModeChange]);

  useLayoutEffect(() => {
    onHaoTmMFileFooterFormModeChange?.(
      !!(active && subStep === "file-tmm-before-hearing" && leadId)
    );
  }, [active, subStep, leadId, onHaoTmMFileFooterFormModeChange]);

  useLayoutEffect(() => {
    onHaoTmMInformFooterSendModeChange?.(
      !!(
        active &&
        subStep === "inform-client-about-filing-tmm-before-hearing" &&
        !hasSentTmMInform &&
        leadId
      )
    );
  }, [active, subStep, hasSentTmMInform, leadId, onHaoTmMInformFooterSendModeChange]);

  useLayoutEffect(() => {
    onHaoTmMInformFooterSaveModeChange?.(
      !!(
        active &&
        subStep === "inform-client-about-filing-tmm-before-hearing" &&
        hasSentTmMInform
      )
    );
  }, [active, subStep, hasSentTmMInform, onHaoTmMInformFooterSaveModeChange]);

  useLayoutEffect(() => {
    onHaoPaymentFooterFormModeChange?.(
      !!(active && subStep === "payment-from-client-for-hearing" && leadId)
    );
  }, [active, subStep, leadId, onHaoPaymentFooterFormModeChange]);

  useLayoutEffect(() => {
    onHaoStampVendorFooterSendModeChange?.(
      !!(
        active &&
        subStep === "stamp-paper-details-to-vendor-for-hearing" &&
        !hasSentStampVendor &&
        leadId
      )
    );
  }, [
    active,
    subStep,
    hasSentStampVendor,
    leadId,
    onHaoStampVendorFooterSendModeChange,
  ]);

  useLayoutEffect(() => {
    onHaoStampVendorFooterSaveModeChange?.(
      !!(
        active &&
        subStep === "stamp-paper-details-to-vendor-for-hearing" &&
        hasSentStampVendor
      )
    );
  }, [
    active,
    subStep,
    hasSentStampVendor,
    onHaoStampVendorFooterSaveModeChange,
  ]);

  // 7 new HAO sub-steps: Save & Continue submits the form
  useLayoutEffect(() => {
    onHaoStampFromVendorFooterFormModeChange?.(
      !!(active && subStep === "stamp-paper-from-vendor-for-hearing" && leadId)
    );
  }, [active, subStep, leadId, onHaoStampFromVendorFooterFormModeChange]);

  useLayoutEffect(() => {
    onHaoDocsFromClientFooterFormModeChange?.(
      !!(active && subStep === "documents-from-client-for-hearing" && leadId)
    );
  }, [active, subStep, leadId, onHaoDocsFromClientFooterFormModeChange]);

  useLayoutEffect(() => {
    onHaoUserAffidavitFooterFormModeChange?.(
      !!(
        active &&
        subStep === "preparation-of-user-affidavit-for-hearing" &&
        leadId
      )
    );
  }, [active, subStep, leadId, onHaoUserAffidavitFooterFormModeChange]);

  useLayoutEffect(() => {
    onHaoSignatureFooterFormModeChange?.(
      !!(active && subStep === "signature-from-client-for-hearing" && leadId)
    );
  }, [active, subStep, leadId, onHaoSignatureFooterFormModeChange]);

  useLayoutEffect(() => {
    onHaoNotarisationFooterFormModeChange?.(
      !!(active && subStep === "notarisation-of-documents-for-hearing" && leadId)
    );
  }, [active, subStep, leadId, onHaoNotarisationFooterFormModeChange]);

  useLayoutEffect(() => {
    onHaoScanningFooterFormModeChange?.(
      !!(active && subStep === "scanning-of-documents-for-hearing" && leadId)
    );
  }, [active, subStep, leadId, onHaoScanningFooterFormModeChange]);

  useLayoutEffect(() => {
    onHaoFileHearingDocFooterFormModeChange?.(
      !!(active && subStep === "file-hearing-document" && leadId)
    );
  }, [active, subStep, leadId, onHaoFileHearingDocFooterFormModeChange]);

  // 7 next HAO sub-steps
  useLayoutEffect(() => {
    onHaoNoteOnHearingFooterFormModeChange?.(
      !!(active && subStep === "note-on-hearing" && leadId)
    );
  }, [active, subStep, leadId, onHaoNoteOnHearingFooterFormModeChange]);

  useLayoutEffect(() => {
    onHaoAttendHearingFooterFormModeChange?.(
      !!(active && subStep === "attend-hearing" && leadId)
    );
  }, [active, subStep, leadId, onHaoAttendHearingFooterFormModeChange]);

  useLayoutEffect(() => {
    onHaoHearingNotHappenedFooterSendModeChange?.(
      !!(
        active &&
        subStep === "notice-to-client-for-hearing-not-happened" &&
        !hasSentHearingNotHappened &&
        leadId
      )
    );
  }, [
    active,
    subStep,
    hasSentHearingNotHappened,
    leadId,
    onHaoHearingNotHappenedFooterSendModeChange,
  ]);

  useLayoutEffect(() => {
    onHaoHearingNotHappenedFooterSaveModeChange?.(
      !!(
        active &&
        subStep === "notice-to-client-for-hearing-not-happened" &&
        hasSentHearingNotHappened
      )
    );
  }, [
    active,
    subStep,
    hasSentHearingNotHappened,
    onHaoHearingNotHappenedFooterSaveModeChange,
  ]);

  useLayoutEffect(() => {
    onHaoInformAfterHearingFooterSendModeChange?.(
      !!(
        active &&
        subStep === "inform-client-after-hearing" &&
        !hasSentInformAfterHearing &&
        leadId
      )
    );
  }, [
    active,
    subStep,
    hasSentInformAfterHearing,
    leadId,
    onHaoInformAfterHearingFooterSendModeChange,
  ]);

  useLayoutEffect(() => {
    onHaoInformAfterHearingFooterSaveModeChange?.(
      !!(
        active &&
        subStep === "inform-client-after-hearing" &&
        hasSentInformAfterHearing
      )
    );
  }, [
    active,
    subStep,
    hasSentInformAfterHearing,
    onHaoInformAfterHearingFooterSaveModeChange,
  ]);

  useLayoutEffect(() => {
    onHaoUploadDocsAfterHearingFooterFormModeChange?.(
      !!(active && subStep === "upload-documents-after-hearing" && leadId)
    );
  }, [active, subStep, leadId, onHaoUploadDocsAfterHearingFooterFormModeChange]);

  useLayoutEffect(() => {
    onHaoTmMAfterInvoiceFooterFormModeChange?.(
      !!(active && subStep === "invoice-for-tmm-after-hearing" && leadId)
    );
  }, [active, subStep, leadId, onHaoTmMAfterInvoiceFooterFormModeChange]);

  useLayoutEffect(() => {
    onHaoTmMAfterPaymentFooterFormModeChange?.(
      !!(
        active &&
        subStep === "payment-from-client-for-tmm-after-hearing" &&
        leadId
      )
    );
  }, [active, subStep, leadId, onHaoTmMAfterPaymentFooterFormModeChange]);

  // 4 final HAO sub-steps
  useLayoutEffect(() => {
    onHaoFileTmMAfterHearingFooterFormModeChange?.(
      !!(active && subStep === "file-tmm-after-hearing-1" && leadId)
    );
  }, [active, subStep, leadId, onHaoFileTmMAfterHearingFooterFormModeChange]);

  useLayoutEffect(() => {
    onHaoInformTmMAfterHearingFooterSendModeChange?.(
      !!(
        active &&
        subStep === "inform-client-about-filing-tmm-after-hearing" &&
        !hasSentInformTmMAfter &&
        leadId
      )
    );
  }, [
    active,
    subStep,
    hasSentInformTmMAfter,
    leadId,
    onHaoInformTmMAfterHearingFooterSendModeChange,
  ]);

  useLayoutEffect(() => {
    onHaoInformTmMAfterHearingFooterSaveModeChange?.(
      !!(
        active &&
        subStep === "inform-client-about-filing-tmm-after-hearing" &&
        hasSentInformTmMAfter
      )
    );
  }, [
    active,
    subStep,
    hasSentInformTmMAfter,
    onHaoInformTmMAfterHearingFooterSaveModeChange,
  ]);

  useLayoutEffect(() => {
    onHaoSubsequentHearingsFooterSendModeChange?.(
      !!(
        active &&
        subStep === "subsequent-hearings" &&
        !hasSentSubsequentHearings &&
        leadId
      )
    );
  }, [
    active,
    subStep,
    hasSentSubsequentHearings,
    leadId,
    onHaoSubsequentHearingsFooterSendModeChange,
  ]);

  // Footer signaling — Subsequent Hearing Invoice: Send/Save & Continue submits the form
  // (mirrors Invoice for Hearing pattern). Without this the dashboard footer falls back to
  // its default "navigate to next status section" handler and jumps to Advertised in Journal.
  useLayoutEffect(() => {
    onHaoSubsequentInvoiceFooterFormModeChange?.(
      !!(active && subStep === "subsequent-hearings-invoice" && leadId)
    );
  }, [active, subStep, leadId, onHaoSubsequentInvoiceFooterFormModeChange]);

  useLayoutEffect(() => {
    onHaoSubsequentInvoiceSentStatusChange?.(hasSentSubsequentInvoice);
  }, [hasSentSubsequentInvoice, onHaoSubsequentInvoiceSentStatusChange]);

  useLayoutEffect(() => {
    onHaoSubsequentHearingsFooterSaveModeChange?.(
      !!(
        active && subStep === "subsequent-hearings" && hasSentSubsequentHearings
      )
    );
  }, [
    active,
    subStep,
    hasSentSubsequentHearings,
    onHaoSubsequentHearingsFooterSaveModeChange,
  ]);

  useLayoutEffect(() => {
    onHaoSubsequentPaymentFromClientFooterFormModeChange?.(
      !!(
        active &&
        subStep === "subsequent-hearings-payment-from-client" &&
        leadId
      )
    );
  }, [
    active,
    subStep,
    leadId,
    onHaoSubsequentPaymentFromClientFooterFormModeChange,
  ]);

  useLayoutEffect(() => {
    onHaoSubsequentAttendFooterFormModeChange?.(
      !!(active && subStep === "subsequent-hearings-attend" && leadId)
    );
  }, [active, subStep, leadId, onHaoSubsequentAttendFooterFormModeChange]);

  useLayoutEffect(() => {
    onHaoSubsequentPostponementFooterFormModeChange?.(
      !!(active && subStep === "subsequent-hearings-postponement" && leadId)
    );
  }, [
    active,
    subStep,
    leadId,
    onHaoSubsequentPostponementFooterFormModeChange,
  ]);

  useLayoutEffect(() => {
    onHaoOrderOfHearingFooterSendModeChange?.(
      !!(
        active &&
        subStep === "order-of-hearing-to-client" &&
        !orderOfHearingGated &&
        !hasSentOrderOfHearing &&
        leadId
      )
    );
  }, [
    active,
    subStep,
    hasSentOrderOfHearing,
    orderOfHearingGated,
    leadId,
    onHaoOrderOfHearingFooterSendModeChange,
  ]);

  useLayoutEffect(() => {
    onHaoOrderOfHearingFooterSaveModeChange?.(
      !!(
        active &&
        subStep === "order-of-hearing-to-client" &&
        !orderOfHearingGated &&
        hasSentOrderOfHearing
      )
    );
  }, [
    active,
    subStep,
    hasSentOrderOfHearing,
    orderOfHearingGated,
    onHaoOrderOfHearingFooterSaveModeChange,
  ]);

  const handleInformResend = async () => {
    if (!leadId || resendingInform) return;
    setResendingInform(true);
    onSavingChange?.(true);
    try {
      await sendShowCauseHearingInformClient(
        leadId,
        [selected?.tmNo].filter((s): s is string => Boolean(s))
      );
      toast.success("Inform Client (Showcause) resent to client.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to resend");
    } finally {
      setResendingInform(false);
      onSavingChange?.(false);
    }
  };

  const handleNoticeResend = async () => {
    if (!leadId || resendingNotice) return;
    setResendingNotice(true);
    onSavingChange?.(true);
    try {
      await sendShowCauseHearingNoticeToClient(
        leadId,
        [selected?.tmNo].filter((s): s is string => Boolean(s))
      );
      toast.success("Hearing Notice resent to client");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to resend");
    } finally {
      setResendingNotice(false);
      onSavingChange?.(false);
    }
  };

  const handleInformHearingResend = async () => {
    if (!leadId || resendingInformHearing) return;
    setResendingInformHearing(true);
    onSavingChange?.(true);
    try {
      await sendShowCauseHearingInformClientAboutHearing(
        leadId,
        [selected?.tmNo].filter((s): s is string => Boolean(s))
      );
      toast.success("Inform Client about Hearing resent to client");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to resend");
    } finally {
      setResendingInformHearing(false);
      onSavingChange?.(false);
    }
  };

  const handleTmMInformResend = async () => {
    if (!leadId || resendingTmMInform) return;
    setResendingTmMInform(true);
    onSavingChange?.(true);
    try {
      await sendTmMBeforeHearingInformClient(
        leadId,
        [selected?.tmNo].filter((s): s is string => Boolean(s))
      );
      toast.success("Inform Client (TM-M filing) resent to client");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to resend");
    } finally {
      setResendingTmMInform(false);
      onSavingChange?.(false);
    }
  };

  const handleStampVendorResend = async () => {
    if (!leadId || resendingStampVendor) return;
    setResendingStampVendor(true);
    onSavingChange?.(true);
    try {
      await sendStampPaperForHearingToVendor(leadId, [selected?.tmNo].filter(
        (s): s is string => Boolean(s)
      ));
      toast.success("Stamp Paper details resent to vendor");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to resend");
    } finally {
      setResendingStampVendor(false);
      onSavingChange?.(false);
    }
  };

  const handleHearingNotHappenedResend = async () => {
    if (!leadId || resendingHearingNotHappened) return;
    setResendingHearingNotHappened(true);
    onSavingChange?.(true);
    try {
      await sendHearingDidntHappenToClient(
        leadId,
        [selected?.tmNo].filter((s): s is string => Boolean(s))
      );
      toast.success("Notice resent to client");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to resend");
    } finally {
      setResendingHearingNotHappened(false);
      onSavingChange?.(false);
    }
  };

  const handleInformAfterHearingResend = async () => {
    if (!leadId || resendingInformAfterHearing) return;
    setResendingInformAfterHearing(true);
    onSavingChange?.(true);
    try {
      await sendAllInformAfterHearing(
        leadId,
        [selected?.tmNo].filter((s): s is string => Boolean(s))
      );
      toast.success("Inform Client (after hearing) resent");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to resend");
    } finally {
      setResendingInformAfterHearing(false);
      onSavingChange?.(false);
    }
  };

  const handleInformTmMAfterResend = async () => {
    if (!leadId || resendingInformTmMAfter) return;
    setResendingInformTmMAfter(true);
    onSavingChange?.(true);
    try {
      await sendInformClientTmMAfterHearing(
        leadId,
        [selected?.tmNo].filter((s): s is string => Boolean(s))
      );
      toast.success("Inform Client (TM-M after hearing) resent");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to resend");
    } finally {
      setResendingInformTmMAfter(false);
      onSavingChange?.(false);
    }
  };

  const handleSubsequentHearingsResend = async () => {
    if (!leadId || resendingSubsequentHearings) return;
    setResendingSubsequentHearings(true);
    onSavingChange?.(true);
    try {
      await sendSubsequentHearingNotice(
        leadId,
        [selected?.tmNo].filter((s): s is string => Boolean(s))
      );
      toast.success("Subsequent Hearing notice resent");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to resend");
    } finally {
      setResendingSubsequentHearings(false);
      onSavingChange?.(false);
    }
  };

  const handleOrderOfHearingResend = async () => {
    if (!leadId || resendingOrderOfHearing) return;
    setResendingOrderOfHearing(true);
    onSavingChange?.(true);
    try {
      await forwardOrderOfHearingPdfsToClient(
        leadId,
        [selected?.tmNo].filter((s): s is string => Boolean(s))
      );
      toast.success("Order of Hearing PDFs resent");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to resend");
    } finally {
      setResendingOrderOfHearing(false);
      onSavingChange?.(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-[#6D6D6D]">Loading…</p>;
  }

  if (applications.length === 0 || !selected) {
    return (
      <p className="text-sm text-[#6D6D6D]">
        No applications with status "Examination Report – Hearing" in this
        acknowledgement yet. Update application status first.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {/* TM selector */}
      <div className="flex flex-row flex-wrap gap-3">
        {applications.map((app, idx) => {
          const isActive = selected.tmNo === app.tmNo;
          return (
            <button
              key={app.tmNo}
              type="button"
              onClick={() => setSelectedTmIdx(idx)}
              className={`flex items-center gap-2.5 px-2.5 py-2 rounded-[6px] h-10 min-w-[140px] border transition-colors ${
                isActive
                  ? "bg-[#222222] text-white border-transparent"
                  : "bg-[#FCFCFC] text-[#6D6D6D] border-[#EEEEEE]"
              }`}
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: "12px",
                fontWeight: isActive ? 500 : 400,
                lineHeight: "20px",
              }}
            >
              <RadioCircle active={isActive} />
              {formatTmNoForDisplay(app.tmNo)}
            </button>
          );
        })}
      </div>

      {/* Two-column sub-step panel */}
      <div className="border border-[#EEEEEE] rounded-xl overflow-hidden flex flex-col md:flex-row min-h-0">
        {/* Left: sub-steps */}
        <div className="relative w-full md:w-[235px] md:flex-shrink-0 border-b md:border-b-0 md:border-r border-[#EEEEEE] p-2 flex flex-col gap-1 overflow-y-auto">
          <div
            aria-hidden
            className="pointer-events-none absolute left-0 w-[3px] bg-[#222222] rounded-r-md transition-all duration-200 ease-out"
            style={{
              top: subStepIndicator.top,
              height: subStepIndicator.height,
              opacity: subStepIndicator.height > 0 ? 1 : 0,
            }}
          />
          {HAO_SUB_STEPS.map((step) => {
            const isActive = subStep === step.id;
            return (
              <button
                key={step.id}
                ref={isActive ? activeSubStepBtnRef : null}
                type="button"
                onClick={() => setSubStep(step.id)}
                className={`w-full flex items-center justify-between px-2.5 py-2.5 gap-2.5 rounded-[6px] text-xs transition-colors ${
                  isActive
                    ? "bg-[#222222] text-white font-medium"
                    : "bg-[#F7F7F7] text-[#222222] font-normal hover:bg-[#EEEEEE]"
                }`}
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                <span className="text-left line-clamp-1" title={step.label}>{step.label}</span>
                <ChevronRight active={isActive} />
              </button>
            );
          })}
        </div>

        {/* Right: detail panel */}
        <div className="flex-1 min-w-0 px-5 pt-0 pb-5 overflow-y-auto">
          {subStep === "inform-client-showcause" ? (
            <InformClientShowcausePanel
              leadId={leadId}
              status={status!}
              selected={selected}
              hasSent={hasSentInform}
              onSent={() => setHasSentInform(true)}
              onAdvance={() => setSubStep("status-details-of-hearing")}
              onSavingChange={onSavingChange}
              resending={resendingInform}
              onResend={() => void handleInformResend()}
            />
          ) : subStep === "status-details-of-hearing" ? (
            <StatusDetailsOfHearingPanel
              leadId={leadId}
              status={status!}
              selected={selected}
              onSaved={(next) => setStatus(next)}
              onAdvance={() => setSubStep("notice-to-client-for-hearing")}
              onSavingChange={onSavingChange}
            />
          ) : subStep === "notice-to-client-for-hearing" ? (
            <NoticeToClientForHearingPanel
              leadId={leadId}
              applications={applications}
              selected={selected}
              hasSent={hasSentNotice}
              onSent={() => {
                setHasSentNotice(true);
                setSubStep("inform-client-about-hearing");
              }}
              onAdvance={() => setSubStep("inform-client-about-hearing")}
              onSavingChange={onSavingChange}
              resending={resendingNotice}
              onResend={() => void handleNoticeResend()}
            />
          ) : subStep === "inform-client-about-hearing" ? (
            <InformClientAboutHearingPanel
              leadId={leadId}
              selected={selected}
              hasSent={hasSentInformHearing}
              onSent={() => {
                setHasSentInformHearing(true);
                setSubStep("invoice-for-hearing");
              }}
              onAdvance={() => setSubStep("invoice-for-hearing")}
              onSavingChange={onSavingChange}
              resending={resendingInformHearing}
              onResend={() => void handleInformHearingResend()}
            />
          ) : subStep === "invoice-for-hearing" ? (
            <InvoiceForHearingPanel
              leadId={leadId}
              phone={phone}
              status={status!}
              selected={selected}
              hasSent={hasSentInvoice}
              onSent={() => setHasSentInvoice(true)}
              onAdvance={() => setSubStep("call-client-for-hearing")}
              onSavingChange={onSavingChange}
            />
          ) : subStep === "call-client-for-hearing" ? (
            <CallClientForHearingPanel
              leadId={leadId}
              selected={selected}
              hasRecorded={hasRecordedCall}
              onRecorded={() => setHasRecordedCall(true)}
              onAdvance={() =>
                setSubStep("confirmation-from-client-for-hearing")
              }
              onSavingChange={onSavingChange}
            />
          ) : subStep === "invoice-for-tmm-before-hearing" ? (
            <InvoiceForTmMBeforeHearingPanel
              leadId={leadId}
              phone={phone}
              status={status!}
              selected={selected}
              hasSent={hasSentTmMInvoice}
              onSent={() => setHasSentTmMInvoice(true)}
              onAdvance={() =>
                setSubStep("payment-from-client-for-tmm-before-hearing")
              }
              onSavingChange={onSavingChange}
              onGatedChange={onHaoTmMInvoiceFooterSkipModeChange}
              onProgressActionChange={onHaoTmMInvoiceProgressActionChange}
            />
          ) : subStep === "payment-from-client-for-tmm-before-hearing" ? (
            <PaymentForTmMBeforeHearingPanel
              leadId={leadId}
              selected={selected}
              onAdvance={() => setSubStep("file-tmm-before-hearing")}
              onSavingChange={onSavingChange}
              onGatedChange={onHaoTmMPaymentFooterSkipModeChange}
            />
          ) : subStep === "file-tmm-before-hearing" ? (
            <FileTmMBeforeHearingPanel
              leadId={leadId}
              selected={selected}
              onAdvance={() =>
                setSubStep("inform-client-about-filing-tmm-before-hearing")
              }
              onSavingChange={onSavingChange}
            />
          ) : subStep === "inform-client-about-filing-tmm-before-hearing" ? (
            <InformClientTmMBeforeHearingPanel
              leadId={leadId}
              selected={selected}
              hasSent={hasSentTmMInform}
              onSent={() => {
                setHasSentTmMInform(true);
                // Spec 3D(xi): postponement branch skips hearing-prep substeps and
                // progresses directly to 3D(xxx) Subsequent Hearings.
                setSubStep("subsequent-hearings");
              }}
              onAdvance={() => setSubStep("subsequent-hearings")}
              onSavingChange={onSavingChange}
              resending={resendingTmMInform}
              onResend={() => void handleTmMInformResend()}
            />
          ) : subStep === "payment-from-client-for-hearing" ? (
            <PaymentFromClientHearingPanel
              leadId={leadId}
              selected={selected}
              onAdvance={() =>
                setSubStep("stamp-paper-details-to-vendor-for-hearing")
              }
              onSavingChange={onSavingChange}
              onProgressActionChange={onHaoPaymentProgressActionChange}
            />
          ) : subStep === "stamp-paper-details-to-vendor-for-hearing" ? (
            <StampPaperVendorForHearingPanel
              leadId={leadId}
              selected={selected}
              hasSent={hasSentStampVendor}
              onSent={() => {
                setHasSentStampVendor(true);
                setSubStep("stamp-paper-from-vendor-for-hearing");
              }}
              onAdvance={() =>
                setSubStep("stamp-paper-from-vendor-for-hearing")
              }
              onSavingChange={onSavingChange}
              resending={resendingStampVendor}
              onResend={() => void handleStampVendorResend()}
              onGatedChange={onHaoStampVendorFooterSkipModeChange}
            />
          ) : subStep === "confirmation-from-client-for-hearing" ? (
            <ConfirmationFromClientHearingPanel
              leadId={leadId}
              selected={selected}
              noOfApplicationsForHearing={status.noOfApplicationsForHearing}
              hasSaved={hasSavedConfirmation}
              onSaved={() => setHasSavedConfirmation(true)}
              // YES (Attended) → skip TM-M block, go directly to Payment for Hearing
              onAdvanceProceed={() => setSubStep("payment-from-client-for-hearing")}
              // POSTPONE → enter the TM-M before Hearing flow
              onAdvancePostpone={() =>
                setSubStep("invoice-for-tmm-before-hearing")
              }
              // YES + hearing no longer for every application → 3D(vii)a revised
              // invoice, which is review item 68's route.
              onAdvanceRevisedInvoice={() =>
                setSubStep("revised-invoice-for-hearing")
              }
              // NO → spec pg 87: progress directly to 5B(i) Status details for
              // Refusal or Abandonment. This used to be a no-op ("user navigates
              // via main status nav"), so choosing NO saved and then sat there
              // with no feedback (review item 67).
              onAdvanceAbandon={() => {
                if (onAdvanceToRefusalAbandonment) {
                  onAdvanceToRefusalAbandonment();
                  return;
                }
                toast.info(
                  "Marked as NO. Continue in Final Order → Refused or Abandoned."
                );
              }}
              onSavingChange={onSavingChange}
            />
          ) : subStep === "revised-invoice-for-hearing" ? (
            <RevisedInvoiceForHearingPanel
              leadId={leadId}
              // After the revised invoice, YES marks continue to Payment for Hearing,
              // the same destination the plain YES route uses.
              onAdvance={() => setSubStep("payment-from-client-for-hearing")}
              onSavingChange={onSavingChange}
            />
          ) : subStep === "stamp-paper-from-vendor-for-hearing" ? (
            <StampPaperFromVendorForHearingPanel
              leadId={leadId}
              selected={selected}
              onAdvance={() =>
                setSubStep("documents-from-client-for-hearing")
              }
              onSavingChange={onSavingChange}
              onGatedChange={onHaoStampFromVendorFooterSkipModeChange}
            />
          ) : subStep === "documents-from-client-for-hearing" ? (
            <DocumentsFromClientForHearingPanel
              leadId={leadId}
              selected={selected}
              onAdvance={() =>
                setSubStep("preparation-of-user-affidavit-for-hearing")
              }
              onSavingChange={onSavingChange}
              onProgressActionChange={onHaoDocsFromClientProgressActionChange}
            />
          ) : subStep === "preparation-of-user-affidavit-for-hearing" ? (
            <PreparationOfUserAffidavitForHearingPanel
              leadId={leadId}
              selected={selected}
              onAdvance={() =>
                setSubStep("signature-from-client-for-hearing")
              }
              onSavingChange={onSavingChange}
              onGatedChange={onHaoUserAffidavitFooterSkipModeChange}
            />
          ) : subStep === "signature-from-client-for-hearing" ? (
            <SignatureFromClientForHearingPanel
              leadId={leadId}
              selected={selected}
              onAdvance={() =>
                setSubStep("notarisation-of-documents-for-hearing")
              }
              onSavingChange={onSavingChange}
            />
          ) : subStep === "notarisation-of-documents-for-hearing" ? (
            <NotarisationOfDocumentsForHearingPanel
              leadId={leadId}
              selected={selected}
              onAdvance={() =>
                setSubStep("scanning-of-documents-for-hearing")
              }
              onSavingChange={onSavingChange}
              onGatedChange={onHaoNotarisationFooterSkipModeChange}
            />
          ) : subStep === "scanning-of-documents-for-hearing" ? (
            <ScanningOfDocumentsForHearingPanel
              leadId={leadId}
              selected={selected}
              onAdvance={() => setSubStep("file-hearing-document")}
              onSavingChange={onSavingChange}
            />
          ) : subStep === "file-hearing-document" ? (
            <FileHearingDocumentPanel
              leadId={leadId}
              selected={selected}
              onAdvance={() => setSubStep("note-on-hearing")}
              onSavingChange={onSavingChange}
            />
          ) : subStep === "note-on-hearing" ? (
            <NoteOnHearingPanel
              leadId={leadId}
              selected={selected}
              onAdvance={() => setSubStep("attend-hearing")}
              onSavingChange={onSavingChange}
            />
          ) : subStep === "attend-hearing" ? (
            <AttendHearingPanel
              leadId={leadId}
              selected={selected}
              onAdvance={() =>
                setSubStep("notice-to-client-for-hearing-not-happened")
              }
              onSavingChange={onSavingChange}
            />
          ) : subStep === "notice-to-client-for-hearing-not-happened" ? (
            <NoticeForHearingNotHappenedPanel
              leadId={leadId}
              applications={applications}
              selected={selected}
              hasSent={hasSentHearingNotHappened}
              /**
               * Review item 69: when 3ax) Hearing Status is
               * "Didn't Happen", 3D(xxiii) is the only extra substep and the
               * workflow returns to 3D(xxx) Subsequent Hearings. Marks with any
               * other status skip this step and continue to 3D(xxiv).
               */
              onAdvance={(selectedDidntHappen) =>
                setSubStep(
                  selectedDidntHappen
                    ? "subsequent-hearings"
                    : "inform-client-after-hearing"
                )
              }
              onSavingChange={onSavingChange}
              resending={resendingHearingNotHappened}
              onResend={() => void handleHearingNotHappenedResend()}
              onGatedChange={onHaoHearingNotHappenedFooterSkipModeChange}
            />
          ) : subStep === "inform-client-after-hearing" ? (
            <InformClientAfterHearingPanel
              leadId={leadId}
              selected={selected}
              hasSent={hasSentInformAfterHearing}
              onAdvance={() => setSubStep("upload-documents-after-hearing")}
              onSavingChange={onSavingChange}
              resending={resendingInformAfterHearing}
              onResend={() => void handleInformAfterHearingResend()}
            />
          ) : subStep === "upload-documents-after-hearing" ? (
            <UploadDocumentsAfterHearingPanel
              leadId={leadId}
              selected={selected}
              onAdvance={() => setSubStep("invoice-for-tmm-after-hearing")}
              onSavingChange={onSavingChange}
              onProgressActionChange={onHaoUploadDocsAfterHearingProgressActionChange}
            />
          ) : subStep === "invoice-for-tmm-after-hearing" ? (
            <InvoiceForTmMAfterHearingPanel
              leadId={leadId}
              phone={phone}
              selected={selected}
              hasSent={hasSentTmMAfterInvoice}
              onSent={() => setHasSentTmMAfterInvoice(true)}
              onAdvance={() =>
                setSubStep("payment-from-client-for-tmm-after-hearing")
              }
              onSavingChange={onSavingChange}
              onGatedChange={onHaoTmMAfterInvoiceFooterSkipModeChange}
              onProgressActionChange={onHaoTmMAfterInvoiceProgressActionChange}
            />
          ) : subStep === "payment-from-client-for-tmm-after-hearing" ? (
            <PaymentFromClientForTmMAfterHearingPanel
              leadId={leadId}
              selected={selected}
              onAdvance={() => setSubStep("file-tmm-after-hearing-1")}
              onSavingChange={onSavingChange}
              onGatedChange={onHaoTmMAfterPaymentFooterSkipModeChange}
            />
          ) : subStep === "file-tmm-after-hearing-1" ? (
            <FileTmMAfterHearingPanel
              leadId={leadId}
              selected={selected}
              onAdvance={() =>
                setSubStep("inform-client-about-filing-tmm-after-hearing")
              }
              onSavingChange={onSavingChange}
              onGatedChange={onHaoFileTmMAfterHearingFooterSkipModeChange}
            />
          ) : subStep === "inform-client-about-filing-tmm-after-hearing" ? (
            <InformClientAfterFilingTmMPanel
              leadId={leadId}
              selected={selected}
              hasSent={hasSentInformTmMAfter}
              onAdvance={() => setSubStep("subsequent-hearings")}
              onSavingChange={onSavingChange}
              resending={resendingInformTmMAfter}
              onResend={() => void handleInformTmMAfterResend()}
              onGatedChange={onHaoInformTmMAfterHearingFooterSkipModeChange}
            />
          ) : subStep === "subsequent-hearings" ? (
            <SubsequentHearingsPanel
              leadId={leadId}
              selected={selected}
              hasSent={hasSentSubsequentHearings}
              onAdvance={() => setSubStep("subsequent-hearings-invoice")}
              onSavingChange={onSavingChange}
              resending={resendingSubsequentHearings}
              onResend={() => void handleSubsequentHearingsResend()}
              onGatedChange={onHaoSubsequentHearingsFooterSkipModeChange}
            />
          ) : subStep === "subsequent-hearings-invoice" ? (
            <SubsequentHearingInvoicePanel
              leadId={leadId}
              selected={selected}
              hasSent={hasSentSubsequentInvoice}
              onSent={() => setHasSentSubsequentInvoice(true)}
              onAdvance={() =>
                setSubStep("subsequent-hearings-payment-from-client")
              }
              onSavingChange={onSavingChange}
              onGatedChange={onHaoSubsequentInvoiceFooterSkipModeChange}
            />
          ) : subStep === "subsequent-hearings-payment-from-client" ? (
            <SubsequentHearingPaymentFromClientPanel
              leadId={leadId}
              selected={selected}
              onAdvance={() => setSubStep("subsequent-hearings-attend")}
              onSavingChange={onSavingChange}
              onGatedChange={onHaoSubsequentPaymentFromClientFooterSkipModeChange}
            />
          ) : subStep === "subsequent-hearings-attend" ? (
            <SubsequentHearingAttendPanel
              leadId={leadId}
              selected={selected}
              onAdvance={async () => {
                // Route to postponement only if status is DIDNT_HAPPEN,
                // otherwise jump directly to Order of Hearing.
                try {
                  const status = await getSubsequentHearingStatus(leadId);
                  const row = status.applications.find(
                    (a) => a.tmNo === selected.tmNo
                  );
                  if (row?.hearingStatus === "DIDNT_HAPPEN") {
                    setSubStep("subsequent-hearings-postponement");
                  } else {
                    setSubStep("order-of-hearing-to-client");
                  }
                } catch {
                  setSubStep("order-of-hearing-to-client");
                }
              }}
              onSavingChange={onSavingChange}
              onGatedChange={onHaoSubsequentAttendFooterSkipModeChange}
            />
          ) : subStep === "subsequent-hearings-postponement" ? (
            <SubsequentHearingPostponementPanel
              leadId={leadId}
              selected={selected}
              onAdvance={() => setSubStep("order-of-hearing-to-client")}
              onSavingChange={onSavingChange}
            />
          ) : subStep === "order-of-hearing-to-client" ? (
            <OrderOfHearingToClientPanel
              leadId={leadId}
              selected={selected}
              hasSent={hasSentOrderOfHearing}
              onSent={() => setHasSentOrderOfHearing(true)}
              onAdvance={() => {
                /* Last sub-step in HAO — stay on it. */
              }}
              onSavingChange={onSavingChange}
              resending={resendingOrderOfHearing}
              onResend={() => void handleOrderOfHearingResend()}
              onGatedChange={setOrderOfHearingGated}
            />
          ) : (
            <ComingSoonPanel
              label={
                HAO_SUB_STEPS.find((s) => s.id === subStep)?.label ??
                "Coming soon"
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export type ExaminationReportViewProps = {
  leadId: string | null | undefined;
  onSuccess?: (leadId: string) => void;
  onSavingChange?: (saving: boolean) => void;
  /**
   * 3D(vii) Confirmation from Client for Hearing = NO. Spec pg 87: the workflow
   * must progress directly to 5B(i) Status details for Refusal or Abandonment,
   * which lives in the Final Order section — outside this view. Navigation is
   * owned by the parent (no dashboard view routes directly), so it is passed in.
   */
  onAdvanceToRefusalAbandonment?: () => void;
  /** When true, dashboard footer switches to "Send & Continue" and submits Notice form */
  onNoticeToClientFooterSendModeChange?: (needsSend: boolean) => void;
  /** When true, dashboard footer's "Save & Continue" submits the Notice form (advance only) */
  onNoticeToClientFooterSaveModeChange?: (needsSave: boolean) => void;
  /** When true, dashboard footer switches to "Send & Continue" and submits Inform form */
  onInformClientFooterSendModeChange?: (needsSend: boolean) => void;
  /** When true, dashboard footer's "Save & Continue" submits the Inform form (advance only) */
  onInformClientFooterSaveModeChange?: (needsSave: boolean) => void;
  /** When true, dashboard footer's "Save & Continue" submits the Status-of-Acceptance form (advances sub-step) */
  onStatusOfAcceptanceFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** When true, dashboard footer's "Save & Continue" submits the Status-of-Objection form (advances sub-step) */
  onStatusOfObjectionFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** When true, dashboard footer switches to "Send & Continue" and submits Notice-to-Client-of-Objection form */
  onNoticeToClientObjectionFooterSendModeChange?: (needsSend: boolean) => void;
  /** When true, dashboard footer's "Save & Continue" submits the Notice-to-Client-of-Objection form (advance only) */
  onNoticeToClientObjectionFooterSaveModeChange?: (needsSave: boolean) => void;
  /** When true, dashboard footer switches to "Send & Continue" and submits Inform-Client-of-Objection form */
  onInformClientObjectionFooterSendModeChange?: (needsSend: boolean) => void;
  /** When true, dashboard footer's "Save & Continue" submits the Inform-Client-of-Objection form (advance only) */
  onInformClientObjectionFooterSaveModeChange?: (needsSave: boolean) => void;
  /** When true, dashboard footer's "Save & Continue" submits the 3B(iv) Call-Client form (advances to 3B(v)) */
  onCallClientObjectionFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** When true, dashboard footer's "Save & Continue" submits the Client-Confirmation (call) form (records call + advances) */
  onClientConfirmationObjectionFooterFormModeChange?: (
    needsSubmit: boolean
  ) => void;
  /** When true, dashboard footer's "Save & Continue" submits the Invoice-to-Client form (advances to Payment) */
  onInvoiceObjectionFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** When true, dashboard footer's "Save & Continue" submits the Payment form (advances to Documents) */
  onPaymentObjectionFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** Register the Progress (skip) handler for the Payment sub-step so the dashboard footer can render its Progress button. */
  onPaymentObjectionProgressActionChange?: (
    run: (() => Promise<void>) | null
  ) => void;
  /** When true, dashboard footer's "Save & Continue" submits the Documents form (records + advances) */
  onDocumentsObjectionFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** Register the Progress (skip) handler for the Documents sub-step so the dashboard footer can render its Progress button. */
  onDocumentsObjectionProgressActionChange?: (
    run: (() => Promise<void>) | null
  ) => void;
  /** When true, dashboard footer's "Save & Continue" submits the Filing Reply form */
  onFilingReplyObjectionFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** When true, dashboard footer switches to "Send & Continue" and submits Inform-Client-Reply form */
  onInformClientReplyObjectionFooterSendModeChange?: (
    needsSend: boolean
  ) => void;
  /** When true, dashboard footer's "Save & Continue" submits the Inform-Client-Reply form (advance only) */
  onInformClientReplyObjectionFooterSaveModeChange?: (
    needsSave: boolean
  ) => void;
  /** When true, dashboard footer's "Save & Continue" submits the Status-of-AAO form */
  onStatusOfAaoFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** When true, dashboard footer switches to "Send & Continue" and submits AAO Notice form */
  onNoticeToClientAaoFooterSendModeChange?: (needsSend: boolean) => void;
  /** When true, dashboard footer's "Save & Continue" submits the AAO Notice form (advance only) */
  onNoticeToClientAaoFooterSaveModeChange?: (needsSave: boolean) => void;
  /** When true, dashboard footer switches to "Send & Continue" and submits AAO Inform form */
  onInformClientAaoFooterSendModeChange?: (needsSend: boolean) => void;
  /** When true, dashboard footer's "Save & Continue" submits the AAO Inform form (advance only) */
  onInformClientAaoFooterSaveModeChange?: (needsSave: boolean) => void;
  /** When true, dashboard footer switches to "Send & Continue" and submits Hearing-after-Objection Inform form */
  onInformClientHaoFooterSendModeChange?: (needsSend: boolean) => void;
  /** When true, dashboard footer's "Save & Continue" submits the Hearing-after-Objection Inform form (advance only) */
  onInformClientHaoFooterSaveModeChange?: (needsSave: boolean) => void;
  /** HAO Status Details: footer Save & Continue submits the form */
  onStatusDetailsOfHearingFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** HAO Notice to Client: footer Send & Continue when not sent */
  onNoticeToClientHearingFooterSendModeChange?: (needsSend: boolean) => void;
  /** HAO Notice to Client: footer Save & Continue when sent */
  onNoticeToClientHearingFooterSaveModeChange?: (needsSave: boolean) => void;
  /** HAO Inform Client about Hearing: footer Send & Continue when not sent */
  onInformClientAboutHearingFooterSendModeChange?: (needsSend: boolean) => void;
  /** HAO Inform Client about Hearing: footer Save & Continue when sent */
  onInformClientAboutHearingFooterSaveModeChange?: (needsSave: boolean) => void;
  /** HAO Call Client for Hearing: footer Save & Continue submits the form */
  onCallClientHearingFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** HAO Confirmation from Client for Hearing: footer Save & Continue submits the form */
  onConfirmationFromClientHearingFooterFormModeChange?: (
    needsSubmit: boolean
  ) => void;
  /** 3D(vii)a Revised Invoice for Hearing 1 — footer Save & Continue submits (item 68). */
  onRevisedInvoiceForHearingFooterFormModeChange?: (
    needsSubmit: boolean
  ) => void;
  /** HAO Invoice for Hearing: footer Save & Continue submits the form */
  onInvoiceForHearingFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** HAO Invoice for Hearing: "sent" status — drives Send & Continue vs Save & Continue label */
  onInvoiceForHearingSentStatusChange?: (sent: boolean) => void;
  onInvoiceForTmMBeforeHearingSentStatusChange?: (sent: boolean) => void;
  /** HAO Invoice for TM-M before Hearing */
  onInvoiceForTmMBeforeHearingFooterFormModeChange?: (
    needsSubmit: boolean
  ) => void;
  /** HAO Invoice for TM-M before Hearing — Skip & Continue when Confirmation != POSTPONE */
  onInvoiceForTmMBeforeHearingFooterSkipModeChange?: (
    needsSkip: boolean
  ) => void;
  /** HAO Invoice for TM-M before Hearing — Progress (skip) handler */
  onInvoiceForTmMBeforeHearingProgressActionChange?: (
    run: (() => Promise<void>) | null
  ) => void;
  /** HAO Payment from Client for TM-M before Hearing */
  onPaymentForTmMBeforeHearingFooterFormModeChange?: (
    needsSubmit: boolean
  ) => void;
  /** HAO Payment from Client for TM-M before Hearing — Skip & Continue when Confirmation != POSTPONE */
  onPaymentForTmMBeforeHearingFooterSkipModeChange?: (
    needsSkip: boolean
  ) => void;
  /** HAO File TM-M before Hearing */
  onFileTmMBeforeHearingFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** HAO Inform Client about Filing TM-M before Hearing — Send mode */
  onInformClientTmMBeforeHearingFooterSendModeChange?: (
    needsSend: boolean
  ) => void;
  /** HAO Inform Client about Filing TM-M before Hearing — Save mode */
  onInformClientTmMBeforeHearingFooterSaveModeChange?: (
    needsSave: boolean
  ) => void;
  /** HAO Payment from Client for Hearing */
  onPaymentFromClientHearingFooterFormModeChange?: (
    needsSubmit: boolean
  ) => void;
  /** HAO Payment from Client for Hearing: Progress (skip) handler */
  onPaymentFromClientHearingProgressActionChange?: (
    run: (() => Promise<void>) | null
  ) => void;
  /** HAO Stamp Paper details to Vendor — Send & Continue */
  onStampPaperVendorHearingFooterSendModeChange?: (needsSend: boolean) => void;
  /** HAO Stamp Paper details to Vendor — Save & Continue (already sent) */
  onStampPaperVendorHearingFooterSaveModeChange?: (needsSave: boolean) => void;
  /** HAO Stamp Paper details to Vendor — Skip & Continue when User Date != "Proposed to be used" */
  onStampPaperVendorHearingFooterSkipModeChange?: (needsSkip: boolean) => void;
  /** HAO Stamp Paper from Vendor — Save & Continue submits the form */
  onStampPaperFromVendorHearingFooterFormModeChange?: (
    needsSubmit: boolean
  ) => void;
  /** HAO Stamp Paper from Vendor — Skip & Continue when User Date != "Proposed to be used" */
  onStampPaperFromVendorHearingFooterSkipModeChange?: (
    needsSkip: boolean
  ) => void;
  /** HAO Documents from Client for hearing — Save & Continue submits the form */
  onDocumentsFromClientHearingFooterFormModeChange?: (
    needsSubmit: boolean
  ) => void;
  /** HAO Documents from Client for hearing — Progress (skip) handler */
  onDocumentsFromClientHearingProgressActionChange?: (
    run: (() => Promise<void>) | null
  ) => void;
  /** HAO Preparation of User Affidavit for hearing — Save & Continue submits the form */
  onUserAffidavitForHearingFooterFormModeChange?: (
    needsSubmit: boolean
  ) => void;
  /** HAO Preparation of User Affidavit for hearing — Skip & Continue when User Date != "Proposed to be used" */
  onUserAffidavitForHearingFooterSkipModeChange?: (
    needsSkip: boolean
  ) => void;
  /** HAO Signature from Client for hearing — Save & Continue submits the form */
  onSignatureFromClientHearingFooterFormModeChange?: (
    needsSubmit: boolean
  ) => void;
  /** HAO Notarisation of Documents for hearing — Save & Continue submits the form */
  onNotarisationOfDocumentsHearingFooterFormModeChange?: (
    needsSubmit: boolean
  ) => void;
  /** HAO Notarisation of Documents for hearing — Skip & Continue when User Date != "Proposed to be used" */
  onNotarisationOfDocumentsHearingFooterSkipModeChange?: (
    needsSkip: boolean
  ) => void;
  /** HAO Scanning of Documents for hearing — Save & Continue submits the form */
  onScanningOfDocumentsHearingFooterFormModeChange?: (
    needsSubmit: boolean
  ) => void;
  /** HAO File Hearing Document — Save & Continue submits the form */
  onFileHearingDocumentFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** HAO Note on Hearing — Save & Continue submits the form */
  onNoteOnHearingFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** HAO Attend Hearing — Save & Continue submits the form */
  onAttendHearingFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** HAO Notice for hearing not happened — Send & Continue when not sent */
  onHearingNotHappenedFooterSendModeChange?: (needsSend: boolean) => void;
  /** HAO Notice for hearing not happened — Save & Continue when sent */
  onHearingNotHappenedFooterSaveModeChange?: (needsSave: boolean) => void;
  /** HAO Notice for hearing not happened — Skip & Continue when no marks need a postponement notice */
  onHearingNotHappenedFooterSkipModeChange?: (needsSkip: boolean) => void;
  /** HAO Inform Client after hearing — Send & Continue when not sent */
  onInformAfterHearingFooterSendModeChange?: (needsSend: boolean) => void;
  /** HAO Inform Client after hearing — Save & Continue when sent */
  onInformAfterHearingFooterSaveModeChange?: (needsSave: boolean) => void;
  /** HAO Upload Documents after Hearing — Save & Continue submits the form */
  onUploadDocsAfterHearingFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** HAO Upload Documents after Hearing — Progress (skip) handler */
  onUploadDocsAfterHearingProgressActionChange?: (
    run: (() => Promise<void>) | null
  ) => void;
  /** HAO Invoice for TM-M after Hearing — Save & Continue submits the form */
  onInvoiceTmMAfterHearingFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** HAO Invoice for TM-M after Hearing — Skip & Continue when hearing direction != "File TM-M" */
  onInvoiceTmMAfterHearingFooterSkipModeChange?: (needsSkip: boolean) => void;
  /** HAO Invoice for TM-M after Hearing — Progress (skip) handler */
  onInvoiceTmMAfterHearingProgressActionChange?: (
    run: (() => Promise<void>) | null
  ) => void;
  /** HAO Payment for TM-M after Hearing — Save & Continue submits the form */
  onPaymentTmMAfterHearingFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** HAO Payment for TM-M after Hearing — Skip & Continue when hearing direction != "File TM-M" */
  onPaymentTmMAfterHearingFooterSkipModeChange?: (needsSkip: boolean) => void;
  /** HAO File TM-M after Hearing 1 — Save & Continue submits the form */
  onFileTmMAfterHearingFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** HAO File TM-M after Hearing 1 — Skip & Continue when hearing direction != "File TM-M" */
  onFileTmMAfterHearingFooterSkipModeChange?: (needsSkip: boolean) => void;
  /** HAO Inform Client about Filing TM-M after Hearing — Send/Save modes */
  onInformTmMAfterHearingFooterSendModeChange?: (needsSend: boolean) => void;
  onInformTmMAfterHearingFooterSaveModeChange?: (needsSave: boolean) => void;
  /** HAO Inform Client about Filing TM-M after Hearing — Skip & Continue when no TM-M filed */
  onInformTmMAfterHearingFooterSkipModeChange?: (needsSkip: boolean) => void;
  /** HAO Subsequent Hearings — Send/Save modes */
  onSubsequentHearingsFooterSendModeChange?: (needsSend: boolean) => void;
  onSubsequentHearingsFooterSaveModeChange?: (needsSave: boolean) => void;
  /** HAO Subsequent Hearings — Skip & Continue when not active for this application */
  onSubsequentHearingsFooterSkipModeChange?: (needsSkip: boolean) => void;
  /** HAO Subsequent Hearing Invoice — Skip & Continue when not active for this application */
  onSubsequentHearingInvoiceFooterSkipModeChange?: (needsSkip: boolean) => void;
  /** HAO Subsequent Hearing Invoice (active) — Send/Save & Continue submits the form */
  onSubsequentHearingInvoiceFooterFormModeChange?: (needsSubmit: boolean) => void;
  /** HAO Subsequent Hearing Invoice — "sent" status; flips footer label between Send & Save */
  onSubsequentHearingInvoiceSentStatusChange?: (sent: boolean) => void;
  /** HAO Payment from Client for Subsequent Hearing — Save & Continue submits the form */
  onSubsequentHearingPaymentFromClientFooterFormModeChange?: (
    needsSubmit: boolean
  ) => void;
  /** HAO Payment from Client for Subsequent Hearing — Skip & Continue when invoice not active for this application */
  onSubsequentHearingPaymentFromClientFooterSkipModeChange?: (
    needsSkip: boolean
  ) => void;
  /** HAO Attend Subsequent Hearing — Save & Continue submits the form */
  onSubsequentHearingAttendFooterFormModeChange?: (
    needsSubmit: boolean
  ) => void;
  /** HAO Attend Subsequent Hearing — Skip & Continue when Subsequent Hearings not active for this application */
  onSubsequentHearingAttendFooterSkipModeChange?: (
    needsSkip: boolean
  ) => void;
  /** HAO Notice for Subsequent Hearing Postponed — Save & Continue submits the form */
  onSubsequentHearingPostponementFooterFormModeChange?: (
    needsSubmit: boolean
  ) => void;
  /** HAO Order of Hearing to Client — Send/Save modes */
  onOrderOfHearingFooterSendModeChange?: (needsSend: boolean) => void;
  onOrderOfHearingFooterSaveModeChange?: (needsSave: boolean) => void;
};

export default function ExaminationReportView({
  leadId,
  onSuccess,
  onSavingChange,
  onAdvanceToRefusalAbandonment,
  onNoticeToClientFooterSendModeChange,
  onNoticeToClientFooterSaveModeChange,
  onInformClientFooterSendModeChange,
  onInformClientFooterSaveModeChange,
  onStatusOfAcceptanceFooterFormModeChange,
  onStatusOfObjectionFooterFormModeChange,
  onNoticeToClientObjectionFooterSendModeChange,
  onNoticeToClientObjectionFooterSaveModeChange,
  onInformClientObjectionFooterSendModeChange,
  onInformClientObjectionFooterSaveModeChange,
  onCallClientObjectionFooterFormModeChange,
  onClientConfirmationObjectionFooterFormModeChange,
  onInvoiceObjectionFooterFormModeChange,
  onPaymentObjectionFooterFormModeChange,
  onPaymentObjectionProgressActionChange,
  onDocumentsObjectionFooterFormModeChange,
  onDocumentsObjectionProgressActionChange,
  onFilingReplyObjectionFooterFormModeChange,
  onInformClientReplyObjectionFooterSendModeChange,
  onInformClientReplyObjectionFooterSaveModeChange,
  onStatusOfAaoFooterFormModeChange,
  onNoticeToClientAaoFooterSendModeChange,
  onNoticeToClientAaoFooterSaveModeChange,
  onInformClientAaoFooterSendModeChange,
  onInformClientAaoFooterSaveModeChange,
  onInformClientHaoFooterSendModeChange,
  onInformClientHaoFooterSaveModeChange,
  onStatusDetailsOfHearingFooterFormModeChange,
  onNoticeToClientHearingFooterSendModeChange,
  onNoticeToClientHearingFooterSaveModeChange,
  onInformClientAboutHearingFooterSendModeChange,
  onInformClientAboutHearingFooterSaveModeChange,
  onCallClientHearingFooterFormModeChange,
  onConfirmationFromClientHearingFooterFormModeChange,
  onRevisedInvoiceForHearingFooterFormModeChange,
  onInvoiceForHearingFooterFormModeChange,
  onInvoiceForHearingSentStatusChange,
  onInvoiceForTmMBeforeHearingSentStatusChange,
  onInvoiceForTmMBeforeHearingFooterFormModeChange,
  onInvoiceForTmMBeforeHearingFooterSkipModeChange,
  onInvoiceForTmMBeforeHearingProgressActionChange,
  onPaymentForTmMBeforeHearingFooterFormModeChange,
  onPaymentForTmMBeforeHearingFooterSkipModeChange,
  onFileTmMBeforeHearingFooterFormModeChange,
  onInformClientTmMBeforeHearingFooterSendModeChange,
  onInformClientTmMBeforeHearingFooterSaveModeChange,
  onPaymentFromClientHearingFooterFormModeChange,
  onPaymentFromClientHearingProgressActionChange,
  onStampPaperVendorHearingFooterSendModeChange,
  onStampPaperVendorHearingFooterSaveModeChange,
  onStampPaperVendorHearingFooterSkipModeChange,
  onStampPaperFromVendorHearingFooterFormModeChange,
  onStampPaperFromVendorHearingFooterSkipModeChange,
  onDocumentsFromClientHearingFooterFormModeChange,
  onDocumentsFromClientHearingProgressActionChange,
  onUserAffidavitForHearingFooterFormModeChange,
  onUserAffidavitForHearingFooterSkipModeChange,
  onSignatureFromClientHearingFooterFormModeChange,
  onNotarisationOfDocumentsHearingFooterFormModeChange,
  onNotarisationOfDocumentsHearingFooterSkipModeChange,
  onScanningOfDocumentsHearingFooterFormModeChange,
  onFileHearingDocumentFooterFormModeChange,
  onNoteOnHearingFooterFormModeChange,
  onAttendHearingFooterFormModeChange,
  onHearingNotHappenedFooterSendModeChange,
  onHearingNotHappenedFooterSaveModeChange,
  onHearingNotHappenedFooterSkipModeChange,
  onInformAfterHearingFooterSendModeChange,
  onInformAfterHearingFooterSaveModeChange,
  onUploadDocsAfterHearingFooterFormModeChange,
  onUploadDocsAfterHearingProgressActionChange,
  onInvoiceTmMAfterHearingFooterFormModeChange,
  onInvoiceTmMAfterHearingFooterSkipModeChange,
  onInvoiceTmMAfterHearingProgressActionChange,
  onPaymentTmMAfterHearingFooterFormModeChange,
  onPaymentTmMAfterHearingFooterSkipModeChange,
  onFileTmMAfterHearingFooterFormModeChange,
  onFileTmMAfterHearingFooterSkipModeChange,
  onInformTmMAfterHearingFooterSendModeChange,
  onInformTmMAfterHearingFooterSaveModeChange,
  onInformTmMAfterHearingFooterSkipModeChange,
  onSubsequentHearingsFooterSendModeChange,
  onSubsequentHearingsFooterSaveModeChange,
  onSubsequentHearingsFooterSkipModeChange,
  onSubsequentHearingInvoiceFooterSkipModeChange,
  onSubsequentHearingInvoiceFooterFormModeChange,
  onSubsequentHearingInvoiceSentStatusChange,
  onSubsequentHearingPaymentFromClientFooterFormModeChange,
  onSubsequentHearingPaymentFromClientFooterSkipModeChange,
  onSubsequentHearingAttendFooterFormModeChange,
  onSubsequentHearingAttendFooterSkipModeChange,
  onSubsequentHearingPostponementFooterFormModeChange,
  onOrderOfHearingFooterSendModeChange,
  onOrderOfHearingFooterSaveModeChange,
}: ExaminationReportViewProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const validSubStatuses: ExamSubStatus[] = useMemo(
    () => SUB_STATUS_TABS.map((t) => t.id),
    []
  );
  const urlSubStatus = searchParams.get("sub") as ExamSubStatus | null;
  const initialSubStatus =
    urlSubStatus && validSubStatuses.includes(urlSubStatus)
      ? urlSubStatus
      : "accepted";
  const [subStatus, setSubStatusState] =
    useState<ExamSubStatus>(initialSubStatus);
  const setSubStatus = (next: ExamSubStatus) => {
    setSubStatusState(next);
    const params = new URLSearchParams(searchParams);
    params.set("sub", next);
    // Changing sub-status invalidates tm/substep
    params.delete("tm");
    params.delete("substep");
    setSearchParams(params, { replace: true });
  };
  const [phone, setPhone] = useState("");
  const [loadingLead, setLoadingLead] = useState(!!leadId);

  // Reset Accepted-branch footer modes when leaving Accepted.
  // Deps intentionally only include `subStatus` — parent setters are stable, but
  // some are wrapped in inline arrows, which would otherwise cause this effect to
  // re-run on every parent render and trigger "Maximum update depth exceeded".
  useLayoutEffect(() => {
    if (subStatus !== "accepted") {
      onNoticeToClientFooterSendModeChange?.(false);
      onNoticeToClientFooterSaveModeChange?.(false);
      onInformClientFooterSendModeChange?.(false);
      onInformClientFooterSaveModeChange?.(false);
      onStatusOfAcceptanceFooterFormModeChange?.(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subStatus]);

  // Reset Objected-branch footer modes when leaving Objected. See note above re: deps.
  useLayoutEffect(() => {
    if (subStatus !== "objected") {
      onStatusOfObjectionFooterFormModeChange?.(false);
      onNoticeToClientObjectionFooterSendModeChange?.(false);
      onNoticeToClientObjectionFooterSaveModeChange?.(false);
      onInformClientObjectionFooterSendModeChange?.(false);
      onInformClientObjectionFooterSaveModeChange?.(false);
      onCallClientObjectionFooterFormModeChange?.(false);
      onClientConfirmationObjectionFooterFormModeChange?.(false);
      onInvoiceObjectionFooterFormModeChange?.(false);
      onPaymentObjectionFooterFormModeChange?.(false);
      onPaymentObjectionProgressActionChange?.(null);
      onDocumentsObjectionFooterFormModeChange?.(false);
      onDocumentsObjectionProgressActionChange?.(null);
      onFilingReplyObjectionFooterFormModeChange?.(false);
      onInformClientReplyObjectionFooterSendModeChange?.(false);
      onInformClientReplyObjectionFooterSaveModeChange?.(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subStatus]);

  // Reset AAO-branch footer modes when leaving AAO. See note above re: deps.
  useLayoutEffect(() => {
    if (subStatus !== "accepted-after-objection") {
      onStatusOfAaoFooterFormModeChange?.(false);
      onNoticeToClientAaoFooterSendModeChange?.(false);
      onNoticeToClientAaoFooterSaveModeChange?.(false);
      onInformClientAaoFooterSendModeChange?.(false);
      onInformClientAaoFooterSaveModeChange?.(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subStatus]);

  // Reset Hearing-after-Objection footer modes when leaving the tab. See note above re: deps.
  useLayoutEffect(() => {
    if (subStatus !== "hearing-after-objection") {
      onInformClientHaoFooterSendModeChange?.(false);
      onInformClientHaoFooterSaveModeChange?.(false);
      onStatusDetailsOfHearingFooterFormModeChange?.(false);
      onNoticeToClientHearingFooterSendModeChange?.(false);
      onNoticeToClientHearingFooterSaveModeChange?.(false);
      onInformClientAboutHearingFooterSendModeChange?.(false);
      onInformClientAboutHearingFooterSaveModeChange?.(false);
      onCallClientHearingFooterFormModeChange?.(false);
      onConfirmationFromClientHearingFooterFormModeChange?.(false);
      onRevisedInvoiceForHearingFooterFormModeChange?.(false);
      onInvoiceForHearingFooterFormModeChange?.(false);
      onInvoiceForHearingSentStatusChange?.(false);
      onInvoiceForTmMBeforeHearingSentStatusChange?.(false);
      onInvoiceForTmMBeforeHearingFooterFormModeChange?.(false);
      onInvoiceForTmMBeforeHearingFooterSkipModeChange?.(false);
      onInvoiceForTmMBeforeHearingProgressActionChange?.(null);
      onPaymentForTmMBeforeHearingFooterFormModeChange?.(false);
      onPaymentForTmMBeforeHearingFooterSkipModeChange?.(false);
      onFileTmMBeforeHearingFooterFormModeChange?.(false);
      onInformClientTmMBeforeHearingFooterSendModeChange?.(false);
      onInformClientTmMBeforeHearingFooterSaveModeChange?.(false);
      onPaymentFromClientHearingFooterFormModeChange?.(false);
      onPaymentFromClientHearingProgressActionChange?.(null);
      onStampPaperVendorHearingFooterSendModeChange?.(false);
      onStampPaperVendorHearingFooterSaveModeChange?.(false);
      onStampPaperVendorHearingFooterSkipModeChange?.(false);
      onStampPaperFromVendorHearingFooterFormModeChange?.(false);
      onStampPaperFromVendorHearingFooterSkipModeChange?.(false);
      onDocumentsFromClientHearingFooterFormModeChange?.(false);
      onDocumentsFromClientHearingProgressActionChange?.(null);
      onUserAffidavitForHearingFooterFormModeChange?.(false);
      onUserAffidavitForHearingFooterSkipModeChange?.(false);
      onSignatureFromClientHearingFooterFormModeChange?.(false);
      onNotarisationOfDocumentsHearingFooterFormModeChange?.(false);
      onNotarisationOfDocumentsHearingFooterSkipModeChange?.(false);
      onScanningOfDocumentsHearingFooterFormModeChange?.(false);
      onFileHearingDocumentFooterFormModeChange?.(false);
      onNoteOnHearingFooterFormModeChange?.(false);
      onAttendHearingFooterFormModeChange?.(false);
      onHearingNotHappenedFooterSendModeChange?.(false);
      onHearingNotHappenedFooterSaveModeChange?.(false);
      onHearingNotHappenedFooterSkipModeChange?.(false);
      onInformAfterHearingFooterSendModeChange?.(false);
      onInformAfterHearingFooterSaveModeChange?.(false);
      onUploadDocsAfterHearingFooterFormModeChange?.(false);
      onUploadDocsAfterHearingProgressActionChange?.(null);
      onInvoiceTmMAfterHearingFooterFormModeChange?.(false);
      onInvoiceTmMAfterHearingFooterSkipModeChange?.(false);
      onInvoiceTmMAfterHearingProgressActionChange?.(null);
      onPaymentTmMAfterHearingFooterFormModeChange?.(false);
      onPaymentTmMAfterHearingFooterSkipModeChange?.(false);
      onFileTmMAfterHearingFooterFormModeChange?.(false);
      onFileTmMAfterHearingFooterSkipModeChange?.(false);
      onInformTmMAfterHearingFooterSendModeChange?.(false);
      onInformTmMAfterHearingFooterSaveModeChange?.(false);
      onInformTmMAfterHearingFooterSkipModeChange?.(false);
      onSubsequentHearingsFooterSendModeChange?.(false);
      onSubsequentHearingsFooterSaveModeChange?.(false);
      onSubsequentHearingsFooterSkipModeChange?.(false);
      onSubsequentHearingInvoiceFooterSkipModeChange?.(false);
      onSubsequentHearingInvoiceFooterFormModeChange?.(false);
      onSubsequentHearingInvoiceSentStatusChange?.(false);
      onSubsequentHearingPaymentFromClientFooterSkipModeChange?.(false);
      onOrderOfHearingFooterSendModeChange?.(false);
      onOrderOfHearingFooterSaveModeChange?.(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subStatus]);

  useEffect(() => {
    if (!leadId) {
      setLoadingLead(false);
      setPhone("");
      return;
    }
    let cancelled = false;
    setLoadingLead(true);
    getLeadDetails(leadId)
      .then((res) => {
        if (!cancelled) setPhone(res.lead.phone ?? "");
      })
      .catch(() => {
        /* silent — child panels surface their own errors */
      })
      .finally(() => {
        if (!cancelled) setLoadingLead(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-5 w-full max-w-[1085px]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Select a lead to view Examination Report.
        </p>
      </div>
    );
  }

  if (loadingLead) {
    return (
      <div
        className="flex flex-col gap-5 w-full max-w-[1085px]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading…</p>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-5 w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Sub-status tabs */}
      <div className="flex flex-row flex-wrap gap-3">
        {SUB_STATUS_TABS.map((tab) => {
          const isActive = subStatus === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setSubStatus(tab.id)}
              className={`flex items-center gap-2.5 w-[230px] h-9 px-2.5 py-2 rounded-[6px] border transition-colors ${
                isActive
                  ? "bg-[#222222] text-white border-transparent"
                  : "bg-[#FCFCFC] text-[#6D6D6D] border-[#EEEEEE]"
              }`}
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: "12px",
                fontWeight: isActive ? 500 : 400,
                lineHeight: "20px",
              }}
            >
              <RadioCircle active={isActive} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Branch content */}
      {subStatus === "accepted" ? (
        <AcceptedBranch
          leadId={leadId}
          phone={phone}
          active={subStatus === "accepted"}
          onSuccess={onSuccess}
          onSavingChange={onSavingChange}
          onNoticeFooterSendModeChange={onNoticeToClientFooterSendModeChange}
          onNoticeFooterSaveModeChange={onNoticeToClientFooterSaveModeChange}
          onInformFooterSendModeChange={onInformClientFooterSendModeChange}
          onInformFooterSaveModeChange={onInformClientFooterSaveModeChange}
          onSoaFooterFormModeChange={onStatusOfAcceptanceFooterFormModeChange}
        />
      ) : subStatus === "objected" ? (
        <ObjectedBranch
          leadId={leadId}
          active={subStatus === "objected"}
          onSavingChange={onSavingChange}
          onAdvanceToRefusalAbandonment={onAdvanceToRefusalAbandonment}
          onSooFooterFormModeChange={onStatusOfObjectionFooterFormModeChange}
          onNtcoFooterSendModeChange={onNoticeToClientObjectionFooterSendModeChange}
          onNtcoFooterSaveModeChange={onNoticeToClientObjectionFooterSaveModeChange}
          onIncoFooterSendModeChange={onInformClientObjectionFooterSendModeChange}
          onIncoFooterSaveModeChange={onInformClientObjectionFooterSaveModeChange}
          onCcroFooterFormModeChange={
            onCallClientObjectionFooterFormModeChange
          }
          onCcoFooterFormModeChange={
            onClientConfirmationObjectionFooterFormModeChange
          }
          onInvFooterFormModeChange={onInvoiceObjectionFooterFormModeChange}
          onPayFooterFormModeChange={onPaymentObjectionFooterFormModeChange}
          onPayProgressActionChange={onPaymentObjectionProgressActionChange}
          onDocFooterFormModeChange={onDocumentsObjectionFooterFormModeChange}
          onDocProgressActionChange={onDocumentsObjectionProgressActionChange}
          onFroFooterFormModeChange={onFilingReplyObjectionFooterFormModeChange}
          onIcrFooterSendModeChange={
            onInformClientReplyObjectionFooterSendModeChange
          }
          onIcrFooterSaveModeChange={
            onInformClientReplyObjectionFooterSaveModeChange
          }
          phone={phone}
        />
      ) : subStatus === "accepted-after-objection" ? (
        <AcceptedAfterObjectionBranch
          leadId={leadId}
          active={subStatus === "accepted-after-objection"}
          onSavingChange={onSavingChange}
          onAaoStatusFooterFormModeChange={onStatusOfAaoFooterFormModeChange}
          onAaoNoticeFooterSendModeChange={
            onNoticeToClientAaoFooterSendModeChange
          }
          onAaoNoticeFooterSaveModeChange={
            onNoticeToClientAaoFooterSaveModeChange
          }
          onAaoInformFooterSendModeChange={
            onInformClientAaoFooterSendModeChange
          }
          onAaoInformFooterSaveModeChange={
            onInformClientAaoFooterSaveModeChange
          }
        />
      ) : (
        <HearingAfterObjectionBranch
          onAdvanceToRefusalAbandonment={onAdvanceToRefusalAbandonment}
          leadId={leadId}
          phone={phone}
          active={subStatus === "hearing-after-objection"}
          onSavingChange={onSavingChange}
          onHaoInformFooterSendModeChange={
            onInformClientHaoFooterSendModeChange
          }
          onHaoInformFooterSaveModeChange={
            onInformClientHaoFooterSaveModeChange
          }
          onHaoStatusFooterFormModeChange={
            onStatusDetailsOfHearingFooterFormModeChange
          }
          onHaoNoticeFooterSendModeChange={
            onNoticeToClientHearingFooterSendModeChange
          }
          onHaoNoticeFooterSaveModeChange={
            onNoticeToClientHearingFooterSaveModeChange
          }
          onHaoInformHearingFooterSendModeChange={
            onInformClientAboutHearingFooterSendModeChange
          }
          onHaoInformHearingFooterSaveModeChange={
            onInformClientAboutHearingFooterSaveModeChange
          }
          onHaoCallClientFooterFormModeChange={
            onCallClientHearingFooterFormModeChange
          }
          onHaoConfirmationFooterFormModeChange={
            onConfirmationFromClientHearingFooterFormModeChange
          }
          onHaoRevisedInvoiceFooterFormModeChange={
            onRevisedInvoiceForHearingFooterFormModeChange
          }
          onHaoInvoiceFooterFormModeChange={
            onInvoiceForHearingFooterFormModeChange
          }
          onHaoInvoiceSentStatusChange={onInvoiceForHearingSentStatusChange}
          onHaoTmMInvoiceSentStatusChange={
            onInvoiceForTmMBeforeHearingSentStatusChange
          }
          onHaoTmMInvoiceFooterFormModeChange={
            onInvoiceForTmMBeforeHearingFooterFormModeChange
          }
          onHaoTmMInvoiceFooterSkipModeChange={
            onInvoiceForTmMBeforeHearingFooterSkipModeChange
          }
          onHaoTmMInvoiceProgressActionChange={
            onInvoiceForTmMBeforeHearingProgressActionChange
          }
          onHaoTmMPaymentFooterFormModeChange={
            onPaymentForTmMBeforeHearingFooterFormModeChange
          }
          onHaoTmMPaymentFooterSkipModeChange={
            onPaymentForTmMBeforeHearingFooterSkipModeChange
          }
          onHaoTmMFileFooterFormModeChange={
            onFileTmMBeforeHearingFooterFormModeChange
          }
          onHaoTmMInformFooterSendModeChange={
            onInformClientTmMBeforeHearingFooterSendModeChange
          }
          onHaoTmMInformFooterSaveModeChange={
            onInformClientTmMBeforeHearingFooterSaveModeChange
          }
          onHaoPaymentProgressActionChange={
            onPaymentFromClientHearingProgressActionChange
          }
          onHaoPaymentFooterFormModeChange={
            onPaymentFromClientHearingFooterFormModeChange
          }
          onHaoStampVendorFooterSendModeChange={
            onStampPaperVendorHearingFooterSendModeChange
          }
          onHaoStampVendorFooterSaveModeChange={
            onStampPaperVendorHearingFooterSaveModeChange
          }
          onHaoStampVendorFooterSkipModeChange={
            onStampPaperVendorHearingFooterSkipModeChange
          }
          onHaoStampFromVendorFooterFormModeChange={
            onStampPaperFromVendorHearingFooterFormModeChange
          }
          onHaoStampFromVendorFooterSkipModeChange={
            onStampPaperFromVendorHearingFooterSkipModeChange
          }
          onHaoDocsFromClientFooterFormModeChange={
            onDocumentsFromClientHearingFooterFormModeChange
          }
          onHaoDocsFromClientProgressActionChange={
            onDocumentsFromClientHearingProgressActionChange
          }
          onHaoUserAffidavitFooterFormModeChange={
            onUserAffidavitForHearingFooterFormModeChange
          }
          onHaoUserAffidavitFooterSkipModeChange={
            onUserAffidavitForHearingFooterSkipModeChange
          }
          onHaoSignatureFooterFormModeChange={
            onSignatureFromClientHearingFooterFormModeChange
          }
          onHaoNotarisationFooterFormModeChange={
            onNotarisationOfDocumentsHearingFooterFormModeChange
          }
          onHaoNotarisationFooterSkipModeChange={
            onNotarisationOfDocumentsHearingFooterSkipModeChange
          }
          onHaoScanningFooterFormModeChange={
            onScanningOfDocumentsHearingFooterFormModeChange
          }
          onHaoFileHearingDocFooterFormModeChange={
            onFileHearingDocumentFooterFormModeChange
          }
          onHaoNoteOnHearingFooterFormModeChange={
            onNoteOnHearingFooterFormModeChange
          }
          onHaoAttendHearingFooterFormModeChange={
            onAttendHearingFooterFormModeChange
          }
          onHaoHearingNotHappenedFooterSendModeChange={
            onHearingNotHappenedFooterSendModeChange
          }
          onHaoHearingNotHappenedFooterSaveModeChange={
            onHearingNotHappenedFooterSaveModeChange
          }
          onHaoHearingNotHappenedFooterSkipModeChange={
            onHearingNotHappenedFooterSkipModeChange
          }
          onHaoInformAfterHearingFooterSendModeChange={
            onInformAfterHearingFooterSendModeChange
          }
          onHaoInformAfterHearingFooterSaveModeChange={
            onInformAfterHearingFooterSaveModeChange
          }
          onHaoUploadDocsAfterHearingFooterFormModeChange={
            onUploadDocsAfterHearingFooterFormModeChange
          }
          onHaoUploadDocsAfterHearingProgressActionChange={
            onUploadDocsAfterHearingProgressActionChange
          }
          onHaoTmMAfterInvoiceFooterFormModeChange={
            onInvoiceTmMAfterHearingFooterFormModeChange
          }
          onHaoTmMAfterInvoiceFooterSkipModeChange={
            onInvoiceTmMAfterHearingFooterSkipModeChange
          }
          onHaoTmMAfterInvoiceProgressActionChange={
            onInvoiceTmMAfterHearingProgressActionChange
          }
          onHaoTmMAfterPaymentFooterFormModeChange={
            onPaymentTmMAfterHearingFooterFormModeChange
          }
          onHaoTmMAfterPaymentFooterSkipModeChange={
            onPaymentTmMAfterHearingFooterSkipModeChange
          }
          onHaoFileTmMAfterHearingFooterFormModeChange={
            onFileTmMAfterHearingFooterFormModeChange
          }
          onHaoFileTmMAfterHearingFooterSkipModeChange={
            onFileTmMAfterHearingFooterSkipModeChange
          }
          onHaoInformTmMAfterHearingFooterSendModeChange={
            onInformTmMAfterHearingFooterSendModeChange
          }
          onHaoInformTmMAfterHearingFooterSaveModeChange={
            onInformTmMAfterHearingFooterSaveModeChange
          }
          onHaoInformTmMAfterHearingFooterSkipModeChange={
            onInformTmMAfterHearingFooterSkipModeChange
          }
          onHaoSubsequentHearingsFooterSendModeChange={
            onSubsequentHearingsFooterSendModeChange
          }
          onHaoSubsequentHearingsFooterSaveModeChange={
            onSubsequentHearingsFooterSaveModeChange
          }
          onHaoSubsequentHearingsFooterSkipModeChange={
            onSubsequentHearingsFooterSkipModeChange
          }
          onHaoSubsequentInvoiceFooterSkipModeChange={
            onSubsequentHearingInvoiceFooterSkipModeChange
          }
          onHaoSubsequentInvoiceFooterFormModeChange={
            onSubsequentHearingInvoiceFooterFormModeChange
          }
          onHaoSubsequentInvoiceSentStatusChange={
            onSubsequentHearingInvoiceSentStatusChange
          }
          onHaoSubsequentPaymentFromClientFooterFormModeChange={
            onSubsequentHearingPaymentFromClientFooterFormModeChange
          }
          onHaoSubsequentPaymentFromClientFooterSkipModeChange={
            onSubsequentHearingPaymentFromClientFooterSkipModeChange
          }
          onHaoSubsequentAttendFooterFormModeChange={
            onSubsequentHearingAttendFooterFormModeChange
          }
          onHaoSubsequentAttendFooterSkipModeChange={
            onSubsequentHearingAttendFooterSkipModeChange
          }
          onHaoSubsequentPostponementFooterFormModeChange={
            onSubsequentHearingPostponementFooterFormModeChange
          }
          onHaoOrderOfHearingFooterSendModeChange={
            onOrderOfHearingFooterSendModeChange
          }
          onHaoOrderOfHearingFooterSaveModeChange={
            onOrderOfHearingFooterSaveModeChange
          }
        />
      )}
    </div>
  );
}
